import {
  Fragment,
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

import type {
  ClinicianWorkbenchActionInput,
  ClinicianWorkbenchOutboundCallAction,
  ClinicianWorkbenchOutboundManualAnswerV1,
  ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
  ClinicianWorkbenchMedicationCatalogCandidateV1,
  ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ClinicianWorkbenchQuestionnaireDefinitionV1,
  PatientPortalFeatureFlagKeyV1,
  PatientPortalFeatureFlagsV1,
  PatientWorkspaceDocumentCompleteUploadResponseV1,
  PatientWorkspaceOnboardingCommandResponseV1,
  PatientWorkspaceSummaryResponseV1,
  TenantAdminClinicianOperationV1,
  TenantAdminClinicianOperationsResponseV1,
  TenantAdminClinicianOperationsSortDirection,
  TenantAdminClinicianOperationsSortKey,
  TenantAdminCommandInputV1,
  TenantAdminCommandResponseV1,
  TenantAdminPatientOperationsCareTypeFilter,
  TenantAdminPatientOperationsResponseV1,
  TenantAdminPatientOperationsSortDirection,
  TenantAdminPatientOperationsSortKey,
  TenantAdminPatientPortalRoute,
  TenantAdminReviewQueueId,
  TenantAdminReviewQueueItemsResponseV1,
  TenantAdminSummaryConnectorRegistrationV1,
  TenantAdminSummaryFeatureFlagV1,
  TenantAdminSummaryPatientOperationV1,
  TenantAdminSummaryResponseV1,
  WorkforceSessionSubjectSummaryV1,
  WorkforceSessionSummaryV1,
} from "@amplecare/contracts";
import {
  patientPortalDefaultFeatureFlagsV1,
  patientPortalFeatureDefinitionsV1,
} from "@amplecare/contracts";
import { designTokens } from "@amplecare/design-tokens";
import {
  defaultLocale,
  enUSLocaleDictionary,
  resolveLocale,
  translationKeys,
  type TranslationKey,
} from "@amplecare/i18n";
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  useWindowDimensions,
} from "react-native";

import {
  downloadClinicianWorkbenchShellOutboundQuestionnaireAnswersPdf,
  loadClinicianWorkbenchShellData,
  loadClinicianWorkbenchShellOutboundCalls,
  loadClinicianWorkbenchShellQuestionnaireDefinition,
  submitClinicianWorkbenchShellAction,
  submitClinicianWorkbenchShellOutboundCallsCommand,
  uploadClinicianWorkbenchShellDocument,
  type ClinicianWorkbenchOutboundCallsShellData,
  type ClinicianWorkbenchShellData,
} from "./workbench-adapter.js";
import { resolveWorkbenchActionErrorFeedback } from "./workbench-fetch-transport.js";
import {
  resolveMobileRuntimeConfig,
  resolveCanonicalWorkspacePathname,
  shouldCanonicalizeWorkspacePathname,
  type MobileRuntimeConfig,
} from "../config/runtime-config.js";
import {
  areClinicianWorkbenchRouteStatesEqual,
  clearWorkbenchRouteStateFromWindow,
  readWorkbenchRouteStateFromWindow,
  replaceWorkbenchPathnameInWindow,
  subscribeToWorkbenchRouteState,
  writeWorkbenchRouteStateToWindow,
  type ClinicianWorkbenchRouteState,
} from "./route-state.js";
import { AdminConsolePage } from "./admin/AdminConsolePage.js";
import { PlatformConsolePage } from "./admin/PlatformConsolePage.js";
import {
  buildAdminAnalyticsSummaryRequest,
  resolveAdminAnalyticsRangeSelection,
  type AdminAnalyticsRangeSelection,
} from "./admin/admin-shared.js";
import { styles } from "./AppShell.styles.js";
import { LineIcon, type LineIconName } from "./line-icons.js";
import {
  workforceConfigurationDialog,
  workforceOperationalLayout,
  workforceOperationalTypography,
  workforceResponsiveBreakpoints,
} from "./workforce-design-system.js";
import {
  parseClinicianStructuredRecordFields,
  serializeClinicianStructuredRecordFields,
  type ClinicianStructuredRecordFields,
  validateClinicianStructuredRecordFields,
} from "./clinician/structured-record.js";
import {
  hasClinicianUnsavedWorkspaceChanges,
  hasUnsavedQuestionnaireReviewNote,
  isClinicianEditorDirty,
  isClinicianInlineEditorDirty,
  isMatchingMedicationResolutionOwner,
  isMatchingOutboundCallOwner,
  isMatchingOwnedCallConfirmation,
  isStaleOutboundCallStateError,
  resolveClinicianOutboundCallErrorText,
  shouldLoadClinicianQuestionnaireDefinition,
} from "./clinician/editor-safety.js";
import {
  shouldOpenClinicianDetailDialog,
  shouldRenderClinicianDetailSurface,
} from "./clinician/detail-routing.js";
import {
  buildClinicianPatientIdentityText,
  formatClinicianQuestionnaireAnswerText,
} from "./clinician/presentation.js";
import { resolveQuestionnaireReviewPromptText } from "./clinician/questionnaire-entry-core.js";
import {
  buildQuestionnaireReviewSections,
  questionnaireReviewAllSectionsKey,
  questionnaireAnswerChanged,
  questionnaireAnswerHasMedicationDetails,
  questionnaireAnswerMatchesFilter,
  questionnaireAnswerNeedsAttention,
  questionnaireReviewHasHiddenAttention,
  type QuestionnaireReviewFilterId,
} from "./clinician/questionnaire-review.js";
import { resolveHorizontalTabNavigationIndex } from "./clinician/keyboard-navigation.js";
import {
  getMessageBodyPanelBackground,
  getMessageBodyPanelBorderColor,
  getMessageBubbleBackground,
  getMessageBubbleBorderColor,
  getMessageDeliveryBackground,
  getMessageDeliveryTextColor,
  getMessageReviewBackground,
  getMessageReviewTextColor,
  getMessageSenderTextColor,
  getMessageTextColor,
  getMessageTimestampTextColor,
  buildReviewFirstChatMessages,
  renderChatCard,
  renderDetailsCard,
  renderPatientListCard,
  renderWorkbenchPanelCard,
  type ClinicianClinicalNoteEditorInput,
  type ClinicianClinicalRecordEditorInput,
  type ClinicianCallTypeFilter,
  type ClinicianCenterPaneView,
  type ClinicianListViewMode,
  type ClinicianComposerMode,
  type ClinicianInlineClinicalEditorState,
} from "./clinician/renderers.js";
import { resolvePatientBrand } from "./patient/brand.js";
import type { CalendarRange } from "./clinician/list-calendar.js";
import type { ClinicianPatientProfileFocusTarget } from "./clinician/patient-profile.js";
import {
  createWebGradientBackgroundStyle,
  createWebOrderStyle,
  createWebViewportMinHeightStyle,
  renderLegacySurfaceHeader,
  renderWorkbenchActionButton,
  renderWorkspaceFilterChip,
} from "./shell-primitives.js";
import {
  getLegacyDetailPreviewPalette,
  getLegacySectionChromeColors,
  patientDesktopPalette,
  patientMobilePalette,
  humanizeWorkspaceKey,
  resolveLegacyCompactLeadText,
  resolveLegacyPreviewFallbackPrimaryText,
  resolveLegacyPreviewFallbackSecondaryText,
  resolveMessageDeliveryLabelKey,
  resolvePatientAccessModeLabel,
  resolvePatientInitials,
  resolveWorkforceSessionRoleLabel,
  type AppShellTheme,
  type LegacySectionChromeTone,
} from "./shell-core.js";
import {
  renderLegacyPreviewListRow,
  renderLegacySectionSummaryRail,
} from "./shell-legacy-ui.js";
import {
  listWorkforceSessionSubjects,
  loginWorkforceSession,
  logoutWorkforceSession,
  matchesWorkforceSessionConfig,
  readCurrentWorkforceSession,
  resolveAutoSelectedWorkforceSessionSubjectKey,
  type WorkforceSessionLoginCredentials,
} from "./workforce-session-client.js";
import {
  loadTenantAdminClinicianOperations,
  loadTenantAdminPatientOperations,
  loadTenantAdminReviewQueueItems,
  loadTenantAdminSummary,
  submitTenantAdminCommand,
} from "./admin-console-client.js";
import { useWorkspaceRefreshScope } from "./refresh/useWorkspaceRefreshScope.js";
import {
  buildClinicianWorkbenchOutboundCallsRefreshAliases,
  buildClinicianWorkbenchOutboundCallsRefreshKey,
  buildClinicianWorkbenchRefreshAliases,
  buildClinicianWorkbenchRefreshKey,
  buildPatientWorkspaceRefreshAlias,
  buildPatientWorkspaceRefreshKey,
  buildTenantAdminClinicianOperationsRefreshAlias,
  buildTenantAdminOutboundRefreshAlias,
  buildTenantAdminPatientOperationsRefreshAlias,
  buildTenantAdminReviewQueueRefreshAlias,
  buildTenantAdminSummaryRefreshAlias,
  buildTenantAdminSummaryRefreshKey,
  buildWorkspaceRefreshAliasesForChangedScopes,
  invalidateWorkspaceRefreshScopes,
  refreshWorkspaceRefreshScopeNow,
  workspaceRefreshCadenceMs,
} from "./refresh/workspace-refresh-service.js";
import {
  loadPatientWorkspaceSummary,
  openPatientWorkspaceDocumentDownload,
  recordPatientWorkspaceMessage,
  recordPatientWorkspaceOnboarding,
  submitSupportSubmission,
  uploadPatientWorkspaceDocument,
} from "./patient-workspace-client.js";
import {
  pickBrowserDocumentUploadFile,
  supportsBrowserDocumentUpload,
} from "./document-upload-client.js";
import { isPatientWorkspaceScaffoldMode } from "./patient-workspace-scaffold.js";
import {
  findLatestReadablePatientAgentMessage,
  usePatientWorkspaceReadAloud,
  usePatientWorkspaceVoiceInput,
  type PatientWorkspaceReadAloudControls,
  type PatientWorkspaceVoiceInputControls,
} from "./patient/patient-speech.js";
import {
  buildPatientMedicationReminderRows,
  createPatientMedicationReminderPreferences,
  formatPatientMobilePreferenceTime,
  renderPatientMedicationPreferenceSlider,
  renderPatientMobileActionListScreen,
  renderPatientMobileBottomNav,
  renderPatientMobileChatScreen,
  renderPatientMobileDocumentsScreen,
  renderPatientMobileFooter,
  renderPatientMobileHeader,
  renderPatientMobileListRow,
  renderPatientMobileMedicationsScreen,
  renderPatientMobilePanelContent,
  renderPatientMobileSectionHeader,
  renderPatientMobileSettingsScreen,
  renderPatientWorkspaceMobileAuthScreen,
  renderPatientWorkspaceMobileDisclaimerScreen,
  renderPatientWorkspaceMobilePanelShell,
  type PatientMobileAuthStep,
  type PatientMobileDetailState,
  type PatientMobileMedicationMode,
  type PatientMobilePanelId,
  type PatientMedicationReminderPreferences,
  type PatientMedicationReminderSlotId,
  type PatientMedicationReminderTimeUpdateInput,
} from "./patient/patient-mobile.js";
import {
  renderPatientWorkspaceDesktopAuthScreen,
  renderPatientWorkspaceDesktopShell,
  type PatientDesktopCareDetailModalState,
} from "./patient/patient-desktop.js";
import {
  buildPatientMedicationWorkspaceRows,
  buildPatientWorkspaceAppointmentItems,
  buildPatientWorkspaceDocumentRows,
  buildPatientWorkspaceOnboardingItems,
  buildPatientWorkspaceProcedureItems,
  buildPatientWorkspaceUpdateRows,
  filterPatientWorkspaceDocuments,
  resolvePatientDocumentType,
  sanitizePatientWorkspaceSummaryForProductDisplay,
  type PatientDocumentAvailabilityFilter,
  type PatientDocumentDateRangeFilter,
  type PatientDocumentTypeFilter,
  type PatientWorkspaceActionableListItem,
  type PatientWorkspaceSummarySurfaceRow,
} from "./patient/patient-workspace-builders.js";
import {
  renderPatientWorkspaceNavigationBar,
  renderPatientWorkspaceQuickActionBand,
  renderPatientWorkspaceSummarySurface,
  type PatientWorkspaceNavigationItem,
} from "./patient/patient-workspace-chrome.js";
import {
  createPatientSupportInitialFormState,
  getPatientSupportPageUrl,
  getPatientSupportUserAgent,
  isValidPatientSupportEmail,
  renderLegacyWorkbenchFooter,
  renderPatientDesktopFooter,
  renderPatientSupportModal,
  resolvePatientSupportSubmissionKind,
  type PatientSupportFormField,
  type PatientSupportFormState,
  type PatientSupportModalState,
} from "./patient/patient-support.js";
import {
  legacyBackgroundTextureSource,
  legacyWebShellAssets,
  renderPatientDesktopAgentAvatarMedia,
  renderPatientDesktopAgentIdentityAvatarMedia,
  renderPatientMobileChatAvatarMedia,
} from "./patient/patient-assets.js";
import {
  clearPatientLegacyOnboardingFlowFromWindow,
  clampPatientLegacyOnboardingStepIndex,
  patientLegacyOnboardingDefaultCareType,
  patientLegacyOnboardingFinalStepIndex,
  readPatientLegacyOnboardingFlowFromWindow,
  renderPatientLegacyOnboardingOverlay,
  resolvePatientLegacyOnboardingCareType,
  toPatientOnboardingLayoutRect,
  type PatientLegacyOnboardingCareType,
  type PatientLegacyOnboardingFlowState,
  type PatientOnboardingCommandInput,
  type PatientOnboardingLayoutRects,
  type PatientOnboardingRegionKey,
  writePatientLegacyOnboardingFlowToWindow,
} from "./patient/patient-onboarding.js";
import type {
  ChatMessageViewModel,
  ClinicianWorkbenchRequestState,
  DetailRowKind,
  DetailSectionViewModel,
  DetailTabId,
  PatientListTabId,
  PatientListSummaryViewModel,
  PatientWorkspaceViewModel,
  VoiceCallSummaryViewModel,
  WorkbenchPanelId,
  WorkbenchPanelViewModel,
} from "./types.js";

type VoiceCallTranscriptModalState = {
  /** Owner: the modal renders and acts only for this patient. */
  patientId: string;
  patientName: string;
  voiceCall: VoiceCallSummaryViewModel;
} | null;

type ClinicianOutboundCallConfirmModalState = {
  action: ClinicianWorkbenchOutboundCallAction;
  call: ClinicianWorkbenchOutboundQuestionnaireCallV1;
  errorText: string | null;
  patientId: string;
  patientName: string;
  warnings: string[];
} | null;

type ClinicianMedicationResolutionState = {
  /** Owner: the resolver renders and submits only for this exact call entry. */
  patientId: string;
  sessionId: string | null;
  targetId: string;
  questionKey: string;
  entryIndex: number;
  query: string;
  isBusy: boolean;
  candidates: ClinicianWorkbenchMedicationCatalogCandidateV1[] | null;
  selectedDrugName: string | null;
  selectedStrength: string | null;
  selectedRxcui: string | null;
  errorText: string | null;
} | null;

type ClinicianProfilePendingNavigation =
  | {
      kind: "center-pane";
      value: ClinicianCenterPaneView;
    }
  | {
      kind: "compact-pane";
      value: "queue";
    }
  | {
      historyAction?: "back";
      kind: "patient";
      pathname?: string;
      routeState?: ClinicianWorkbenchRouteState;
      value: string | null;
    }
  | {
      kind: "sign-out";
      value: null;
    };

type ClinicianHistoryRestoration =
  | {
      kind: "dismiss-profile-dialog";
    }
  | {
      kind: "open-navigation";
      navigation: ClinicianProfilePendingNavigation;
    };

type ClinicianClinicalEditorModalState =
  | ({
      detailText: string;
      kind: "record";
      materialChangeToken: string;
      patientId: string;
      patientName: string;
      structuredFields: ClinicianStructuredRecordFields;
      title: string;
    } & ClinicianClinicalRecordEditorInput)
  | ({
      bodyText: string;
      kind: "note";
      materialChangeToken: string;
      patientId: string;
      patientName: string;
      sourceComposerMode?: Exclude<ClinicianComposerMode, "reply">;
      title: string;
      visibility: "patient-visible" | "clinical";
    } & ClinicianClinicalNoteEditorInput)
  | null;

type WorkforceSessionGateState =
  | "idle"
  | "loading"
  | "password-required"
  | "selection-required"
  | "ready"
  | "error";

const initialMyPatientIds: string[] = [];
const defaultSelectedPatientId: string | null = null;
// White-label assistant naming for the patient workspace surfaces that live
// in this file (mirrors the module-level pattern in ./patient/*).
const patientWorkspaceBrand = resolvePatientBrand();

function resolveDetailTabIdForWorkbenchPanel(
  panelId: WorkbenchPanelId,
): DetailTabId {
  if (panelId === "tasks") {
    return "case-summary";
  }

  if (panelId === "documents") {
    return "documents";
  }

  return "information";
}

function buildCurrentRequestState(input: {
  deferredSearchQuery: string;
  myPatientIds: string[];
  requiresAttentionOnly: boolean;
  selectedDetailTabId: DetailTabId;
  selectedPatientId: string | null;
  selectedPatientTabId: PatientListTabId;
}): ClinicianWorkbenchRequestState {
  return {
    selectedPatientTabId: input.selectedPatientTabId,
    requiresAttentionOnly: input.requiresAttentionOnly,
    searchQuery:
      input.deferredSearchQuery.length > 0 ? input.deferredSearchQuery : null,
    selectedPatientId: input.selectedPatientId,
    selectedDetailTabId: input.selectedDetailTabId,
    myPatientIds: input.myPatientIds,
  };
}

function buildClinicianWorkbenchShellCacheKey(
  requestState: ClinicianWorkbenchRequestState,
  runtimeConfig: MobileRuntimeConfig,
): string {
  return JSON.stringify({
    tenantId: runtimeConfig.requestTenantId,
    siteId: runtimeConfig.requestSiteId,
    clinicianUserId: runtimeConfig.requestClinicianUserId,
    workforceSessionSubjectKey: runtimeConfig.workforceSessionSubjectKey,
    selectedPatientTabId: requestState.selectedPatientTabId,
    requiresAttentionOnly: requestState.requiresAttentionOnly,
    searchQuery: requestState.searchQuery,
    selectedPatientId: requestState.selectedPatientId,
    ...(runtimeConfig.clinicianWorkbenchTransportMode === "http"
      ? {}
      : {
          myPatientIds: requestState.myPatientIds,
        }),
  });
}

function shareClinicianWorkbenchShellOwner(
  leftCacheKey: string | null,
  rightCacheKey: string,
): boolean {
  if (leftCacheKey == null) {
    return false;
  }

  try {
    const left = JSON.parse(leftCacheKey) as Record<string, unknown>;
    const right = JSON.parse(rightCacheKey) as Record<string, unknown>;
    const ownerKeys = [
      "tenantId",
      "siteId",
      "clinicianUserId",
      "workforceSessionSubjectKey",
    ] as const;

    return ownerKeys.every((key) => Object.is(left[key], right[key]));
  } catch {
    return false;
  }
}

function buildWorkspaceMutationRefreshAliases(input: {
  clinicianUserId: string;
  tenantId: string;
}): string[] {
  return [
    buildTenantAdminSummaryRefreshAlias(input.tenantId),
    buildTenantAdminPatientOperationsRefreshAlias(input.tenantId),
    buildTenantAdminClinicianOperationsRefreshAlias(input.tenantId),
    buildTenantAdminReviewQueueRefreshAlias(input.tenantId),
    buildTenantAdminOutboundRefreshAlias(input.tenantId),
    buildPatientWorkspaceRefreshAlias(input.tenantId),
    ...buildClinicianWorkbenchRefreshAliases({
      clinicianUserId: input.clinicianUserId,
      tenantId: input.tenantId,
    }),
  ];
}

function buildPatientWorkspaceSummaryRevision(
  payload: PatientWorkspaceSummaryResponseV1,
): string {
  return [
    payload.projectionVersion,
    payload.generatedAt,
    payload.patient.materialChangeToken,
  ].join(":");
}

function buildClinicianWorkbenchSummaryRevision(
  payload: ClinicianWorkbenchShellData,
): string {
  return [
    payload.response.projectionVersion,
    payload.response.generatedAt,
    payload.response.selectedPatient?.materialChangeToken ?? "",
    ...payload.response.patientList.map(
      (patient) => `${patient.patientId}:${patient.materialChangeToken}`,
    ),
  ].join("|");
}

function resolveSuccessKeyForWorkbenchAction(
  action: ClinicianWorkbenchActionInput,
): TranslationKey {
  switch (action.kind) {
    case "add-patient-to-my-list":
      return translationKeys.shell.workspaceActionAddToMyPatientsSuccess;
    case "complete-task-assignment":
      return translationKeys.shell.workspaceActionCompleteTaskSuccess;
    case "mark-document-reviewed":
      return translationKeys.shell.workspaceActionMarkDocumentReviewedSuccess;
    case "send-reply":
      return translationKeys.shell.workspaceActionReplySuccess;
    case "approve-message-review":
      return translationKeys.shell.workspaceActionApproveSuccess;
    case "reject-message-review":
      return translationKeys.shell.workspaceActionRejectSuccess;
    case "refresh-case-summary":
      return translationKeys.shell.workspaceActionRefreshSummarySuccess;
    case "upsert-patient-record":
      return translationKeys.shell.workspaceActionPatientRecordSuccess;
    case "clear-patient-attention":
      return translationKeys.shell.workspaceActionClearAttentionSuccess;
    case "upsert-clinical-record":
    case "archive-clinical-record":
    case "reactivate-clinical-record":
    case "upsert-clinical-note":
    case "archive-clinical-note":
    case "reactivate-clinical-note":
    case "mark-red-flags-reviewed":
      return translationKeys.shell.workspaceActionRefreshSummarySuccess;
  }
}

function formatPatientHumanNamePart(part: string): string {
  return part
    .trim()
    .split(/(\s+|-|')/u)
    .map((segment) => {
      if (!/^[A-Za-z]/u.test(segment)) {
        return segment;
      }

      return `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}`;
    })
    .join("");
}

function formatPatientDisplayName(
  displayName: string | null | undefined,
): string {
  const trimmed = displayName?.trim() ?? "";

  if (!trimmed) {
    return "Patient";
  }

  const commaParts = trimmed
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  if (commaParts.length >= 2) {
    const lastName = commaParts[0] ?? "";
    const givenNames = commaParts.slice(1).join(" ");

    return [givenNames, lastName]
      .filter((part) => part.length > 0)
      .map(formatPatientHumanNamePart)
      .join(" ");
  }

  return trimmed.split(/\s+/u).map(formatPatientHumanNamePart).join(" ");
}

function resolvePatientWorkspaceGivenName(
  displayName: string | null | undefined,
): string {
  const firstName = formatPatientDisplayName(displayName).split(/\s+/u)[0];

  return firstName && firstName.length > 0 ? firstName : "there";
}

function formatPatientProfileDate(value: string | null | undefined): string | null {
  const trimmedValue = value?.trim() ?? "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/u.exec(trimmedValue);

  if (!match) {
    return trimmedValue.length > 0 ? trimmedValue : null;
  }

  const [, yearText, monthText, dayText] = match;
  const year = Number.parseInt(yearText ?? "", 10);
  const month = Number.parseInt(monthText ?? "", 10);
  const day = Number.parseInt(dayText ?? "", 10);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return trimmedValue;
  }

  return new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function readPatientProfileText(
  value: string | null | undefined,
  fallback = "Not recorded",
): string {
  const trimmedValue = value?.trim() ?? "";

  return trimmedValue.length > 0 ? trimmedValue : fallback;
}

function resolveDocumentBadgeText(labelText: string): string {
  const normalized = labelText
    .trim()
    .split(/\s+/u)
    .filter((part) => part.length > 0);

  if (normalized.length === 0) {
    return "DOC";
  }

  const joined = normalized
    .slice(0, 2)
    .map((part) => part.slice(0, 2))
    .join("")
    .toUpperCase();

  return joined.slice(0, 3) || "DOC";
}

function parseWorkbenchDateTimeText(valueText: string): Date | null {
  const trimmed = valueText.trim();

  if (!/^\d{4}-\d{2}-\d{2}/u.test(trimmed)) {
    return null;
  }

  const normalized = trimmed.includes("T")
    ? trimmed
    : trimmed.replace(" ", "T");
  const withSeconds = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/u.test(normalized)
    ? `${normalized}:00`
    : normalized;
  const parsed = new Date(withSeconds);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatLegacyPreviewGroupLabel(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(date);
  target.setHours(0, 0, 0, 0);

  const dayDelta = Math.round(
    (target.getTime() - today.getTime()) / 86_400_000,
  );

  if (dayDelta === 0) {
    return "Today";
  }

  if (dayDelta === 1) {
    return "Tomorrow";
  }

  if (dayDelta > 1 && dayDelta < 7) {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
    }).format(date);
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatLegacyPreviewDatePrimaryText(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  })
    .format(date)
    .toUpperCase();
}

function formatLegacyPreviewDateSecondaryText(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
  })
    .format(date)
    .toUpperCase();
}

function formatLegacyPreviewVerboseText(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
  }).format(date);
}

function isAdminWorkspaceRole(roleKey: string): boolean {
  return [
    "tenant-admin",
    "site-manager",
    "department-manager",
    "program-manager",
    "care-team-lead",
    "integration-operator",
    "auditor",
    "support-read-only",
  ].includes(roleKey);
}

function isPatientWorkspaceRole(roleKey: string): boolean {
  return roleKey === "patient";
}

function isPlatformOperatorRole(roleKey: string): boolean {
  return roleKey === "platform-operator";
}

function canManageOutboundQuestionnaireReview(roleKeys: readonly string[]): boolean {
  return roleKeys.some((roleKey) =>
    [
      "tenant-admin",
      "program-manager",
      "care-team-lead",
      "department-manager",
      "site-manager",
    ].includes(roleKey),
  );
}

function resolveWorkspaceMode(
  roleKeys: readonly string[],
): "platform" | "admin" | "clinician" | "patient" {
  if (roleKeys.some((roleKey) => isPlatformOperatorRole(roleKey))) {
    return "platform";
  }

  if (roleKeys.some((roleKey) => isAdminWorkspaceRole(roleKey))) {
    return "admin";
  }

  if (roleKeys.some((roleKey) => isPatientWorkspaceRole(roleKey))) {
    return "patient";
  }

  return "clinician";
}

/**
 * Whether a session's roles SATISFY a workspace. resolveWorkspaceMode picks
 * one dominant mode, which is right for chrome/tone — but as a login gate a
 * strict mode comparison locks out supersets: the demo admin account carries
 * clinician + tenant-admin + platform-operator, resolves to "platform", and
 * an exact match rejected it on /admin ("Use an admin account…") even though
 * it IS an admin account. The gate's real job is only to keep accounts
 * without the required role (chiefly patients) off workforce surfaces.
 */
function sessionSatisfiesWorkspaceMode(
  expectedWorkspaceMode: "platform" | "admin" | "clinician" | "patient",
  roleKeys: readonly string[],
): boolean {
  if (expectedWorkspaceMode === "platform") {
    return roleKeys.some((roleKey) => isPlatformOperatorRole(roleKey));
  }

  if (expectedWorkspaceMode === "admin") {
    return roleKeys.some((roleKey) => isAdminWorkspaceRole(roleKey));
  }

  if (expectedWorkspaceMode === "patient") {
    return roleKeys.some((roleKey) => isPatientWorkspaceRole(roleKey));
  }

  // Clinician surfaces admit any workforce account (clinician, admin roles,
  // platform operators) — only patient-only accounts stay out.
  return roleKeys.some(
    (roleKey) =>
      roleKey === "clinician" ||
      isAdminWorkspaceRole(roleKey) ||
      isPlatformOperatorRole(roleKey),
  );
}

function resolveWorkspaceModeTone(
  roleKeys: readonly string[],
): LegacySectionChromeTone {
  const workspaceMode = resolveWorkspaceMode(roleKeys);

  if (workspaceMode === "platform") {
    return "neutral";
  }

  if (workspaceMode === "admin") {
    return "warning";
  }

  if (workspaceMode === "patient") {
    return "info";
  }

  return "accent";
}

type WorkforceThemeName = "warmth" | "legacy-warm" | "blue-grey";

function normalizeWorkforceThemeName(
  value: string | null | undefined,
): WorkforceThemeName {
  const normalizedValue = value?.trim().toLowerCase();

  return normalizedValue === "blue-grey" ||
    normalizedValue === "bluegray" ||
    normalizedValue === "blue_grey"
    ? "blue-grey"
    : normalizedValue === "legacy-warm" ||
        normalizedValue === "legacy_warm" ||
        normalizedValue === "warm-neutral"
      ? "legacy-warm"
      : "warmth";
}

function resolvePreferredWorkforceThemeName(): WorkforceThemeName {
  if (typeof window === "undefined") {
    return "warmth";
  }

  const urlTheme = new URLSearchParams(window.location.search).get(
    "acWorkforceTheme",
  );

  if (urlTheme) {
    const themeName = normalizeWorkforceThemeName(urlTheme);
    window.localStorage?.setItem("amplecare.vnext.workforce-theme", themeName);
    return themeName;
  }

  return normalizeWorkforceThemeName(
    window.localStorage?.getItem("amplecare.vnext.workforce-theme"),
  );
}

function resolveWorkforceTheme(themeName: WorkforceThemeName): AppShellTheme {
  if (themeName === "blue-grey") {
    return designTokens.semanticTheme.workforceBlueGrey;
  }

  if (themeName === "legacy-warm") {
    return designTokens.semanticTheme.workforceWarm;
  }

  return designTokens.semanticTheme.workforceWarmth;
}

function buildWorkforceSessionSupportText(input: {
  roleKeys: readonly string[];
  patientAccessMode: string;
  siteId: string | null;
}): string {
  if (input.roleKeys.includes("patient")) {
    return [
      "Patient portal",
      input.siteId ? humanizeWorkspaceKey(input.siteId) : "Tenant-wide",
    ].join(" | ");
  }

  const parts = [
    resolveWorkforceSessionRoleLabel(input.roleKeys),
    resolvePatientAccessModeLabel(input.patientAccessMode),
    input.siteId ? humanizeWorkspaceKey(input.siteId) : "Tenant-wide",
  ];

  return parts.join(" | ");
}

function resolveWorkforceSessionPreferenceStorageKey(tenantId: string): string {
  return `amplecare.vnext.workforce-session.last-subject.${tenantId}`;
}

function readStoredWorkforceSubjectKey(tenantId: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const value = window.localStorage.getItem(
      resolveWorkforceSessionPreferenceStorageKey(tenantId),
    );

    return typeof value === "string" && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeStoredWorkforceSubjectKey(
  tenantId: string,
  subjectKey: string | null,
): void {
  if (typeof window === "undefined") {
    return;
  }

  const storageKey = resolveWorkforceSessionPreferenceStorageKey(tenantId);

  try {
    if (subjectKey) {
      window.localStorage.setItem(storageKey, subjectKey);
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch {}
}

function prioritizeWorkforceSubjects(
  subjects: readonly WorkforceSessionSubjectSummaryV1[],
  preferredSubjectKey: string | null,
): WorkforceSessionSubjectSummaryV1[] {
  if (!preferredSubjectKey) {
    return [...subjects];
  }

  return [...subjects].sort((left, right) => {
    const leftRank = left.subjectKey === preferredSubjectKey ? 0 : 1;
    const rightRank = right.subjectKey === preferredSubjectKey ? 0 : 1;

    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }

    return (left.displayName ?? left.subjectKey).localeCompare(
      right.displayName ?? right.subjectKey,
    );
  });
}

function matchesPreferredWorkspaceMode(
  roleKeys: readonly string[],
  preferredWorkspaceMode: "platform" | "admin" | "clinician" | "patient" | null,
): boolean {
  return (
    preferredWorkspaceMode == null ||
    sessionSatisfiesWorkspaceMode(preferredWorkspaceMode, roleKeys)
  );
}

function findWorkforceSubjectByKey(
  subjects: readonly WorkforceSessionSubjectSummaryV1[],
  subjectKey: string | null,
): WorkforceSessionSubjectSummaryV1 | null {
  if (!subjectKey) {
    return null;
  }

  return (
    subjects.find((subject) => subject.subjectKey === subjectKey) ?? null
  );
}

function renderPatientWorkspaceMobileShell(input: {
  activeWorkforceSession: WorkforceSessionSummaryV1 | null;
  canSwitchWorkspaces: boolean;
  detailState: PatientMobileDetailState;
  draftValue: string;
  featureFlags: PatientPortalFeatureFlagsV1;
  formState: PatientSupportFormState;
  hasPatientWorkspaceError: boolean;
  isDocumentUploadSubmitting: boolean;
  isMessageCommandSubmitting: boolean;
  isOnboardingCommandSubmitting: boolean;
  isPatientWorkspaceLoading: boolean;
  layout: typeof designTokens.semanticLayout;
  layoutRects: PatientOnboardingLayoutRects;
  medicationReminderPreferences: PatientMedicationReminderPreferences;
  medicationMode: PatientMobileMedicationMode;
  navItems: readonly PatientWorkspaceNavigationItem[];
  noticeText: string | null;
  onBackFromDetail: () => void;
  onChangeMedicationMode: (value: PatientMobileMedicationMode) => void;
  onChangeReminderTime: (
    slotId: PatientMedicationReminderSlotId,
    wakeUpTime: string,
  ) => void;
  onChangeSupportField: (field: PatientSupportFormField, value: string) => void;
  onClosePanel: () => void;
  onConversationContentSizeChange: () => void;
  onCopyCallTranscript: (voiceCall: PatientWorkspaceVoiceCallSummary) => void;
  onDraftChange: (value: string) => void;
  onDownloadDocument: (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => void;
  onUploadDocument: () => void;
  onNavigateTab: (tabId: PatientWorkspaceTabId) => void;
  onNoticeChange: (value: string | null) => void;
  onOnboardingBack: () => void;
  onOnboardingNext: () => void;
  onOnboardingRegionLayout: (
    regionKey: PatientOnboardingRegionKey,
    event: LayoutChangeEvent,
  ) => void;
  onOnboardingSkip: () => void;
  onOpenDetail: (detail: PatientMobileDetailState) => void;
  onOpenFooterLink: (panelId: PatientMobilePanelId) => void;
  onOpenSupportPanel: (input: PatientSupportModalState) => void;
  onRequestCallback: () => void;
  onReviewCall: (voiceCall: PatientWorkspaceVoiceCallSummary) => void;
  onSetRemindersEnabled: (enabled: boolean) => void;
  onSendMessage: (input: PatientMessageCommandInput) => Promise<boolean>;
  onSignOut: () => void;
  onStartOnboarding: () => void;
  onSubmitSupport: () => void;
  onUpdateReminderTime: (
    input: PatientMedicationReminderTimeUpdateInput,
  ) => void;
  onboardingFlow: PatientLegacyOnboardingFlowState;
  panelId: PatientMobilePanelId | null;
  patientCallSearchQuery: string;
  patientConversationScrollRef: RefObject<ScrollView | null>;
  readAloudControls: PatientWorkspaceReadAloudControls;
  selectedPatientWorkspaceTabId: PatientWorkspaceTabId;
  setPatientCallSearchQuery: (value: string) => void;
  setSelectedPatientWorkspaceTabId: (value: PatientWorkspaceTabId) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  supportModalState: PatientSupportModalState | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  voiceInputControls: PatientWorkspaceVoiceInputControls;
}) {
  const {
    activeWorkforceSession,
    canSwitchWorkspaces,
    detailState,
    draftValue,
    featureFlags,
    formState,
    hasPatientWorkspaceError,
    isDocumentUploadSubmitting,
    isMessageCommandSubmitting,
    isOnboardingCommandSubmitting,
    isPatientWorkspaceLoading,
    layout,
    layoutRects,
    medicationReminderPreferences,
    medicationMode,
    navItems,
    noticeText,
    onBackFromDetail,
    onChangeMedicationMode,
    onChangeReminderTime,
    onChangeSupportField,
    onClosePanel,
    onConversationContentSizeChange,
    onCopyCallTranscript,
    onDraftChange,
    onDownloadDocument,
    onUploadDocument,
    onNavigateTab,
    onNoticeChange,
    onOnboardingBack,
    onOnboardingNext,
    onOnboardingRegionLayout,
    onOnboardingSkip,
    onOpenDetail,
    onOpenFooterLink,
    onOpenSupportPanel,
    onRequestCallback,
    onReviewCall,
    onSetRemindersEnabled,
    onSendMessage,
    onSignOut,
    onStartOnboarding,
    onSubmitSupport,
    onUpdateReminderTime,
    onboardingFlow,
    panelId,
    patientCallSearchQuery,
    patientConversationScrollRef,
    readAloudControls,
    selectedPatientWorkspaceTabId,
    setPatientCallSearchQuery,
    setSelectedPatientWorkspaceTabId,
    summary,
    supportModalState,
    theme,
    tokens,
    voiceInputControls,
  } = input;
  const updateRows = buildPatientWorkspaceUpdateRows(summary);
  const appointmentItems = featureFlags["patient.feature.appointments"]
    ? buildPatientWorkspaceAppointmentItems(summary, updateRows)
    : [];
  const procedureItems = featureFlags["patient.feature.procedures"]
    ? buildPatientWorkspaceProcedureItems(summary, updateRows)
    : [];
  const medicationRows = featureFlags["patient.feature.medications"]
    ? buildPatientMedicationWorkspaceRows(summary, updateRows)
    : [];
  const reviewPatientMobileDocumentWithAgent = (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => {
    onNoticeChange(`Sending document review to ${patientWorkspaceBrand.assistantName}.`);
    void onSendMessage({
      bodyText: buildPatientWorkspaceDocumentReviewPrompt(document),
    }).then((wasApplied) => {
      onNoticeChange(
        wasApplied
          ? `Document review request saved. Check messages for ${patientWorkspaceBrand.assistantName}'s response.`
          : "The document review request could not be saved. Please retry after the workspace refreshes.",
      );

      if (wasApplied) {
        onNavigateTab("messages");
      }
    });
  };
  const reviewPatientMobileActionItemWithAgent = (
    tabId: "appointments" | "procedures",
    item: {
      detailRows: ReadonlyArray<{
        labelText: string;
        valueText: string;
      }>;
      subtitle?: string | null;
      title: string;
    },
  ) => {
    const kindText = tabId === "appointments" ? "appointment" : "procedure";
    const detailText = item.detailRows
      .map((row) => `${row.labelText}: ${row.valueText}`)
      .join("; ");

    onNoticeChange(`Sending ${kindText} review to ${patientWorkspaceBrand.assistantName}.`);
    void onSendMessage({
      bodyText: [
        `Please review this ${kindText} with me: ${item.title}.`,
        item.subtitle ? `Schedule: ${item.subtitle}.` : null,
        detailText ? `Details: ${detailText}.` : null,
        "Summarize what matters and tell me what I should ask next.",
      ]
        .filter((part): part is string => typeof part === "string")
        .join(" "),
    }).then((wasApplied) => {
      onNoticeChange(
        wasApplied
          ? `Review request saved. Check messages for ${patientWorkspaceBrand.assistantName}'s response.`
          : "The review request could not be saved. Please retry after the workspace refreshes.",
      );

      if (wasApplied) {
        onNavigateTab("messages");
      }
    });
  };
  const reviewPatientMobileMedicationWithAgent = (
    row: {
      labelText: string;
      noteText: string;
      statusText?: string | null;
    },
  ) => {
    onNoticeChange(`Sending medication review to ${patientWorkspaceBrand.assistantName}.`);
    void onSendMessage({
      bodyText: [
        `Please review this medication with me: ${row.labelText}.`,
        row.noteText ? `Instructions: ${row.noteText}.` : null,
        row.statusText ? `Status: ${row.statusText}.` : null,
        "Summarize what matters and tell me what I should ask next.",
      ]
        .filter((part): part is string => typeof part === "string")
        .join(" "),
    }).then((wasApplied) => {
      onNoticeChange(
        wasApplied
          ? `Medication review request saved. Check messages for ${patientWorkspaceBrand.assistantName}'s response.`
          : "The medication review request could not be saved. Please retry after the workspace refreshes.",
      );

      if (wasApplied) {
        onNavigateTab("messages");
      }
    });
  };
  const canOpenMessages = navItems.some((item) => item.id === "messages");
  const showQuestionBar =
    canOpenMessages && selectedPatientWorkspaceTabId !== "messages";
  const statusText = hasPatientWorkspaceError
    ? "The patient workspace could not load the current care summary."
    : isPatientWorkspaceLoading
      ? "Refreshing patient workspace."
      : null;

  return (
    <View
      onLayout={(event) => onOnboardingRegionLayout("root", event)}
      style={[
        styles.patientMobileRoot,
        { backgroundColor: patientMobilePalette.body },
      ]}
    >
      {renderPatientMobileHeader({
        layout,
        onAskQuestion: canOpenMessages
          ? () => setSelectedPatientWorkspaceTabId("messages")
          : undefined,
        onOpenProfile: () => setSelectedPatientWorkspaceTabId("settings"),
        onRegionLayout: onOnboardingRegionLayout,
        profileInitials: resolvePatientInitials(
          summary?.displayName ?? activeWorkforceSession?.displayName ?? "Patient",
        ),
        showQuestionBar,
      })}
      {selectedPatientWorkspaceTabId === "messages" ? (
        renderPatientMobileChatScreen({
          conversationMessages: mapConversationMessagesToChatViewModels(
            summary?.conversation.messages ?? [],
          ),
          draftValue,
          isMessageCommandSubmitting,
          layout,
          noticeText,
          onConversationContentSizeChange,
          onDraftChange,
          onNavigateTab,
          onOpenSupportPanel,
          onSendMessage,
          patientConversationScrollRef,
          readAloudControls,
          summary,
          voiceInputControls,
        })
      ) : (
        <ScrollView
          onLayout={(event) => onOnboardingRegionLayout("main", event)}
          style={styles.patientMobileContentScroll}
          contentContainerStyle={[
            styles.patientMobileScreenContent,
            {
              gap: 12,
              paddingHorizontal: 18,
              paddingVertical: 16,
            },
          ]}
        >
          {statusText ? (
            <Text
              style={[
                styles.metricLabel,
                {
                  color: hasPatientWorkspaceError
                    ? theme.criticalText
                    : patientMobilePalette.mutedText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  textAlign: "center",
                },
              ]}
            >
              {statusText}
            </Text>
          ) : null}
          {panelId
            ? renderPatientMobilePanelContent({
                formState,
                layout,
                onChangeSupportField,
                onClosePanel,
                onSubmitSupport,
                panelId,
                supportModalState,
                theme,
                tokens,
              })
            : selectedPatientWorkspaceTabId === "appointments"
              ? renderPatientMobileActionListScreen({
                  detailState,
                  detailTabId: "appointments",
                  emptyText: "No appointments are available yet.",
                  items: appointmentItems,
                  layout,
                  onBackFromDetail,
                  onOpenDetail,
                  onReviewItem: reviewPatientMobileActionItemWithAgent,
                  summary,
                  title: "Upcoming appointments",
                })
              : selectedPatientWorkspaceTabId === "procedures"
                ? renderPatientMobileActionListScreen({
                    detailState,
                    detailTabId: "procedures",
                    emptyText: "No procedures are available yet.",
                    items: procedureItems,
                    layout,
                    onBackFromDetail,
                    onOpenDetail,
                    onReviewItem: reviewPatientMobileActionItemWithAgent,
                    summary,
                    title: "Upcoming procedures",
                  })
                : selectedPatientWorkspaceTabId === "medications"
                  ? renderPatientMobileMedicationsScreen({
                      detailState,
                      layout,
                      medicationMode,
                      onBackFromDetail,
                      onChangeMedicationMode,
                      onOpenDetail,
                      onReviewMedication: reviewPatientMobileMedicationWithAgent,
                      rows: medicationRows,
                      summary,
                    })
                  : selectedPatientWorkspaceTabId === "documents"
                    ? renderPatientMobileDocumentsScreen({
                        detailState,
                        isUploadSubmitting: isDocumentUploadSubmitting,
                        layout,
                        onBackFromDetail,
                        onDownloadDocument,
                        onOpenDetail,
                        onReviewDocument: reviewPatientMobileDocumentWithAgent,
                        onUploadDocument,
                        summary,
                      })
                    : selectedPatientWorkspaceTabId === "calls"
                      ? renderPatientWorkspaceCallsSurface({
                          layout,
                          noticeText,
                          onCopyTranscript: onCopyCallTranscript,
                          onNavigateTab,
                          onRequestCallback,
                          onReviewCall,
                          onSearchQueryChange: setPatientCallSearchQuery,
                          searchQuery: patientCallSearchQuery,
                          summary,
                          theme,
                          tokens,
                        })
                    : selectedPatientWorkspaceTabId === "settings"
                      ? renderPatientMobileSettingsScreen({
                          activeWorkforceSession,
                          canSwitchWorkspaces,
                          layout,
                          medicationReminderPreferences,
                          noticeText,
                          onChangeReminderTime,
                          onSetRemindersEnabled,
                          onSignOut,
                          onStartOnboarding,
                          onUpdateReminderTime,
                          summary,
                        })
                      : renderPatientMobileHomeScreen({
                          appointmentItems,
                          enabledTabIds: navItems.map((item) => item.id),
                          layout,
                          medicationRows,
                          onNavigateTab,
                          summary,
                        })}
        </ScrollView>
      )}
      {renderPatientMobileBottomNav({
        layout,
        navItems,
        onNavigateTab,
        onRegionLayout: onOnboardingRegionLayout,
        selectedPatientWorkspaceTabId,
      })}
      {renderPatientMobileFooter({ layout, onOpenFooterLink })}
      {renderPatientLegacyOnboardingOverlay({
        flow: onboardingFlow,
        isCommandSubmitting: isOnboardingCommandSubmitting,
        isMediumLayout: false,
        layout,
        layoutRects,
        onBack: onOnboardingBack,
        onNext: onOnboardingNext,
        onSkip: onOnboardingSkip,
        theme,
        tokens,
      })}
    </View>
  );
}

function renderPatientMobileHomeScreen(input: {
  appointmentItems: PatientWorkspaceActionableListItem[];
  enabledTabIds: ReadonlyArray<PatientWorkspaceTabId>;
  layout: typeof designTokens.semanticLayout;
  medicationRows: ReturnType<typeof buildPatientMedicationWorkspaceRows>;
  onNavigateTab: (tabId: PatientWorkspaceTabId) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
}) {
  const {
    appointmentItems,
    enabledTabIds,
    layout,
    medicationRows,
    onNavigateTab,
    summary,
  } = input;
  const enabledTabIdSet = new Set(enabledTabIds);
  const givenName = resolvePatientWorkspaceGivenName(summary?.displayName);
  const documentRows = buildPatientWorkspaceDocumentRows(summary, 2);
  const currentMedicationPreviewRows = medicationRows.filter(
    (row) => !row.isHistorical,
  );
  const dashboardModuleLabels = [
    enabledTabIdSet.has("appointments") ? "appointments" : null,
    enabledTabIdSet.has("procedures") ? "procedures" : null,
    enabledTabIdSet.has("medications") ? "medications" : null,
    enabledTabIdSet.has("documents") ? "documents" : null,
    enabledTabIdSet.has("calls") ? "phone calls" : null,
  ].filter((label): label is string => typeof label === "string");
  const dashboardModuleText =
    dashboardModuleLabels.length > 1
      ? `${dashboardModuleLabels.slice(0, -1).join(", ")}, and ${
          dashboardModuleLabels[dashboardModuleLabels.length - 1]
        }`
      : dashboardModuleLabels[0] ?? "your care updates";
  const canOpenMessages = enabledTabIdSet.has("messages");

  return (
    <View
      style={[
        styles.workspaceGrid,
        { alignSelf: "stretch", gap: 14, minWidth: 0, width: "auto" },
      ]}
    >
      <View
        style={[
          styles.patientMobileAgentCard,
          {
            alignSelf: "stretch",
            backgroundColor: patientMobilePalette.white,
            borderColor: "transparent",
            borderRadius: 20,
            maxWidth: "100%",
            padding: 18,
            shadowColor: patientMobilePalette.actionBlue,
            shadowOffset: { height: 2, width: 0 },
            shadowOpacity: 0.08,
            shadowRadius: 12,
            width: "auto",
          },
        ]}
      >
        <View style={[styles.sectionChromeCopy, { gap: 5, minWidth: 0, width: "100%" }]}>
          <Text
            style={[
              styles.sectionTitle,
              {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 17,
                  fontWeight: "800",
                  lineHeight: 21,
                },
              ]}
          >
            {`Good morning, ${givenName}`}
          </Text>
          <Text
            numberOfLines={4}
            style={[
              styles.sectionSubtitle,
              {
                  color: patientMobilePalette.actionBlue,
                  flexShrink: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  lineHeight: 19,
                  maxWidth: "100%",
                  width: "100%",
                },
              ]}
            >
            {`Your dashboard has ${dashboardModuleText}.`}
          </Text>
          {canOpenMessages ? (
          <View style={[styles.patientRowFooter, { gap: 8 }]}>
            <Pressable
              onPress={() => onNavigateTab("messages")}
              style={[
                styles.patientMobileTinyPill,
                createWebGradientBackgroundStyle(patientMobilePalette.primaryGradient),
                {
                  backgroundColor: patientMobilePalette.actionBlue,
                  borderColor: "transparent",
                  borderRadius: 12,
                  minHeight: 36,
                  paddingHorizontal: 18,
                  paddingVertical: 9,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.white,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: "700",
                  },
                ]}
              >
                {`Ask ${patientWorkspaceBrand.assistantName}`}
              </Text>
            </Pressable>
          </View>
          ) : null}
        </View>
      </View>

      {enabledTabIdSet.has("medications") ? (
        <>
      {renderPatientMobileSectionHeader({
        layout,
        onPress: () => onNavigateTab("medications"),
        title: "Your next medication",
      })}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={{ width: "100%" }}
        contentContainerStyle={[
          styles.patientMobileMedicationPreviewRow,
          { gap: 10, paddingBottom: 4 },
        ]}
      >
        {currentMedicationPreviewRows.length > 0 ? (
          currentMedicationPreviewRows.slice(0, 3).map((row, index) => (
            <Pressable
              key={row.id}
              onPress={() => onNavigateTab("medications")}
              style={[
                styles.patientMobileMedicationPreviewCard,
                {
                  backgroundColor: patientMobilePalette.white,
                  borderColor: "transparent",
                  borderRadius: 18,
                  paddingHorizontal: 10,
                  paddingVertical: 14,
                  shadowColor: patientMobilePalette.primaryText,
                  shadowOffset: { height: 2, width: 0 },
                  shadowOpacity: 0.06,
                  shadowRadius: 12,
                },
              ]}
            >
              <View
                style={{
                  backgroundColor:
                    index === 0
                      ? patientMobilePalette.actionBlue
                      : index === 1
                        ? patientMobilePalette.accentTeal
                        : patientMobilePalette.statusWarningText,
                  borderRadius: 4,
                  height: 8,
                  marginBottom: 3,
                  width: 8,
                }}
              />
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.mutedText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    fontWeight: "700",
                    textTransform: "uppercase",
                  },
                ]}
              >
                {index === 0 ? "evening" : index === 1 ? "midday" : "today"}
              </Text>
              <Text
                numberOfLines={2}
                style={[
                  styles.navigationText,
                  {
                    color: patientMobilePalette.primaryText,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 11,
                    fontWeight: "800",
                    lineHeight: 14,
                  },
                ]}
              >
                {row.labelText}
              </Text>
              <Text
                numberOfLines={2}
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.primaryText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    lineHeight: 14,
                  },
                ]}
              >
                {row.noteText}
              </Text>
            </Pressable>
          ))
        ) : (
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.mutedText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                lineHeight: 16,
              },
            ]}
          >
            No current medications are listed.
          </Text>
        )}
      </ScrollView>
        </>
      ) : null}

      {enabledTabIdSet.has("appointments") ? (
        <>
      {renderPatientMobileSectionHeader({
        layout,
        onPress: () => onNavigateTab("appointments"),
        title: "Upcoming appointments",
      })}
      <View style={[styles.workspaceGrid, { gap: 8 }]}>
        {appointmentItems.slice(0, 3).map((item, index) =>
          renderPatientMobileListRow({
            item,
            index,
            layout,
            onPress: () => onNavigateTab("appointments"),
            thumbnailText: index === 0 ? "VIS" : index === 1 ? "PHY" : "LAB",
          }),
        )}
      </View>
        </>
      ) : null}

      {enabledTabIdSet.has("documents") ? (
        <>
      {renderPatientMobileSectionHeader({
        layout,
        onPress: () => onNavigateTab("documents"),
        title: "Recent documents",
      })}
      <View style={[styles.workspaceGrid, { gap: 8 }]}>
        {documentRows.map((row, index) =>
          renderPatientMobileListRow({
            item: {
              id: row.id,
              title: row.title,
              subtitle: row.supportingText ?? row.subtitle ?? null,
              supportingText: row.subtitle ?? null,
            },
            index,
            layout,
            onPress: () => onNavigateTab("documents"),
            thumbnailText: "DOC",
          }),
        )}
      </View>
        </>
      ) : null}
    </View>
  );
}

function resolveAutoSelectedWorkforceSessionSubjectKeyForMode(
  subjects: readonly WorkforceSessionSubjectSummaryV1[],
  preferredWorkspaceMode: "platform" | "admin" | "clinician" | "patient" | null,
): string | null {
  if (preferredWorkspaceMode != null) {
    const matchingSubjects = subjects.filter((subject) =>
      matchesPreferredWorkspaceMode(subject.roleKeys, preferredWorkspaceMode),
    );

    if (matchingSubjects.length === 1) {
      return matchingSubjects[0]?.subjectKey ?? null;
    }
  }

  return resolveAutoSelectedWorkforceSessionSubjectKey(subjects);
}

function resolveEffectiveRuntimeConfig(
  runtimeConfig: MobileRuntimeConfig,
  activeWorkforceSession: WorkforceSessionSummaryV1 | null,
  selectedWorkforceSubjectKey: string | null,
): MobileRuntimeConfig {
  return {
    ...runtimeConfig,
    workforceSessionSubjectKey:
      activeWorkforceSession?.subjectKey ??
      selectedWorkforceSubjectKey ??
      runtimeConfig.workforceSessionSubjectKey,
    requestSiteId:
      activeWorkforceSession?.siteId ?? runtimeConfig.requestSiteId,
    requestClinicianUserId:
      activeWorkforceSession?.clinicianUserId ??
      runtimeConfig.requestClinicianUserId,
  };
}

function buildWorkspaceRoleMismatchMessage(
  expectedWorkspaceMode: "platform" | "admin" | "clinician" | "patient",
): string {
  if (expectedWorkspaceMode === "platform") {
    return "Use a platform operator account for this workspace.";
  }

  if (expectedWorkspaceMode === "admin") {
    return "Use an admin account for this workspace.";
  }

  if (expectedWorkspaceMode === "patient") {
    return "Use a patient account for this workspace.";
  }

  return "Use a clinician account for this workspace.";
}

function mapConversationMessagesToChatViewModels(
  messages: readonly {
    messageId: string;
    senderDisplayName: string;
    senderKind: ChatMessageViewModel["senderKind"];
    bodyText: string;
    sentAt: string;
    deliveryState: ChatMessageViewModel["deliveryState"];
    review: {
      state: NonNullable<ChatMessageViewModel["review"]>["state"];
      confidencePercent: number | null;
      criticalityPercent: number | null;
      clinicianNotes: string | null;
      canApprove: boolean;
      canReject: boolean;
      canEdit: boolean;
    } | null;
  }[],
): ChatMessageViewModel[] {
  return messages.map((message) => ({
    id: message.messageId,
    senderText: message.senderDisplayName,
    senderKind: message.senderKind,
    bodyText: message.bodyText,
    timestampText: formatPatientConversationTimestamp(message.sentAt),
    deliveryState: message.deliveryState,
    alignment:
      message.senderKind === "clinician"
        ? "right"
        : message.senderKind === "system"
          ? "center"
          : "left",
    tone:
      message.senderKind === "patient"
        ? "accent"
        : message.senderKind === "system"
          ? "info"
          : "neutral",
    review: message.review
      ? {
          state: message.review.state,
          confidencePercent: message.review.confidencePercent,
          criticalityPercent: message.review.criticalityPercent,
          clinicianNotesText: message.review.clinicianNotes,
          canApprove: message.review.canApprove,
          canReject: message.review.canReject,
          canEdit: message.review.canEdit,
        }
      : null,
  }));
}

function formatPatientConversationTimestamp(value: string): string {
  const sentAtDate = new Date(value);

  if (Number.isNaN(sentAtDate.getTime())) {
    return value.slice(11, 16);
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      hour12: false,
      minute: "2-digit",
    }).format(sentAtDate);
  } catch {
    return value.slice(11, 16);
  }
}

function isWorkforceSessionUnauthorizedError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes("failed-401") ||
      error.message.includes("failed-403"))
  );
}

function isPatientWorkspaceUnauthorizedError(error: unknown): boolean {
  return isWorkforceSessionUnauthorizedError(error);
}

function isStaleMaterialChangeTokenError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes("stale-material-change-token") ||
      error.message.includes("expected materialChangeToken"))
  );
}

function resolveClinicianMetricIconName(
  itemId: string,
  labelText: string,
): LineIconName {
  const normalizedValue = `${itemId} ${labelText}`.toLowerCase();

  if (normalizedValue.includes("pre")) {
    return "user";
  }

  if (normalizedValue.includes("post")) {
    return "pulse";
  }

  if (normalizedValue.includes("imaging")) {
    return "image";
  }

  if (normalizedValue.includes("reply")) {
    return "clock";
  }

  if (normalizedValue.includes("touch")) {
    return "clock";
  }

  if (normalizedValue.includes("escal")) {
    return "flag";
  }

  if (normalizedValue.includes("auto")) {
    return "check";
  }

  if (normalizedValue.includes("edited")) {
    return "edit";
  }

  if (normalizedValue.includes("pending")) {
    return "document";
  }

  return "info";
}

function hasActiveClinicianOutboundCall(
  items: ClinicianWorkbenchOutboundQuestionnaireCallV1[],
): boolean {
  return items.some((call) => {
    const targetStatus = call.targetStatus?.toLowerCase() ?? "";

    return (
      call.primaryActionLabel === "Call in progress" ||
      targetStatus === "calling"
    );
  });
}

export function AppShell() {
  const { height: viewportHeight, width } = useWindowDimensions();
  const isWideLayout = width >= 1260;
  const previousIsWideLayoutRef = useRef(isWideLayout);
  const clinicianWorkspacePathnameRef = useRef(
    Platform.OS === "web" && typeof window !== "undefined"
      ? resolveCanonicalWorkspacePathname(window.location.pathname)
      : "/clinician",
  );
  const isTransitioningFromWideToCompact =
    previousIsWideLayoutRef.current && !isWideLayout;
  const isMediumLayout = width >= 900;
  const showsLegacyWebAssets = Platform.OS === "web";
  const runtimeConfig = resolveMobileRuntimeConfig();
  const requiresWorkforceSessionGate =
    runtimeConfig.tenantSessionBootstrapMode === "bearer-token";
  const locale = resolveLocale();
  const dictionary =
    locale === defaultLocale ? enUSLocaleDictionary : enUSLocaleDictionary;
  const t = (key: TranslationKey) => dictionary[key];
  const workforceTheme = resolveWorkforceTheme(
    resolvePreferredWorkforceThemeName(),
  );
  // Phase 1 (patient-portal unification): patient mode now uses the same
  // neutral+teal variant as clinician/admin instead of the warm/blue variant.
  const patientShellTheme = designTokens.semanticTheme.clinicianSlate;
  const layout = designTokens.semanticLayout;
  const tokens = designTokens;
  const pageMaxWidth = 1680;
  const clinicianLegacyPageMaxWidth = 1552;
  const legacyWorkbenchFooterHeight = showsLegacyWebAssets ? 32 : 0;
  // Fills the viewer's viewport: reactive window height minus fixed chrome
  // (header + page paddings + footer). The floor keeps small windows usable;
  // there is deliberately no ceiling so any screen height is filled.
  const legacyClinicianStageHeight =
    showsLegacyWebAssets
      ? Math.max(
          isWideLayout ? 490 : 400,
          Math.floor(viewportHeight - (isWideLayout ? 108 : 124)),
        )
      : undefined;
  const legacyHospitalLogoSource = {
    uri: legacyWebShellAssets.hospitalLogoUri,
  };
  const legacyHospitalBlueLogoSource = {
    uri: "/merritt_healthcare_logo_blue.png",
  };
  const legacyClinicianAvatarSource = {
    uri: legacyWebShellAssets.clinicianAvatarUri,
  };

  useEffect(() => {
    previousIsWideLayoutRef.current = isWideLayout;
  }, [isWideLayout]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return;
    }

    if (!shouldCanonicalizeWorkspacePathname(window.location.pathname)) {
      return;
    }

    const canonicalPathname = resolveCanonicalWorkspacePathname(
      window.location.pathname,
    );

    window.history.replaceState(
      null,
      "",
      `${canonicalPathname}${window.location.search}${window.location.hash}`,
    );
  }, []);

  const [selectedPatientTabId, setSelectedPatientTabId] =
    useState<PatientListTabId>(
      () => readWorkbenchRouteStateFromWindow()?.selectedPatientTabId ?? "my",
    );
  const [selectedWorkbenchPanelId, setSelectedWorkbenchPanelId] =
    useState<WorkbenchPanelId>(
      () =>
        readWorkbenchRouteStateFromWindow()?.selectedWorkbenchPanelId ??
        "inbox",
    );
  const [requiresAttentionOnly, setRequiresAttentionOnly] = useState(
    () => readWorkbenchRouteStateFromWindow()?.requiresAttentionOnly ?? false,
  );
  const [searchQuery, setSearchQuery] = useState(
    () => readWorkbenchRouteStateFromWindow()?.searchQuery ?? "",
  );
  const deferredSearchQuery = useDeferredValue(searchQuery.trim());
  const [selectedPatientWorkspaceTabId, setSelectedPatientWorkspaceTabId] =
    useState<PatientWorkspaceTabId>("overview");
  // Live view of the current selection for async response guards: a late
  // shell/action response for a previously selected patient must never
  // reinstall that patient's workspace over the new selection.
  const selectedPatientIdRef = useRef<string | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    () =>
      readWorkbenchRouteStateFromWindow()?.selectedPatientId ??
      defaultSelectedPatientId,
  );
  const [selectedDetailTabId, setSelectedDetailTabId] = useState<DetailTabId>(
    () =>
      readWorkbenchRouteStateFromWindow()?.selectedDetailTabId ?? "information",
  );
  const clinicianRouteStateRef = useRef<ClinicianWorkbenchRouteState>({
    selectedPatientTabId,
    selectedWorkbenchPanelId,
    requiresAttentionOnly,
    searchQuery,
    selectedPatientId,
    selectedDetailTabId,
  });
  clinicianRouteStateRef.current = {
    selectedPatientTabId,
    selectedWorkbenchPanelId,
    requiresAttentionOnly,
    searchQuery,
    selectedPatientId,
    selectedDetailTabId,
  };
  const [selectedDetailModalViewId, setSelectedDetailModalViewId] = useState<
    "all" | "secondary"
  >("all");
  const [voiceCallTranscriptModal, setVoiceCallTranscriptModal] =
    useState<VoiceCallTranscriptModalState>(null);
  const [clinicianOutboundAnswersModal, setClinicianOutboundAnswersModal] =
    useState<{
      call: ClinicianWorkbenchOutboundQuestionnaireCallV1;
      /** Owner: the modal renders and acts only for this patient. */
      patientId: string;
      patientName: string;
      persistedReviewNotes: string;
      reviewNotes: string;
    } | null>(null);
  const [clinicianMedicationResolution, setClinicianMedicationResolution] =
    useState<ClinicianMedicationResolutionState>(null);
  const [
    clinicianOutboundCallConfirmModal,
    setClinicianOutboundCallConfirmModal,
  ] = useState<ClinicianOutboundCallConfirmModalState>(null);
  const [
    clinicianOutboundCallsByPatientId,
    setClinicianOutboundCallsByPatientId,
  ] = useState<
    Record<
      string,
      {
        errorText: string | null;
        generatedAt: string | null;
        isLoading: boolean;
        items: ClinicianWorkbenchOutboundQuestionnaireCallV1[];
        noticeText: string | null;
      }
    >
  >({});
  const [
    clinicianOutboundPendingTargetId,
    setClinicianOutboundPendingTargetId,
  ] = useState<string | null>(null);
  const [
    clinicianOutboundPdfPendingTargetId,
    setClinicianOutboundPdfPendingTargetId,
  ] = useState<string | null>(null);
  const [
    clinicianOutboundManualPending,
    setClinicianOutboundManualPending,
  ] = useState(false);
  const [clinicianQuestionnaireDefinition, setClinicianQuestionnaireDefinition] =
    useState<ClinicianWorkbenchQuestionnaireDefinitionV1 | null>(null);
  const loadedClinicianQuestionnaireDefinitionKeyRef = useRef<string | null>(
    null,
  );
  const [clinicianClinicalEditorModal, setClinicianClinicalEditorModal] =
    useState<ClinicianClinicalEditorModalState>(null);
  const [
    clinicianClinicalEditorDiscardPromptOpen,
    setClinicianClinicalEditorDiscardPromptOpen,
  ] = useState(false);
  const [
    clinicianOutboundAnswersDiscardPromptOpen,
    setClinicianOutboundAnswersDiscardPromptOpen,
  ] = useState(false);
  const [inlineClinicalEditor, setInlineClinicalEditor] =
    useState<ClinicianInlineClinicalEditorState>(null);
  const [composerDraft, setComposerDraft] = useState("");
  const [clinicianComposerMode, setClinicianComposerMode] =
    useState<ClinicianComposerMode>("reply");
  const [reviewEditingMessageId, setReviewEditingMessageId] = useState<
    string | null
  >(null);
  const [reviewEditDraft, setReviewEditDraft] = useState("");
  const reviewEditBaselineRef = useRef("");
  const [editedReviewBodiesByMessageId, setEditedReviewBodiesByMessageId] =
    useState<Record<string, string>>({});
  const [clinicianCenterPaneView, setClinicianCenterPaneView] =
    useState<ClinicianCenterPaneView>("conversation");
  const [clinicianProfileModalOpen, setClinicianProfileModalOpen] =
    useState(false);
  const clinicianProfileDirtyRef = useRef(false);
  const clinicianNonProfileDirtyRef = useRef(false);
  const clinicianProfileDiscardDialogOpenRef = useRef(false);
  const clinicianHistoryRestorationRef =
    useRef<ClinicianHistoryRestoration | null>(null);
  const clinicianSessionEpochRef = useRef(0);
  const [
    clinicianProfilePendingNavigation,
    setClinicianProfilePendingNavigation,
  ] = useState<ClinicianProfilePendingNavigation | null>(null);
  const [
    clinicianProfileDiscardDismissRequestKey,
    setClinicianProfileDiscardDismissRequestKey,
  ] = useState(0);
  const [
    clinicianProfileDiscardChangesRequestKey,
    setClinicianProfileDiscardChangesRequestKey,
  ] = useState(0);
  const handleClinicianProfileDirtyChange = useCallback((dirty: boolean) => {
    clinicianProfileDirtyRef.current = dirty;
  }, []);
  const handleClinicianProfileDiscardDialogOpenChange = useCallback(
    (open: boolean) => {
      clinicianProfileDiscardDialogOpenRef.current = open;
    },
    [],
  );
  const handleClinicianProfileModalOpenChange = useCallback((open: boolean) => {
    setClinicianProfileModalOpen(open);
  }, []);
  const hasClinicianUnsavedChanges = useCallback(
    () =>
      clinicianProfileDirtyRef.current ||
      clinicianNonProfileDirtyRef.current,
    [],
  );
  useEffect(() => {
    clinicianNonProfileDirtyRef.current =
      hasClinicianUnsavedWorkspaceChanges({
        clinicalEditorDirty: isClinicianEditorDirty(
          clinicianClinicalEditorModal,
        ),
        composerDraft,
        inlineEditorDirty: isClinicianInlineEditorDirty(inlineClinicalEditor),
        messageReviewEditorDirty:
          reviewEditingMessageId != null &&
          reviewEditDraft.trim() !== reviewEditBaselineRef.current.trim(),
        pendingMessageReviewEditCount: Object.keys(
          editedReviewBodiesByMessageId,
        ).length,
        profileDirty: false,
        questionnaireReviewNote:
          clinicianOutboundAnswersModal?.reviewNotes ?? "",
        questionnaireReviewNoteBaseline:
          clinicianOutboundAnswersModal?.persistedReviewNotes ?? "",
      });
  }, [
    clinicianClinicalEditorModal,
    clinicianOutboundAnswersModal?.persistedReviewNotes,
    clinicianOutboundAnswersModal?.reviewNotes,
    composerDraft,
    editedReviewBodiesByMessageId,
    inlineClinicalEditor,
    reviewEditDraft,
    reviewEditingMessageId,
  ]);
  const [clinicianCompactPane, setClinicianCompactPane] = useState<
    "queue" | "workspace"
  >(() =>
    readWorkbenchRouteStateFromWindow()?.selectedPatientId != null
      ? "workspace"
      : "queue",
  );
  const [clinicianProfileFocusSection, setClinicianProfileFocusSection] =
    useState<ClinicianPatientProfileFocusTarget | null>(null);
  const [clinicianListViewMode, setClinicianListViewMode] =
    useState<ClinicianListViewMode>("list");
  const [clinicianCalendarRange, setClinicianCalendarRange] =
    useState<CalendarRange | null>(null);
  const [clinicianCalendarPendingStart, setClinicianCalendarPendingStart] =
    useState<string | null>(null);
  const [clinicianCalendarMonth, setClinicianCalendarMonth] = useState(() => {
    const now = new Date();

    return { year: now.getUTCFullYear(), monthIndex: now.getUTCMonth() };
  });
  const [clinicianCallTypeFilter, setClinicianCallTypeFilter] =
    useState<ClinicianCallTypeFilter>("all");
  const [myPatientIds, setMyPatientIds] = useState<string[]>([
    ...initialMyPatientIds,
  ]);
  const [shellData, setShellData] =
    useState<ClinicianWorkbenchShellData | null>(null);
  const [loadedShellDataCacheKey, setLoadedShellDataCacheKey] = useState<
    string | null
  >(null);
  const shellDataCacheRef = useRef<Map<string, ClinicianWorkbenchShellData>>(
    new Map(),
  );
  const shellDataPrefetchInFlightRef = useRef<Set<string>>(new Set());
  const [isShellDataLoading, setIsShellDataLoading] = useState(true);
  const [hasShellDataError, setHasShellDataError] = useState(false);
  const [workforceSessionGateState, setWorkforceSessionGateState] =
    useState<WorkforceSessionGateState>(
      requiresWorkforceSessionGate ? "loading" : "ready",
    );
  const [availableWorkforceSubjects, setAvailableWorkforceSubjects] = useState<
    WorkforceSessionSubjectSummaryV1[]
  >([]);
  const [activeWorkforceSession, setActiveWorkforceSession] =
    useState<WorkforceSessionSummaryV1 | null>(null);
  const [workforceLoginEmail, setWorkforceLoginEmail] = useState("");
  const [workforceLoginPassword, setWorkforceLoginPassword] = useState("");
  const [workforceLoginError, setWorkforceLoginError] = useState<string | null>(
    null,
  );
  const [isWorkforceLoginSubmitting, setIsWorkforceLoginSubmitting] =
    useState(false);
  const [recentWorkforceSubjectKey, setRecentWorkforceSubjectKey] = useState<
    string | null
  >(() => readStoredWorkforceSubjectKey(runtimeConfig.requestTenantId));
  const [selectedWorkforceSubjectKey, setSelectedWorkforceSubjectKey] =
    useState<string | null>(
      () =>
        runtimeConfig.workforceSessionSubjectKey ??
        readStoredWorkforceSubjectKey(runtimeConfig.requestTenantId),
    );
  const [adminSummary, setAdminSummary] =
    useState<TenantAdminSummaryResponseV1 | null>(null);
  const [adminAnalyticsRangeSelection, setAdminAnalyticsRangeSelection] =
    useState<AdminAnalyticsRangeSelection>(() =>
      resolveAdminAnalyticsRangeSelection("7d"),
    );
  const [isAdminSummaryLoading, setIsAdminSummaryLoading] = useState(false);
  const [hasAdminSummaryError, setHasAdminSummaryError] = useState(false);
  const [isAdminCommandSubmitting, setIsAdminCommandSubmitting] =
    useState(false);
  const [patientWorkspaceSummary, setPatientWorkspaceSummary] =
    useState<PatientWorkspaceSummaryResponseV1 | null>(null);
  const [isPatientWorkspaceLoading, setIsPatientWorkspaceLoading] =
    useState(false);
  const [hasPatientWorkspaceError, setHasPatientWorkspaceError] =
    useState(false);
  const [patientPortalSession, setPatientPortalSession] =
    useState<WorkforceSessionSummaryV1 | null>(null);
  const [patientPortalAuthNotice, setPatientPortalAuthNotice] = useState<
    string | null
  >(null);
  const [isPatientPortalSessionLoading, setIsPatientPortalSessionLoading] =
    useState(false);
  const [
    isPatientMessageCommandSubmitting,
    setIsPatientMessageCommandSubmitting,
  ] = useState(false);
  const [
    isPatientOnboardingCommandSubmitting,
    setIsPatientOnboardingCommandSubmitting,
  ] = useState(false);
  const [isWorkbenchActionSubmitting, setIsWorkbenchActionSubmitting] =
    useState(false);
  const [
    isClinicianDocumentUploadSubmitting,
    setIsClinicianDocumentUploadSubmitting,
  ] = useState(false);
  const [workbenchActionFeedbackKey, setWorkbenchActionFeedbackKey] =
    useState<TranslationKey | null>(null);
  const [workbenchActionFeedbackText, setWorkbenchActionFeedbackText] =
    useState<string | null>(null);
  const effectiveRuntimeConfig = resolveEffectiveRuntimeConfig(
    runtimeConfig,
    activeWorkforceSession,
    selectedWorkforceSubjectKey,
  );
  const activeRoleKeys =
    activeWorkforceSession?.roleKeys ?? runtimeConfig.tenantSessionRoleKeys;
  // The URL path decides which workspace renders (its runtime config declares
  // the expected roles); the session only has to satisfy it. Rendering by the
  // session's dominant mode let an admin sign-in bleed into /clinician — the
  // admin console appeared under the clinician URL. One deliberate exception:
  // a platform-operator session on an admin path keeps the platform console,
  // which has no path of its own and is reached exactly that way.
  const pathWorkspaceMode = resolveWorkspaceMode(
    runtimeConfig.tenantSessionRoleKeys,
  );
  const sessionWorkspaceMode = resolveWorkspaceMode(activeRoleKeys);
  const workspaceMode =
    activeWorkforceSession == null
      ? pathWorkspaceMode
      : !sessionSatisfiesWorkspaceMode(pathWorkspaceMode, activeRoleKeys)
        ? sessionWorkspaceMode
        : sessionWorkspaceMode === "platform" && pathWorkspaceMode === "admin"
          ? "platform"
          : pathWorkspaceMode;
  const discardClinicianWorkspaceDrafts = useCallback(() => {
    clinicianProfileDirtyRef.current = false;
    clinicianNonProfileDirtyRef.current = false;
    clinicianProfileDiscardDialogOpenRef.current = false;
    setClinicianProfileDiscardChangesRequestKey((value) => value + 1);
    setClinicianProfilePendingNavigation(null);
    setClinicianProfileModalOpen(false);
    setClinicianClinicalEditorDiscardPromptOpen(false);
    setClinicianClinicalEditorModal(null);
    setClinicianOutboundAnswersDiscardPromptOpen(false);
    setClinicianOutboundAnswersModal(null);
    setClinicianMedicationResolution(null);
    setClinicianOutboundCallConfirmModal(null);
    setVoiceCallTranscriptModal(null);
    setInlineClinicalEditor(null);
    setComposerDraft("");
    setClinicianComposerMode("reply");
    setReviewEditingMessageId(null);
    setReviewEditDraft("");
    setEditedReviewBodiesByMessageId({});
  }, []);
  const clearClinicianSessionState = useCallback(() => {
    clinicianSessionEpochRef.current += 1;
    discardClinicianWorkspaceDrafts();
    shellDataCacheRef.current.clear();
    shellDataPrefetchInFlightRef.current.clear();
    setShellData(null);
    setLoadedShellDataCacheKey(null);
    setIsShellDataLoading(true);
    setHasShellDataError(false);
    setIsWorkbenchActionSubmitting(false);
    setIsClinicianDocumentUploadSubmitting(false);
    setClinicianOutboundCallsByPatientId({});
    setClinicianOutboundPendingTargetId(null);
    setClinicianOutboundPdfPendingTargetId(null);
    setClinicianOutboundManualPending(false);
    loadedClinicianQuestionnaireDefinitionKeyRef.current = null;
    setClinicianQuestionnaireDefinition(null);
    setClinicianProfileFocusSection(null);
    setClinicianCallTypeFilter("all");
    setClinicianCenterPaneView("conversation");
    setSelectedPatientId(null);
    setSelectedDetailTabId("information");
    setSelectedDetailModalViewId("all");
    setMyPatientIds([...initialMyPatientIds]);
    setWorkbenchActionFeedbackKey(null);
    setWorkbenchActionFeedbackText(null);
  }, [discardClinicianWorkspaceDrafts]);
  const resolveClinicianSessionIdentity = useCallback(
    (session: WorkforceSessionSummaryV1 | null): string | null =>
      session == null
        ? null
        : [
            runtimeConfig.requestTenantId,
            session.subjectKey,
            session.clinicianUserId ?? "",
            session.siteId ?? "",
          ].join(":"),
    [runtimeConfig.requestTenantId],
  );
  const clinicianSessionIdentity = resolveClinicianSessionIdentity(
    activeWorkforceSession,
  );
  const isCurrentClinicianSessionRequest = useCallback(
    (requestSessionEpoch: number): boolean =>
      clinicianSessionEpochRef.current === requestSessionEpoch,
    [],
  );
  const previousClinicianSessionIdentityRef = useRef<
    string | null | undefined
  >(undefined);
  const transitionActiveWorkforceSession = useCallback(
    (nextSession: WorkforceSessionSummaryV1 | null): void => {
      const nextIdentity = resolveClinicianSessionIdentity(nextSession);
      const previousIdentity = previousClinicianSessionIdentityRef.current;

      if (
        previousIdentity !== undefined &&
        previousIdentity !== nextIdentity
      ) {
        clearClinicianSessionState();
      }

      previousClinicianSessionIdentityRef.current = nextIdentity;
      setActiveWorkforceSession(nextSession);
    },
    [clearClinicianSessionState, resolveClinicianSessionIdentity],
  );
  useEffect(() => {
    const previousIdentity = previousClinicianSessionIdentityRef.current;

    if (
      previousIdentity !== undefined &&
      previousIdentity !== clinicianSessionIdentity
    ) {
      clearClinicianSessionState();
    }

    previousClinicianSessionIdentityRef.current = clinicianSessionIdentity;
  }, [clearClinicianSessionState, clinicianSessionIdentity]);
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return;
    }

    document.title =
      workspaceMode === "admin"
        ? "AmpleCare Admin"
        : workspaceMode === "platform"
          ? "AmpleCare Platform"
          : workspaceMode === "patient"
            ? "AmpleCare Patient"
            : "AmpleCare Clinician";
  }, [workspaceMode]);
  const theme =
    workspaceMode === "patient"
      ? patientShellTheme
      : workspaceMode === "clinician" ||
          workspaceMode === "admin" ||
          workspaceMode === "platform"
        ? designTokens.semanticTheme.clinicianSlate
        : workforceTheme;
  const legacyFooterPalette =
    workspaceMode === "clinician" || workspaceMode === "admin"
      ? ({
          ...patientDesktopPalette,
          darkBlue: theme.headerBand,
          shellGradient: theme.shellGradient,
        } as unknown as typeof patientDesktopPalette)
      : patientDesktopPalette;
  const orderedWorkforceSubjects = prioritizeWorkforceSubjects(
    availableWorkforceSubjects,
    recentWorkforceSubjectKey,
  );
  const selectedWorkforceSubject = findWorkforceSubjectByKey(
    availableWorkforceSubjects,
    runtimeConfig.workforceSessionSubjectKey ?? selectedWorkforceSubjectKey,
  );
  const workforceModeSummaryItems = [
    {
      id: "platform",
      labelText: "Platform",
      valueText: String(
        orderedWorkforceSubjects.filter(
          (subject) => resolveWorkspaceMode(subject.roleKeys) === "platform",
        ).length,
      ),
      toneColor: theme.textPrimary,
    },
    {
      id: "admin",
      labelText: "Admin",
      valueText: String(
        orderedWorkforceSubjects.filter(
          (subject) => resolveWorkspaceMode(subject.roleKeys) === "admin",
        ).length,
      ),
      toneColor: theme.warning,
    },
    {
      id: "clinician",
      labelText: "Clinician",
      valueText: String(
        orderedWorkforceSubjects.filter(
          (subject) => resolveWorkspaceMode(subject.roleKeys) === "clinician",
        ).length,
      ),
      toneColor: theme.accentStrong,
    },
    {
      id: "patient",
      labelText: "Patient",
      valueText: String(
        orderedWorkforceSubjects.filter(
          (subject) => resolveWorkspaceMode(subject.roleKeys) === "patient",
        ).length,
      ),
      toneColor: theme.info,
    },
  ];
  const clinicianUserId =
    activeWorkforceSession?.clinicianUserId ??
    effectiveRuntimeConfig.requestClinicianUserId;
  const usesPatientPortalSession =
    workspaceMode === "patient" &&
    !isPatientWorkspaceScaffoldMode(effectiveRuntimeConfig);
  const patientWorkforceSession = usesPatientPortalSession
    ? patientPortalSession
    : activeWorkforceSession;
  const patientWorkspacePatientId =
    patientWorkforceSession?.patientId ??
    (workspaceMode === "patient" &&
    isPatientWorkspaceScaffoldMode(effectiveRuntimeConfig)
      ? (patientWorkspaceSummary?.patientId ?? null)
      : null);
  const currentClinicianRequestState = buildCurrentRequestState({
    deferredSearchQuery,
    myPatientIds,
    requiresAttentionOnly,
    selectedDetailTabId,
    selectedPatientId,
    selectedPatientTabId,
  });
  const currentShellDataCacheKey = buildClinicianWorkbenchShellCacheKey(
    currentClinicianRequestState,
    effectiveRuntimeConfig,
  );
  const adminAnalyticsSummaryRequest =
    buildAdminAnalyticsSummaryRequest(adminAnalyticsRangeSelection);
  const adminSummaryRefreshKey = buildTenantAdminSummaryRefreshKey({
    analyticsKey: JSON.stringify(adminAnalyticsSummaryRequest),
    tenantId: effectiveRuntimeConfig.requestTenantId,
  });
  const clinicianWorkbenchRefreshKey = [
    buildClinicianWorkbenchRefreshKey({
      clinicianUserId: effectiveRuntimeConfig.requestClinicianUserId,
      routeKey: currentShellDataCacheKey,
      tenantId: effectiveRuntimeConfig.requestTenantId,
    }),
    effectiveRuntimeConfig.requestSiteId ?? "all-sites",
    clinicianSessionIdentity ?? "scaffold-session",
    String(clinicianSessionEpochRef.current),
  ].join("|");
  const clinicianWorkbenchRefreshAliases =
    buildClinicianWorkbenchRefreshAliases({
      clinicianUserId: effectiveRuntimeConfig.requestClinicianUserId,
      tenantId: effectiveRuntimeConfig.requestTenantId,
    });
  const clinicianRefreshSessionEpoch = clinicianSessionEpochRef.current;
  const patientWorkspaceRefreshKey = buildPatientWorkspaceRefreshKey({
    patientRef: patientWorkspacePatientId ?? "none",
    tenantId: effectiveRuntimeConfig.requestTenantId,
  });
  const workspaceMutationRefreshAliases = buildWorkspaceMutationRefreshAliases({
    clinicianUserId: effectiveRuntimeConfig.requestClinicianUserId,
    tenantId: effectiveRuntimeConfig.requestTenantId,
  });
  const lastLoadedShellData = shareClinicianWorkbenchShellOwner(
    loadedShellDataCacheKey,
    currentShellDataCacheKey,
  )
    ? loadedShellDataCacheKey == null
      ? null
      : (shellDataCacheRef.current.get(loadedShellDataCacheKey) ?? shellData)
    : null;

  const applyPatientDocumentUploadResponse = useCallback(
    (response: PatientWorkspaceDocumentCompleteUploadResponseV1) => {
      const changedRefreshAliases =
        buildWorkspaceRefreshAliasesForChangedScopes(response.changedScopes);

      startTransition(() => {
        setPatientWorkspaceSummary(response.summary);
      });
      invalidateWorkspaceRefreshScopes(
        changedRefreshAliases.length > 0
          ? changedRefreshAliases
          : workspaceMutationRefreshAliases,
        "patient-document-upload-success",
        { tenantId: effectiveRuntimeConfig.requestTenantId },
      );
    },
    [
      effectiveRuntimeConfig.requestTenantId,
      workspaceMutationRefreshAliases,
    ],
  );

  useEffect(() => {
    setSelectedDetailModalViewId("all");
  }, [selectedDetailTabId, selectedPatientId]);
  const shellDataForCurrentRequest =
    shellDataCacheRef.current.get(currentShellDataCacheKey) ?? null;
  const renderedShellData = shellDataForCurrentRequest ?? lastLoadedShellData;
  const isClinicianShellRefreshing =
    isShellDataLoading && renderedShellData != null;
  const {
    detailSections: renderedDetailSections,
    listSummary,
    metricGroups,
    patientWorkspaces: visiblePatients,
    selectedPatient: renderedSelectedPatient,
    workbenchPanels,
  } = renderedShellData ?? {
    detailSections: [],
    listSummary: {
      myPatientsCount: 0,
      allPatientsCount: 0,
      attentionPatientCount: 0,
      visiblePatientCount: 0,
    },
    metricGroups: [],
    patientWorkspaces: [],
    selectedPatient: null,
    workbenchPanels: [],
  };
  // A stale shell (the previous request's data) may carry ANOTHER patient's
  // workspace while the selection and URL have already moved on. Never render
  // one patient's chart under another's highlight: the workspace pane shows
  // its empty/loading state until the matching shell arrives. The list-level
  // data (patient queue, counts) is cross-patient and stays. With no explicit
  // selection, only a shell answering the CURRENT request may supply the
  // server-chosen default patient — a stale fallback shell may not.
  const selectedPatient =
    renderedSelectedPatient != null &&
    (selectedPatientId != null
      ? renderedSelectedPatient.id === selectedPatientId
      : shellDataForCurrentRequest != null)
      ? renderedSelectedPatient
      : null;
  const detailSections = selectedPatient == null ? [] : renderedDetailSections;
  const selectedPatientOutboundCallsState =
    selectedPatient != null
      ? clinicianOutboundCallsByPatientId[selectedPatient.id] ?? null
      : null;
  const clinicianOutboundCallsPollMs =
    selectedPatientOutboundCallsState != null &&
    hasActiveClinicianOutboundCall(selectedPatientOutboundCallsState.items)
      ? workspaceRefreshCadenceMs.liveCall
      : workspaceRefreshCadenceMs.standard;
  const selectedPatientOutboundQuestionnaireCalls =
    selectedPatientOutboundCallsState?.items ?? [];
  const isClinicianPhoneCallsPanelVisible =
    workspaceMode === "clinician" &&
    selectedPatient != null &&
    (selectedDetailTabId === "phone-calls" ||
      clinicianCenterPaneView === "phone-calls");
  const clinicianOutboundCallsRefreshKey =
    buildClinicianWorkbenchOutboundCallsRefreshKey({
      patientId: selectedPatient?.id ?? "none",
      tenantId: effectiveRuntimeConfig.requestTenantId,
    });
  const clinicianOutboundCallsRefreshAliases =
    buildClinicianWorkbenchOutboundCallsRefreshAliases({
      clinicianUserId: effectiveRuntimeConfig.requestClinicianUserId,
      tenantId: effectiveRuntimeConfig.requestTenantId,
    });
  const clinicianConversationScrollRef = useRef<ScrollView | null>(null);
  const clinicianConversationNearEndRef = useRef(true);
  const lastClinicianConversationAutoScrollKeyRef = useRef<string | null>(null);
  const suspendedClinicianConversationAutoScrollKeyRef = useRef<string | null>(
    null,
  );
  const clinicianConversationMessages =
    selectedPatient != null
      ? buildReviewFirstChatMessages(selectedPatient.chatMessages)
      : [];
  const latestClinicianConversationMessage =
    clinicianConversationMessages.length > 0
      ? clinicianConversationMessages[clinicianConversationMessages.length - 1]
      : null;
  const latestClinicianConversationMessageKey =
    selectedPatient && latestClinicianConversationMessage
      ? [
          selectedPatient.id,
          latestClinicianConversationMessage.id,
          latestClinicianConversationMessage.senderKind,
          latestClinicianConversationMessage.timestampText,
          latestClinicianConversationMessage.bodyText,
          latestClinicianConversationMessage.review?.state ?? "",
        ].join(":")
      : `${selectedPatient?.id ?? "none"}:empty`;
  const firstPendingClinicianApprovalMessage =
    selectedPatient != null
      ? clinicianConversationMessages.find(
          (message) => message.review?.state === "pending-approval",
        )
      : null;
  const firstPendingClinicianApprovalMessageKey =
    selectedPatient && firstPendingClinicianApprovalMessage
      ? [
          selectedPatient.id,
          firstPendingClinicianApprovalMessage.id,
          firstPendingClinicianApprovalMessage.timestampText,
          firstPendingClinicianApprovalMessage.bodyText,
        ].join(":")
      : null;
  const scrollClinicianConversationToStart = useCallback((animated = true) => {
    const runScroll = () => {
      clinicianConversationScrollRef.current?.scrollTo({
        animated,
        y: 0,
      });
    };

    setTimeout(runScroll, 0);
  }, []);
  const scrollClinicianConversationToEnd = useCallback((animated = true) => {
    const runScroll = () => {
      clinicianConversationScrollRef.current?.scrollToEnd({ animated });
    };

    setTimeout(runScroll, 0);
  }, []);
  const handleClinicianConversationContentSizeChange = useCallback(() => {
    if (reviewEditingMessageId != null) {
      return;
    }

    if (firstPendingClinicianApprovalMessageKey != null) {
      scrollClinicianConversationToStart(false);
      return;
    }

    if (!clinicianConversationNearEndRef.current) {
      return;
    }

    scrollClinicianConversationToEnd(false);
  }, [
    firstPendingClinicianApprovalMessageKey,
    reviewEditingMessageId,
    scrollClinicianConversationToEnd,
    scrollClinicianConversationToStart,
  ]);
  const handleClinicianConversationScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const {
        contentOffset,
        contentSize,
        layoutMeasurement,
      } = event.nativeEvent;

      clinicianConversationNearEndRef.current =
        contentSize.height -
          (contentOffset.y + layoutMeasurement.height) <=
        96;
    },
    [],
  );

  useWorkspaceRefreshScope<TenantAdminSummaryResponseV1>({
    key: adminSummaryRefreshKey,
    aliases: [
      buildTenantAdminSummaryRefreshAlias(effectiveRuntimeConfig.requestTenantId),
    ],
    enabled:
      (!requiresWorkforceSessionGate || workforceSessionGateState === "ready") &&
      workspaceMode === "admin",
    loader: (signal) =>
      loadTenantAdminSummary(
        effectiveRuntimeConfig,
        adminAnalyticsSummaryRequest,
        signal,
      ),
    apply: (nextAdminSummary) => {
      startTransition(() => {
        setAdminSummary(nextAdminSummary);
        setHasAdminSummaryError(false);
        setIsAdminSummaryLoading(false);
      });
    },
    onError: (error) => {
      if (isWorkforceSessionUnauthorizedError(error)) {
        showWorkforceSessionExpiredGate();
        return;
      }

      if (adminSummary != null) {
        return;
      }

      startTransition(() => {
        setHasAdminSummaryError(true);
        setIsAdminSummaryLoading(false);
      });
    },
    readGeneratedAt: (payload) => payload.summary.generatedAt,
    readRevision: (payload) => payload.summary.generatedAt,
    minStaleMs: 5_000,
    activePollMs: workspaceRefreshCadenceMs.standard,
  });

  useWorkspaceRefreshScope<ClinicianWorkbenchShellData>({
    key: clinicianWorkbenchRefreshKey,
    aliases: clinicianWorkbenchRefreshAliases,
    enabled:
      (!requiresWorkforceSessionGate || workforceSessionGateState === "ready") &&
      workspaceMode === "clinician" &&
      !isWorkbenchActionSubmitting &&
      !isClinicianDocumentUploadSubmitting,
    loader: (signal) =>
      loadClinicianWorkbenchShellData(
        currentClinicianRequestState,
        effectiveRuntimeConfig,
        signal,
      ),
    apply: (nextShellData) => {
      if (
        !isCurrentClinicianSessionRequest(clinicianRefreshSessionEpoch)
      ) {
        return;
      }

      const resolvedRequestState: ClinicianWorkbenchRequestState = {
        ...currentClinicianRequestState,
        selectedPatientId: nextShellData.response.routeState.selectedPatientId,
        selectedDetailTabId: nextShellData.response.routeState.detailTab,
      };
      const requestCacheKey = buildClinicianWorkbenchShellCacheKey(
        currentClinicianRequestState,
        effectiveRuntimeConfig,
      );
      const resolvedCacheKey = buildClinicianWorkbenchShellCacheKey(
        resolvedRequestState,
        effectiveRuntimeConfig,
      );
      const responsePatientId =
        nextShellData.response.routeState.selectedPatientId;

      if (
        currentClinicianRequestState.selectedPatientId != null &&
        responsePatientId !== currentClinicianRequestState.selectedPatientId
      ) {
        return;
      }

      shellDataCacheRef.current.set(requestCacheKey, nextShellData);
      shellDataCacheRef.current.set(resolvedCacheKey, nextShellData);

      // A late poll for a previously selected patient only warms the cache —
      // including after the selection was cleared to null.
      if (
        (currentClinicianRequestState.selectedPatientId ?? null) !==
          (selectedPatientIdRef.current ?? null) ||
        (responsePatientId != null &&
          selectedPatientIdRef.current != null &&
          responsePatientId !== selectedPatientIdRef.current)
      ) {
        return;
      }

      startTransition(() => {
        setShellData(nextShellData);
        setLoadedShellDataCacheKey(resolvedCacheKey);
        setHasShellDataError(false);
        setIsShellDataLoading(false);
      });
    },
    onError: (error) => {
      if (
        !isCurrentClinicianSessionRequest(clinicianRefreshSessionEpoch)
      ) {
        return;
      }

      if (isWorkforceSessionUnauthorizedError(error)) {
        showWorkforceSessionExpiredGate();
        return;
      }

      if (renderedShellData != null) {
        return;
      }

      startTransition(() => {
        setHasShellDataError(true);
        setIsShellDataLoading(false);
      });
    },
    readGeneratedAt: (payload) => payload.response.generatedAt,
    readRevision: buildClinicianWorkbenchSummaryRevision,
    minStaleMs: 5_000,
    activePollMs: workspaceRefreshCadenceMs.standard,
  });

  useWorkspaceRefreshScope<ClinicianWorkbenchOutboundCallsShellData>({
    key: clinicianOutboundCallsRefreshKey,
    aliases: clinicianOutboundCallsRefreshAliases,
    enabled:
      (!requiresWorkforceSessionGate || workforceSessionGateState === "ready") &&
      isClinicianPhoneCallsPanelVisible &&
      clinicianOutboundPendingTargetId == null,
    loader: (signal) =>
      loadClinicianWorkbenchShellOutboundCalls(
        currentClinicianRequestState,
        selectedPatient?.id ?? "none",
        effectiveRuntimeConfig,
        signal,
      ),
    apply: (response) => {
      if (
        !isCurrentClinicianSessionRequest(clinicianRefreshSessionEpoch)
      ) {
        return;
      }

      setClinicianOutboundCallsByPatientId((current) => ({
        ...current,
        [response.patientId]: {
          errorText: null,
          generatedAt: response.generatedAt,
          isLoading: false,
          items: response.items,
          noticeText: current[response.patientId]?.noticeText ?? null,
        },
      }));
    },
    onError: (error) => {
      if (
        !isCurrentClinicianSessionRequest(clinicianRefreshSessionEpoch)
      ) {
        return;
      }

      if (isWorkforceSessionUnauthorizedError(error)) {
        showWorkforceSessionExpiredGate();
        return;
      }

      const patientId = selectedPatient?.id;

      if (!patientId) {
        return;
      }

      setClinicianOutboundCallsByPatientId((current) => ({
        ...current,
        [patientId]: {
          errorText:
            current[patientId]?.items.length > 0
              ? null
              : "Questionnaire calls could not be loaded. Try refreshing this panel.",
          generatedAt: current[patientId]?.generatedAt ?? null,
          isLoading: false,
          items: current[patientId]?.items ?? [],
          noticeText: current[patientId]?.noticeText ?? null,
        },
      }));
    },
    readGeneratedAt: (payload) => payload.generatedAt,
    readRevision: (payload) => payload.generatedAt,
    minStaleMs: 2_500,
    activePollMs: clinicianOutboundCallsPollMs,
    refreshOnRegister: true,
  });

  useWorkspaceRefreshScope<PatientWorkspaceSummaryResponseV1>({
    key: patientWorkspaceRefreshKey,
    aliases: [
      buildPatientWorkspaceRefreshAlias(effectiveRuntimeConfig.requestTenantId),
    ],
    enabled:
      (!requiresWorkforceSessionGate || workforceSessionGateState === "ready") &&
      workspaceMode === "patient" &&
      patientWorkspacePatientId != null &&
      !isPatientMessageCommandSubmitting &&
      !isPatientOnboardingCommandSubmitting,
    loader: (signal) =>
      loadPatientWorkspaceSummary(
        effectiveRuntimeConfig,
        patientWorkspacePatientId,
        signal,
      ),
    apply: (nextPatientWorkspaceSummary) => {
      startTransition(() => {
        setPatientWorkspaceSummary(
          sanitizePatientWorkspaceSummaryForProductDisplay(
            nextPatientWorkspaceSummary,
          ),
        );
        setHasPatientWorkspaceError(false);
        setIsPatientWorkspaceLoading(false);
      });
    },
    onError: (error) => {
      if (
        usesPatientPortalSession &&
        isPatientWorkspaceUnauthorizedError(error)
      ) {
        startTransition(() => {
          setPatientPortalSession(null);
          setPatientWorkspaceSummary(null);
          setPatientPortalAuthNotice("Your session expired. Sign in again.");
          setHasPatientWorkspaceError(false);
          setIsPatientWorkspaceLoading(false);
        });
        return;
      }

      if (patientWorkspaceSummary != null) {
        return;
      }

      startTransition(() => {
        setHasPatientWorkspaceError(true);
        setIsPatientWorkspaceLoading(false);
      });
    },
    readGeneratedAt: (payload) => payload.generatedAt,
    readRevision: buildPatientWorkspaceSummaryRevision,
    minStaleMs: 5_000,
    activePollMs: workspaceRefreshCadenceMs.standard,
  });

  function queueClinicianShellPrefetch(
    requestState: ClinicianWorkbenchRequestState,
  ): void {
    const requestSessionEpoch = clinicianSessionEpochRef.current;
    const requestCacheKey = buildClinicianWorkbenchShellCacheKey(
      requestState,
      effectiveRuntimeConfig,
    );

    if (
      shellDataCacheRef.current.has(requestCacheKey) ||
      shellDataPrefetchInFlightRef.current.has(requestCacheKey)
    ) {
      return;
    }

    shellDataPrefetchInFlightRef.current.add(requestCacheKey);

    void loadClinicianWorkbenchShellData(requestState, effectiveRuntimeConfig)
      .then((nextShellData) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        const resolvedRequestState: ClinicianWorkbenchRequestState = {
          ...requestState,
          selectedPatientId:
            nextShellData.response.routeState.selectedPatientId,
          selectedDetailTabId: nextShellData.response.routeState.detailTab,
        };
        const resolvedCacheKey = buildClinicianWorkbenchShellCacheKey(
          resolvedRequestState,
          effectiveRuntimeConfig,
        );

        shellDataCacheRef.current.set(requestCacheKey, nextShellData);
        shellDataCacheRef.current.set(resolvedCacheKey, nextShellData);
      })
      .catch((error: unknown) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          showWorkforceSessionExpiredGate();
        }
      })
      .finally(() => {
        if (isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          shellDataPrefetchInFlightRef.current.delete(requestCacheKey);
        }
      });
  }

  function openWorkforcePasswordGate(subjectKey: string | null): void {
    setSelectedWorkforceSubjectKey(subjectKey);
    setWorkforceLoginEmail("");
    setWorkforceLoginPassword("");
    setWorkforceLoginError(null);
    setWorkforceSessionGateState("password-required");
  }

  async function signInWorkforceSession(input: {
    credentials?: WorkforceSessionLoginCredentials | null;
    subjectKey: string | null;
  }): Promise<void> {
    const hasCredentialedLogin =
      input.credentials != null &&
      input.credentials.email.trim().length > 0 &&
      input.credentials.password.length > 0;

    if (!input.subjectKey && !hasCredentialedLogin) {
      setWorkforceLoginError("Choose a workspace before signing in.");
      setWorkforceSessionGateState("selection-required");
      return;
    }

    setIsWorkforceLoginSubmitting(true);
    setWorkforceLoginError(null);

    try {
      const nextSession = await loginWorkforceSession(
        runtimeConfig,
        input.subjectKey,
        input.credentials ?? null,
      );
      const expectedWorkspaceMode = resolveWorkspaceMode(
        runtimeConfig.tenantSessionRoleKeys,
      );

      if (
        !sessionSatisfiesWorkspaceMode(
          expectedWorkspaceMode,
          nextSession.roleKeys,
        )
      ) {
        void logoutWorkforceSession(runtimeConfig).catch(() => {});
        throw new Error(
          `workforce-session-workspace-role-mismatch:${expectedWorkspaceMode}`,
        );
      }

      startTransition(() => {
        transitionActiveWorkforceSession(nextSession);
        setSelectedWorkforceSubjectKey(nextSession.subjectKey);
        setRecentWorkforceSubjectKey(nextSession.subjectKey);
        writeStoredWorkforceSubjectKey(
          runtimeConfig.requestTenantId,
          nextSession.subjectKey,
        );
        setWorkforceLoginPassword("");
        setWorkforceSessionGateState("ready");
      });

      void listWorkforceSessionSubjects(runtimeConfig)
        .then((subjects) => {
          setAvailableWorkforceSubjects(subjects);
        })
        .catch(() => {});
    } catch (error) {
      const expectedWorkspaceMode = resolveWorkspaceMode(
        runtimeConfig.tenantSessionRoleKeys,
      );
      const loginErrorMessage =
        error instanceof Error &&
        error.message ===
          `workforce-session-workspace-role-mismatch:${expectedWorkspaceMode}`
          ? buildWorkspaceRoleMismatchMessage(expectedWorkspaceMode)
          : "Email or password is incorrect.";

      startTransition(() => {
        transitionActiveWorkforceSession(null);
        setWorkforceLoginError(loginErrorMessage);
        setWorkforceSessionGateState("password-required");
      });
    } finally {
      setIsWorkforceLoginSubmitting(false);
    }
  }

  function submitWorkforcePasswordLogin(): void {
    const fixedRouteSubjectKey = runtimeConfig.workforceSessionSubjectKey;
    const selectedSubjectKey = fixedRouteSubjectKey ?? selectedWorkforceSubjectKey;
    const hasCredentialedLogin =
      workforceLoginEmail.trim().length > 0 &&
      workforceLoginPassword.length > 0;

    void signInWorkforceSession({
      credentials: {
        email: workforceLoginEmail.trim(),
        password: workforceLoginPassword,
      },
      subjectKey: hasCredentialedLogin ? null : selectedSubjectKey,
    });
  }

  function beginWorkspaceSelection(): void {
    if (
      !requiresWorkforceSessionGate ||
      availableWorkforceSubjects.length < 2
    ) {
      return;
    }

    setSelectedWorkforceSubjectKey(
      activeWorkforceSession?.subjectKey ?? selectedWorkforceSubjectKey,
    );
    setWorkforceSessionGateState("selection-required");
  }

  function showWorkforceSessionExpiredGate(
    message = "Your session expired. Sign in again.",
  ): void {
    const fallbackSubjectKey =
      activeWorkforceSession?.subjectKey ??
      selectedWorkforceSubjectKey ??
      runtimeConfig.workforceSessionSubjectKey ??
      null;

    shellDataCacheRef.current.clear();
    shellDataPrefetchInFlightRef.current.clear();
    setLoadedShellDataCacheKey(null);
    clearClinicianSessionState();

    startTransition(() => {
      transitionActiveWorkforceSession(null);
      setSelectedWorkforceSubjectKey(fallbackSubjectKey);
      setShellData(null);
      setAdminSummary(null);
      setPatientWorkspaceSummary(null);
      setIsShellDataLoading(false);
      setHasShellDataError(false);
      setIsAdminSummaryLoading(false);
      setHasAdminSummaryError(false);
      setIsPatientWorkspaceLoading(false);
      setHasPatientWorkspaceError(false);
      setIsAdminCommandSubmitting(false);
      setIsWorkbenchActionSubmitting(false);
      setWorkbenchActionFeedbackKey(null);
      setWorkforceLoginPassword("");
      setWorkforceLoginError(message);
      setWorkforceSessionGateState("password-required");
    });
  }

  function performWorkforceSessionSignOut(): void {
    if (!requiresWorkforceSessionGate) {
      return;
    }

    setWorkforceLoginError(null);
    void logoutWorkforceSession(runtimeConfig)
      .catch(() => {
        // If the cookie is already gone or expired, the local session state
        // still needs to be cleared so the next action cannot appear to work.
      })
      .finally(() => {
        showWorkforceSessionExpiredGate("Signed out. Sign in again.");
      });
  }

  function signOutWorkforceSession(): void {
    if (!requiresWorkforceSessionGate) {
      return;
    }

    if (workspaceMode === "clinician" && hasClinicianUnsavedChanges()) {
      setClinicianProfilePendingNavigation({
        kind: "sign-out",
        value: null,
      });
      return;
    }

    performWorkforceSessionSignOut();
  }

  useEffect(() => {
    if (runtimeConfig.workforceSessionSubjectKey == null) {
      return;
    }

    setSelectedWorkforceSubjectKey(runtimeConfig.workforceSessionSubjectKey);
  }, [runtimeConfig.workforceSessionSubjectKey]);

  useEffect(() => {
    shellDataCacheRef.current.clear();
    shellDataPrefetchInFlightRef.current.clear();
    setLoadedShellDataCacheKey(null);
  }, [
    effectiveRuntimeConfig.requestClinicianUserId,
    effectiveRuntimeConfig.requestSiteId,
    effectiveRuntimeConfig.requestTenantId,
    effectiveRuntimeConfig.workforceSessionSubjectKey,
  ]);

  useEffect(() => {
    if (!shellData) {
      return;
    }

    setSelectedPatientId((current) =>
      current === shellData.response.routeState.selectedPatientId
        ? current
        : shellData.response.routeState.selectedPatientId,
    );
    setSelectedDetailTabId((current) =>
      current === shellData.response.routeState.detailTab
        ? current
        : shellData.response.routeState.detailTab,
    );
  }, [shellData]);

  useEffect(() => {
    if (
      reviewEditingMessageId == null ||
      !selectedPatient?.chatMessages.some(
        (message) => message.id === reviewEditingMessageId,
      )
    ) {
      setReviewEditingMessageId(null);
      setReviewEditDraft("");
    }
  }, [reviewEditingMessageId, selectedPatient]);

  useEffect(() => {
    if (voiceCallTranscriptModal == null) {
      return;
    }

    const matchingVoiceCall = selectedPatient?.voiceCallSummaries.find(
      (voiceCall) => voiceCall.id === voiceCallTranscriptModal.voiceCall.id,
    );

    if (!selectedPatient || !matchingVoiceCall) {
      setVoiceCallTranscriptModal(null);
    }
  }, [selectedPatient, voiceCallTranscriptModal]);

  useEffect(() => {
    if (clinicianClinicalEditorModal == null) {
      return;
    }

    if (
      !selectedPatient ||
      selectedPatient.id !== clinicianClinicalEditorModal.patientId
    ) {
      setClinicianClinicalEditorModal(null);
    }
  }, [clinicianClinicalEditorModal, selectedPatient]);

  const loadClinicianOutboundCallsForPatient = useCallback(
    (patientId: string, options: { silent?: boolean } = {}) => {
      const requestSessionEpoch = clinicianSessionEpochRef.current;

      if (!options.silent) {
        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [patientId]: {
            errorText: null,
            generatedAt: current[patientId]?.generatedAt ?? null,
            isLoading: true,
            items: current[patientId]?.items ?? [],
            noticeText: current[patientId]?.noticeText ?? null,
          },
        }));
      }

      void loadClinicianWorkbenchShellOutboundCalls(
        currentClinicianRequestState,
        patientId,
        effectiveRuntimeConfig,
      )
        .then((response) => {
          if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
            return;
          }

          if (response.patientId !== patientId) {
            throw new Error("clinician-outbound-calls-patient-mismatch");
          }

          setClinicianOutboundCallsByPatientId((current) => ({
            ...current,
            [patientId]: {
              errorText: null,
              generatedAt: response.generatedAt,
              isLoading: false,
              items: response.items,
              noticeText: current[patientId]?.noticeText ?? null,
            },
          }));
        })
        .catch((error: unknown) => {
          if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
            return;
          }

          if (isWorkforceSessionUnauthorizedError(error)) {
            showWorkforceSessionExpiredGate();
            return;
          }

          setClinicianOutboundCallsByPatientId((current) => ({
            ...current,
            [patientId]: {
              errorText:
                "Questionnaire calls could not be loaded. Try refreshing this panel.",
              generatedAt: current[patientId]?.generatedAt ?? null,
              isLoading: false,
              items: current[patientId]?.items ?? [],
              noticeText: current[patientId]?.noticeText ?? null,
            },
          }));
        });
    },
    [
      currentClinicianRequestState,
      effectiveRuntimeConfig,
      isCurrentClinicianSessionRequest,
      showWorkforceSessionExpiredGate,
    ],
  );

  // The tenant-served questionnaire definition drives the manual-entry surface
  // and the printed History Form. It only changes when the tenant flips its
  // questionnaire feature flag, so it loads once per tenant/site/clinician
  // config when the clinician workbench becomes active instead of riding the
  // workbench refresh loop. Failures are non-fatal: the definition stays null
  // and the chart pack keeps its bundled-fixture fallback.
  // A patient whose session is pinned to a version other than the campaign's
  // (a run in progress when a new version went live) gets THAT definition:
  // the manual form, status card and History Form must never be processed
  // against a different question set than the answers were given on.
  const selectedPatientPinnedQuestionnaireVersionId =
    selectedPatientOutboundQuestionnaireCalls.find(
      (call) =>
        call.questionnaireVersionPinnedToSession &&
        call.questionnaireTemplateVersionId != null &&
        call.questionnaireTemplateVersionId.length > 0,
    )?.questionnaireTemplateVersionId ?? null;
  const clinicianQuestionnaireDefinitionRequestKey = [
    effectiveRuntimeConfig.requestTenantId,
    effectiveRuntimeConfig.requestSiteId ?? "",
    effectiveRuntimeConfig.requestClinicianUserId,
    selectedPatientPinnedQuestionnaireVersionId ?? "",
  ].join("|");
  useEffect(() => {
    if (
      workspaceMode !== "clinician" ||
      (requiresWorkforceSessionGate && workforceSessionGateState !== "ready")
    ) {
      return;
    }

    if (
      !shouldLoadClinicianQuestionnaireDefinition(
        loadedClinicianQuestionnaireDefinitionKeyRef.current,
        clinicianQuestionnaireDefinitionRequestKey,
      )
    ) {
      return;
    }

    loadedClinicianQuestionnaireDefinitionKeyRef.current =
      clinicianQuestionnaireDefinitionRequestKey;
    let requestIsCurrent = true;
    const requestSessionEpoch = clinicianSessionEpochRef.current;

    void loadClinicianWorkbenchShellQuestionnaireDefinition(
      currentClinicianRequestState,
      effectiveRuntimeConfig,
      undefined,
      { questionnaireTemplateVersionId: selectedPatientPinnedQuestionnaireVersionId },
    )
      .then((response) => {
        if (
          !requestIsCurrent ||
          !isCurrentClinicianSessionRequest(requestSessionEpoch) ||
          loadedClinicianQuestionnaireDefinitionKeyRef.current !==
            clinicianQuestionnaireDefinitionRequestKey
        ) {
          return;
        }

        setClinicianQuestionnaireDefinition(response.definition);
      })
      .catch((error: unknown) => {
        if (
          !requestIsCurrent ||
          !isCurrentClinicianSessionRequest(requestSessionEpoch)
        ) {
          return;
        }

        if (
          loadedClinicianQuestionnaireDefinitionKeyRef.current ===
          clinicianQuestionnaireDefinitionRequestKey
        ) {
          loadedClinicianQuestionnaireDefinitionKeyRef.current = null;
        }

        console.warn(
          "clinician-workbench-questionnaire-definition-load-failed",
          error,
        );
      });

    return () => {
      requestIsCurrent = false;

      if (
        loadedClinicianQuestionnaireDefinitionKeyRef.current ===
        clinicianQuestionnaireDefinitionRequestKey
      ) {
        loadedClinicianQuestionnaireDefinitionKeyRef.current = null;
      }
    };
  }, [
    clinicianSessionIdentity,
    clinicianQuestionnaireDefinitionRequestKey,
    currentClinicianRequestState,
    effectiveRuntimeConfig,
    isCurrentClinicianSessionRequest,
    requiresWorkforceSessionGate,
    selectedPatientPinnedQuestionnaireVersionId,
    workforceSessionGateState,
    workspaceMode,
  ]);

  useEffect(() => {
    const patientId = selectedPatient?.id ?? null;
    const noticeText = selectedPatientOutboundCallsState?.noticeText ?? null;
    const hasPendingOutboundOperation =
      clinicianOutboundPendingTargetId != null ||
      clinicianOutboundPdfPendingTargetId != null ||
      clinicianOutboundManualPending;

    if (patientId == null || noticeText == null || hasPendingOutboundOperation) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setClinicianOutboundCallsByPatientId((current) => {
        const currentState = current[patientId];

        if (currentState?.noticeText !== noticeText) {
          return current;
        }

        return {
          ...current,
          [patientId]: {
            ...currentState,
            noticeText: null,
          },
        };
      });
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [
    clinicianOutboundManualPending,
    clinicianOutboundPdfPendingTargetId,
    clinicianOutboundPendingTargetId,
    selectedPatient?.id,
    selectedPatientOutboundCallsState?.noticeText,
  ]);

  useEffect(() => {
    if (
      workspaceMode !== "clinician" ||
      // The patient-profile view needs the outbound data too: the manual
      // questionnaire entry prefills from the active AI-call session, and a
      // clinician often opens the form without visiting the phone panel.
      (selectedDetailTabId !== "phone-calls" &&
        clinicianCenterPaneView !== "phone-calls" &&
        clinicianCenterPaneView !== "patient-profile") ||
      selectedPatient == null ||
      // The registered refresh scope owns initial loading and polling for the
      // phone panel. This one-shot path only supplies patient-profile, where
      // outbound data is needed for manual questionnaire prefill.
      isClinicianPhoneCallsPanelVisible
    ) {
      return;
    }

    const existingState =
      clinicianOutboundCallsByPatientId[selectedPatient.id] ?? null;

    if (existingState?.isLoading || existingState?.generatedAt) {
      return;
    }

    loadClinicianOutboundCallsForPatient(selectedPatient.id, { silent: true });
  }, [
    clinicianCenterPaneView,
    clinicianOutboundCallsByPatientId,
    isClinicianPhoneCallsPanelVisible,
    loadClinicianOutboundCallsForPatient,
    selectedDetailTabId,
    selectedPatient,
    workspaceMode,
  ]);

  function executeClinicianOutboundCallAction(input: {
    action: ClinicianWorkbenchOutboundCallAction;
    acknowledgeOutsideWindow?: boolean;
    acknowledgeRepeat?: boolean;
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1;
    patientId: string;
  }): void {
    if (clinicianOutboundPendingTargetId != null) {
      return;
    }

    const requestSessionEpoch = clinicianSessionEpochRef.current;
    setClinicianOutboundPendingTargetId(input.call.targetId);
    setClinicianOutboundCallConfirmModal((current) =>
      current != null &&
      isMatchingOwnedCallConfirmation(
        {
          patientId: current.patientId,
          targetId: current.call.targetId,
        },
        {
          patientId: input.patientId,
          targetId: input.call.targetId,
        },
      )
        ? { ...current, errorText: null }
        : current,
    );
    setClinicianOutboundCallsByPatientId((current) => ({
      ...current,
      [input.patientId]: {
        errorText: null,
        generatedAt: current[input.patientId]?.generatedAt ?? null,
        isLoading: false,
        items: current[input.patientId]?.items ?? [],
        noticeText: "Starting AmpleCare questionnaire call.",
      },
    }));

    void submitClinicianWorkbenchShellOutboundCallsCommand(
      currentClinicianRequestState,
      {
        action: input.action,
        acknowledgeOutsideWindow: input.acknowledgeOutsideWindow ?? false,
        acknowledgeRepeat: input.acknowledgeRepeat ?? false,
        attemptId: input.call.queuedAttemptId,
        patientId: input.patientId,
        reasonText: "Clinician started from patient phone calls panel.",
        sessionId: input.call.queuedSessionId ?? input.call.sessionId,
        targetId: input.call.targetId,
      },
      effectiveRuntimeConfig,
    )
      .then((response) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [input.patientId]: {
            errorText: null,
            generatedAt: response.generatedAt,
            isLoading: false,
            items: response.items,
            noticeText: response.summaryText,
          },
        }));
        setClinicianOutboundCallConfirmModal((current) =>
          isMatchingOwnedCallConfirmation(
            current == null
              ? null
              : {
                  patientId: current.patientId,
                  targetId: current.call.targetId,
                },
            {
              patientId: input.patientId,
              targetId: input.call.targetId,
            },
          )
            ? null
            : current,
        );
      })
      .catch((error: unknown) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          showWorkforceSessionExpiredGate();
          return;
        }

        const errorText = resolveClinicianOutboundCallErrorText(error);

        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [input.patientId]: {
            errorText,
            generatedAt: current[input.patientId]?.generatedAt ?? null,
            isLoading: false,
            items: current[input.patientId]?.items ?? [],
            noticeText: null,
          },
        }));
        // A stale-state conflict means the modal's captured call snapshot can
        // never succeed again; keeping the retry button armed guarantees an
        // identical 409. Close it and reload so the panel shows real state.
        const isStaleState = isStaleOutboundCallStateError(error);

        setClinicianOutboundCallConfirmModal((current) =>
          current != null &&
          isMatchingOwnedCallConfirmation(
            {
              patientId: current.patientId,
              targetId: current.call.targetId,
            },
            {
              patientId: input.patientId,
              targetId: input.call.targetId,
            },
          )
            ? isStaleState
              ? null
              : { ...current, errorText }
            : current,
        );

        if (isStaleState) {
          loadClinicianOutboundCallsForPatient(input.patientId, {
            silent: true,
          });
        }
      })
      .finally(() => {
        if (isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          setClinicianOutboundPendingTargetId(null);
        }
      });
  }

  // Manual continuation of an interrupted AI call (GP workflow): mirrors the
  // clinician's form answers into the outbound session; completing settles
  // the session and stops automated dialing for the round.
  function executeClinicianOutboundManualQuestionnaireCommand(
    input: {
      action: "record-manual-answers" | "complete-session-manually";
      clearedQuestionKeys: string[];
      expectedSessionRevision: string;
      manualAnswers: ClinicianWorkbenchOutboundManualAnswerV1[];
      patientId: string;
      sessionId: string;
      targetId: string;
    },
    options?: {
      onError?: (message: string) => void;
      onSuccess?: (
        summaryText: string,
        sessionRevision: string | null,
      ) => void;
    },
  ): void {
    const requestSessionEpoch = clinicianSessionEpochRef.current;
    setClinicianOutboundManualPending(true);

    void submitClinicianWorkbenchShellOutboundCallsCommand(
      currentClinicianRequestState,
      {
        action: input.action,
        clearedQuestionKeys: input.clearedQuestionKeys,
        expectedSessionRevision: input.expectedSessionRevision,
        manualAnswers: input.manualAnswers,
        patientId: input.patientId,
        sessionId: input.sessionId,
        targetId: input.targetId,
      },
      effectiveRuntimeConfig,
    )
      .then((response) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [input.patientId]: {
            errorText: null,
            generatedAt: response.generatedAt,
            isLoading: false,
            items: response.items,
            noticeText: response.summaryText,
          },
        }));
        invalidateWorkspaceRefreshScopes(
          workspaceMutationRefreshAliases,
          "clinician-outbound-manual-questionnaire-success",
          { tenantId: effectiveRuntimeConfig.requestTenantId },
        );
        const updatedSessionRevision =
          response.items.find(
            (item) => item.sessionId === input.sessionId,
          )?.sessionRevision ?? null;
        options?.onSuccess?.(
          response.summaryText,
          updatedSessionRevision,
        );
      })
      .catch((error: unknown) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          showWorkforceSessionExpiredGate();
          return;
        }

        const errorText =
          error instanceof Error && error.message.includes(": ")
            ? error.message.split(": ").slice(1).join(": ")
            : "Manual questionnaire answers could not be recorded to the call session.";

        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [input.patientId]: {
            errorText,
            generatedAt: current[input.patientId]?.generatedAt ?? null,
            isLoading: false,
            items: current[input.patientId]?.items ?? [],
            noticeText: null,
          },
        }));
        options?.onError?.(errorText);
      })
      .finally(() => {
        if (isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          setClinicianOutboundManualPending(false);
        }
      });
  }

  function executeClinicianMedicationSearch(input: {
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1;
    patientId: string;
  }): void {
    const resolution = clinicianMedicationResolution;

    if (
      !resolution ||
      resolution.isBusy ||
      resolution.patientId !== input.patientId ||
      resolution.sessionId !== input.call.sessionId ||
      resolution.targetId !== input.call.targetId
    ) {
      return;
    }

    const query = resolution.query.trim();

    if (query.length < 2) {
      setClinicianMedicationResolution((current) =>
        current
          ? { ...current, errorText: "Type at least two characters to search." }
          : current,
      );
      return;
    }

    setClinicianMedicationResolution((current) =>
      current ? { ...current, isBusy: true, errorText: null } : current,
    );

    // Async ownership: the completion may land after the resolver was closed
    // and reopened for a DIFFERENT patient/question/entry — apply it only to
    // the exact resolver it was dispatched for.
    const dispatchedFor = {
      entryIndex: resolution.entryIndex,
      patientId: resolution.patientId,
      questionKey: resolution.questionKey,
      sessionId: resolution.sessionId,
      targetId: resolution.targetId,
    };
    const requestSessionEpoch = clinicianSessionEpochRef.current;
    const matchesDispatched = (
      current: ClinicianMedicationResolutionState,
    ): current is NonNullable<ClinicianMedicationResolutionState> =>
      isMatchingMedicationResolutionOwner(current, dispatchedFor);

    void submitClinicianWorkbenchShellOutboundCallsCommand(
      currentClinicianRequestState,
      {
        action: "search-medication-catalog",
        medicationQuery: query,
        patientId: input.patientId,
        sessionId: input.call.sessionId,
        targetId: input.call.targetId,
      },
      effectiveRuntimeConfig,
    )
      .then((response) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        setClinicianMedicationResolution((current) =>
          matchesDispatched(current)
            ? {
                ...current,
                candidates: response.medicationCatalogCandidates ?? [],
                isBusy: false,
                selectedDrugName: null,
                selectedRxcui: null,
                selectedStrength: null,
              }
            : current,
        );
      })
      .catch((error: unknown) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          showWorkforceSessionExpiredGate();
          return;
        }

        setClinicianMedicationResolution((current) =>
          matchesDispatched(current)
            ? {
                ...current,
                errorText:
                  error instanceof Error && error.message.includes(": ")
                    ? error.message.split(": ").slice(1).join(": ")
                    : "The medication catalog search failed. Try again.",
                isBusy: false,
              }
            : current,
        );
      });
  }

  function executeClinicianMedicationResolve(input: {
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1;
    patientId: string;
  }): void {
    const resolution = clinicianMedicationResolution;

    if (
      !resolution ||
      !resolution.selectedDrugName ||
      resolution.isBusy ||
      resolution.patientId !== input.patientId ||
      resolution.sessionId !== input.call.sessionId ||
      resolution.targetId !== input.call.targetId
    ) {
      return;
    }

    setClinicianMedicationResolution((current) =>
      current ? { ...current, isBusy: true, errorText: null } : current,
    );

    // Async ownership: a late completion must only touch the exact resolver
    // it was dispatched for — never close or error one reopened elsewhere.
    const dispatchedFor = {
      entryIndex: resolution.entryIndex,
      patientId: resolution.patientId,
      questionKey: resolution.questionKey,
      sessionId: resolution.sessionId,
      targetId: resolution.targetId,
    };
    const requestSessionEpoch = clinicianSessionEpochRef.current;
    const matchesDispatched = (
      current: ClinicianMedicationResolutionState,
    ): current is NonNullable<ClinicianMedicationResolutionState> =>
      isMatchingMedicationResolutionOwner(current, dispatchedFor);

    void submitClinicianWorkbenchShellOutboundCallsCommand(
      currentClinicianRequestState,
      {
        action: "resolve-medication",
        medicationEntryIndex: resolution.entryIndex,
        medicationQuestionKey: resolution.questionKey,
        patientId: input.patientId,
        resolvedDrugName: resolution.selectedDrugName,
        resolvedRxcui: resolution.selectedRxcui,
        resolvedStrength: resolution.selectedStrength,
        sessionId: input.call.sessionId,
        targetId: input.call.targetId,
      },
      effectiveRuntimeConfig,
    )
      .then((response) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        setClinicianMedicationResolution((current) =>
          matchesDispatched(current) ? null : current,
        );
        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [input.patientId]: {
            errorText: null,
            generatedAt: response.generatedAt,
            isLoading: false,
            items: response.items,
            noticeText: response.summaryText,
          },
        }));
        setClinicianOutboundAnswersModal((current) => {
          const expectedOwner = {
            patientId: input.patientId,
            sessionId: input.call.sessionId,
            targetId: input.call.targetId,
          };

          if (
            current == null ||
            !isMatchingOutboundCallOwner(
              {
                patientId: current.patientId,
                sessionId: current.call.sessionId,
                targetId: current.call.targetId,
              },
              expectedOwner,
            )
          ) {
            return current;
          }

          const updatedCall = response.items.find(
            (candidate) =>
              isMatchingOutboundCallOwner(
                {
                  patientId: input.patientId,
                  sessionId: candidate.sessionId,
                  targetId: candidate.targetId,
                },
                expectedOwner,
              ),
          );

          return updatedCall ? { ...current, call: updatedCall } : current;
        });
      })
      .catch((error: unknown) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          showWorkforceSessionExpiredGate();
          return;
        }

        setClinicianMedicationResolution((current) =>
          matchesDispatched(current)
            ? {
                ...current,
                errorText:
                  error instanceof Error && error.message.includes(": ")
                    ? error.message.split(": ").slice(1).join(": ")
                    : "The medication could not be resolved. Try again.",
                isBusy: false,
              }
            : current,
        );
      });
  }

  function executeClinicianOutboundReviewAction(input: {
    action: "assign-review" | "mark-review-reviewed" | "escalate-review";
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1;
    patientId: string;
    reviewNotes?: string | null;
  }): void {
    if (clinicianOutboundPendingTargetId != null) {
      return;
    }

    const requestSessionEpoch = clinicianSessionEpochRef.current;
    setClinicianOutboundPendingTargetId(input.call.targetId);
    setClinicianOutboundCallsByPatientId((current) => ({
      ...current,
      [input.patientId]: {
        errorText: null,
        generatedAt: current[input.patientId]?.generatedAt ?? null,
        isLoading: false,
        items: current[input.patientId]?.items ?? [],
        noticeText: "Updating questionnaire review.",
      },
    }));

    const escalationQueueKey =
      input.call.answerPreview
        .find((answer) => answer.reviewQueueKey?.trim())
        ?.reviewQueueKey?.trim() ?? "outbound-questionnaire-review";
    const reviewNotes = input.reviewNotes?.trim() ?? "";
    const reviewReasonText =
      input.action === "mark-review-reviewed"
        ? "Clinician completed outbound questionnaire review."
        : input.action === "escalate-review"
          ? "Clinician escalated outbound questionnaire review for follow-up."
          : "Clinician took ownership of outbound questionnaire review.";

    void submitClinicianWorkbenchShellOutboundCallsCommand(
      currentClinicianRequestState,
      {
        action: input.action,
        escalationReasonKey:
          input.action === "escalate-review"
            ? "clinical_follow_up_needed"
            : null,
        patientId: input.patientId,
        reasonText: reviewReasonText,
        reviewNotes:
          input.reviewNotes == null ? null : input.reviewNotes.trim(),
        reviewOwnerKind:
          input.action === "escalate-review" ? "queue" : "clinician",
        reviewOwnerReference:
          input.action === "escalate-review"
            ? escalationQueueKey
            : clinicianUserId,
        sessionId: input.call.sessionId,
        targetId: input.call.targetId,
      },
      effectiveRuntimeConfig,
    )
      .then((response) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [input.patientId]: {
            errorText: null,
            generatedAt: response.generatedAt,
            isLoading: false,
            items: response.items,
            noticeText: response.summaryText,
          },
        }));
        setClinicianOutboundAnswersModal((current) => {
          const expectedOwner = {
            patientId: input.patientId,
            sessionId: input.call.sessionId,
            targetId: input.call.targetId,
          };

          if (
            current == null ||
            !isMatchingOutboundCallOwner(
              {
                patientId: current.patientId,
                sessionId: current.call.sessionId,
                targetId: current.call.targetId,
              },
              expectedOwner,
            )
          ) {
            return current;
          }

          const updatedCall = response.items.find(
            (candidate) =>
              isMatchingOutboundCallOwner(
                {
                  patientId: input.patientId,
                  sessionId: candidate.sessionId,
                  targetId: candidate.targetId,
                },
                expectedOwner,
              ),
          );

          if (!updatedCall) {
            return current;
          }

          const persistedReviewNotes =
            resolveOutboundQuestionnaireReviewNotes(updatedCall);

          return {
            ...current,
            call: updatedCall,
            persistedReviewNotes,
            reviewNotes: persistedReviewNotes,
          };
        });
      })
      .catch((error: unknown) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          showWorkforceSessionExpiredGate();
          return;
        }

        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [input.patientId]: {
            errorText:
              error instanceof Error && error.message.includes(": ")
                ? error.message.split(": ").slice(1).join(": ")
                : "The questionnaire review could not be updated. Check permissions and try again.",
            generatedAt: current[input.patientId]?.generatedAt ?? null,
            isLoading: false,
            items: current[input.patientId]?.items ?? [],
            noticeText: null,
          },
        }));
      })
      .finally(() => {
        if (isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          setClinicianOutboundPendingTargetId(null);
        }
      });
  }

  function requestClinicianOutboundCallAction(
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ): void {
    if (
      selectedPatient == null ||
      call.primaryAction == null ||
      clinicianOutboundPendingTargetId != null
    ) {
      return;
    }

    const warnings = [
      ...(call.outsideCallWindow ? call.warningMessages.slice(0, 1) : []),
      ...(call.primaryAction === "start-new-round" && call.repeatWarning
        ? call.warningMessages.filter((message) =>
            message.toLowerCase().includes("already"),
          )
        : []),
    ].filter((warning) => warning.trim().length > 0);

    // EVERY dial (and queue) goes through the confirmation modal — a bare
    // click must never place a live call to a patient. Warnings, when
    // present, render inside the same dialog.
    setClinicianOutboundCallConfirmModal({
      action: call.primaryAction,
      call,
      errorText: null,
      patientId: selectedPatient.id,
      patientName: selectedPatient.displayName,
      warnings,
    });
  }

  function downloadClinicianOutboundQuestionnaireAnswersPdf(
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ): void {
    if (selectedPatient == null || clinicianOutboundPdfPendingTargetId != null) {
      return;
    }

    const patientId = selectedPatient.id;
    const requestSessionEpoch = clinicianSessionEpochRef.current;

    if (
      Platform.OS !== "web" ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      setClinicianOutboundCallsByPatientId((current) => ({
        ...current,
        [patientId]: {
          errorText:
            "Questionnaire answer PDFs can be downloaded from the hosted web clinician workspace.",
          generatedAt: current[patientId]?.generatedAt ?? null,
          isLoading: false,
          items: current[patientId]?.items ?? [],
          noticeText: null,
        },
      }));
      return;
    }

    setClinicianOutboundPdfPendingTargetId(call.targetId);
    setClinicianOutboundCallsByPatientId((current) => ({
      ...current,
      [patientId]: {
        errorText: null,
        generatedAt: current[patientId]?.generatedAt ?? null,
        isLoading: false,
        items: current[patientId]?.items ?? [],
        noticeText: "Preparing questionnaire answer PDF...",
      },
    }));

    void downloadClinicianWorkbenchShellOutboundQuestionnaireAnswersPdf(
      currentClinicianRequestState,
      {
        patientId,
        sessionId: call.sessionId,
        targetId: call.targetId,
      },
      effectiveRuntimeConfig,
    )
      .then((result) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        const blob = new Blob([result.bodyBytes], {
          type: result.contentType || "application/pdf",
        });
        const objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");

        anchor.href = objectUrl;
        anchor.download = result.filename || "one-mnet-preop-answers.pdf";
        anchor.rel = "noopener";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [patientId]: {
            errorText: null,
            generatedAt: current[patientId]?.generatedAt ?? null,
            isLoading: false,
            items: current[patientId]?.items ?? [],
            noticeText: "Questionnaire answer PDF downloaded.",
          },
        }));
      })
      .catch((error: unknown) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          showWorkforceSessionExpiredGate();
          return;
        }

        setClinicianOutboundCallsByPatientId((current) => ({
          ...current,
          [patientId]: {
            errorText:
              error instanceof Error && error.message.includes(": ")
                ? error.message.split(": ").slice(1).join(": ")
                : "The questionnaire answer PDF could not be prepared. Refresh and try again.",
            generatedAt: current[patientId]?.generatedAt ?? null,
            isLoading: false,
            items: current[patientId]?.items ?? [],
            noticeText: null,
          },
        }));
      })
      .finally(() => {
        if (isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          setClinicianOutboundPdfPendingTargetId(null);
        }
      });
  }

  selectedPatientIdRef.current = selectedPatientId;

  // Per-patient interaction state is owned by the patient it was opened for.
  // Switching patients discards drafts, editors, and modals outright — a
  // draft typed under one patient must never submit against another.
  useEffect(() => {
    clinicianConversationNearEndRef.current = true;
    setInlineClinicalEditor(null);
    setClinicianClinicalEditorModal(null);
    setClinicianClinicalEditorDiscardPromptOpen(false);
    setComposerDraft("");
    setReviewEditingMessageId(null);
    setReviewEditDraft("");
    setEditedReviewBodiesByMessageId({});
    setClinicianOutboundAnswersModal(null);
    setClinicianOutboundAnswersDiscardPromptOpen(false);
    setClinicianMedicationResolution(null);
    setVoiceCallTranscriptModal(null);
    setClinicianOutboundCallConfirmModal(null);
  }, [selectedPatientId]);

  useEffect(() => {
    if (!selectedPatient) {
      setInlineClinicalEditor(null);
    }
  }, [selectedPatient]);

  useEffect(() => {
    if (
      selectedDetailTabId !== "case-summary" &&
      selectedDetailTabId !== "appointments"
    ) {
      setInlineClinicalEditor(null);
    }
  }, [selectedDetailTabId]);

  useEffect(() => {
    if (!selectedPatient) {
      lastClinicianConversationAutoScrollKeyRef.current = null;
      suspendedClinicianConversationAutoScrollKeyRef.current = null;
      return;
    }

    if (reviewEditingMessageId != null) {
      suspendedClinicianConversationAutoScrollKeyRef.current =
        latestClinicianConversationMessageKey;
      return;
    }

    if (firstPendingClinicianApprovalMessageKey != null) {
      const reviewFirstKey = `review-first:${firstPendingClinicianApprovalMessageKey}`;

      if (lastClinicianConversationAutoScrollKeyRef.current !== reviewFirstKey) {
        lastClinicianConversationAutoScrollKeyRef.current = reviewFirstKey;
        suspendedClinicianConversationAutoScrollKeyRef.current = null;
        scrollClinicianConversationToStart(true);
      }

      return;
    }

    if (
      suspendedClinicianConversationAutoScrollKeyRef.current ===
      latestClinicianConversationMessageKey
    ) {
      lastClinicianConversationAutoScrollKeyRef.current =
        latestClinicianConversationMessageKey;
      suspendedClinicianConversationAutoScrollKeyRef.current = null;
      return;
    }

    if (
      lastClinicianConversationAutoScrollKeyRef.current ===
      latestClinicianConversationMessageKey
    ) {
      return;
    }

    if (!clinicianConversationNearEndRef.current) {
      lastClinicianConversationAutoScrollKeyRef.current =
        latestClinicianConversationMessageKey;
      return;
    }

    lastClinicianConversationAutoScrollKeyRef.current =
      latestClinicianConversationMessageKey;
    scrollClinicianConversationToEnd(true);
  }, [
    latestClinicianConversationMessageKey,
    firstPendingClinicianApprovalMessageKey,
    reviewEditingMessageId,
    scrollClinicianConversationToEnd,
    scrollClinicianConversationToStart,
    selectedPatient?.id,
  ]);

  useEffect(() => {
    if (!requiresWorkforceSessionGate) {
      setWorkforceSessionGateState("ready");
      setAvailableWorkforceSubjects([]);
      transitionActiveWorkforceSession(null);
      return;
    }

    let isCancelled = false;
    const preferredSubjectKey =
      runtimeConfig.workforceSessionSubjectKey ?? selectedWorkforceSubjectKey;
    const preferredWorkspaceMode = resolveWorkspaceMode(
      runtimeConfig.tenantSessionRoleKeys,
    );

    setWorkforceSessionGateState("loading");

    void readCurrentWorkforceSession(runtimeConfig)
      .then(async (currentSession) => {
        if (
          currentSession &&
          matchesWorkforceSessionConfig(
            runtimeConfig,
            currentSession,
            null,
          ) &&
          matchesPreferredWorkspaceMode(
            currentSession.roleKeys,
            preferredWorkspaceMode,
          )
        ) {
          const subjects = await listWorkforceSessionSubjects(runtimeConfig);

          if (isCancelled) {
            return;
          }

          setAvailableWorkforceSubjects(subjects);

          if (!isCancelled) {
            transitionActiveWorkforceSession(currentSession);
            setSelectedWorkforceSubjectKey(currentSession.subjectKey);
            setRecentWorkforceSubjectKey(currentSession.subjectKey);
            writeStoredWorkforceSubjectKey(
              runtimeConfig.requestTenantId,
              currentSession.subjectKey,
            );
            setWorkforceSessionGateState("ready");
          }
          return;
        }

        const subjects = await listWorkforceSessionSubjects(runtimeConfig);

        if (isCancelled) {
          return;
        }

        setAvailableWorkforceSubjects(subjects);

        if (runtimeConfig.workforceSessionSubjectKey != null) {
          const runtimeSubject = findWorkforceSubjectByKey(
            subjects,
            runtimeConfig.workforceSessionSubjectKey,
          );

          setSelectedWorkforceSubjectKey(runtimeConfig.workforceSessionSubjectKey);

          if (runtimeSubject?.requiresPassword ?? true) {
            transitionActiveWorkforceSession(null);
            setWorkforceLoginEmail("");
            setWorkforceLoginPassword("");
            setWorkforceLoginError(null);
            setWorkforceSessionGateState("password-required");
            return;
          }

          const nextSession = await loginWorkforceSession(
            runtimeConfig,
            runtimeConfig.workforceSessionSubjectKey,
          );

          if (isCancelled) {
            return;
          }

          transitionActiveWorkforceSession(nextSession);
          setSelectedWorkforceSubjectKey(nextSession.subjectKey);
          setRecentWorkforceSubjectKey(nextSession.subjectKey);
          writeStoredWorkforceSubjectKey(
            runtimeConfig.requestTenantId,
            nextSession.subjectKey,
          );
          setWorkforceSessionGateState("ready");
          return;
        }

        const preferredSubject = preferredSubjectKey
          ? findWorkforceSubjectByKey(subjects, preferredSubjectKey)
          : null;
        const preferredSubjectIsAvailable =
          preferredSubjectKey == null ||
          (preferredSubject != null &&
            matchesPreferredWorkspaceMode(
              preferredSubject.roleKeys,
              preferredWorkspaceMode,
            ));
        const resolvedSubjectKey = preferredSubjectIsAvailable
          ? (preferredSubjectKey ??
            resolveAutoSelectedWorkforceSessionSubjectKeyForMode(
              subjects,
              preferredWorkspaceMode,
            ))
          : resolveAutoSelectedWorkforceSessionSubjectKeyForMode(
              subjects,
              preferredWorkspaceMode,
            );

        if (isCancelled) {
          return;
        }

        if (!preferredSubjectIsAvailable) {
          setSelectedWorkforceSubjectKey(null);
          setRecentWorkforceSubjectKey(null);
          writeStoredWorkforceSubjectKey(runtimeConfig.requestTenantId, null);
        }

        if (!resolvedSubjectKey) {
          transitionActiveWorkforceSession(null);
          setWorkforceSessionGateState("selection-required");
          return;
        }

        const resolvedSubject = findWorkforceSubjectByKey(
          subjects,
          resolvedSubjectKey,
        );

        setSelectedWorkforceSubjectKey(resolvedSubjectKey);

        if (resolvedSubject?.requiresPassword) {
          transitionActiveWorkforceSession(null);
          setWorkforceLoginEmail("");
          setWorkforceLoginPassword("");
          setWorkforceLoginError(null);
          setWorkforceSessionGateState("password-required");
          return;
        }

        const nextSession = await loginWorkforceSession(runtimeConfig, resolvedSubjectKey);

        if (isCancelled) {
          return;
        }

        transitionActiveWorkforceSession(nextSession);
        setSelectedWorkforceSubjectKey(nextSession.subjectKey);
        setRecentWorkforceSubjectKey(nextSession.subjectKey);
        writeStoredWorkforceSubjectKey(
          runtimeConfig.requestTenantId,
          nextSession.subjectKey,
        );
        setWorkforceSessionGateState("ready");
        setAvailableWorkforceSubjects(subjects);
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        transitionActiveWorkforceSession(null);
        setWorkforceSessionGateState("error");
      });

    return () => {
      isCancelled = true;
    };
  }, [
    requiresWorkforceSessionGate,
    runtimeConfig.requestTenantId,
    runtimeConfig.sessionApiBaseUrl,
    runtimeConfig.workforceSessionSubjectKey,
    transitionActiveWorkforceSession,
  ]);

  useEffect(() => {
    if (!usesPatientPortalSession) {
      setPatientPortalSession(null);
      setIsPatientPortalSessionLoading(false);
      return;
    }

    let isCancelled = false;

    setIsPatientPortalSessionLoading(true);

    void readCurrentWorkforceSession(runtimeConfig)
      .then((currentSession) => {
        if (isCancelled) {
          return;
        }

        if (
          currentSession &&
          currentSession.tenantId === runtimeConfig.requestTenantId &&
          resolveWorkspaceMode(currentSession.roleKeys) === "patient"
        ) {
          setPatientPortalSession(currentSession);
          setHasPatientWorkspaceError(false);
          return;
        }

        setPatientPortalSession(null);
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setPatientPortalSession(null);
      })
      .finally(() => {
        if (isCancelled) {
          return;
        }

        setIsPatientPortalSessionLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [
    runtimeConfig.requestTenantId,
    runtimeConfig.sessionApiBaseUrl,
    usesPatientPortalSession,
  ]);

  useEffect(() => {
    setComposerDraft("");
    setClinicianComposerMode("reply");
    setWorkbenchActionFeedbackKey(null);
  }, [selectedPatientId]);

  useEffect(() => {
    if (workspaceMode !== "clinician") {
      return () => {};
    }

    if (Platform.OS === "web" && typeof window !== "undefined") {
      clinicianWorkspacePathnameRef.current =
        resolveCanonicalWorkspacePathname(window.location.pathname);
    }

    const currentRouteState = readWorkbenchRouteStateFromWindow();

    if (currentRouteState) {
      setSelectedPatientTabId(currentRouteState.selectedPatientTabId);
      setSelectedWorkbenchPanelId(currentRouteState.selectedWorkbenchPanelId);
      setRequiresAttentionOnly(currentRouteState.requiresAttentionOnly);
      setSearchQuery(currentRouteState.searchQuery);
      setSelectedPatientId(currentRouteState.selectedPatientId);
      setSelectedDetailTabId(currentRouteState.selectedDetailTabId);
    }

    const unsubscribe = subscribeToWorkbenchRouteState((routeState) => {
      const historyRestoration = clinicianHistoryRestorationRef.current;

      // A guarded Back traverses forward without changing React state. This
      // second popstate runs on the restored clinician entry, where it is safe
      // to dismiss the top dialog or show the navigation confirmation.
      if (historyRestoration) {
        clinicianHistoryRestorationRef.current = null;

        if (historyRestoration.kind === "dismiss-profile-dialog") {
          setClinicianProfileDiscardDismissRequestKey((value) => value + 1);
        } else if (historyRestoration.kind === "open-navigation") {
          setClinicianProfilePendingNavigation(
            historyRestoration.navigation,
          );
        }

        return;
      }

      const destinationPathname =
        Platform.OS === "web" && typeof window !== "undefined"
          ? resolveCanonicalWorkspacePathname(window.location.pathname)
          : undefined;

      if (
        hasClinicianUnsavedChanges() &&
        (!areClinicianWorkbenchRouteStatesEqual(
          clinicianRouteStateRef.current,
          routeState,
        ) ||
          (destinationPathname != null &&
            destinationPathname !== clinicianWorkspacePathnameRef.current))
      ) {
        const pendingNavigation: ClinicianProfilePendingNavigation = {
          historyAction: "back",
          kind: "patient",
          pathname: destinationPathname,
          routeState,
          value: routeState.selectedPatientId,
        };

        if (Platform.OS === "web" && typeof window !== "undefined") {
          clinicianHistoryRestorationRef.current =
            clinicianProfileDiscardDialogOpenRef.current
              ? { kind: "dismiss-profile-dialog" }
              : { kind: "open-navigation", navigation: pendingNavigation };
          window.history.forward();
          return;
        }

        replaceWorkbenchPathnameInWindow(
          clinicianWorkspacePathnameRef.current,
        );
        setClinicianProfilePendingNavigation(pendingNavigation);
        return;
      }

      setSelectedPatientTabId(routeState.selectedPatientTabId);
      setSelectedWorkbenchPanelId(routeState.selectedWorkbenchPanelId);
      setRequiresAttentionOnly(routeState.requiresAttentionOnly);
      setSearchQuery(routeState.searchQuery);
      setSelectedPatientId(routeState.selectedPatientId);
      setSelectedDetailTabId(routeState.selectedDetailTabId);
    });

    return () => {
      clinicianHistoryRestorationRef.current = null;
      unsubscribe();
    };
  }, [hasClinicianUnsavedChanges, workspaceMode]);

  useEffect(() => {
    if (
      workspaceMode !== "clinician" ||
      Platform.OS !== "web" ||
      typeof window === "undefined"
    ) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasClinicianUnsavedChanges()) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasClinicianUnsavedChanges, workspaceMode]);

  useEffect(() => {
    if (workspaceMode !== "clinician") {
      clearWorkbenchRouteStateFromWindow();
      return;
    }

    writeWorkbenchRouteStateToWindow({
      selectedPatientTabId,
      selectedWorkbenchPanelId,
      requiresAttentionOnly,
      searchQuery,
      selectedPatientId,
      selectedDetailTabId,
    });
  }, [
    requiresAttentionOnly,
    searchQuery,
    selectedWorkbenchPanelId,
    selectedDetailTabId,
    selectedPatientId,
    selectedPatientTabId,
    workspaceMode,
  ]);

  useEffect(() => {
    if (workspaceMode !== "clinician" || isWideLayout) {
      return;
    }

    setClinicianCompactPane(
      selectedPatientId == null ? "queue" : "workspace",
    );
  }, [isWideLayout, selectedPatientId, workspaceMode]);

  useEffect(() => {
    if (requiresWorkforceSessionGate && workforceSessionGateState !== "ready") {
      setIsShellDataLoading(workforceSessionGateState === "loading");
      setHasShellDataError(workforceSessionGateState === "error");
      return;
    }

    if (workspaceMode !== "clinician") {
      setIsShellDataLoading(false);
      setHasShellDataError(false);
      return;
    }

    const abortController = new AbortController();
    const requestSessionEpoch = clinicianSessionEpochRef.current;
    const requestState = buildCurrentRequestState({
      deferredSearchQuery,
      myPatientIds,
      requiresAttentionOnly,
      selectedDetailTabId,
      selectedPatientId,
      selectedPatientTabId,
    });
    const requestCacheKey = buildClinicianWorkbenchShellCacheKey(
      requestState,
      effectiveRuntimeConfig,
    );
    const cachedShellData =
      shellDataCacheRef.current.get(requestCacheKey) ?? null;

    setIsShellDataLoading(cachedShellData == null);
    setHasShellDataError(false);

    void loadClinicianWorkbenchShellData(
      requestState,
      effectiveRuntimeConfig,
      abortController.signal,
    )
      .then((nextShellData) => {
        if (
          abortController.signal.aborted ||
          !isCurrentClinicianSessionRequest(requestSessionEpoch)
        ) {
          return;
        }

        const resolvedRequestState: ClinicianWorkbenchRequestState = {
          ...requestState,
          selectedPatientId:
            nextShellData.response.routeState.selectedPatientId,
          selectedDetailTabId: nextShellData.response.routeState.detailTab,
        };
        const resolvedCacheKey = buildClinicianWorkbenchShellCacheKey(
          resolvedRequestState,
          effectiveRuntimeConfig,
        );
        shellDataCacheRef.current.set(requestCacheKey, nextShellData);
        shellDataCacheRef.current.set(resolvedCacheKey, nextShellData);

        // Belt to the abort guard: a response resolved for another patient —
        // or for a request whose selection no longer matches (including a
        // selection cleared to null) — only warms the cache.
        const responsePatientId =
          nextShellData.response.routeState.selectedPatientId;

        if (
          (requestState.selectedPatientId ?? null) !==
            (selectedPatientIdRef.current ?? null) ||
          (responsePatientId != null &&
            selectedPatientIdRef.current != null &&
            responsePatientId !== selectedPatientIdRef.current)
        ) {
          return;
        }

        startTransition(() => {
          setShellData(nextShellData);
          setLoadedShellDataCacheKey(resolvedCacheKey);
          setIsShellDataLoading(false);
        });
      })
      .catch((error) => {
        if (
          abortController.signal.aborted ||
          !isCurrentClinicianSessionRequest(requestSessionEpoch)
        ) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          showWorkforceSessionExpiredGate();
          return;
        }

        startTransition(() => {
          setHasShellDataError(true);
          setIsShellDataLoading(false);
        });
      });

    return () => {
      abortController.abort();
    };
  }, [
    deferredSearchQuery,
    effectiveRuntimeConfig.requestClinicianUserId,
    effectiveRuntimeConfig.requestSiteId,
    effectiveRuntimeConfig.workforceSessionSubjectKey,
    isCurrentClinicianSessionRequest,
    myPatientIds,
    requiresAttentionOnly,
    requiresWorkforceSessionGate,
    selectedDetailTabId,
    selectedPatientId,
    selectedPatientTabId,
    workforceSessionGateState,
    workspaceMode,
  ]);

  useEffect(() => {
    if (
      workspaceMode !== "clinician" ||
      renderedShellData == null ||
      hasShellDataError ||
      isShellDataLoading
    ) {
      return;
    }

    for (const patient of renderedShellData.patientWorkspaces
      .filter((item) => item.id !== renderedShellData.selectedPatient?.id)
      .slice(0, 8)) {
      queueClinicianShellPrefetch({
        ...currentClinicianRequestState,
        selectedPatientId: patient.id,
      });
    }

    queueClinicianShellPrefetch({
      ...currentClinicianRequestState,
      selectedPatientTabId: selectedPatientTabId === "my" ? "all" : "my",
      selectedPatientId: null,
    });
  }, [
    currentClinicianRequestState,
    hasShellDataError,
    isShellDataLoading,
    renderedShellData,
    selectedPatientTabId,
    workspaceMode,
  ]);

  useEffect(() => {
    if (requiresWorkforceSessionGate && workforceSessionGateState !== "ready") {
      setIsAdminSummaryLoading(workforceSessionGateState === "loading");
      setHasAdminSummaryError(workforceSessionGateState === "error");
      return;
    }

    if (workspaceMode !== "admin") {
      setAdminSummary(null);
      setIsAdminSummaryLoading(false);
      setHasAdminSummaryError(false);
      return;
    }

    const abortController = new AbortController();

    setIsAdminSummaryLoading(true);
    setHasAdminSummaryError(false);

    void loadTenantAdminSummary(
      effectiveRuntimeConfig,
      adminAnalyticsSummaryRequest,
      abortController.signal,
    )
      .then((nextAdminSummary) => {
        if (abortController.signal.aborted) {
          return;
        }

        startTransition(() => {
          setAdminSummary(nextAdminSummary);
          setIsAdminSummaryLoading(false);
        });
      })
      .catch((error: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          showWorkforceSessionExpiredGate();
          return;
        }

        startTransition(() => {
          setHasAdminSummaryError(true);
          setIsAdminSummaryLoading(false);
        });
      });

    return () => {
      abortController.abort();
    };
  }, [
    effectiveRuntimeConfig.requestTenantId,
    effectiveRuntimeConfig.workforceSessionSubjectKey,
    effectiveRuntimeConfig.sessionApiBaseUrl,
    adminAnalyticsRangeSelection.endDate,
    adminAnalyticsRangeSelection.departmentCode,
    adminAnalyticsRangeSelection.startDate,
    requiresWorkforceSessionGate,
    workforceSessionGateState,
    workspaceMode,
  ]);

  const runTenantAdminCommand = async (
    command: TenantAdminCommandInputV1,
  ): Promise<{
    ok: boolean;
    result?: TenantAdminCommandResponseV1["result"] | null;
    summaryText: string | null;
  }> => {
    setIsAdminCommandSubmitting(true);
    setHasAdminSummaryError(false);

    try {
      const commandResponse = await submitTenantAdminCommand(
        effectiveRuntimeConfig,
        command,
      );

      startTransition(() => {
        setAdminSummary(commandResponse.summary);
      });
      const changedRefreshAliases = buildWorkspaceRefreshAliasesForChangedScopes(
        commandResponse.changedScopes,
      );
      invalidateWorkspaceRefreshScopes(
        changedRefreshAliases.length > 0
          ? changedRefreshAliases
          : workspaceMutationRefreshAliases,
        "tenant-admin-command-success",
        { tenantId: effectiveRuntimeConfig.requestTenantId },
      );

      return {
        ok: true,
        result: commandResponse.result ?? null,
        summaryText: commandResponse.summaryText,
      };
    } catch (error) {
      if (isWorkforceSessionUnauthorizedError(error)) {
        showWorkforceSessionExpiredGate();

        return {
          ok: false,
          result: null,
          summaryText: "Your session expired. Sign in again.",
        };
      }

      return {
        ok: false,
        result: null,
        summaryText: error instanceof Error ? error.message : null,
      };
    } finally {
      setIsAdminCommandSubmitting(false);
    }
  };

  const loadTenantAdminReviewQueueItemsForAdmin = async (request: {
    queueId: TenantAdminReviewQueueId;
    offset?: number | null;
    limit?: number | null;
  }): Promise<TenantAdminReviewQueueItemsResponseV1 | null> => {
    try {
      return await loadTenantAdminReviewQueueItems(
        effectiveRuntimeConfig,
        request,
      );
    } catch (error) {
      if (isWorkforceSessionUnauthorizedError(error)) {
        showWorkforceSessionExpiredGate();
      }

      return null;
    }
  };

  useEffect(() => {
    if (requiresWorkforceSessionGate && workforceSessionGateState !== "ready") {
      setIsPatientWorkspaceLoading(workforceSessionGateState === "loading");
      setHasPatientWorkspaceError(workforceSessionGateState === "error");
      return;
    }

    if (workspaceMode !== "patient") {
      setPatientWorkspaceSummary(null);
      setIsPatientWorkspaceLoading(false);
      setHasPatientWorkspaceError(false);
      return;
    }

    if (!patientWorkspacePatientId) {
      setPatientWorkspaceSummary(null);
      setIsPatientWorkspaceLoading(isPatientPortalSessionLoading);
      setHasPatientWorkspaceError(false);
      return;
    }

    const abortController = new AbortController();

    setIsPatientWorkspaceLoading(true);
    setHasPatientWorkspaceError(false);

    void loadPatientWorkspaceSummary(
      effectiveRuntimeConfig,
      patientWorkspacePatientId,
      abortController.signal,
    )
      .then((nextPatientWorkspaceSummary) => {
        if (abortController.signal.aborted) {
          return;
        }

        startTransition(() => {
          setPatientWorkspaceSummary(
            sanitizePatientWorkspaceSummaryForProductDisplay(
              nextPatientWorkspaceSummary,
            ),
          );
          setIsPatientWorkspaceLoading(false);
        });
      })
      .catch((error: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }

        if (
          usesPatientPortalSession &&
          isPatientWorkspaceUnauthorizedError(error)
        ) {
          startTransition(() => {
            setPatientPortalSession(null);
            setPatientWorkspaceSummary(null);
            setPatientPortalAuthNotice("Your session expired. Sign in again.");
            setIsPatientWorkspaceLoading(false);
            setHasPatientWorkspaceError(false);
          });
          return;
        }

        startTransition(() => {
          setHasPatientWorkspaceError(true);
          setIsPatientWorkspaceLoading(false);
        });
      });

    return () => {
      abortController.abort();
    };
  }, [
    effectiveRuntimeConfig.requestTenantId,
    effectiveRuntimeConfig.workforceSessionSubjectKey,
    effectiveRuntimeConfig.sessionApiBaseUrl,
    isPatientPortalSessionLoading,
    patientWorkspacePatientId,
    requiresWorkforceSessionGate,
    usesPatientPortalSession,
    workforceSessionGateState,
    workspaceMode,
  ]);

  async function recordPatientMessage(
    input: PatientMessageCommandInput,
  ): Promise<boolean> {
    if (!patientWorkspacePatientId || isPatientMessageCommandSubmitting) {
      return false;
    }

    setIsPatientMessageCommandSubmitting(true);

    try {
      const response = await recordPatientWorkspaceMessage(
        effectiveRuntimeConfig,
        {
          patientId: patientWorkspacePatientId,
          bodyText: input.bodyText,
        },
      );

      startTransition(() => {
        setPatientWorkspaceSummary(
          sanitizePatientWorkspaceSummaryForProductDisplay(response.summary),
        );
        setHasPatientWorkspaceError(false);
        setIsPatientMessageCommandSubmitting(false);
      });
      const changedRefreshAliases = buildWorkspaceRefreshAliasesForChangedScopes(
        response.changedScopes,
      );
      invalidateWorkspaceRefreshScopes(
        changedRefreshAliases.length > 0
          ? changedRefreshAliases
          : workspaceMutationRefreshAliases,
        "patient-message-command-success",
        { tenantId: effectiveRuntimeConfig.requestTenantId },
      );

      return true;
    } catch {
      startTransition(() => {
        setIsPatientMessageCommandSubmitting(false);
      });

      return false;
    }
  }

  async function recordPatientOnboarding(
    input: PatientOnboardingCommandInput,
  ): Promise<PatientWorkspaceOnboardingCommandResponseV1 | null> {
    const allowsUnauthenticatedAuthChallenge = input.authChallenge != null;

    if (
      (!patientWorkspacePatientId && !allowsUnauthenticatedAuthChallenge) ||
      isPatientOnboardingCommandSubmitting
    ) {
      return null;
    }

    setIsPatientOnboardingCommandSubmitting(true);

    try {
      const response = await recordPatientWorkspaceOnboarding(
        effectiveRuntimeConfig,
        {
          patientId: patientWorkspacePatientId,
          ...input,
        },
      );

      startTransition(() => {
        if (response.summary) {
          setPatientWorkspaceSummary(
            sanitizePatientWorkspaceSummaryForProductDisplay(response.summary),
          );
        }
        setHasPatientWorkspaceError(false);
        setIsPatientOnboardingCommandSubmitting(false);
      });
      const changedRefreshAliases = buildWorkspaceRefreshAliasesForChangedScopes(
        response.changedScopes,
      );
      invalidateWorkspaceRefreshScopes(
        changedRefreshAliases.length > 0
          ? changedRefreshAliases
          : workspaceMutationRefreshAliases,
        "patient-onboarding-command-success",
        { tenantId: effectiveRuntimeConfig.requestTenantId },
      );

      return response;
    } catch {
      startTransition(() => {
        setIsPatientOnboardingCommandSubmitting(false);
      });

      return null;
    }
  }

  function submitWorkbenchAction(
    action: ClinicianWorkbenchActionInput,
    options: {
      onSuccess?: () => void;
      onError?: (message: string) => void;
    } = {},
  ): void {
    const requestSessionEpoch = clinicianSessionEpochRef.current;
    const requestState = buildCurrentRequestState({
      deferredSearchQuery,
      myPatientIds,
      requiresAttentionOnly,
      selectedDetailTabId,
      selectedPatientId,
      selectedPatientTabId,
    });

    setIsWorkbenchActionSubmitting(true);
    setWorkbenchActionFeedbackText(null);
    setWorkbenchActionFeedbackKey(
      translationKeys.shell.workspaceActionSubmitting,
    );

    void submitClinicianWorkbenchShellAction(
      requestState,
      action,
      effectiveRuntimeConfig,
    )
      .then(({ changedScopes, shellData: nextShellData }) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        const resolvedRequestState: ClinicianWorkbenchRequestState = {
          ...requestState,
          selectedPatientId:
            nextShellData.response.routeState.selectedPatientId,
          selectedDetailTabId: nextShellData.response.routeState.detailTab,
        };
        const resolvedCacheKey = buildClinicianWorkbenchShellCacheKey(
          resolvedRequestState,
          effectiveRuntimeConfig,
        );
        const responsePatientId =
          nextShellData.response.routeState.selectedPatientId;

        // A late action response must never reinstall a previously selected
        // patient's workspace over the current selection — including a
        // selection cleared to null (installing would let the routeState sync
        // re-select the old patient). Compare the REQUEST-time selection with
        // the current one; on mismatch the response only warms the cache.
        if (
          (requestState.selectedPatientId ?? null) !==
            (selectedPatientIdRef.current ?? null) ||
          (responsePatientId != null &&
            selectedPatientIdRef.current != null &&
            responsePatientId !== selectedPatientIdRef.current)
        ) {
          shellDataCacheRef.current.set(resolvedCacheKey, nextShellData);
          startTransition(() => {
            setIsWorkbenchActionSubmitting(false);
            setWorkbenchActionFeedbackText(null);
            setWorkbenchActionFeedbackKey(null);
          });
          return;
        }

        shellDataCacheRef.current.clear();
        shellDataCacheRef.current.set(resolvedCacheKey, nextShellData);

        startTransition(() => {
          setShellData(nextShellData);
          setLoadedShellDataCacheKey(resolvedCacheKey);
          setHasShellDataError(false);
          setIsWorkbenchActionSubmitting(false);
          setWorkbenchActionFeedbackText(null);
          setWorkbenchActionFeedbackKey(
            resolveSuccessKeyForWorkbenchAction(action),
          );
          if (action.kind === "add-patient-to-my-list") {
            setMyPatientIds((current) =>
              current.includes(action.patientId)
                ? current
                : [...current, action.patientId],
            );
          }
          if (action.kind === "send-reply") {
            setComposerDraft("");
          }
          options.onSuccess?.();
        });
        const changedRefreshAliases = buildWorkspaceRefreshAliasesForChangedScopes(
          changedScopes,
        );
        invalidateWorkspaceRefreshScopes(
          changedRefreshAliases.length > 0
            ? changedRefreshAliases
            : workspaceMutationRefreshAliases,
          "clinician-workbench-action-success",
          { tenantId: effectiveRuntimeConfig.requestTenantId },
        );
      })
      .catch((error: unknown) => {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          // Without this the flag stays set, so formBusy never clears and both
          // Save buttons sit disabled reading "Saving…" until the surface
          // remounts — a quieter second way for saving to look broken.
          setIsWorkbenchActionSubmitting(false);
          showWorkforceSessionExpiredGate();
          return;
        }

        if (isStaleMaterialChangeTokenError(error)) {
          void refreshWorkspaceRefreshScopeNow(
            clinicianWorkbenchRefreshKey,
            "stale-material-change-token",
          );
        }

        const errorFeedbackText = resolveWorkbenchActionErrorFeedback(error);

        startTransition(() => {
          setHasShellDataError(false);
          setIsWorkbenchActionSubmitting(false);
          setWorkbenchActionFeedbackText(errorFeedbackText);
          setWorkbenchActionFeedbackKey(
            translationKeys.shell.workspaceActionError,
          );
          // The workspace feedback line renders behind any open modal, so a
          // caller inside one only learns the action failed through this.
          // resolveWorkbenchActionErrorFeedback describes every refusal the API
          // explains — field validation, a stale token, a locked questionnaire
          // — and returns null only for server faults, whose bodies are not
          // authored for anyone to read, so fall back to the generic copy
          // rather than handing a caller "null" to display.
          options.onError?.(
            errorFeedbackText ?? t(translationKeys.shell.workspaceActionError),
          );
        });
      });
  }

  function uploadClinicianDocumentForSelectedPatient(): void {
    if (isClinicianDocumentUploadSubmitting || isWorkbenchActionSubmitting) {
      return;
    }

    if (!selectedPatient) {
      setWorkbenchActionFeedbackKey(translationKeys.shell.workspaceActionError);
      return;
    }

    if (!supportsBrowserDocumentUpload()) {
      setWorkbenchActionFeedbackKey(translationKeys.shell.workspaceActionError);
      return;
    }

    const requestSessionEpoch = clinicianSessionEpochRef.current;

    void (async () => {
      const selectedFile = await pickBrowserDocumentUploadFile();

      if (
        selectedFile == null ||
        !isCurrentClinicianSessionRequest(requestSessionEpoch)
      ) {
        return;
      }

      const requestState = buildCurrentRequestState({
        deferredSearchQuery,
        myPatientIds,
        requiresAttentionOnly,
        selectedDetailTabId: "documents",
        selectedPatientId: selectedPatient.id,
        selectedPatientTabId,
      });

      startTransition(() => {
        setIsClinicianDocumentUploadSubmitting(true);
        setWorkbenchActionFeedbackKey(
          translationKeys.shell.workspaceActionSubmitting,
        );
        setSelectedDetailTabId("documents");
      });

      try {
        const { changedScopes, shellData: nextShellData } =
          await uploadClinicianWorkbenchShellDocument(
            requestState,
            {
              file: selectedFile,
              patientId: selectedPatient.id,
            },
            effectiveRuntimeConfig,
          );

        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        const resolvedRequestState: ClinicianWorkbenchRequestState = {
          ...requestState,
          selectedPatientId:
            nextShellData.response.routeState.selectedPatientId,
          selectedDetailTabId: nextShellData.response.routeState.detailTab,
        };
        const resolvedCacheKey = buildClinicianWorkbenchShellCacheKey(
          resolvedRequestState,
          effectiveRuntimeConfig,
        );
        const responsePatientId =
          nextShellData.response.routeState.selectedPatientId;

        // A late upload response for a previously selected patient only warms
        // the cache — it must not reinstall that patient's workspace, even
        // when the selection has since been cleared to null (installing would
        // let the routeState sync re-select the old patient).
        if (
          (requestState.selectedPatientId ?? null) !==
            (selectedPatientIdRef.current ?? null) ||
          (responsePatientId != null &&
            selectedPatientIdRef.current != null &&
            responsePatientId !== selectedPatientIdRef.current)
        ) {
          shellDataCacheRef.current.set(resolvedCacheKey, nextShellData);
          startTransition(() => {
            setIsClinicianDocumentUploadSubmitting(false);
            setWorkbenchActionFeedbackKey(null);
          });
        } else {
          shellDataCacheRef.current.clear();
          shellDataCacheRef.current.set(resolvedCacheKey, nextShellData);

          startTransition(() => {
            setShellData(nextShellData);
            setLoadedShellDataCacheKey(resolvedCacheKey);
            setHasShellDataError(false);
            setIsClinicianDocumentUploadSubmitting(false);
            setWorkbenchActionFeedbackKey(
              translationKeys.shell.workspaceActionRefreshSummarySuccess,
            );
          });
        }
        const changedRefreshAliases =
          buildWorkspaceRefreshAliasesForChangedScopes(changedScopes);
        invalidateWorkspaceRefreshScopes(
          changedRefreshAliases.length > 0
            ? changedRefreshAliases
            : workspaceMutationRefreshAliases,
          "clinician-document-upload-success",
          { tenantId: effectiveRuntimeConfig.requestTenantId },
        );
      } catch (error: unknown) {
        if (!isCurrentClinicianSessionRequest(requestSessionEpoch)) {
          return;
        }

        if (isWorkforceSessionUnauthorizedError(error)) {
          startTransition(() => {
            setIsClinicianDocumentUploadSubmitting(false);
          });
          showWorkforceSessionExpiredGate();
          return;
        }

        startTransition(() => {
          setHasShellDataError(false);
          setIsClinicianDocumentUploadSubmitting(false);
          setWorkbenchActionFeedbackKey(
            translationKeys.shell.workspaceActionError,
          );
        });
      }
    })();
  }

  function updateClinicianClinicalEditorModalField(
    fieldName: "bodyText" | "defaultBodyText" | "defaultDetailText" | "defaultTitle" | "detailText" | "title" | "visibility",
    value: string,
  ): void {
    setClinicianClinicalEditorModal((current) =>
      current == null
        ? current
        : ({
            ...current,
            [fieldName]: value,
          } as ClinicianClinicalEditorModalState),
    );
  }

  function updateClinicianClinicalEditorStructuredField(
    fieldName: keyof ClinicianStructuredRecordFields,
    value: string,
  ): void {
    setClinicianClinicalEditorModal((current) => {
      if (current == null || current.kind !== "record") {
        return current;
      }

      const structuredFields = {
        ...current.structuredFields,
        [fieldName]: value,
      };

      return {
        ...current,
        detailText: serializeClinicianStructuredRecordFields(structuredFields),
        structuredFields,
      };
    });
  }

  function submitClinicianClinicalEditorModal(): void {
    if (clinicianClinicalEditorModal == null || isWorkbenchActionSubmitting) {
      return;
    }

    // Owner guard: never submit an editor opened for a different patient.
    if (
      selectedPatient == null ||
      clinicianClinicalEditorModal.patientId !== selectedPatient.id
    ) {
      setClinicianClinicalEditorDiscardPromptOpen(false);
      setClinicianClinicalEditorModal(null);
      return;
    }

    if (clinicianClinicalEditorModal.kind === "record") {
      const title = clinicianClinicalEditorModal.title.trim();
      const detailText = clinicianClinicalEditorModal.detailText.trim();
      const structuredErrors = validateClinicianStructuredRecordFields(
        clinicianClinicalEditorModal.structuredFields,
        {
          previousFields: parseClinicianStructuredRecordFields(
            clinicianClinicalEditorModal.defaultDetailText,
          ),
        },
      );

      if (
        !title ||
        !detailText ||
        Object.keys(structuredErrors).length > 0
      ) {
        setWorkbenchActionFeedbackKey(translationKeys.shell.workspaceActionError);
        return;
      }

      submitWorkbenchAction(
        {
          detailText,
          expectedMaterialChangeToken:
            clinicianClinicalEditorModal.materialChangeToken,
          kind: "upsert-clinical-record",
          patientId: clinicianClinicalEditorModal.patientId,
          recordId: clinicianClinicalEditorModal.recordId ?? null,
          targetKind: clinicianClinicalEditorModal.targetKind,
          title,
        },
        {
          onSuccess: () => {
            setClinicianClinicalEditorDiscardPromptOpen(false);
            setClinicianClinicalEditorModal(null);
          },
        },
      );
      return;
    }

    const title = clinicianClinicalEditorModal.title.trim();
    const bodyText = clinicianClinicalEditorModal.bodyText.trim();
    const shouldClearComposerDraft =
      clinicianClinicalEditorModal.sourceComposerMode != null;
    const visibility =
      clinicianClinicalEditorModal.visibility === "clinical"
        ? "clinical"
        : "patient-visible";

    if (!title || !bodyText) {
      setWorkbenchActionFeedbackKey(translationKeys.shell.workspaceActionError);
      return;
    }

    submitWorkbenchAction(
      {
        bodyText,
        expectedMaterialChangeToken:
          clinicianClinicalEditorModal.materialChangeToken,
        kind: "upsert-clinical-note",
        noteId: clinicianClinicalEditorModal.noteId ?? null,
        patientId: clinicianClinicalEditorModal.patientId,
        targetId: clinicianClinicalEditorModal.targetId ?? null,
        targetKind: clinicianClinicalEditorModal.targetKind,
        title,
        visibility,
      },
      {
        onSuccess: () => {
          if (shouldClearComposerDraft) {
            setComposerDraft("");
          }

          setClinicianClinicalEditorDiscardPromptOpen(false);
          setClinicianClinicalEditorModal(null);
        },
      },
    );
  }

  function openComposerDraftClinicalNote(
    mode: Exclude<ClinicianComposerMode, "reply">,
  ): void {
    if (!selectedPatient) {
      return;
    }

    const trimmedDraft = composerDraft.trim();

    if (!trimmedDraft) {
      setWorkbenchActionFeedbackKey(translationKeys.shell.workspaceActionError);
      return;
    }

    const title = mode === "handoff" ? "Staff handoff" : "Clinical note";

    setClinicianClinicalEditorDiscardPromptOpen(false);
    setClinicianClinicalEditorModal({
      bodyText: trimmedDraft,
      defaultBodyText: trimmedDraft,
      defaultTitle: title,
      defaultVisibility: "clinical",
      kind: "note",
      materialChangeToken: selectedPatient.materialChangeToken,
      noteId: null,
      patientId: selectedPatient.id,
      patientName: selectedPatient.displayName,
      sourceComposerMode: mode,
      targetId: selectedPatient.id,
      targetKind: "patient",
      title,
      visibility: "clinical",
    });
  }

  if (requiresWorkforceSessionGate && workforceSessionGateState !== "ready") {
    const gateTitle =
      workforceSessionGateState === "selection-required"
        ? t(translationKeys.shell.workforceSessionSelectionTitle)
        : workforceSessionGateState === "error"
          ? t(translationKeys.app.error)
          : t(translationKeys.shell.workforceSessionLoadingTitle);
    const gateBody =
      workforceSessionGateState === "selection-required"
        ? t(translationKeys.shell.workforceSessionSelectionBody)
        : workforceSessionGateState === "error"
          ? t(translationKeys.shell.workforceSessionErrorBody)
          : t(translationKeys.shell.workforceSessionLoadingBody);

    if (workforceSessionGateState === "loading") {
      return (
        <View
          style={[
            styles.root,
            showsLegacyWebAssets ? createWebViewportMinHeightStyle() : null,
            {
              backgroundColor: "transparent",
              position: "relative",
            },
          ]}
        >
          {showsLegacyWebAssets ? (
            <Image
              resizeMode="cover"
              source={legacyBackgroundTextureSource}
              style={styles.legacyBackgroundTexture}
            />
          ) : null}
          <View
            pointerEvents="none"
            style={[
              styles.backgroundWash,
              {
                backgroundColor: theme.backdropOverlay,
              },
            ]}
          />

          <View
            style={[
              styles.loginCenterStage,
              {
                paddingHorizontal: isMediumLayout ? 32 : 18,
                paddingBottom: showsLegacyWebAssets
                  ? legacyWorkbenchFooterHeight
                  : 0,
              },
            ]}
          >
            <View
              accessibilityLabel={gateTitle}
              accessibilityRole="progressbar"
              style={[
                styles.inlinePill,
                {
                  alignItems: "center",
                  alignSelf: "center",
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: tokens.radius.pill,
                  flexDirection: "row",
                  gap: 8,
                  paddingHorizontal: 14,
                  paddingVertical: 8,
                },
              ]}
            >
              <ActivityIndicator color={theme.accentStrong} size="small" />
              <Text
                style={[
                  styles.badgeText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {gateTitle}
              </Text>
            </View>
          </View>

          {showsLegacyWebAssets
            ? renderLegacyWorkbenchFooter({
                layout,
                palette: legacyFooterPalette,
                viewportWidth: width,
              })
            : null}
        </View>
      );
    }

    if (workforceSessionGateState === "password-required") {
      const canSubmitPasswordLogin =
        workforceLoginEmail.trim().length > 0 &&
        workforceLoginPassword.length > 0 &&
        !isWorkforceLoginSubmitting;

      return (
        <View style={[styles.root, styles.loginRoot]}>
          {showsLegacyWebAssets ? (
            <Image
              resizeMode="cover"
              source={legacyBackgroundTextureSource}
              style={styles.loginBackgroundImage}
            />
          ) : null}
          <View
            pointerEvents="none"
            style={[
              styles.loginBackgroundWash,
              createWebGradientBackgroundStyle(theme.shellGradient),
              {
                backgroundColor: theme.headerBand,
              },
            ]}
          />

          <View
            style={[
                styles.loginTopBar,
                createWebGradientBackgroundStyle(theme.shellGradient),
                {
                  backgroundColor: theme.headerBand,
                  paddingHorizontal: isMediumLayout ? 140 : 22,
                },
            ]}
          >
            <Text
              style={[
                styles.loginHeaderBrandText,
                {
                  color: theme.headerText,
                  fontFamily: layout.bodyFontFamily,
                },
              ]}
            >
              {resolvePatientBrand(effectiveRuntimeConfig).brandName}
            </Text>
          </View>

          <View
            style={[
              styles.loginCenterStage,
              {
                paddingHorizontal: isMediumLayout ? 32 : 18,
              },
            ]}
          >
            <View
              role="form"
              style={[
                styles.loginCard,
                {
                  backgroundColor: theme.surface,
                  borderRadius: layout.modalRadius,
                  gap: 18,
                  maxWidth: 500,
                  paddingHorizontal: isMediumLayout ? 48 : 24,
                  paddingVertical: isMediumLayout ? 34 : 28,
                },
              ]}
            >
              <Text
                accessibilityRole="header"
                style={[
                  styles.loginTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                  },
                ]}
              >
                {`Please sign in to ${resolvePatientBrand(effectiveRuntimeConfig).brandName}`}
              </Text>

              <View style={[styles.loginFieldGroup, { gap: 12 }]}>
                <Text
                  style={[
                    styles.loginFieldLabel,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                >
                  Email
                </Text>
                <TextInput
                  accessibilityLabel="Email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect={false}
                  keyboardType="email-address"
                  onChangeText={setWorkforceLoginEmail}
                  onSubmitEditing={submitWorkforcePasswordLogin}
                  placeholder="Enter your username"
                  placeholderTextColor={theme.accentStrong}
                  returnKeyType="next"
                  style={[
                    styles.loginInput,
                    {
                      borderColor: theme.borderSubtle,
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                  textContentType="username"
                  value={workforceLoginEmail}
                />
              </View>

              <View style={[styles.loginFieldGroup, { gap: 12 }]}>
                <Text
                  style={[
                    styles.loginFieldLabel,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                >
                  Password
                </Text>
                <TextInput
                  accessibilityLabel="Password"
                  autoCapitalize="none"
                  autoComplete="current-password"
                  onChangeText={setWorkforceLoginPassword}
                  onSubmitEditing={submitWorkforcePasswordLogin}
                  placeholder="Enter your password"
                  placeholderTextColor={theme.accentStrong}
                  returnKeyType="go"
                  secureTextEntry
                  style={[
                    styles.loginInput,
                    {
                      borderColor: theme.borderSubtle,
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                  textContentType="password"
                  value={workforceLoginPassword}
                />
              </View>

              {workforceLoginError ? (
                <Text
                  style={[
                    styles.loginSupportText,
                    {
                      color: theme.criticalText,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                >
                  {workforceLoginError}
                </Text>
              ) : null}

              <View style={styles.loginActions}>
                <Pressable
                  accessibilityLabel={
                    isWorkforceLoginSubmitting
                      ? "Signing in"
                      : `Sign in to ${resolvePatientBrand(effectiveRuntimeConfig).brandName}`
                  }
                  accessibilityRole="button"
                  accessibilityState={{
                    busy: isWorkforceLoginSubmitting,
                    disabled: !canSubmitPasswordLogin,
                  }}
                  disabled={!canSubmitPasswordLogin}
                  onPress={submitWorkforcePasswordLogin}
                  style={[
                    styles.loginSubmitButton,
                    canSubmitPasswordLogin
                      ? createWebGradientBackgroundStyle(theme.primaryGradient)
                      : null,
                    {
                      backgroundColor: canSubmitPasswordLogin
                        ? theme.accentStrong
                        : theme.borderStrong,
                      borderRadius: 8,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.loginSubmitButtonText,
                      {
                        color: theme.headerText,
                        fontFamily: layout.bodyFontFamily,
                      },
                    ]}
                  >
                    {isWorkforceLoginSubmitting ? "SIGNING IN" : "SIGN IN"}
                  </Text>
                </Pressable>

                <Text
                  style={[
                    styles.loginForgotPasswordText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                >
                  Forgot password?
                </Text>
              </View>
            </View>
          </View>

          <View
            style={[
              styles.loginFooterBar,
              createWebGradientBackgroundStyle(theme.shellGradient),
              {
                backgroundColor: theme.headerBand,
                paddingHorizontal: isMediumLayout ? 140 : 22,
              },
            ]}
          >
            <Text
              style={[
                styles.loginFooterText,
                {
                  color: theme.headerText,
                  fontFamily: layout.bodyFontFamily,
                },
              ]}
            >
              {`${resolvePatientBrand(effectiveRuntimeConfig).poweredByText != null && workspaceMode === "patient" ? `${resolvePatientBrand(effectiveRuntimeConfig).poweredByText ?? ""} · ` : ""}(c) AmpleCare.ai - All rights reserved.`}
            </Text>
            <View style={[styles.loginFooterLinks, { gap: 18 }]}>
              {[
                "Terms of use",
                "Privacy policy",
                "Report a bug",
                "Send feedback",
              ].map((labelText) => (
                <Text
                  key={labelText}
                  style={[
                    styles.loginFooterText,
                    {
                      color: theme.headerText,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                >
                  {labelText}
                </Text>
              ))}
            </View>
          </View>
        </View>
      );
    }

    return (
      <View
        style={[
          styles.root,
          {
            backgroundColor: "transparent",
          },
        ]}
      >
        {showsLegacyWebAssets ? (
          <Image
            resizeMode="cover"
            source={legacyBackgroundTextureSource}
            style={styles.legacyBackgroundTexture}
          />
        ) : null}
        <View
          pointerEvents="none"
          style={[
            styles.backgroundWash,
            {
              backgroundColor: theme.backdropOverlay,
            },
          ]}
        />
        <View
          pointerEvents="none"
          style={[
            styles.accentOrb,
            {
              backgroundColor: theme.accentSubtle,
            },
          ]}
        />
        <View
          pointerEvents="none"
          style={[
            styles.infoOrb,
            {
              backgroundColor: theme.backgroundSubtle,
              borderColor: theme.borderSubtle,
            },
          ]}
        />

        <View
          style={[
            styles.scrollContent,
            {
              gap: tokens.spacing.xl,
              justifyContent: "center",
              minHeight: "100%",
              padding: isMediumLayout ? tokens.spacing.xxl : tokens.spacing.xl,
            },
          ]}
        >
          <View
            style={[
              styles.surfaceCard,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderStrong,
                borderRadius: layout.cardRadius,
                gap: tokens.spacing.lg,
                maxWidth: 620,
                padding: tokens.spacing.xl,
                width: "100%",
              },
            ]}
          >
            <View
              style={[
                styles.brandLockupRow,
                {
                  gap: tokens.spacing.md,
                },
              ]}
            >
              <Text
                style={[
                  styles.badgeText,
                  {
                    color: theme.accentStrong,
                    fontFamily: layout.bodyFontFamily,
                  },
                ]}
              >
                {t(translationKeys.shell.badge)}
              </Text>
            </View>

            <Text
              style={[
                styles.surfaceTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: tokens.typography.fontSize.xl,
                  lineHeight: tokens.typography.lineHeight.xl,
                },
              ]}
            >
              {gateTitle}
            </Text>

            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {gateBody}
            </Text>

            {workforceSessionGateState === "selection-required" ? (
              <View
                style={[
                  styles.sectionChromeStack,
                  {
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                <Pressable
                  accessibilityLabel="Sign in with email"
                  accessibilityRole="button"
                  onPress={() => openWorkforcePasswordGate(null)}
                  style={[
                    styles.sectionChromeButton,
                    {
                      backgroundColor: theme.backgroundSubtle,
                      borderColor: theme.borderSubtle,
                      borderRadius: layout.controlRadius,
                      gap: tokens.spacing.sm,
                      padding: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.sectionChromeCopy,
                      {
                        gap: tokens.spacing.xs,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      Sign in with email
                    </Text>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                        },
                      ]}
                    >
                      Use your AmpleCare email and temporary password.
                    </Text>
                  </View>
                </Pressable>

                <View
                  style={[
                    styles.patientMetaGrid,
                    {
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  {workforceModeSummaryItems.map((item) => (
                    <View
                      key={item.id}
                      style={[
                        styles.patientMetaCard,
                        {
                          backgroundColor: theme.backgroundSubtle,
                          borderColor: theme.borderSubtle,
                          borderRadius: layout.controlRadius,
                          gap: tokens.spacing.xs,
                          padding: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.xs,
                            textTransform: "uppercase",
                          },
                        ]}
                      >
                        {item.labelText}
                      </Text>
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: item.toneColor,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.md,
                          },
                        ]}
                      >
                        {item.valueText}
                      </Text>
                    </View>
                  ))}
                </View>

                <ScrollView
                  contentContainerStyle={{
                    gap: tokens.spacing.sm,
                    paddingBottom: tokens.spacing.sm,
                  }}
                  showsVerticalScrollIndicator={orderedWorkforceSubjects.length > 4}
                  style={{
                    maxHeight: Math.max(
                      220,
                      Math.min(440, viewportHeight * 0.42),
                    ),
                    width: "100%",
                  }}
                >
                  {orderedWorkforceSubjects.map((subject) => {
                    const isSelected =
                      selectedWorkforceSubjectKey === subject.subjectKey;
                    const subjectWorkspaceLabel =
                      resolveWorkforceSessionRoleLabel(subject.roleKeys);
                    const isCurrentWorkspace =
                      activeWorkforceSession?.subjectKey === subject.subjectKey;
                    const chromeColors = getLegacySectionChromeColors(
                      resolveWorkspaceModeTone(subject.roleKeys),
                      isSelected,
                      theme,
                    );

                    return (
                      <Pressable
                        key={subject.subjectKey}
                        onPress={() => {
                          if (isCurrentWorkspace) {
                            setWorkforceSessionGateState("ready");
                            return;
                          }

                          if (subject.requiresPassword) {
                            openWorkforcePasswordGate(subject.subjectKey);
                            return;
                          }

                          void signInWorkforceSession({
                            subjectKey: subject.subjectKey,
                          });
                        }}
                        style={[
                          styles.sectionChromeButton,
                          {
                            backgroundColor: chromeColors.backgroundColor,
                            borderColor: chromeColors.borderColor,
                            borderRadius: layout.controlRadius,
                            gap: tokens.spacing.md,
                            padding: tokens.spacing.sm,
                          },
                        ]}
                      >
                      <View
                        style={[
                          styles.sectionChromeLead,
                          {
                            backgroundColor: chromeColors.leadBackgroundColor,
                            borderColor: chromeColors.leadBorderColor,
                            borderRadius: layout.controlRadius,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.sectionChromeLeadText,
                            {
                              color: chromeColors.leadTextColor,
                              fontFamily: layout.bodyFontFamily,
                            },
                          ]}
                        >
                          {subjectWorkspaceLabel.slice(0, 5).toUpperCase()}
                        </Text>
                      </View>

                      <View
                        style={[
                          styles.sectionChromeCopy,
                          {
                            gap: tokens.spacing.xs,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.navigationText,
                            {
                              color: chromeColors.titleColor,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: tokens.typography.fontSize.sm,
                            },
                          ]}
                        >
                          {subject.displayName ??
                            humanizeWorkspaceKey(subject.subjectKey)}
                        </Text>
                        <View
                          style={[
                            styles.flagRow,
                            {
                              gap: tokens.spacing.xs,
                            },
                          ]}
                        >
                          <View
                            style={[
                              styles.inlinePill,
                              {
                                backgroundColor: theme.surface,
                                borderColor: theme.borderSubtle,
                                borderRadius: tokens.radius.pill,
                                paddingHorizontal: tokens.spacing.sm,
                                paddingVertical: tokens.spacing.xs,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.badgeText,
                                {
                                  color: chromeColors.titleColor,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: tokens.typography.fontSize.xs,
                                },
                              ]}
                            >
                              {`${subjectWorkspaceLabel} workspace`}
                            </Text>
                          </View>
                          {isCurrentWorkspace ? (
                            <View
                              style={[
                                styles.inlinePill,
                                {
                                  backgroundColor: theme.surface,
                                  borderColor: theme.borderSubtle,
                                  borderRadius: tokens.radius.pill,
                                  paddingHorizontal: tokens.spacing.sm,
                                  paddingVertical: tokens.spacing.xs,
                                },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.badgeText,
                                  {
                                    color: theme.success,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: tokens.typography.fontSize.xs,
                                  },
                                ]}
                              >
                                Current
                              </Text>
                            </View>
                          ) : null}
                          {subject.subjectKey === recentWorkforceSubjectKey ? (
                            <View
                              style={[
                                styles.inlinePill,
                                {
                                  backgroundColor: theme.surface,
                                  borderColor: theme.borderSubtle,
                                  borderRadius: tokens.radius.pill,
                                  paddingHorizontal: tokens.spacing.sm,
                                  paddingVertical: tokens.spacing.xs,
                                },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.badgeText,
                                  {
                                    color: theme.accentStrong,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: tokens.typography.fontSize.xs,
                                  },
                                ]}
                              >
                                Recent workspace
                              </Text>
                            </View>
                          ) : null}
                        </View>
                        <Text
                          style={[
                            styles.metricLabel,
                            {
                              color: chromeColors.supportColor,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: tokens.typography.fontSize.xs,
                            },
                          ]}
                        >
                          {`Opens ${subjectWorkspaceLabel.toLowerCase()} view for ${subject.displayName ?? humanizeWorkspaceKey(subject.subjectKey)}.`}
                        </Text>
                        <Text
                          style={[
                            styles.sectionChromeSupportText,
                            {
                              color: chromeColors.supportColor,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: tokens.typography.fontSize.xs,
                              lineHeight: tokens.typography.lineHeight.sm,
                            },
                          ]}
                        >
                          {buildWorkforceSessionSupportText(subject)}
                        </Text>
                      </View>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    );
  }

  if (workspaceMode === "platform") {
    return (
      <PlatformConsolePage
        activeWorkforceSession={activeWorkforceSession}
        beginWorkspaceSelection={beginWorkspaceSelection}
        canSwitchWorkspaces={availableWorkforceSubjects.length > 1}
        isMediumLayout={isMediumLayout}
        layout={layout}
        onSessionExpired={showWorkforceSessionExpiredGate}
        onSignOut={signOutWorkforceSession}
        runtimeConfig={effectiveRuntimeConfig}
        theme={theme}
        tokens={tokens}
      />
    );
  }

  if (workspaceMode === "admin") {
    return (
      <AdminConsolePage
        activeWorkforceSession={activeWorkforceSession}
        adminAnalyticsRangeSelection={adminAnalyticsRangeSelection}
        adminSummary={adminSummary}
        beginWorkspaceSelection={beginWorkspaceSelection}
        canSwitchWorkspaces={availableWorkforceSubjects.length > 1}
        hasAdminSummaryError={hasAdminSummaryError}
        isAdminCommandSubmitting={isAdminCommandSubmitting}
        isAdminSummaryLoading={isAdminSummaryLoading}
        isMediumLayout={isMediumLayout}
        isWideLayout={isWideLayout}
        layout={layout}
        legacyBackgroundTextureSource={legacyBackgroundTextureSource}
        legacyHospitalLogoSource={legacyHospitalBlueLogoSource}
        onLoadAdminReviewQueueItems={loadTenantAdminReviewQueueItemsForAdmin}
        onRunAdminCommand={runTenantAdminCommand}
        onSessionExpired={showWorkforceSessionExpiredGate}
        onSignOut={signOutWorkforceSession}
        onUpdateAdminAnalyticsRange={setAdminAnalyticsRangeSelection}
        patientDesktopPalette={patientDesktopPalette}
        renderLegacyWorkbenchFooter={renderLegacyWorkbenchFooter}
        runtimeConfig={effectiveRuntimeConfig}
        showsLegacyWebAssets={showsLegacyWebAssets}
        theme={theme}
        tokens={tokens}
        viewportHeight={viewportHeight}
        viewportWidth={width}
      />
    );
  }

  if (workspaceMode === "patient") {
    return (
      <PatientWorkspacePage
        activeWorkforceSession={activeWorkforceSession}
        beginWorkspaceSelection={beginWorkspaceSelection}
        canSwitchWorkspaces={availableWorkforceSubjects.length > 1}
        hasPatientWorkspaceError={hasPatientWorkspaceError}
        isPatientMessageCommandSubmitting={isPatientMessageCommandSubmitting}
        isPatientOnboardingCommandSubmitting={
          isPatientOnboardingCommandSubmitting
        }
        isMediumLayout={isMediumLayout}
        isPatientPortalSessionLoading={isPatientPortalSessionLoading}
        isPatientWorkspaceLoading={isPatientWorkspaceLoading}
        isWideLayout={isWideLayout}
        layout={layout}
        legacyHospitalLogoSource={legacyHospitalLogoSource}
        onRecordPatientMessage={recordPatientMessage}
        onRecordPatientOnboarding={recordPatientOnboarding}
        onPatientDocumentUploadComplete={applyPatientDocumentUploadResponse}
        patientPortalSession={patientPortalSession}
        patientPortalAuthNotice={patientPortalAuthNotice}
        patientWorkspaceSummary={patientWorkspaceSummary}
        runtimeConfig={effectiveRuntimeConfig}
        selectedPatientWorkspaceTabId={selectedPatientWorkspaceTabId}
        setSelectedPatientWorkspaceTabId={setSelectedPatientWorkspaceTabId}
        setPatientPortalAuthNotice={setPatientPortalAuthNotice}
        setPatientPortalSession={setPatientPortalSession}
        showsLegacyWebAssets={showsLegacyWebAssets}
        theme={theme}
        tokens={tokens}
        usesPatientPortalSession={usesPatientPortalSession}
      />
    );
  }

  if (!renderedShellData) {
    if (!hasShellDataError) {
      const loadingTitle = t(translationKeys.app.loading);

      return (
        <View
          style={[
            styles.root,
            showsLegacyWebAssets ? createWebViewportMinHeightStyle() : null,
            {
              backgroundColor: "transparent",
              position: "relative",
            },
          ]}
        >
          {showsLegacyWebAssets ? (
            <Image
              resizeMode="cover"
              source={legacyBackgroundTextureSource}
              style={styles.legacyBackgroundTexture}
            />
          ) : null}
          <View
            pointerEvents="none"
            style={[
              styles.backgroundWash,
              {
                backgroundColor: showsLegacyWebAssets
                  ? patientDesktopPalette.backdropOverlay
                  : theme.backdropOverlay,
              },
            ]}
          />

          <View
            style={[
              styles.loginCenterStage,
              {
                paddingHorizontal: isMediumLayout ? 32 : 18,
                paddingBottom: showsLegacyWebAssets
                  ? legacyWorkbenchFooterHeight
                  : 0,
              },
            ]}
          >
            <View
              accessibilityLabel={loadingTitle}
              accessibilityRole="progressbar"
              style={[
                styles.inlinePill,
                {
                  alignItems: "center",
                  alignSelf: "center",
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: tokens.radius.pill,
                  flexDirection: "row",
                  gap: 8,
                  paddingHorizontal: 14,
                  paddingVertical: 8,
                },
              ]}
            >
              <ActivityIndicator color={theme.accentStrong} size="small" />
              <Text
                style={[
                  styles.badgeText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {loadingTitle}
              </Text>
            </View>
          </View>

          {showsLegacyWebAssets
            ? renderLegacyWorkbenchFooter({
                layout,
                palette: legacyFooterPalette,
                viewportWidth: width,
              })
            : null}
        </View>
      );
    }

    return (
      <View
        style={[
          styles.root,
          {
            alignItems: "center",
            backgroundColor: theme.backdropOverlay,
            justifyContent: "center",
            padding: tokens.spacing.xxl,
          },
        ]}
      >
        <View
          style={[
            styles.headerCard,
            {
              backgroundColor: theme.surface,
              borderColor: theme.borderStrong,
              borderRadius: layout.cardRadius,
              gap: tokens.spacing.md,
              maxWidth: 480,
              padding: tokens.spacing.xl,
              width: "100%",
            },
          ]}
        >
          <Text
            style={[
              styles.surfaceTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: tokens.typography.fontSize.xl,
                lineHeight: tokens.typography.lineHeight.xl,
              },
            ]}
          >
            {t(
              hasShellDataError
                ? translationKeys.app.error
                : translationKeys.app.loading,
            )}
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {t(translationKeys.shell.subtitle)}
          </Text>
        </View>
      </View>
    );
  }

  const selectedDetailSection =
    selectedPatient != null
      ? (detailSections.find((section) => section.id === selectedDetailTabId) ??
        detailSections[0] ??
        null)
      : null;
  const selectedWorkbenchPanel =
    workbenchPanels.find((panel) => panel.id === selectedWorkbenchPanelId) ??
    workbenchPanels[0];
  const hasClinicianPrimaryDialog =
    clinicianClinicalEditorModal != null ||
    clinicianOutboundAnswersModal != null ||
    clinicianOutboundCallConfirmModal != null ||
    clinicianProfilePendingNavigation != null ||
    clinicianProfileModalOpen ||
    voiceCallTranscriptModal != null;
  const isClinicianDetailDialogOpen =
    selectedPatient != null &&
    selectedDetailSection != null &&
    shouldOpenClinicianDetailDialog({
      hasPrimaryDialog: hasClinicianPrimaryDialog,
      isWideLayout,
      legacyStageHeight: legacyClinicianStageHeight,
      selectedDetailTabId,
    });
  const hasClinicianDialogOpen =
    hasClinicianPrimaryDialog || isClinicianDetailDialogOpen;
  const showLegacyClinicianSecondaryPanel =
    !showsLegacyWebAssets || !isWideLayout;
  const clinicianIdentityText =
    activeWorkforceSession?.displayName ?? clinicianUserId;
  const clinicianPatientIdentityText =
    selectedPatient == null
      ? null
      : buildClinicianPatientIdentityText({
          dateOfBirth: selectedPatient.record.patient.dateOfBirth,
          hasPatientRecord: true,
          identifier:
            selectedPatient.listMetaText.trim().length > 0 &&
            !/no identifier/iu.test(selectedPatient.listMetaText)
              ? selectedPatient.listMetaText
              : null,
          mrn: selectedPatient.record.patient.mrn,
          phone: selectedPatient.record.patient.primaryPhone.number,
        });
  const performClinicianProfileNavigation = (
    navigation: ClinicianProfilePendingNavigation,
  ): void => {
    if (navigation.kind === "sign-out") {
      performWorkforceSessionSignOut();
      return;
    }

    if (navigation.kind === "center-pane") {
      setClinicianCenterPaneView(navigation.value);
      return;
    }

    if (navigation.kind === "compact-pane") {
      setClinicianCompactPane(navigation.value);
      return;
    }

    if (navigation.pathname) {
      replaceWorkbenchPathnameInWindow(navigation.pathname);
    }

    if (navigation.routeState) {
      setSelectedPatientTabId(navigation.routeState.selectedPatientTabId);
      setSelectedWorkbenchPanelId(
        navigation.routeState.selectedWorkbenchPanelId,
      );
      setRequiresAttentionOnly(navigation.routeState.requiresAttentionOnly);
      setSearchQuery(navigation.routeState.searchQuery);
      setSelectedDetailTabId(navigation.routeState.selectedDetailTabId);
    }

    setSelectedPatientId(navigation.value);
    if (navigation.value != null && !isWideLayout) {
      setClinicianCompactPane("workspace");
    }
  };
  const requestClinicianProfileNavigation = (
    navigation: ClinicianProfilePendingNavigation,
  ): void => {
    const isCurrentDestination =
      (navigation.kind === "center-pane" &&
        navigation.value === clinicianCenterPaneView) ||
      (navigation.kind === "compact-pane" &&
        navigation.value === clinicianCompactPane) ||
      (navigation.kind === "patient" &&
        navigation.value === selectedPatientId &&
        (navigation.value == null ||
          isWideLayout ||
          clinicianCompactPane === "workspace"));

    if (isCurrentDestination) {
      return;
    }

    if (hasClinicianUnsavedChanges()) {
      setClinicianProfilePendingNavigation(navigation);
      return;
    }

    performClinicianProfileNavigation(navigation);
  };
  const cancelClinicianProfileNavigation = (): void => {
    setClinicianProfilePendingNavigation(null);
    writeWorkbenchRouteStateToWindow(
      {
        selectedPatientTabId,
        selectedWorkbenchPanelId,
        requiresAttentionOnly,
        searchQuery,
        selectedPatientId,
        selectedDetailTabId,
      },
      clinicianWorkspacePathnameRef.current,
    );
  };
  const clinicianProfileDiscardPromptElement =
    clinicianProfilePendingNavigation == null ? null : (
      <Modal
        accessibilityLabel="Discard unsaved clinician changes"
        accessibilityViewIsModal={true}
        animationType="fade"
        onRequestClose={cancelClinicianProfileNavigation}
        transparent={true}
        visible={true}
      >
        <View style={{ flex: 1, position: "relative" }}>
          <ClinicianDiscardChangesPrompt
            bodyText="This clinician workspace has unsaved changes. Leaving now discards them."
            confirmAccessibilityLabel="Discard unsaved clinician changes"
            confirmLabel="Discard changes"
            controlHeight={
              width < workforceResponsiveBreakpoints.wide
                ? workforceOperationalLayout.mobileControlHeight
                : workforceConfigurationDialog.controlHeight
            }
            onCancel={cancelClinicianProfileNavigation}
            onConfirm={() => {
              const pendingNavigation = clinicianProfilePendingNavigation;
              const usesBrowserBack =
                pendingNavigation.kind === "patient" &&
                pendingNavigation.historyAction === "back" &&
                Platform.OS === "web" &&
                typeof window !== "undefined";

              discardClinicianWorkspaceDrafts();

              if (usesBrowserBack) {
                window.history.back();
                return;
              }

              performClinicianProfileNavigation(pendingNavigation);
            }}
            stayAccessibilityLabel="Stay in clinician workspace"
            theme={theme}
            titleText="Discard unsaved changes?"
            viewportWidth={width}
          />
        </View>
      </Modal>
    );
  const requestCloseClinicianClinicalEditor = (): void => {
    if (isWorkbenchActionSubmitting) {
      return;
    }

    if (isClinicianEditorDirty(clinicianClinicalEditorModal)) {
      setClinicianClinicalEditorDiscardPromptOpen(true);
      return;
    }

    setClinicianClinicalEditorDiscardPromptOpen(false);
    setClinicianClinicalEditorModal(null);
  };
  const clinicianClinicalEditorModalElement =
    renderClinicianClinicalEditorModal({
      discardConfirmationVisible:
        clinicianClinicalEditorDiscardPromptOpen,
      isSubmitting: isWorkbenchActionSubmitting,
      layout,
      // Owner-gated in render too (the effect reset lands a frame later).
      modalState:
        clinicianProfilePendingNavigation == null &&
        clinicianClinicalEditorModal != null &&
        clinicianClinicalEditorModal.patientId === selectedPatient?.id
          ? clinicianClinicalEditorModal
          : null,
      patientIdentityText: clinicianPatientIdentityText,
      onCancelDiscard: () =>
        setClinicianClinicalEditorDiscardPromptOpen(false),
      onClose: requestCloseClinicianClinicalEditor,
      onConfirmDiscard: () => {
        setClinicianClinicalEditorDiscardPromptOpen(false);
        setClinicianClinicalEditorModal(null);
      },
      onSubmit: submitClinicianClinicalEditorModal,
      onUpdateField: updateClinicianClinicalEditorModalField,
      onUpdateStructuredField: updateClinicianClinicalEditorStructuredField,
      theme,
      tokens,
      viewportHeight,
      viewportWidth: width,
    });
  const voiceCallTranscriptModalElement = renderVoiceCallTranscriptModal({
    layout,
    // Owner-gated: a modal opened for one patient never renders over another.
    modalState:
      clinicianProfilePendingNavigation == null &&
      voiceCallTranscriptModal != null &&
      voiceCallTranscriptModal.patientId === selectedPatient?.id
        ? voiceCallTranscriptModal
        : null,
    onClose: () => setVoiceCallTranscriptModal(null),
    patientIdentityText: clinicianPatientIdentityText,
    theme,
    tokens,
    viewportWidth: width,
  });
  const ownedClinicianOutboundAnswersModal =
    clinicianProfilePendingNavigation == null &&
    clinicianOutboundAnswersModal != null &&
    clinicianOutboundAnswersModal.patientId === selectedPatient?.id
      ? clinicianOutboundAnswersModal
      : null;
  const isOwnedClinicianOutboundAnswersSubmitting =
    clinicianOutboundPendingTargetId ===
    ownedClinicianOutboundAnswersModal?.call.targetId;
  const requestCloseClinicianOutboundAnswers = (): void => {
    if (isOwnedClinicianOutboundAnswersSubmitting) {
      return;
    }

    if (
      hasUnsavedQuestionnaireReviewNote(
        ownedClinicianOutboundAnswersModal?.reviewNotes ?? "",
        ownedClinicianOutboundAnswersModal?.persistedReviewNotes ?? "",
      )
    ) {
      setClinicianOutboundAnswersDiscardPromptOpen(true);
      return;
    }

    setClinicianOutboundAnswersDiscardPromptOpen(false);
    setClinicianMedicationResolution(null);
    setClinicianOutboundAnswersModal(null);
  };
  const clinicianOutboundAnswersModalElement =
    renderClinicianOutboundAnswersModal({
      actorCanManageReview: canManageOutboundQuestionnaireReview(activeRoleKeys),
      clinicianUserId,
      discardConfirmationVisible:
        clinicianOutboundAnswersDiscardPromptOpen,
      isSubmitting: isOwnedClinicianOutboundAnswersSubmitting,
      layout,
      modalState: ownedClinicianOutboundAnswersModal,
      patientIdentityText: clinicianPatientIdentityText,
      onCancelDiscard: () =>
        setClinicianOutboundAnswersDiscardPromptOpen(false),
      onClose: requestCloseClinicianOutboundAnswers,
      onConfirmDiscard: () => {
        setClinicianOutboundAnswersDiscardPromptOpen(false);
        setClinicianMedicationResolution(null);
        setClinicianOutboundAnswersModal(null);
      },
      onReviewNotesChange: (reviewNotes) =>
        setClinicianOutboundAnswersModal((current) =>
          current ? { ...current, reviewNotes } : current,
        ),
      onReviewAction:
        selectedPatient == null
          ? undefined
          : (action, call) =>
              executeClinicianOutboundReviewAction({
                action,
                call,
                patientId: selectedPatient.id,
                reviewNotes:
                  clinicianOutboundAnswersModal?.reviewNotes.trim() ?? null,
              }),
      medicationResolution:
        clinicianMedicationResolution != null &&
        clinicianMedicationResolution.patientId === selectedPatient?.id &&
        clinicianMedicationResolution.sessionId ===
          ownedClinicianOutboundAnswersModal?.call.sessionId &&
        clinicianMedicationResolution.targetId ===
          ownedClinicianOutboundAnswersModal?.call.targetId
          ? clinicianMedicationResolution
          : null,
      onMedicationResolutionOpen: (questionKey, entryIndex, initialQuery) =>
        selectedPatient == null || ownedClinicianOutboundAnswersModal == null
          ? undefined
          : setClinicianMedicationResolution({
              candidates: null,
              entryIndex,
              errorText: null,
              isBusy: false,
              patientId: selectedPatient.id,
              query: initialQuery,
              questionKey,
              sessionId: ownedClinicianOutboundAnswersModal.call.sessionId,
              selectedDrugName: null,
              selectedRxcui: null,
              selectedStrength: null,
              targetId: ownedClinicianOutboundAnswersModal.call.targetId,
            }),
      onMedicationResolutionClose: () => setClinicianMedicationResolution(null),
      onMedicationResolutionQueryChange: (query) =>
        setClinicianMedicationResolution((current) =>
          current ? { ...current, query } : current,
        ),
      onMedicationResolutionSelect: (drugName, rxcui, strength) =>
        setClinicianMedicationResolution((current) =>
          current
            ? {
                ...current,
                selectedDrugName: drugName,
                selectedRxcui: rxcui,
                selectedStrength: strength,
              }
            : current,
        ),
      onMedicationSearch:
        selectedPatient == null
          ? undefined
          : (call) =>
              executeClinicianMedicationSearch({
                call,
                patientId: selectedPatient.id,
              }),
      onMedicationResolve:
        selectedPatient == null
          ? undefined
          : (call) =>
              executeClinicianMedicationResolve({
                call,
                patientId: selectedPatient.id,
              }),
      reviewNotes: clinicianOutboundAnswersModal?.reviewNotes ?? "",
      theme,
      tokens,
      viewportWidth: width,
    });
  const clinicianOutboundCallConfirmModalElement =
    renderClinicianOutboundCallConfirmModal({
      isSubmitting:
        clinicianOutboundPendingTargetId ===
        clinicianOutboundCallConfirmModal?.call.targetId,
      layout,
      // Owner-gated: never confirm a dial for a patient who is no longer
      // the selected one.
      modalState:
        clinicianProfilePendingNavigation == null &&
        clinicianOutboundCallConfirmModal != null &&
        clinicianOutboundCallConfirmModal.patientId === selectedPatient?.id
          ? clinicianOutboundCallConfirmModal
          : null,
      patientIdentityText: clinicianPatientIdentityText,
      onClose: () => {
        if (clinicianOutboundPendingTargetId != null) {
          return;
        }

        setClinicianOutboundCallConfirmModal(null);
      },
      onConfirm: (modalState) => {
        executeClinicianOutboundCallAction({
          action: modalState.action,
          acknowledgeOutsideWindow: modalState.call.outsideCallWindow,
          acknowledgeRepeat:
            modalState.action === "start-new-round" &&
            modalState.call.repeatWarning,
          call: modalState.call,
          patientId: modalState.patientId,
        });
      },
      theme,
      tokens,
      viewportWidth: width,
    });

  function beginReviewEdit(messageId: string, bodyText: string): void {
    const initialBodyText =
      editedReviewBodiesByMessageId[messageId] ?? bodyText;

    reviewEditBaselineRef.current = initialBodyText;
    setReviewEditingMessageId(messageId);
    setReviewEditDraft(initialBodyText);
  }

  function saveReviewEdit(messageId: string, originalBodyText: string): void {
    setEditedReviewBodiesByMessageId((current) => {
      const next = { ...current };
      const trimmedDraft = reviewEditDraft.trim();
      const trimmedOriginal = originalBodyText.trim();

      if (trimmedDraft.length === 0 || trimmedDraft === trimmedOriginal) {
        delete next[messageId];
      } else {
        next[messageId] = trimmedDraft;
      }

      return next;
    });
    setReviewEditingMessageId(null);
    setReviewEditDraft("");
  }

  function clearReviewEdit(messageId?: string): void {
    if (messageId) {
      setEditedReviewBodiesByMessageId((current) => {
        if (!(messageId in current)) {
          return current;
        }

        const next = { ...current };

        delete next[messageId];

        return next;
      });
    }

    setReviewEditingMessageId((current) =>
      messageId == null || current === messageId ? null : current,
    );
    if (messageId == null || reviewEditingMessageId === messageId) {
      reviewEditBaselineRef.current = "";
      setReviewEditDraft("");
    }
  }

  const showClinicianQueuePane =
    isWideLayout ||
    selectedPatient == null ||
    (!isTransitioningFromWideToCompact && clinicianCompactPane === "queue");
  const showClinicianWorkspacePane =
    isWideLayout ||
    (selectedPatient != null &&
      (isTransitioningFromWideToCompact ||
        clinicianCompactPane === "workspace"));

  return (
    <View
      style={[
        styles.root,
        showsLegacyWebAssets ? createWebViewportMinHeightStyle() : null,
        {
          backgroundColor: showsLegacyWebAssets
            ? theme.background
            : "transparent",
          position: "relative",
        },
      ]}
    >
      <View
        pointerEvents="none"
        style={[
          styles.backgroundWash,
          {
            backgroundColor: theme.background,
          },
        ]}
      />
      {!showsLegacyWebAssets ? (
        <>
          <View
            pointerEvents="none"
            style={[
              styles.accentOrb,
              {
                backgroundColor: theme.accentSubtle,
              },
            ]}
          />
          <View
            pointerEvents="none"
            style={[
              styles.infoOrb,
              {
                backgroundColor: theme.backgroundSubtle,
                borderColor: theme.borderSubtle,
              },
            ]}
          />
        </>
      ) : null}

      <ScrollView
        accessibilityElementsHidden={hasClinicianDialogOpen}
        aria-hidden={hasClinicianDialogOpen}
        importantForAccessibility={
          hasClinicianDialogOpen ? "no-hide-descendants" : "auto"
        }
        pointerEvents={hasClinicianDialogOpen ? "none" : "auto"}
        style={
          showsLegacyWebAssets
            ? {
                flex: 1,
                height: Math.max(0, viewportHeight - legacyWorkbenchFooterHeight),
                maxHeight: Math.max(
                  0,
                  viewportHeight - legacyWorkbenchFooterHeight,
                ),
              }
            : undefined
        }
        contentContainerStyle={[
          styles.scrollContent,
          {
            gap: showsLegacyWebAssets ? tokens.spacing.sm : tokens.spacing.xl,
            minHeight: showsLegacyWebAssets
              ? Math.max(0, viewportHeight - legacyWorkbenchFooterHeight)
              : undefined,
            padding: showsLegacyWebAssets ? tokens.spacing.sm : tokens.spacing.lg,
            paddingBottom: showsLegacyWebAssets
              ? legacyWorkbenchFooterHeight + tokens.spacing.xl
              : tokens.spacing.lg,
          },
        ]}
      >
        <View
          style={[
            styles.pageFrame,
            {
              gap: showsLegacyWebAssets ? tokens.spacing.sm : tokens.spacing.lg,
              maxWidth: showsLegacyWebAssets
                ? clinicianLegacyPageMaxWidth
                : pageMaxWidth,
            },
          ]}
        >
          <View
            style={[
              styles.headerCard,
              showsLegacyWebAssets
                ? createWebGradientBackgroundStyle(theme.shellGradient)
                : null,
              {
                backgroundColor: theme.headerBand,
                borderColor: "transparent",
                borderRadius: showsLegacyWebAssets ? 0 : layout.cardRadius,
                gap: tokens.spacing.xs,
                height:
                  showsLegacyWebAssets && isMediumLayout
                    ? workforceOperationalLayout.headerHeight
                    : undefined,
                justifyContent:
                  showsLegacyWebAssets && isMediumLayout ? "center" : undefined,
                paddingHorizontal: showsLegacyWebAssets
                  ? 20
                  : tokens.spacing.lg,
                paddingVertical: showsLegacyWebAssets
                  ? isMediumLayout
                    ? 0
                    : 8
                  : tokens.spacing.sm,
                shadowOffset: showsLegacyWebAssets
                  ? { width: 0, height: 0 }
                  : { width: 0, height: 8 },
                shadowOpacity: showsLegacyWebAssets ? 0 : 0.08,
                shadowRadius: showsLegacyWebAssets ? 0 : 18,
              },
            ]}
          >
            <View
              style={[
                styles.headerRow,
                {
                  alignItems: isMediumLayout ? "center" : "flex-start",
                  flexDirection: isMediumLayout ? "row" : "column",
                  gap: showsLegacyWebAssets ? tokens.spacing.sm : tokens.spacing.md,
                  justifyContent: "space-between",
                },
              ]}
            >
              <View
                style={[
                  styles.brandLockupRow,
                  {
                    alignItems: "center",
                    flex: isMediumLayout ? 1 : 0,
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.brand,
                    {
                      color: hasShellDataError
                        ? theme.criticalText
                        : theme.headerText,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.md,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  {hasShellDataError
                    ? t(translationKeys.app.error)
                      : isShellDataLoading && !isClinicianShellRefreshing
                        ? t(translationKeys.app.loading)
                      : showsLegacyWebAssets
                        ? "AmpleCare Clinician"
                        : t(translationKeys.app.name)}
                </Text>
                {showsLegacyWebAssets ? (
                  <View
                    style={[
                      styles.inlinePill,
                      {
                        alignItems: "center",
                        alignSelf: "center",
                        backgroundColor: theme.onGradientPillBackground,
                        borderColor: theme.onGradientPillBorder,
                        borderRadius: 999,
                        justifyContent: "center",
                        minHeight: 22,
                        paddingHorizontal: 11,
                        paddingVertical: 3,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: theme.headerText,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 10,
                          fontWeight: "700",
                          letterSpacing: 0,
                          lineHeight: 12,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      Workbench
                    </Text>
                  </View>
                ) : null}
              </View>

              <View
                style={[
                  styles.brandLockupRow,
                  {
                    alignItems: "center",
                    gap: tokens.spacing.sm,
                    justifyContent: isMediumLayout ? "flex-end" : "flex-start",
                  },
                ]}
              >
                <Text
                  numberOfLines={1}
                  style={[
                    styles.navigationText,
                    {
                      color: theme.headerText,
                      flexShrink: 1,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: showsLegacyWebAssets
                        ? tokens.typography.fontSize.md
                        : tokens.typography.fontSize.lg,
                      lineHeight: showsLegacyWebAssets ? 18 : undefined,
                      textAlign: isMediumLayout ? "right" : "left",
                    },
                  ]}
                >
                  {clinicianIdentityText}
                </Text>

                <View
                  style={[
                    styles.patientAvatar,
                    {
                      backgroundColor: showsLegacyWebAssets
                        ? theme.onGradientAvatarBackground
                        : theme.surface,
                      borderColor: showsLegacyWebAssets
                        ? theme.onGradientAvatarBorder
                        : theme.surface,
                      borderRadius: showsLegacyWebAssets ? 16 : 16,
                      height: showsLegacyWebAssets ? 32 : 32,
                      width: showsLegacyWebAssets ? 32 : 32,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.patientAvatarText,
                      {
                        color: showsLegacyWebAssets
                          ? theme.headerText
                          : theme.accentStrong,
                        fontFamily: layout.bodyFontFamily,
                      },
                    ]}
                  >
                    {resolvePatientInitials(clinicianIdentityText)}
                  </Text>
                </View>

                {requiresWorkforceSessionGate ? (
                  <Pressable
                    accessibilityLabel="Sign out"
                    accessibilityRole="button"
                    onPress={signOutWorkforceSession}
                    style={[
                      {
                        alignItems: "center",
                        alignSelf: "center",
                        backgroundColor: theme.onGradientPillBackground,
                        borderColor: theme.onGradientPillBorder,
                        borderRadius: 999,
                        borderWidth: 1,
                        height: isWideLayout
                          ? 32
                          : workforceOperationalLayout.mobileControlHeight,
                        justifyContent: "center",
                        paddingHorizontal: 11,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.badgeText,
                        {
                          color: theme.headerText,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: "800",
                        },
                      ]}
                    >
                      Sign out
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            </View>
          </View>


          <View
            style={[
              styles.workspaceGrid,
              {
                // Stretch in BOTH orientations: centering the stacked column
                // let cards size to their content and overflow the viewport
                // horizontally on phones (audit P2).
                alignItems: "stretch",
                backgroundColor: "transparent",
                borderColor: "transparent",
                flexDirection: isWideLayout ? "row" : "column",
                gap: isWideLayout ? 16 : tokens.spacing.sm,
                overflow: "visible",
                width: "100%",
              },
            ]}
          >
            {showClinicianQueuePane
              ? renderPatientListCard({
              calendarMonth: clinicianCalendarMonth,
              calendarPendingStart: clinicianCalendarPendingStart,
              isRefreshing: isClinicianShellRefreshing,
              isWideLayout,
              layout,
              listSummary,
              listViewMode: clinicianListViewMode,
              requiresAttentionOnly,
              searchQuery,
              selectedCalendarRange: clinicianCalendarRange,
              selectedPatientId,
              selectedPatientTabId,
              setCalendarMonth: setClinicianCalendarMonth,
              setCalendarPendingStart: setClinicianCalendarPendingStart,
              setCalendarRange: setClinicianCalendarRange,
              setListViewMode: setClinicianListViewMode,
              setRequiresAttentionOnly,
              setSearchQuery,
              setSelectedPatientId: (patientId) => {
                requestClinicianProfileNavigation({
                  kind: "patient",
                  value: patientId,
                });
              },
              setSelectedPatientTabId,
              t,
              theme,
              tokens,
              visiblePatients,
              legacyStageHeight: legacyClinicianStageHeight,
            })
              : null}
            {!isWideLayout &&
            showClinicianWorkspacePane &&
            selectedPatient != null ? (
              <View
                style={{
                  alignItems: "center",
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: workforceOperationalLayout.panelRadius,
                  borderWidth: 1,
                  flexDirection: "row",
                  gap: 10,
                  minHeight: workforceOperationalLayout.mobileControlHeight,
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  width: "100%",
                }}
              >
                <Pressable
                  accessibilityLabel="Back to patient queue"
                  accessibilityRole="button"
                  onPress={() =>
                    requestClinicianProfileNavigation({
                      kind: "compact-pane",
                      value: "queue",
                    })
                  }
                  style={{
                    alignItems: "center",
                    borderColor: theme.borderStrong,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    borderWidth: 1,
                    height: workforceOperationalLayout.mobileControlHeight,
                    justifyContent: "center",
                    width: workforceOperationalLayout.mobileControlHeight,
                  }}
                >
                  <LineIcon
                    color={theme.accentStrong}
                    name="chevron-left"
                    size={18}
                    strokeWidth={2.25}
                  />
                </Pressable>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    numberOfLines={1}
                    style={{
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.body.fontSize,
                      fontWeight: "700",
                      lineHeight: workforceOperationalTypography.body.lineHeight,
                    }}
                  >
                    {selectedPatient.displayName}
                  </Text>
                  <Text
                    numberOfLines={1}
                    style={{
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize:
                        workforceOperationalTypography.metadata.fontSize,
                      lineHeight:
                        workforceOperationalTypography.metadata.lineHeight,
                    }}
                  >
                    {selectedPatient.listMetaText}
                  </Text>
                </View>
              </View>
            ) : null}
            {showClinicianWorkspacePane
              ? renderChatCard({
              callTypeFilter: clinicianCallTypeFilter,
              centerPaneView: clinicianCenterPaneView,
              clinicianUserId,
              onDownloadOutboundQuestionnaireAnswersPdf:
                downloadClinicianOutboundQuestionnaireAnswersPdf,
              onOpenOutboundQuestionnaireAnswers: (call) => {
                if (!selectedPatient) {
                  return;
                }

                const persistedReviewNotes =
                  resolveOutboundQuestionnaireReviewNotes(call);

                setClinicianOutboundAnswersDiscardPromptOpen(false);
                setClinicianMedicationResolution(null);
                setClinicianOutboundAnswersModal({
                  call,
                  patientId: selectedPatient.id,
                  patientName: selectedPatient.displayName,
                  persistedReviewNotes,
                  reviewNotes: persistedReviewNotes,
                });
              },
              onOpenVoiceCallTranscript: (voiceCall) => {
                if (!selectedPatient) {
                  return;
                }

                setVoiceCallTranscriptModal({
                  patientId: selectedPatient.id,
                  patientName: selectedPatient.displayName,
                  voiceCall,
                });
              },
              onStartOutboundQuestionnaireCall:
                requestClinicianOutboundCallAction,
              onSubmitOutboundManualQuestionnaireCommand: (command, options) => {
                if (!selectedPatient) {
                  return;
                }

                executeClinicianOutboundManualQuestionnaireCommand(
                  { ...command, patientId: selectedPatient.id },
                  options,
                );
              },
              outboundManualCommandPending: clinicianOutboundManualPending,
              outboundQuestionnaireCalls:
                selectedPatientOutboundQuestionnaireCalls,
              outboundQuestionnaireErrorText:
                selectedPatientOutboundCallsState?.errorText ?? null,
              outboundQuestionnaireIsLoading:
                selectedPatientOutboundCallsState == null ||
                selectedPatientOutboundCallsState.isLoading,
              outboundQuestionnaireNoticePending:
                clinicianOutboundPendingTargetId != null ||
                clinicianOutboundPdfPendingTargetId != null ||
                clinicianOutboundManualPending,
              outboundQuestionnaireNoticeText:
                selectedPatientOutboundCallsState?.noticeText ?? null,
              outboundQuestionnairePdfPendingTargetId:
                clinicianOutboundPdfPendingTargetId,
              outboundQuestionnairePendingTargetId:
                clinicianOutboundPendingTargetId,
              onRefreshOutboundQuestionnaireCalls:
                loadClinicianOutboundCallsForPatient,
              setCallTypeFilter: setClinicianCallTypeFilter,
              setCenterPaneView: (value) =>
                requestClinicianProfileNavigation({
                  kind: "center-pane",
                  value,
                }),
              onProfileDirtyChange: handleClinicianProfileDirtyChange,
              onProfileDiscardConfirmationOpenChange:
                handleClinicianProfileDiscardDialogOpenChange,
              onProfileFocusSectionHandled: () =>
                setClinicianProfileFocusSection(null),
              onProfileModalOpenChange:
                handleClinicianProfileModalOpenChange,
              onSubmitWorkbenchAction: (action, options) =>
                submitWorkbenchAction(action, options),
              patientIdentityText: clinicianPatientIdentityText,
              profileDiscardChangesRequestKey:
                clinicianProfileDiscardChangesRequestKey,
              profileDiscardConfirmationDismissRequestKey:
                clinicianProfileDiscardDismissRequestKey,
              profileFocusSection: clinicianProfileFocusSection,
              profileNavigationConfirmationOpen:
                clinicianProfilePendingNavigation != null,
              questionnaireDefinition: clinicianQuestionnaireDefinition,
              isWorkbenchActionSubmitting,
              composerDraft,
              composerMode: clinicianComposerMode,
              editedReviewBodiesByMessageId,
              isWideLayout,
              layout,
              legacyClinicianAvatarSource,
              legacyHospitalLogoSource,
              onAddSelectedPatientToMyList: () => {
                if (!selectedPatient) {
                  return;
                }

                submitWorkbenchAction({
                  kind: "add-patient-to-my-list",
                  patientId: selectedPatient.id,
                  expectedMaterialChangeToken:
                    selectedPatient.materialChangeToken,
                });
              },
              onApproveMessageReview: (messageId, editedBodyText) => {
                const submittedEditedBodyText =
                  editedBodyText ??
                  editedReviewBodiesByMessageId[messageId] ??
                  null;

                if (!selectedPatient) {
                  return;
                }

                submitWorkbenchAction(
                  {
                    kind: "approve-message-review",
                    patientId: selectedPatient.id,
                    expectedMaterialChangeToken:
                      selectedPatient.materialChangeToken,
                    messageId,
                    editedBodyText: submittedEditedBodyText,
                  },
                  {
                    onSuccess: () => clearReviewEdit(messageId),
                  },
                );
              },
              onRejectMessageReview: (messageId) => {
                if (!selectedPatient) {
                  return;
                }

                submitWorkbenchAction(
                  {
                    kind: "reject-message-review",
                    patientId: selectedPatient.id,
                    expectedMaterialChangeToken:
                      selectedPatient.materialChangeToken,
                    messageId,
                  },
                  {
                    onSuccess: () => clearReviewEdit(messageId),
                  },
                );
              },
              onBeginReviewEdit: beginReviewEdit,
              onChangeReviewEditDraft: setReviewEditDraft,
              onCreateComposerNoteFromDraft: openComposerDraftClinicalNote,
              onSaveReviewEdit: saveReviewEdit,
              onSendReply: () => {
                if (!selectedPatient) {
                  return;
                }

                submitWorkbenchAction({
                  kind: "send-reply",
                  patientId: selectedPatient.id,
                  expectedMaterialChangeToken:
                    selectedPatient.materialChangeToken,
                  bodyText: composerDraft,
                });
              },
              reviewEditDraft,
              reviewEditingMessageId,
              selectedPatient,
              setComposerDraft,
              setComposerMode: setClinicianComposerMode,
              showsLegacyWebAssets,
              t,
              theme,
              tokens,
              workbenchActionFeedbackKey,
              workbenchActionFeedbackText,
              clinicianConversationScrollRef,
              legacyStageHeight: legacyClinicianStageHeight,
              onConversationContentSizeChange:
                handleClinicianConversationContentSizeChange,
              onConversationScroll: handleClinicianConversationScroll,
            })
              : null}
            {shouldRenderClinicianDetailSurface(
              isWideLayout,
              selectedDetailTabId,
            )
              ? renderDetailsCard({
              detailSections,
              inlineClinicalEditor,
              isDocumentUploadSubmitting: isClinicianDocumentUploadSubmitting,
              isWorkbenchActionSubmitting,
              isWideLayout,
              layout,
              onOpenPatientProfileSection: (section) => {
                setClinicianProfileFocusSection(section);
                setClinicianCenterPaneView("patient-profile");
              },
              onOpenClinicalNoteEditor: (input) => {
                if (!selectedPatient) {
                  return;
                }

                setClinicianClinicalEditorDiscardPromptOpen(false);
                setClinicianClinicalEditorModal({
                  ...input,
                  bodyText: input.defaultBodyText,
                  kind: "note",
                  materialChangeToken: selectedPatient.materialChangeToken,
                  patientId: selectedPatient.id,
                  patientName: selectedPatient.displayName,
                  title: input.defaultTitle,
                  visibility: input.defaultVisibility ?? "patient-visible",
                });
              },
              onOpenClinicalRecordEditor: (input) => {
                if (!selectedPatient) {
                  return;
                }

                setClinicianClinicalEditorDiscardPromptOpen(false);
                setClinicianClinicalEditorModal({
                  ...input,
                  detailText: input.defaultDetailText,
                  kind: "record",
                  materialChangeToken: selectedPatient.materialChangeToken,
                  patientId: selectedPatient.id,
                  patientName: selectedPatient.displayName,
                  structuredFields: parseClinicianStructuredRecordFields(
                    input.defaultDetailText,
                  ),
                  title: input.defaultTitle,
                });
              },
              onOpenVoiceCallTranscript: (voiceCall) => {
                if (!selectedPatient) {
                  return;
                }

                setVoiceCallTranscriptModal({
                  patientId: selectedPatient.id,
                  patientName: selectedPatient.displayName,
                  voiceCall,
                });
              },
              onDownloadOutboundQuestionnaireAnswersPdf:
                downloadClinicianOutboundQuestionnaireAnswersPdf,
              onRefreshOutboundQuestionnaireCalls:
                loadClinicianOutboundCallsForPatient,
              onSubmitWorkbenchAction: submitWorkbenchAction,
              onStartOutboundQuestionnaireCall:
                requestClinicianOutboundCallAction,
              onUploadDocument: uploadClinicianDocumentForSelectedPatient,
              patientIdentityText: clinicianPatientIdentityText,
              outboundQuestionnaireCalls:
                selectedPatientOutboundQuestionnaireCalls,
              outboundQuestionnaireErrorText:
                selectedPatientOutboundCallsState?.errorText ?? null,
              outboundQuestionnaireIsLoading:
                selectedPatientOutboundCallsState?.isLoading ?? false,
              outboundQuestionnaireNoticeText:
                selectedPatientOutboundCallsState?.noticeText ?? null,
              outboundQuestionnairePdfPendingTargetId:
                clinicianOutboundPdfPendingTargetId,
              outboundQuestionnairePendingTargetId:
                clinicianOutboundPendingTargetId,
              selectedDetailSection,
              selectedDetailModalViewId,
              selectedDetailTabId,
              selectedPatient,
              showDetailDialog: isClinicianDetailDialogOpen,
              setInlineClinicalEditor,
              setSelectedDetailModalViewId,
              setSelectedDetailTabId,
              t,
              theme,
              tokens,
              legacyStageHeight: hasClinicianPrimaryDialog
                ? undefined
                : legacyClinicianStageHeight,
            })
              : null}
          </View>


          <View
            style={[
              styles.workspaceGrid,
              {
                flexDirection: "column",
                gap: tokens.spacing.lg,
              },
            ]}
          >
            {showLegacyClinicianSecondaryPanel
              ? renderWorkbenchPanelCard({
                  isWorkbenchActionSubmitting,
                  layout,
                  onSubmitWorkbenchAction: submitWorkbenchAction,
                  selectedPatient,
                  selectedWorkbenchPanel,
                  selectedWorkbenchPanelId,
                  setSelectedDetailTabId,
                  setSelectedPatientId: (patientId) =>
                    requestClinicianProfileNavigation({
                      kind: "patient",
                      value: patientId,
                    }),
                  setSelectedWorkbenchPanelId,
                  t,
                  theme,
                  tokens,
                  workbenchPanels,
                })
              : null}
          </View>
        </View>
      </ScrollView>
      {showsLegacyWebAssets
        ? renderLegacyWorkbenchFooter({
            layout,
            palette: legacyFooterPalette,
            position: "relative",
            viewportWidth: width,
          })
        : null}
      {clinicianClinicalEditorModalElement}
      {clinicianOutboundCallConfirmModalElement}
      {clinicianOutboundAnswersModalElement}
      {voiceCallTranscriptModalElement}
      {clinicianProfileDiscardPromptElement}
    </View>
  );
}

type PatientWorkspaceTabId =
  | "overview"
  | "onboarding"
  | "appointments"
  | "procedures"
  | "medications"
  | "documents"
  | "calls"
  | "messages"
  | "settings";

const patientWorkspaceTabFeatureFlagKeys: Partial<
  Record<PatientWorkspaceTabId, PatientPortalFeatureFlagKeyV1>
> = {
  appointments: "patient.feature.appointments",
  calls: "patient.feature.calls",
  documents: "patient.feature.documents",
  medications: "patient.feature.medications",
  messages: "patient.feature.messaging",
  procedures: "patient.feature.procedures",
};

const patientPortalFeatureFlagKeySet = new Set<string>(
  patientPortalFeatureDefinitionsV1.map((definition) => definition.flagKey),
);

function readPatientPortalFeatureFlagPreviewOverride():
  | Partial<PatientPortalFeatureFlagsV1>
  | null {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return null;
  }

  const rawValue = new URLSearchParams(window.location.search).get(
    "patientPortalFeatureFlags",
  );

  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as unknown;

    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return null;
    }

    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([key, value]) =>
          patientPortalFeatureFlagKeySet.has(key) && typeof value === "boolean",
      ),
    ) as Partial<PatientPortalFeatureFlagsV1>;
  } catch {
    return null;
  }
}

function resolvePatientPortalFeatureFlags(
  summary: PatientWorkspaceSummaryResponseV1 | null,
): PatientPortalFeatureFlagsV1 {
  return {
    ...patientPortalDefaultFeatureFlagsV1,
    ...(summary?.featureFlags ?? {}),
    ...(readPatientPortalFeatureFlagPreviewOverride() ?? {}),
  };
}

function isPatientWorkspaceTabEnabled(
  tabId: PatientWorkspaceTabId,
  featureFlags: PatientPortalFeatureFlagsV1,
): boolean {
  const flagKey = patientWorkspaceTabFeatureFlagKeys[tabId];

  return flagKey == null || featureFlags[flagKey] !== false;
}

type PatientWorkspaceVoiceCallSummary =
  PatientWorkspaceSummaryResponseV1["patient"]["voiceCallSummaries"][number];

type PatientWorkspaceDocumentSummary =
  PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number];

type PatientMessageCommandInput = {
  bodyText: string;
};

type PatientRenderContext = {
  activeWorkforceSession: WorkforceSessionSummaryV1 | null;
  beginWorkspaceSelection: () => void;
  canSwitchWorkspaces: boolean;
  hasPatientWorkspaceError: boolean;
  isMediumLayout: boolean;
  isPatientPortalSessionLoading: boolean;
  isPatientWorkspaceLoading: boolean;
  isWideLayout: boolean;
  layout: typeof designTokens.semanticLayout;
  legacyHospitalLogoSource: { uri: string };
  isPatientMessageCommandSubmitting: boolean;
  isPatientOnboardingCommandSubmitting: boolean;
  onRecordPatientMessage: (
    input: PatientMessageCommandInput,
  ) => Promise<boolean>;
  onRecordPatientOnboarding: (
    input: PatientOnboardingCommandInput,
  ) => Promise<PatientWorkspaceOnboardingCommandResponseV1 | null>;
  onPatientDocumentUploadComplete: (
    response: PatientWorkspaceDocumentCompleteUploadResponseV1,
  ) => void;
  patientPortalAuthNotice: string | null;
  patientPortalSession: WorkforceSessionSummaryV1 | null;
  patientWorkspaceSummary: PatientWorkspaceSummaryResponseV1 | null;
  runtimeConfig: MobileRuntimeConfig;
  selectedPatientWorkspaceTabId: PatientWorkspaceTabId;
  setSelectedPatientWorkspaceTabId: (value: PatientWorkspaceTabId) => void;
  setPatientPortalAuthNotice: (value: string | null) => void;
  setPatientPortalSession: (value: WorkforceSessionSummaryV1 | null) => void;
  showsLegacyWebAssets: boolean;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  usesPatientPortalSession: boolean;
};

function PatientWorkspacePage(context: PatientRenderContext) {
  const {
    activeWorkforceSession,
    beginWorkspaceSelection,
    canSwitchWorkspaces,
    hasPatientWorkspaceError,
    isPatientMessageCommandSubmitting,
    isPatientOnboardingCommandSubmitting,
    isMediumLayout,
    isPatientPortalSessionLoading,
    isPatientWorkspaceLoading,
    isWideLayout,
    layout,
    legacyHospitalLogoSource,
    onPatientDocumentUploadComplete,
    onRecordPatientMessage,
    onRecordPatientOnboarding,
    patientPortalAuthNotice,
    patientPortalSession,
    patientWorkspaceSummary,
    runtimeConfig,
    selectedPatientWorkspaceTabId,
    setSelectedPatientWorkspaceTabId,
    setPatientPortalAuthNotice,
    setPatientPortalSession,
    showsLegacyWebAssets,
    theme,
    tokens,
    usesPatientPortalSession,
  } = context;
  const summary = patientWorkspaceSummary?.patient ?? null;
  const patientPortalFeatureFlags =
    resolvePatientPortalFeatureFlags(patientWorkspaceSummary);
  const pageMaxWidth = 1440;
  const patientWorkforceSession = usesPatientPortalSession
    ? patientPortalSession
    : activeWorkforceSession;
  const [patientDocumentSearchQuery, setPatientDocumentSearchQuery] =
    useState("");
  const [patientDocumentDateRangeFilter, setPatientDocumentDateRangeFilter] =
    useState<PatientDocumentDateRangeFilter>("all");
  const [patientDocumentTypeFilter, setPatientDocumentTypeFilter] =
    useState<PatientDocumentTypeFilter>("all");
  const [
    patientDocumentAvailabilityFilter,
    setPatientDocumentAvailabilityFilter,
  ] = useState<PatientDocumentAvailabilityFilter>("all");
  const [patientDocumentWorkspaceNotice, setPatientDocumentWorkspaceNotice] =
    useState<string | null>(null);
  const [
    isPatientDocumentUploadSubmitting,
    setIsPatientDocumentUploadSubmitting,
  ] = useState(false);
  const [patientCallSearchQuery, setPatientCallSearchQuery] = useState("");
  const [selectedPatientOnboardingItemId, setSelectedPatientOnboardingItemId] =
    useState("onboarding-intake");
  const [
    selectedPatientAppointmentItemId,
    setSelectedPatientAppointmentItemId,
  ] = useState("appointment-visit");
  const [selectedPatientProcedureItemId, setSelectedPatientProcedureItemId] =
    useState("procedure-plan");
  const [patientWorkspaceMessageDraft, setPatientWorkspaceMessageDraft] =
    useState("");
  const [
    patientWorkspaceInteractionNotice,
    setPatientWorkspaceInteractionNotice,
  ] = useState<string | null>(null);
  const [patientSupportModal, setPatientSupportModal] =
    useState<PatientSupportModalState | null>(null);
  const [patientSupportFormState, setPatientSupportFormState] =
    useState<PatientSupportFormState>(() =>
      createPatientSupportInitialFormState(),
    );
  const [patientMobileDetail, setPatientMobileDetail] =
    useState<PatientMobileDetailState>(null);
  const [patientDesktopCareDetailModal, setPatientDesktopCareDetailModal] =
    useState<PatientDesktopCareDetailModalState>(null);
  const [patientMobilePanel, setPatientMobilePanel] =
    useState<PatientMobilePanelId | null>(null);
  const [patientMobileMedicationMode, setPatientMobileMedicationMode] =
    useState<PatientMobileMedicationMode>("alphabetical");
  const [patientMobileAuthStep, setPatientMobileAuthStep] =
    useState<PatientMobileAuthStep>("phone");
  const [patientMobilePhoneNumber, setPatientMobilePhoneNumber] =
    useState("");
  const [patientMobileOtpCode, setPatientMobileOtpCode] = useState("");
  const [patientMobileAuthNotice, setPatientMobileAuthNotice] = useState<
    string | null
  >(null);
  const [
    patientMedicationReminderPreferences,
    setPatientMedicationReminderPreferences,
  ] = useState<PatientMedicationReminderPreferences>(() =>
    createPatientMedicationReminderPreferences(),
  );
  const [hasPatientMobileDisclaimerAccepted, setHasPatientMobileDisclaimerAccepted] =
    useState(false);
  const { height: viewportHeight, width: viewportWidth } =
    useWindowDimensions();
  const isMobileWebLayout = showsLegacyWebAssets && !isMediumLayout;
  const [patientOnboardingFlow, setPatientOnboardingFlow] =
    useState<PatientLegacyOnboardingFlowState>(
      () =>
        readPatientLegacyOnboardingFlowFromWindow() ?? {
          careType: patientLegacyOnboardingDefaultCareType,
          isActive: false,
          stepIndex: 0,
        },
    );
  const [patientOnboardingLayoutRects, setPatientOnboardingLayoutRects] =
    useState<PatientOnboardingLayoutRects>({});
  const locallyCompletedOnboardingPatientIdRef = useRef<string | null>(null);
  const patientOnboardingCareType =
    resolvePatientLegacyOnboardingCareType(summary);

  useEffect(() => {
    if (
      !isPatientWorkspaceTabEnabled(
        selectedPatientWorkspaceTabId,
        patientPortalFeatureFlags,
      )
    ) {
      setSelectedPatientWorkspaceTabId("overview");
    }
  }, [
    patientPortalFeatureFlags,
    selectedPatientWorkspaceTabId,
    setSelectedPatientWorkspaceTabId,
  ]);

  const updatePatientOnboardingLayoutRect = useCallback(
    (regionKey: PatientOnboardingRegionKey, event: LayoutChangeEvent) => {
      const nextRect = toPatientOnboardingLayoutRect(event);

      setPatientOnboardingLayoutRects((currentRects) => {
        const currentRect = currentRects[regionKey];

        if (
          currentRect &&
          currentRect.x === nextRect.x &&
          currentRect.y === nextRect.y &&
          currentRect.width === nextRect.width &&
          currentRect.height === nextRect.height
        ) {
          return currentRects;
        }

        return {
          ...currentRects,
          [regionKey]: nextRect,
        };
      });
    },
    [],
  );

  const startPatientOnboardingFlow = useCallback(
    (careType: PatientLegacyOnboardingCareType = patientOnboardingCareType) => {
      const nextFlow: PatientLegacyOnboardingFlowState = {
        careType,
        isActive: true,
        stepIndex: 0,
      };

      setSelectedPatientWorkspaceTabId("overview");
      setPatientWorkspaceInteractionNotice(null);
      setPatientOnboardingFlow(nextFlow);
      writePatientLegacyOnboardingFlowToWindow(nextFlow);
    },
    [patientOnboardingCareType, setSelectedPatientWorkspaceTabId],
  );

  const updatePatientOnboardingStep = useCallback((stepIndex: number) => {
    setPatientOnboardingFlow((currentFlow) => {
      const nextFlow: PatientLegacyOnboardingFlowState = {
        careType: currentFlow.careType,
        isActive: true,
        stepIndex: clampPatientLegacyOnboardingStepIndex(stepIndex),
      };

      writePatientLegacyOnboardingFlowToWindow(nextFlow);

      return nextFlow;
    });
  }, []);

  const finishPatientOnboardingFlow = useCallback(
    (noticeText: string) => {
      const patientId = summary?.patientId ?? null;

      if (patientId) {
        locallyCompletedOnboardingPatientIdRef.current = patientId;
      }

      setPatientOnboardingFlow((currentFlow) => ({
        ...currentFlow,
        isActive: false,
      }));
      clearPatientLegacyOnboardingFlowFromWindow();
      setPatientWorkspaceInteractionNotice(noticeText);

      void onRecordPatientOnboarding({
        entryPointRoute: "overview",
        initialFlowComplete: true,
      }).then((response) => {
        if (!response) {
          setPatientWorkspaceInteractionNotice(
            "Onboarding closed. The completion status could not be saved yet.",
          );
        }
      });
    },
    [onRecordPatientOnboarding, summary?.patientId],
  );

  const goToNextPatientOnboardingStep = useCallback(() => {
    if (
      patientOnboardingFlow.stepIndex >= patientLegacyOnboardingFinalStepIndex
    ) {
      finishPatientOnboardingFlow("Onboarding complete.");
      return;
    }

    updatePatientOnboardingStep(patientOnboardingFlow.stepIndex + 1);
  }, [
    finishPatientOnboardingFlow,
    patientOnboardingFlow.stepIndex,
    updatePatientOnboardingStep,
  ]);

  const goToPreviousPatientOnboardingStep = useCallback(() => {
    if (patientOnboardingFlow.stepIndex <= 0) {
      return;
    }

    updatePatientOnboardingStep(patientOnboardingFlow.stepIndex - 1);
  }, [patientOnboardingFlow.stepIndex, updatePatientOnboardingStep]);

  const skipPatientOnboardingFlow = useCallback(() => {
    finishPatientOnboardingFlow("Onboarding skipped.");
  }, [finishPatientOnboardingFlow]);

  useEffect(() => {
    setPatientDocumentSearchQuery("");
    setPatientDocumentDateRangeFilter("all");
    setPatientDocumentTypeFilter("all");
    setPatientDocumentAvailabilityFilter("all");
    setPatientDocumentWorkspaceNotice(null);
    setSelectedPatientOnboardingItemId("onboarding-intake");
    setSelectedPatientAppointmentItemId("appointment-visit");
    setSelectedPatientProcedureItemId("procedure-plan");
    setPatientWorkspaceMessageDraft("");
    setPatientWorkspaceInteractionNotice(null);
    setPatientSupportModal(null);
    setPatientSupportFormState(createPatientSupportInitialFormState());
    setPatientOnboardingLayoutRects({});
    setPatientMobileDetail(null);
    setPatientMobilePanel(null);
    setPatientMobileMedicationMode("alphabetical");
    setPatientMobileAuthStep("phone");
    setPatientMobilePhoneNumber("");
    setPatientMobileOtpCode("");
    setPatientMobileAuthNotice(null);
    setPatientMedicationReminderPreferences(
      createPatientMedicationReminderPreferences(
        summary?.onboarding.preferences.wakeUpTime,
      ),
    );
    setHasPatientMobileDisclaimerAccepted(false);
  }, [summary?.patientId]);

  useEffect(() => {
    if (patientPortalAuthNotice == null) {
      return;
    }

    setPatientMobileAuthNotice(patientPortalAuthNotice);
  }, [patientPortalAuthNotice]);

  useEffect(() => {
    const authStatus = summary?.onboarding.auth.status;

    if (authStatus === "challenge-issued") {
      setPatientMobileAuthStep("code");
      return;
    }

    if (authStatus === "verified") {
      setPatientMobileAuthNotice(null);
      setPatientMobileOtpCode("");
    }
  }, [summary?.onboarding.auth.status]);

  useEffect(() => {
    const urlFlow = readPatientLegacyOnboardingFlowFromWindow();

    if (urlFlow) {
      setSelectedPatientWorkspaceTabId("overview");
      setPatientOnboardingFlow(urlFlow);
      return;
    }

    if (!summary || summary.onboarding.initialFlowComplete) {
      return;
    }

    if (locallyCompletedOnboardingPatientIdRef.current === summary.patientId) {
      return;
    }

    const nextFlow: PatientLegacyOnboardingFlowState = {
      careType: patientOnboardingCareType,
      isActive: true,
      stepIndex: 0,
    };

    setSelectedPatientWorkspaceTabId("overview");
    setPatientOnboardingFlow(nextFlow);
    writePatientLegacyOnboardingFlowToWindow(nextFlow);
  }, [patientOnboardingCareType, setSelectedPatientWorkspaceTabId, summary]);

  const conversationMessages = mapConversationMessagesToChatViewModels(
    summary?.conversation.messages ?? [],
  );
  const patientConversationScrollRef = useRef<ScrollView | null>(null);
  const latestPatientConversationMessageKey =
    conversationMessages.length > 0
      ? conversationMessages
          .map((message) =>
            [
              message.id,
              message.senderKind,
              message.timestampText,
              message.bodyText,
            ].join(":"),
          )
          .join("|")
      : "empty";
  const scrollPatientConversationToEnd = useCallback((animated = true) => {
    const runScroll = () => {
      patientConversationScrollRef.current?.scrollToEnd({ animated });
    };

    setTimeout(runScroll, 0);
  }, []);
  const handlePatientConversationContentSizeChange = useCallback(() => {}, []);

  useEffect(() => {
    if (selectedPatientWorkspaceTabId !== "messages") {
      return;
    }

    scrollPatientConversationToEnd(true);
  }, [
    isPatientMessageCommandSubmitting,
    latestPatientConversationMessageKey,
    scrollPatientConversationToEnd,
    selectedPatientWorkspaceTabId,
  ]);
  useEffect(() => {
    setPatientWorkspaceInteractionNotice(null);
  }, [selectedPatientWorkspaceTabId]);
  const patientWorkspaceMessageDraftRef = useRef(patientWorkspaceMessageDraft);

  useEffect(() => {
    patientWorkspaceMessageDraftRef.current = patientWorkspaceMessageDraft;
  }, [patientWorkspaceMessageDraft]);

  const handlePatientVoiceAutoSend = useCallback(
    async (transcript: string): Promise<boolean> => {
      const trimmedTranscript = transcript.trim();

      if (!trimmedTranscript) {
        return false;
      }

      if (
        patientWorkspaceMessageDraftRef.current.trim() !== trimmedTranscript
      ) {
        setPatientWorkspaceInteractionNotice(
          "Voice message is ready. Press SEND when you are done editing it.",
        );
        return false;
      }

      if (!summary) {
        setPatientWorkspaceInteractionNotice(
          "Voice message is ready, but no active patient workspace is available.",
        );
        return false;
      }

      if (isPatientMessageCommandSubmitting) {
        setPatientWorkspaceInteractionNotice(
          "Voice message is ready. It can be sent after the current message finishes.",
        );
        return false;
      }

      const wasApplied = await onRecordPatientMessage({
        bodyText: trimmedTranscript,
      });

      if (wasApplied) {
        setPatientWorkspaceMessageDraft("");
        setPatientWorkspaceInteractionNotice(
          `Voice message sent. Check the conversation for ${patientWorkspaceBrand.assistantName}'s response.`,
        );
        return true;
      }

      setPatientWorkspaceInteractionNotice(
        "Voice message could not be sent. Please review it and press SEND.",
      );
      return false;
    },
    [isPatientMessageCommandSubmitting, onRecordPatientMessage, summary],
  );
  const readAloudControls = usePatientWorkspaceReadAloud({
    conversationMessages,
    patientId: summary?.patientId ?? null,
    runtimeConfig,
    onNoticeChange: setPatientWorkspaceInteractionNotice,
  });
  const voiceInputControls = usePatientWorkspaceVoiceInput({
    onAutoSendTranscript: handlePatientVoiceAutoSend,
    onNoticeChange: setPatientWorkspaceInteractionNotice,
    onTranscript: setPatientWorkspaceMessageDraft,
  });
  const sendPatientWorkspaceActionMessage = useCallback(
    async (input: {
      bodyText: string;
      failureNotice: string;
      startNotice: string;
      successNotice: string;
    }): Promise<boolean> => {
      if (!summary) {
        setPatientWorkspaceInteractionNotice(
          "No active patient workspace is available for this action.",
        );
        return false;
      }

      if (isPatientMessageCommandSubmitting) {
        setPatientWorkspaceInteractionNotice(
          "A patient message is already being saved. Try again in a moment.",
        );
        return false;
      }

      setPatientWorkspaceInteractionNotice(input.startNotice);

      const wasApplied = await onRecordPatientMessage({
        bodyText: input.bodyText,
      });

      setPatientWorkspaceInteractionNotice(
        wasApplied ? input.successNotice : input.failureNotice,
      );

      if (wasApplied) {
        setSelectedPatientWorkspaceTabId("messages");
      }

      return wasApplied;
    },
    [isPatientMessageCommandSubmitting, onRecordPatientMessage, summary],
  );
  const requestPatientWorkspaceCallback = useCallback(() => {
    const patientName = summary?.displayName?.trim() || "me";
    void sendPatientWorkspaceActionMessage({
      bodyText: `Please ask my care team to call ${patientName} back when they are available.`,
      failureNotice:
        "The callback request could not be saved. Please retry after the workspace refreshes.",
      startNotice: `Sending callback request to ${patientWorkspaceBrand.assistantName}.`,
      successNotice:
        `Callback request saved. Check messages for ${patientWorkspaceBrand.assistantName}'s response.`,
    });
  }, [sendPatientWorkspaceActionMessage, summary?.displayName]);
  const copyPatientWorkspaceVoiceCallTranscript = useCallback(
    (voiceCall: PatientWorkspaceVoiceCallSummary) => {
      const clipboard = globalThis.navigator?.clipboard;
      const transcriptText =
        buildPatientWorkspaceVoiceCallTranscriptText(voiceCall);

      if (!clipboard?.writeText) {
        setPatientWorkspaceInteractionNotice(
          "Transcript copy is unavailable in this browser.",
        );
        return;
      }

      void clipboard
        .writeText(transcriptText)
        .then(() =>
          setPatientWorkspaceInteractionNotice(
            "Call transcript copied to the clipboard.",
          ),
        )
        .catch(() =>
          setPatientWorkspaceInteractionNotice(
            "The call transcript could not be copied.",
          ),
        );
    },
    [],
  );
  const downloadPatientWorkspaceDocument = useCallback(
    (
      documentSummary: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
    ) => {
      if (documentSummary.availability !== "download-ready") {
        setPatientDocumentWorkspaceNotice(
          `${documentSummary.label} is metadata-only and is not available for download.`,
        );
        return;
      }

      const didOpenDownload = openPatientWorkspaceDocumentDownload(
        runtimeConfig,
        {
          documentId: documentSummary.documentId,
          patientId: summary?.patientId ?? null,
        },
      );

      setPatientDocumentWorkspaceNotice(
        didOpenDownload
          ? `Downloading ${documentSummary.label}.`
          : "Document downloads are only available in the hosted patient portal.",
      );
    },
    [runtimeConfig, summary?.patientId],
  );
  const uploadPatientWorkspaceDocumentFromBrowser = useCallback(() => {
    if (isPatientDocumentUploadSubmitting) {
      return;
    }

    if (!supportsBrowserDocumentUpload()) {
      setPatientDocumentWorkspaceNotice(
        "Document uploads are available from the hosted web patient portal.",
      );
      return;
    }

    if (summary == null) {
      setPatientDocumentWorkspaceNotice(
        "The patient workspace must finish loading before a document can be uploaded.",
      );
      return;
    }

    void (async () => {
      const selectedFile = await pickBrowserDocumentUploadFile();

      if (selectedFile == null) {
        return;
      }

      startTransition(() => {
        setIsPatientDocumentUploadSubmitting(true);
        setPatientDocumentWorkspaceNotice(
          `Uploading ${selectedFile.name || "document"} for analysis.`,
        );
      });

      try {
        const response = await uploadPatientWorkspaceDocument(runtimeConfig, {
          file: selectedFile,
          patientId: summary.patientId,
        });
        startTransition(() => {
          setIsPatientDocumentUploadSubmitting(false);
          setPatientDocumentWorkspaceNotice(
            response.ingestion.aiContextEligible
              ? `${response.document.label} was uploaded and added to ${patientWorkspaceBrand.assistantName} context.`
              : `${response.document.label} was uploaded. Text analysis is ${response.ingestion.extractionStatus}.`,
          );
        });
        onPatientDocumentUploadComplete(response);
      } catch {
        startTransition(() => {
          setIsPatientDocumentUploadSubmitting(false);
          setPatientDocumentWorkspaceNotice(
            "The document could not be uploaded. Please retry after the workspace refreshes.",
          );
        });
      }
    })();
  }, [
    isPatientDocumentUploadSubmitting,
    onPatientDocumentUploadComplete,
    runtimeConfig,
    summary,
  ]);
  const reviewPatientWorkspaceVoiceCall = useCallback(
    (voiceCall: PatientWorkspaceVoiceCallSummary) => {
      void sendPatientWorkspaceActionMessage({
        bodyText: buildPatientWorkspaceVoiceCallReviewPrompt(voiceCall),
        failureNotice:
          "The call review request could not be saved. Please retry after the workspace refreshes.",
        startNotice: `Sending call transcript to ${patientWorkspaceBrand.assistantName}.`,
        successNotice:
          `Call review request saved. Check messages for ${patientWorkspaceBrand.assistantName}'s response.`,
      });
    },
    [sendPatientWorkspaceActionMessage],
  );
  const openPatientSupportModal = useCallback(
    (modal: PatientSupportModalState) => {
      setPatientSupportModal(modal);
      setPatientSupportFormState(createPatientSupportInitialFormState(modal));
      setPatientWorkspaceInteractionNotice(null);
    },
    [],
  );
  const closePatientSupportModal = useCallback(() => {
    setPatientSupportModal(null);
    setPatientSupportFormState(createPatientSupportInitialFormState());
    setPatientMobilePanel((currentPanel) =>
      currentPanel === "feedback" || currentPanel === "bug"
        ? null
        : currentPanel,
    );
  }, []);
  const openPatientMobilePanel = useCallback(
    (panelId: PatientMobilePanelId) => {
      setPatientMobilePanel(panelId);
      setPatientMobileDetail(null);

      if (panelId === "feedback" || panelId === "bug") {
        const modalState: PatientSupportModalState = {
          kind: panelId,
          source: `patient-mobile-footer:${panelId}`,
        };
        setPatientSupportModal(modalState);
        setPatientSupportFormState(createPatientSupportInitialFormState(modalState));
      } else {
        setPatientSupportModal(null);
        setPatientSupportFormState(createPatientSupportInitialFormState());
      }
    },
    [],
  );
  const closePatientMobilePanel = useCallback(() => {
    setPatientMobilePanel(null);
    setPatientSupportModal(null);
    setPatientSupportFormState(createPatientSupportInitialFormState());
  }, []);
  const openPatientMobileSupportPanel = useCallback(
    (modalState: PatientSupportModalState) => {
      setPatientMobilePanel(
        modalState.kind === "bug" ? "bug" : "feedback",
      );
      setPatientMobileDetail(null);
      setPatientSupportModal(modalState);
      setPatientSupportFormState(createPatientSupportInitialFormState(modalState));
    },
    [],
  );
  const navigatePatientMobileTab = useCallback(
    (tabId: PatientWorkspaceTabId) => {
      if (!isPatientWorkspaceTabEnabled(tabId, patientPortalFeatureFlags)) {
        setPatientMobilePanel(null);
        setPatientMobileDetail(null);
        setSelectedPatientWorkspaceTabId("overview");
        setPatientWorkspaceInteractionNotice(
          "That patient portal section is currently unavailable.",
        );
        return;
      }

      setPatientMobilePanel(null);
      setPatientMobileDetail(null);
      setPatientWorkspaceInteractionNotice(null);
      if (tabId === "documents") {
        setPatientDocumentWorkspaceNotice(null);
      }
      setSelectedPatientWorkspaceTabId(tabId);
    },
    [patientPortalFeatureFlags, setSelectedPatientWorkspaceTabId],
  );
  const changePatientMedicationReminderTime = useCallback(
    (slotId: PatientMedicationReminderSlotId, wakeUpTime: string) => {
      setPatientMedicationReminderPreferences((currentPreferences) => ({
        ...currentPreferences,
        [slotId]: wakeUpTime,
      }));
    },
    [],
  );
  const updatePatientMobileReminderTime = useCallback(
    (input: PatientMedicationReminderTimeUpdateInput) => {
      const isDisablingReminders = input.wakeUpTime === "off";

      if (!isDisablingReminders) {
        setPatientMedicationReminderPreferences((currentPreferences) => ({
          ...currentPreferences,
          [input.slotId]: input.wakeUpTime,
        }));
      }

      setPatientWorkspaceInteractionNotice(
        isDisablingReminders
          ? "Turning off medication reminders."
          : `Saving ${input.label.toLowerCase()} medication reminder.`,
      );

      void onRecordPatientOnboarding({
        remindersEnabled: !isDisablingReminders,
        wakeUpTime: isDisablingReminders ? null : input.wakeUpTime,
      })
        .then((response) => {
          setPatientWorkspaceInteractionNotice(
            response
              ? isDisablingReminders
                ? "Medication reminders turned off."
                : `${input.label} reminder set to ${input.displayTime}.`
              : "The medication preference could not be saved.",
          );
        })
        .catch(() => {
          setPatientWorkspaceInteractionNotice(
            "The medication preference could not be saved.",
          );
        });
    },
    [onRecordPatientOnboarding],
  );
  const setPatientRemindersEnabled = useCallback(
    (enabled: boolean) => {
      const fallbackWakeUpTime =
        patientMedicationReminderPreferences.morning ||
        summary?.onboarding.preferences.wakeUpTime ||
        "07:00";

      setPatientWorkspaceInteractionNotice(
        enabled ? "Turning on reminders." : "Turning off reminders.",
      );

      void onRecordPatientOnboarding({
        remindersEnabled: enabled,
        wakeUpTime: enabled ? fallbackWakeUpTime : null,
      })
        .then((response) => {
          setPatientWorkspaceInteractionNotice(
            response
              ? enabled
                ? "Reminders turned on."
                : "Reminders turned off."
              : "The reminder preference could not be saved.",
          );
        })
        .catch(() => {
          setPatientWorkspaceInteractionNotice(
            "The reminder preference could not be saved.",
          );
        });
    },
    [
      onRecordPatientOnboarding,
      patientMedicationReminderPreferences.morning,
      summary?.onboarding.preferences.wakeUpTime,
    ],
  );
  const updatePatientMobilePhoneNumber = useCallback((value: string) => {
    setPatientMobilePhoneNumber(value.replace(/[^\d()+\-\s]/g, ""));
    setPatientMobileAuthNotice(null);
    setPatientPortalAuthNotice(null);
  }, [setPatientPortalAuthNotice]);
  const updatePatientMobileOtpCode = useCallback((value: string) => {
    setPatientMobileOtpCode(value.replace(/\D/g, "").slice(0, 8));
    setPatientMobileAuthNotice(null);
    setPatientPortalAuthNotice(null);
  }, [setPatientPortalAuthNotice]);
  const signOutPatientMobileWorkspace = useCallback(() => {
    if (!usesPatientPortalSession) {
      if (canSwitchWorkspaces) {
        beginWorkspaceSelection();
        return;
      }

      setPatientWorkspaceInteractionNotice(
        "Sign out is unavailable for this workspace.",
      );
      return;
    }

    setPatientWorkspaceInteractionNotice("Signing out.");

    void logoutWorkforceSession(runtimeConfig)
      .then(() => {
        setPatientPortalSession(null);
        setPatientWorkspaceInteractionNotice(null);
        setPatientMobileAuthNotice("Signed out.");
        setPatientPortalAuthNotice("Signed out.");
        setPatientMobileAuthStep("phone");
        setPatientMobilePhoneNumber("");
        setPatientMobileOtpCode("");
        setHasPatientMobileDisclaimerAccepted(false);
        setPatientWorkspaceMessageDraft("");
      })
      .catch(() => {
        setPatientWorkspaceInteractionNotice(
          "The patient session could not be signed out. Please refresh and try again.",
        );
      });
  }, [
    beginWorkspaceSelection,
    canSwitchWorkspaces,
    runtimeConfig,
    setPatientPortalAuthNotice,
    usesPatientPortalSession,
  ]);
  const updatePatientSupportFormField = useCallback(
    (field: PatientSupportFormField, value: string) => {
      setPatientSupportFormState((currentState) => ({
        ...currentState,
        [field]: value,
        errorText: null,
      }));
    },
    [],
  );
  const submitPatientSupportModal = useCallback(() => {
    if (!patientSupportModal) {
      return;
    }

    if (patientSupportFormState.isSubmitting) {
      return;
    }

    const trimmedName = patientSupportFormState.name.trim();
    const trimmedEmailAddress = patientSupportFormState.emailAddress.trim();
    const trimmedFeedbackText = patientSupportFormState.feedbackText.trim();
    const trimmedBugContext = patientSupportFormState.bugContext.trim();
    const trimmedBugImpact = patientSupportFormState.bugImpact.trim();

    if (trimmedName.length > 100) {
      setPatientSupportFormState((currentState) => ({
        ...currentState,
        errorText: "Name must be 100 characters or fewer.",
      }));
      return;
    }

    if (!isValidPatientSupportEmail(trimmedEmailAddress)) {
      setPatientSupportFormState((currentState) => ({
        ...currentState,
        errorText: "Enter a valid email address.",
      }));
      return;
    }

    if (
      patientSupportModal.kind === "bug" &&
      (trimmedBugContext.length === 0 || trimmedBugImpact.length === 0)
    ) {
      setPatientSupportFormState((currentState) => ({
        ...currentState,
        errorText: "Context and impact are required.",
      }));
      return;
    }

    if (
      patientSupportModal.kind !== "bug" &&
      trimmedFeedbackText.length === 0
    ) {
      setPatientSupportFormState((currentState) => ({
        ...currentState,
        errorText: "Feedback is required.",
      }));
      return;
    }

    setPatientSupportFormState((currentState) => ({
      ...currentState,
      errorText: null,
      isSubmitting: true,
    }));

    void submitSupportSubmission(runtimeConfig, {
      bugContext: patientSupportModal.kind === "bug" ? trimmedBugContext : null,
      bugImpact: patientSupportModal.kind === "bug" ? trimmedBugImpact : null,
      emailAddress: trimmedEmailAddress.length > 0 ? trimmedEmailAddress : null,
      feedbackText:
        patientSupportModal.kind === "bug" ? null : trimmedFeedbackText,
      messageId: patientSupportModal.message?.id ?? null,
      messageText: patientSupportModal.message?.bodyText ?? null,
      name: trimmedName.length > 0 ? trimmedName : null,
      pageUrl: getPatientSupportPageUrl(),
      patientId:
        summary?.patientId ?? patientWorkforceSession?.patientId ?? null,
      sentiment: patientSupportModal.sentiment ?? null,
      siteId: patientWorkforceSession?.siteId ?? runtimeConfig.requestSiteId,
      source: patientSupportModal.source,
      subjectKey:
        patientWorkforceSession?.subjectKey ??
        runtimeConfig.workforceSessionSubjectKey,
      submissionKind: resolvePatientSupportSubmissionKind(patientSupportModal),
      userAgent: getPatientSupportUserAgent(),
    })
      .then((response) => {
        setPatientSupportFormState((currentState) => ({
          ...currentState,
          errorText: null,
          isSubmitting: false,
          receiptId: response.submissionId,
          successText:
            patientSupportModal.kind === "bug"
              ? "We appreciate your help in improving the app. Our team will review the issue and address it as soon as possible."
              : "Your input helps us improve the app and create a better experience for you. Stay tuned for updates.",
        }));
        setPatientWorkspaceInteractionNotice(response.summaryText);
      })
      .catch(() => {
        setPatientSupportFormState((currentState) => ({
          ...currentState,
          errorText:
            "The submission could not be saved. Please check your connection and try again.",
          isSubmitting: false,
        }));
      });
  }, [
    patientWorkforceSession?.patientId,
    patientWorkforceSession?.siteId,
    patientWorkforceSession?.subjectKey,
    patientSupportFormState,
    patientSupportModal,
    runtimeConfig,
    summary?.patientId,
  ]);
  const requestPatientMobileAuthCode = useCallback(() => {
    const trimmedPhoneNumber = patientMobilePhoneNumber.trim();

    if (trimmedPhoneNumber.length === 0) {
      setPatientMobileAuthNotice("Enter the phone number to continue.");
      return;
    }

    setPatientMobileAuthNotice("Preparing your verification code.");

    void onRecordPatientOnboarding({
      authChallenge: {
        action: "request",
        phoneNumber: trimmedPhoneNumber,
      },
    }).then((response) => {
      const challenge = response?.authChallenge ?? null;

      if (challenge?.status === "challenge-issued") {
        setPatientMobileAuthStep("code");
        setPatientMobileAuthNotice(
          challenge.developmentCode
            ? `Use code ${challenge.developmentCode} to continue.`
            : `Enter the code sent to your phone ending in ${challenge.phoneNumberLast4 ?? "that number"}.`,
        );
        return;
      }

      setPatientMobileAuthNotice(
        "The verification code could not be prepared. Please try again.",
      );
    });
  }, [onRecordPatientOnboarding, patientMobilePhoneNumber]);
  const verifyPatientMobileAuthCode = useCallback(() => {
    const trimmedCode = patientMobileOtpCode.trim();
    const trimmedPhoneNumber = patientMobilePhoneNumber.trim();

    if (trimmedCode.length === 0) {
      setPatientMobileAuthNotice("Enter the 6-digit code to continue.");
      return;
    }

    if (trimmedPhoneNumber.length === 0) {
      setPatientMobileAuthNotice("Enter the phone number used for this code.");
      return;
    }

    setPatientMobileAuthNotice("Checking your verification code.");

    void onRecordPatientOnboarding({
      authChallenge: {
        action: "verify",
        otpCode: trimmedCode,
        phoneNumber: trimmedPhoneNumber,
      },
    }).then(async (response) => {
      const challenge = response?.authChallenge ?? null;

      if (challenge?.status === "verified") {
        try {
          const subjects = await listWorkforceSessionSubjects(runtimeConfig);
          const patientSubject =
            response?.patientId != null
              ? subjects.find(
                  (subject) =>
                    subject.patientId === response.patientId &&
                    resolveWorkspaceMode(subject.roleKeys) === "patient",
                ) ?? null
              : null;

          if (!patientSubject) {
            setPatientMobileAuthNotice(
              "The patient workspace could not finish sign-in for this phone number.",
            );
            return;
          }

          const nextSession = await loginWorkforceSession(
            runtimeConfig,
            patientSubject.subjectKey,
          );

          setPatientPortalSession(nextSession);
          setPatientMobileOtpCode("");
          setPatientMobileAuthNotice(null);
          setPatientPortalAuthNotice(null);
          setHasPatientMobileDisclaimerAccepted(false);
        } catch {
          setPatientMobileAuthNotice(
            "The verification code matched, but the patient session could not be started.",
          );
        }
        return;
      }

      setPatientMobileAuthNotice(
        challenge?.status === "locked"
          ? "Too many attempts. Request a fresh code and try again."
          : challenge?.status === "expired"
            ? "That code expired. Request a fresh code and try again."
            : "That code did not match. Please check it and try again.",
      );
    });
  }, [
    runtimeConfig,
    onRecordPatientOnboarding,
    patientMobileOtpCode,
    patientMobilePhoneNumber,
    setPatientPortalAuthNotice,
  ]);
  const acknowledgePatientMobileDisclaimer = useCallback(() => {
    setHasPatientMobileDisclaimerAccepted(true);
    startPatientOnboardingFlow(patientOnboardingCareType);
  }, [patientOnboardingCareType, startPatientOnboardingFlow]);
  const patientSupportModalElement = renderPatientSupportModal({
    formState: patientSupportFormState,
    layout,
    modalState: patientSupportModal,
    onChangeField: updatePatientSupportFormField,
    onClose: closePatientSupportModal,
    onSubmit: submitPatientSupportModal,
    theme,
    tokens,
  });
  const patientWorkspaceNavItems = [
    {
      id: "overview" as const,
      label: "Overview",
      badgeText: null,
    },
    {
      id: "onboarding" as const,
      label: "Onboarding",
      badgeText:
        (summary?.pendingDocumentCount ?? 0) > 0
          ? "START"
          : summary?.documentSummaries.length
            ? "READY"
            : "FLOW",
    },
    {
      id: "appointments" as const,
      label: "Appointments",
      badgeText: summary?.nextAppointmentText ? "NEXT" : null,
    },
    {
      id: "procedures" as const,
      label: "Procedures",
      badgeText: summary?.procedureSummaryText ? "PLAN" : null,
    },
    {
      id: "medications" as const,
      label: "Medications",
      badgeText: summary?.medicationSummaryText ? "RX" : null,
    },
    {
      id: "documents" as const,
      label: "Documents",
      badgeText:
        summary && summary.documentSummaries.length > 0
          ? String(summary.documentSummaries.length)
          : null,
    },
    {
      id: "calls" as const,
      label: "Calls",
      badgeText:
        summary && summary.voiceCallSummaries.length > 0
          ? String(summary.voiceCallSummaries.length)
          : null,
    },
    {
      id: "messages" as const,
      label: "Messages",
      badgeText:
        (summary?.unreadMessageCount ?? 0) > 0
          ? String(summary?.unreadMessageCount ?? 0)
          : "AGT",
    },
    {
      id: "settings" as const,
      label: "Profile",
      badgeText: null,
    },
  ].filter((item) =>
    isPatientWorkspaceTabEnabled(item.id, patientPortalFeatureFlags),
  );
  const enabledPatientWorkspaceTabIds = patientWorkspaceNavItems.map(
    (item) => item.id,
  );
  const navigatePatientWorkspaceTabIfEnabled = (
    tabId: PatientWorkspaceTabId,
  ) => {
    if (!isPatientWorkspaceTabEnabled(tabId, patientPortalFeatureFlags)) {
      setSelectedPatientWorkspaceTabId("overview");
      setPatientWorkspaceInteractionNotice(
        "That patient portal section is currently unavailable.",
      );
      return;
    }

    setSelectedPatientWorkspaceTabId(tabId);
  };
  const patientWorkspaceQuickActionBand = renderPatientWorkspaceQuickActionBand(
    {
      enabledTabIds: enabledPatientWorkspaceTabIds,
      isMediumLayout,
      layout,
      onRequestCallback: requestPatientWorkspaceCallback,
      selectedPatientWorkspaceTabId,
      setSelectedPatientWorkspaceTabId: navigatePatientWorkspaceTabIfEnabled,
      summary,
      theme,
      tokens,
    },
  );
  const patientWorkspaceTabContent = renderPatientWorkspaceTabContent({
    activeWorkforceSession: patientWorkforceSession,
    canSwitchWorkspaces,
    conversationMessages,
    featureFlags: patientPortalFeatureFlags,
    isDocumentUploadSubmitting: isPatientDocumentUploadSubmitting,
    isMessageCommandSubmitting: isPatientMessageCommandSubmitting,
    isOnboardingCommandSubmitting: isPatientOnboardingCommandSubmitting,
    isWideLayout,
    layout,
    onConversationContentSizeChange: handlePatientConversationContentSizeChange,
    onDownloadDocument: downloadPatientWorkspaceDocument,
    onUploadDocument: uploadPatientWorkspaceDocumentFromBrowser,
    onRecordMessage: onRecordPatientMessage,
    onRecordOnboarding: onRecordPatientOnboarding,
    onOpenSupportModal: openPatientSupportModal,
    onSetRemindersEnabled: setPatientRemindersEnabled,
    onSignOut: signOutPatientMobileWorkspace,
    onStartOnboarding: startPatientOnboardingFlow,
    onChangeReminderTime: changePatientMedicationReminderTime,
    onUpdateReminderTime: updatePatientMobileReminderTime,
    medicationReminderPreferences: patientMedicationReminderPreferences,
    patientDocumentAvailabilityFilter,
    patientCallSearchQuery,
    patientDocumentDateRangeFilter,
    patientDocumentSearchQuery,
    patientDocumentTypeFilter,
    patientDocumentWorkspaceNotice,
    patientConversationScrollRef,
    patientWorkspaceInteractionNotice,
    patientWorkspaceMessageDraft,
    readAloudControls,
    selectedPatientWorkspaceTabId,
    selectedPatientOnboardingItemId,
    selectedPatientAppointmentItemId,
    selectedPatientProcedureItemId,
    setPatientDocumentAvailabilityFilter,
    setPatientCallSearchQuery,
    setPatientDocumentDateRangeFilter,
    setPatientDocumentSearchQuery,
    setPatientDocumentTypeFilter,
    setPatientDocumentWorkspaceNotice,
    setPatientWorkspaceInteractionNotice,
    setPatientWorkspaceMessageDraft,
    onCopyCallTranscript: copyPatientWorkspaceVoiceCallTranscript,
    onRequestCallback: requestPatientWorkspaceCallback,
    onReviewCall: reviewPatientWorkspaceVoiceCall,
    setSelectedPatientWorkspaceTabId: navigatePatientWorkspaceTabIfEnabled,
    setSelectedPatientOnboardingItemId,
    setSelectedPatientAppointmentItemId,
    setSelectedPatientProcedureItemId,
    summary,
    theme,
    tokens,
    voiceInputControls,
  });
  const patientPortalAuthIsRequired =
    usesPatientPortalSession && patientWorkforceSession == null;
  const patientMobileAuthIsRequired =
    isMobileWebLayout && patientPortalAuthIsRequired;
  const patientDesktopAuthIsRequired =
    showsLegacyWebAssets &&
    isMediumLayout &&
    patientPortalAuthIsRequired;
  const patientMobileDisclaimerIsRequired =
    isMobileWebLayout &&
    summary != null &&
    summary.onboarding.auth.status === "verified" &&
    !summary.onboarding.initialFlowComplete &&
    !hasPatientMobileDisclaimerAccepted;

  if (isMobileWebLayout && patientMobilePanel != null) {
    return renderPatientWorkspaceMobilePanelShell({
      formState: patientSupportFormState,
      layout,
      onChangeSupportField: updatePatientSupportFormField,
      onClosePanel: closePatientMobilePanel,
      onOpenFooterLink: openPatientMobilePanel,
      onSubmitSupport: submitPatientSupportModal,
      panelId: patientMobilePanel,
      supportModalState: patientSupportModal,
      theme,
      tokens,
    });
  }

  if (patientMobileAuthIsRequired) {
    return renderPatientWorkspaceMobileAuthScreen({
      authNotice: patientMobileAuthNotice,
      authStep: patientMobileAuthStep,
      layout,
      onChangeOtpCode: updatePatientMobileOtpCode,
      onChangePhoneNumber: updatePatientMobilePhoneNumber,
      onOpenFooterLink: openPatientMobilePanel,
      onRequestCode: requestPatientMobileAuthCode,
      onVerifyCode: verifyPatientMobileAuthCode,
      otpCode: patientMobileOtpCode,
      phoneNumber: patientMobilePhoneNumber,
      summary,
      theme,
      tokens,
    });
  }

  if (patientMobileDisclaimerIsRequired) {
    return renderPatientWorkspaceMobileDisclaimerScreen({
      layout,
      onAcknowledge: acknowledgePatientMobileDisclaimer,
      onOpenFooterLink: openPatientMobilePanel,
      theme,
      tokens,
    });
  }

  if (patientDesktopAuthIsRequired) {
    return renderPatientWorkspaceDesktopAuthScreen({
      authNotice: patientMobileAuthNotice,
      authStep: patientMobileAuthStep,
      layout,
      legacyHospitalLogoSource,
      onChangeOtpCode: updatePatientMobileOtpCode,
      onChangePhoneNumber: updatePatientMobilePhoneNumber,
      onRequestCode: requestPatientMobileAuthCode,
      onVerifyCode: verifyPatientMobileAuthCode,
      otpCode: patientMobileOtpCode,
      phoneNumber: patientMobilePhoneNumber,
      theme,
      tokens,
      viewportHeight,
      viewportWidth,
    });
  }

  if (isMobileWebLayout) {
    return renderPatientWorkspaceMobileShell({
      activeWorkforceSession: patientWorkforceSession,
      canSwitchWorkspaces,
      detailState: patientMobileDetail,
      featureFlags: patientPortalFeatureFlags,
      formState: patientSupportFormState,
      hasPatientWorkspaceError,
      isDocumentUploadSubmitting: isPatientDocumentUploadSubmitting,
      isMessageCommandSubmitting: isPatientMessageCommandSubmitting,
      isPatientWorkspaceLoading,
      layout,
      navItems: patientWorkspaceNavItems,
      noticeText: patientWorkspaceInteractionNotice,
      medicationReminderPreferences: patientMedicationReminderPreferences,
      onBackFromDetail: () => setPatientMobileDetail(null),
      onChangeSupportField: updatePatientSupportFormField,
      onChangeMedicationMode: setPatientMobileMedicationMode,
      onChangeReminderTime: changePatientMedicationReminderTime,
      onClosePanel: closePatientMobilePanel,
      onConversationContentSizeChange:
        handlePatientConversationContentSizeChange,
      onCopyCallTranscript: copyPatientWorkspaceVoiceCallTranscript,
      onDraftChange: setPatientWorkspaceMessageDraft,
      onDownloadDocument: downloadPatientWorkspaceDocument,
      onUploadDocument: uploadPatientWorkspaceDocumentFromBrowser,
      onOnboardingBack: goToPreviousPatientOnboardingStep,
      onOnboardingNext: goToNextPatientOnboardingStep,
      onOnboardingRegionLayout: updatePatientOnboardingLayoutRect,
      onOnboardingSkip: skipPatientOnboardingFlow,
      onNavigateTab: navigatePatientMobileTab,
      onNoticeChange: setPatientWorkspaceInteractionNotice,
      onOpenDetail: setPatientMobileDetail,
      onOpenFooterLink: openPatientMobilePanel,
      onOpenSupportPanel: openPatientMobileSupportPanel,
      onRequestCallback: requestPatientWorkspaceCallback,
      onReviewCall: reviewPatientWorkspaceVoiceCall,
      onSetRemindersEnabled: setPatientRemindersEnabled,
      onSendMessage: onRecordPatientMessage,
      onSignOut: signOutPatientMobileWorkspace,
      onStartOnboarding: startPatientOnboardingFlow,
      onSubmitSupport: submitPatientSupportModal,
      onUpdateReminderTime: updatePatientMobileReminderTime,
      onboardingFlow: patientOnboardingFlow,
      isOnboardingCommandSubmitting: isPatientOnboardingCommandSubmitting,
      layoutRects: patientOnboardingLayoutRects,
      panelId: patientMobilePanel,
      patientCallSearchQuery,
      patientConversationScrollRef,
      readAloudControls,
      selectedPatientWorkspaceTabId,
      medicationMode: patientMobileMedicationMode,
      setPatientCallSearchQuery,
      setSelectedPatientWorkspaceTabId: navigatePatientMobileTab,
      summary,
      supportModalState: patientSupportModal,
      theme,
      tokens,
      draftValue: patientWorkspaceMessageDraft,
      voiceInputControls,
    });
  }

  if (showsLegacyWebAssets && isMediumLayout) {
    return renderPatientWorkspaceDesktopShell({
      activeWorkforceSession: patientWorkforceSession,
      conversationMessages,
      draftValue: patientWorkspaceMessageDraft,
      hasPatientWorkspaceError,
      isDocumentUploadSubmitting: isPatientDocumentUploadSubmitting,
      isOnboardingCommandSubmitting: isPatientOnboardingCommandSubmitting,
      isPatientWorkspaceLoading,
      isMessageCommandSubmitting: isPatientMessageCommandSubmitting,
      layout,
      layoutRects: patientOnboardingLayoutRects,
      legacyHospitalLogoSource,
      navItems: patientWorkspaceNavItems,
      noticeText: patientWorkspaceInteractionNotice,
      onConversationContentSizeChange:
        handlePatientConversationContentSizeChange,
      onDownloadDocument: downloadPatientWorkspaceDocument,
      onUploadDocument: uploadPatientWorkspaceDocumentFromBrowser,
      onDraftChange: setPatientWorkspaceMessageDraft,
      onOnboardingBack: goToPreviousPatientOnboardingStep,
      onOnboardingNext: goToNextPatientOnboardingStep,
      onOnboardingRegionLayout: updatePatientOnboardingLayoutRect,
      onOnboardingSkip: skipPatientOnboardingFlow,
      onCloseCareDetailModal: () => setPatientDesktopCareDetailModal(null),
      onSignOut: signOutPatientMobileWorkspace,
      onStartOnboarding: startPatientOnboardingFlow,
      onNoticeChange: setPatientWorkspaceInteractionNotice,
      onOpenCareDetailModal: setPatientDesktopCareDetailModal,
      onOpenSupportModal: openPatientSupportModal,
      readAloudControls,
      onSendMessage: onRecordPatientMessage,
      onUpdateReminderTime: updatePatientMobileReminderTime,
      onboardingFlow: patientOnboardingFlow,
      patientConversationScrollRef,
      patientDesktopCareDetailModal,
      selectedPatientWorkspaceTabId,
      setSelectedPatientWorkspaceTabId: navigatePatientWorkspaceTabIfEnabled,
      summary,
      supportModalElement: patientSupportModalElement,
      tabContent: patientWorkspaceTabContent,
      theme,
      tokens,
      voiceInputControls,
      viewportHeight,
      viewportWidth,
    });
  }

  return (
    <View
      style={[
        styles.root,
        {
          backgroundColor: "transparent",
        },
      ]}
    >
      {showsLegacyWebAssets ? (
        <Image
          resizeMode="cover"
          source={legacyBackgroundTextureSource}
          style={styles.legacyBackgroundTexture}
        />
      ) : null}
      <View
        pointerEvents="none"
        style={[
          styles.backgroundWash,
          {
            backgroundColor: theme.backdropOverlay,
          },
        ]}
      />
      <View
        pointerEvents="none"
        style={[
          styles.accentOrb,
          {
            backgroundColor: theme.accentSubtle,
          },
        ]}
      />
      <View
        pointerEvents="none"
        style={[
          styles.infoOrb,
          {
            backgroundColor: theme.backgroundSubtle,
            borderColor: theme.borderSubtle,
          },
        ]}
      />

      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          {
            gap: tokens.spacing.xl,
            padding: isMediumLayout ? tokens.spacing.xxl : tokens.spacing.xl,
          },
        ]}
      >
        <View
          style={[
            styles.pageFrame,
            {
              gap: tokens.spacing.lg,
              maxWidth: pageMaxWidth,
            },
          ]}
        >
          <View
            style={[
              styles.headerCard,
              {
                backgroundColor: theme.headerBand,
                borderColor: theme.headerBand,
                borderRadius: layout.cardRadius,
                gap: tokens.spacing.lg,
                padding: isMediumLayout
                  ? tokens.spacing.xxl
                  : tokens.spacing.xl,
              },
            ]}
          >
            <View
              style={[
                styles.headerRow,
                {
                  flexDirection: isMediumLayout ? "row" : "column",
                  gap: tokens.spacing.lg,
                },
              ]}
            >
              <View
                style={[
                  styles.headerContent,
                  {
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                <View
                  style={[
                    styles.brandLockupRow,
                    {
                      gap: tokens.spacing.md,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.brand,
                      {
                        color: hasPatientWorkspaceError
                          ? theme.criticalText
                          : theme.headerTextMuted,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                        lineHeight: tokens.typography.lineHeight.sm,
                      },
                    ]}
                  >
                    {hasPatientWorkspaceError
                      ? "Patient workspace unavailable"
                      : isPatientWorkspaceLoading
                        ? "Refreshing patient workspace"
                        : "Patient workspace"}
                  </Text>
                </View>

                <View
                  style={[
                    styles.badge,
                    {
                      backgroundColor: theme.surfaceMuted,
                      borderRadius: tokens.radius.pill,
                      paddingHorizontal: tokens.spacing.md,
                      paddingVertical: tokens.spacing.xs,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.badgeText,
                      {
                        color: theme.accentStrong,
                        fontFamily: layout.bodyFontFamily,
                      },
                    ]}
                  >
                    Patient mode
                  </Text>
                </View>

                <Text
                  style={[
                    styles.title,
                    {
                      color: theme.headerText,
                      fontFamily: layout.displayFontFamily,
                      fontSize: tokens.typography.fontSize["4xl"],
                      lineHeight: tokens.typography.lineHeight["4xl"],
                    },
                  ]}
                >
                  {summary?.displayName ?? "Patient workspace"}
                </Text>

                <Text
                  style={[
                    styles.subtitle,
                    {
                      color: theme.headerTextMuted,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.md,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  {hasPatientWorkspaceError
                    ? "The patient workspace could not load the current care summary."
                    : isPatientWorkspaceLoading
                      ? "Loading messages, documents, and schedule details."
                      : (summary?.summaryText ??
                        "Messages, care-plan context, documents, and schedule details.")}
                </Text>
              </View>

              <View
                style={[
                  styles.selectedPatientCard,
                  {
                    backgroundColor: theme.surfaceElevated,
                    borderColor: theme.borderStrong,
                    borderRadius: layout.controlRadius,
                    gap: tokens.spacing.md,
                    minWidth: isMediumLayout ? 320 : undefined,
                    padding: tokens.spacing.lg,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                    },
                  ]}
                >
                  Active workspace
                </Text>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: tokens.typography.fontSize["2xl"],
                      lineHeight: tokens.typography.lineHeight["2xl"],
                    },
                  ]}
                >
                  {activeWorkforceSession?.displayName ??
                    summary?.displayName ??
                    "Patient"}
                </Text>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  {[
                    "Patient portal",
                    activeWorkforceSession?.siteId
                      ? humanizeWorkspaceKey(activeWorkforceSession.siteId)
                      : "Tenant-wide",
                  ].join(" | ")}
                </Text>
                <View
                  style={[
                    styles.metadataGrid,
                    {
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.metadataTile,
                      {
                        backgroundColor: theme.surfaceMuted,
                        borderColor: theme.borderSubtle,
                        borderRadius: layout.controlRadius,
                        gap: tokens.spacing.xs,
                        padding: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                        },
                      ]}
                    >
                      Session
                    </Text>
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {activeWorkforceSession?.displayName ?? "Patient"}
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.metadataTile,
                      {
                        backgroundColor: theme.surfaceMuted,
                        borderColor: theme.borderSubtle,
                        borderRadius: layout.controlRadius,
                        gap: tokens.spacing.xs,
                        padding: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                        },
                      ]}
                    >
                      Next appointment
                    </Text>
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {summary?.nextAppointmentText ?? "Not scheduled yet"}
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.metadataTile,
                      {
                        backgroundColor: theme.surfaceMuted,
                        borderColor: theme.borderSubtle,
                        borderRadius: layout.controlRadius,
                        gap: tokens.spacing.xs,
                        padding: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                        },
                      ]}
                    >
                      Pending docs
                    </Text>
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color:
                            (summary?.pendingDocumentCount ?? 0) > 0
                              ? theme.warning
                              : theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {String(summary?.pendingDocumentCount ?? 0)}
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.metadataTile,
                      {
                        backgroundColor: theme.surfaceMuted,
                        borderColor: theme.borderSubtle,
                        borderRadius: layout.controlRadius,
                        gap: tokens.spacing.xs,
                        padding: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                        },
                      ]}
                    >
                      Status
                    </Text>
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: summary?.statusLabel?.trim().length
                            ? theme.info
                            : theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {summary?.statusLabel ?? "Status unavailable"}
                    </Text>
                  </View>
                </View>

                <View
                  style={[
                    styles.flagRow,
                    {
                      gap: tokens.spacing.xs,
                    },
                  ]}
                >
                  {[
                    {
                      key: "program",
                      labelText: summary?.programLabel?.trim().length
                        ? summary.programLabel
                        : "Program not assigned",
                    },
                    {
                      key: "coordinator",
                      labelText: summary?.coordinatorLabel?.trim().length
                        ? summary.coordinatorLabel
                        : "Coordinator not assigned",
                    },
                    {
                      key: "onboarding",
                      labelText: summary?.onboarding.initialFlowComplete
                        ? "Onboarding complete"
                        : "Onboarding open",
                    },
                    {
                      key: "auth",
                      labelText:
                        summary?.onboarding.auth.status === "verified"
                          ? "Portal auth verified"
                          : summary?.onboarding.auth.status ===
                              "challenge-issued"
                            ? "Portal OTP issued"
                            : summary?.onboarding.auth.status === "locked"
                              ? "Portal OTP locked"
                              : "Portal auth pending",
                    },
                    {
                      key: "wake",
                      labelText: summary?.onboarding.preferences.wakeUpTime
                        ? `Wake ${summary.onboarding.preferences.wakeUpTime}`
                        : "Wake time not set",
                    },
                    {
                      key: "unread",
                      labelText:
                        (summary?.unreadMessageCount ?? 0) > 0
                          ? `${summary?.unreadMessageCount ?? 0} unread updates`
                          : "No unread updates",
                    },
                  ].map(({ key, labelText }) => (
                    <View
                      key={key}
                      style={[
                        styles.inlinePill,
                        {
                          backgroundColor: theme.surface,
                          borderColor: theme.borderSubtle,
                          borderRadius: tokens.radius.pill,
                          paddingHorizontal: tokens.spacing.sm,
                          paddingVertical: tokens.spacing.xs,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.pillValueText,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                          },
                        ]}
                      >
                        {labelText}
                      </Text>
                    </View>
                  ))}
                </View>

                <View
                  style={[
                    styles.patientRowFooter,
                    {
                      gap: tokens.spacing.xs,
                    },
                  ]}
                >
                  {renderWorkbenchActionButton({
                    disabled: false,
                    label: "Open agent",
                    onPress: () => {
                      setPatientWorkspaceMessageDraft("");
                      navigatePatientWorkspaceTabIfEnabled("messages");
                    },
                    size: "compact",
                    theme,
                    tone: "accent",
                    tokens,
                  })}
                  {renderWorkbenchActionButton({
                    disabled: false,
                    label: "Open documents",
                    onPress: () =>
                      navigatePatientWorkspaceTabIfEnabled("documents"),
                    size: "compact",
                    theme,
                    tone: "success",
                    tokens,
                  })}
                </View>

                {canSwitchWorkspaces
                  ? renderWorkbenchActionButton({
                      disabled: false,
                      label: "Switch workspace",
                      onPress: beginWorkspaceSelection,
                      theme,
                      tone: "accent",
                      tokens,
                    })
                  : null}
              </View>
            </View>
          </View>

          {patientWorkspaceQuickActionBand}

          {renderPatientWorkspaceNavigationBar({
            isMediumLayout,
            items: patientWorkspaceNavItems,
            layout,
            selectedPatientWorkspaceTabId,
            setSelectedPatientWorkspaceTabId:
              navigatePatientWorkspaceTabIfEnabled,
            theme,
            tokens,
          })}

          {patientWorkspaceTabContent}
        </View>
      </ScrollView>
      {renderPatientLegacyOnboardingOverlay({
        flow: patientOnboardingFlow,
        isCommandSubmitting: isPatientOnboardingCommandSubmitting,
        isMediumLayout,
        layout,
        layoutRects: patientOnboardingLayoutRects,
        onBack: goToPreviousPatientOnboardingStep,
        onNext: goToNextPatientOnboardingStep,
        onSkip: skipPatientOnboardingFlow,
        theme,
        tokens,
      })}
      {patientSupportModalElement}
    </View>
  );
}

function ClinicianDiscardChangesPrompt(input: {
  bodyText: string;
  confirmAccessibilityLabel: string;
  confirmLabel: string;
  controlHeight: number;
  onCancel: () => void;
  onConfirm: () => void;
  stayAccessibilityLabel: string;
  theme: AppShellTheme;
  titleText: string;
  viewportWidth: number;
}): ReactNode {
  const {
    bodyText,
    confirmAccessibilityLabel,
    confirmLabel,
    controlHeight,
    onCancel,
    onConfirm,
    stayAccessibilityLabel,
    theme,
    titleText,
    viewportWidth,
  } = input;
  const promptRef = useRef<View | null>(null);
  const stayButtonRef = useRef<View | null>(null);

  useEffect(() => {
    const focusTimeout = setTimeout(() => {
      const stayButton =
        stayButtonRef.current as unknown as { focus?: () => void } | null;

      stayButton?.focus?.();
    }, 0);

    if (Platform.OS !== "web" || typeof document === "undefined") {
      return () => clearTimeout(focusTimeout);
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const promptNode = promptRef.current as unknown as HTMLElement | null;
      const focusableElements = promptNode
        ? Array.from(
            promptNode.querySelectorAll<HTMLElement>(
              '[role="button"]:not([aria-disabled="true"]), button:not([disabled]), [tabindex="0"]',
            ),
          )
        : [];

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      clearTimeout(focusTimeout);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  return (
    <View
      style={{
        alignItems: "center",
        backgroundColor: theme.backdropOverlay,
        bottom: 0,
        justifyContent: "center",
        left: 0,
        padding: 16,
        position: "absolute",
        right: 0,
        top: 0,
        zIndex: 100,
      }}
    >
      <View
        accessibilityLabel={titleText}
        ref={promptRef}
        style={{
          backgroundColor: theme.surface,
          borderColor: theme.borderSubtle,
          borderRadius: workforceOperationalLayout.panelRadius,
          borderWidth: 1,
          elevation: 12,
          gap: workforceConfigurationDialog.fieldGap,
          maxWidth: 440,
          padding: workforceConfigurationDialog.paddingWide,
          shadowColor: designTokens.palette.neutral[900],
          shadowOffset: { width: 0, height: 14 },
          shadowOpacity: 0.22,
          shadowRadius: 24,
          width: Math.min(440, Math.max(280, viewportWidth - 32)),
        }}
      >
        <View
          style={{
            alignItems: "center",
            flexDirection: "row",
            gap: workforceConfigurationDialog.actionGap,
          }}
        >
          <LineIcon
            color={theme.warning}
            name="info"
            size={20}
            strokeWidth={2.3}
          />
          <Text
            style={{
              color: theme.textPrimary,
              flex: 1,
              fontFamily: designTokens.semanticLayout.displayFontFamily,
              fontSize: workforceOperationalTypography.sectionTitle.fontSize,
              fontWeight: "700",
              lineHeight:
                workforceOperationalTypography.sectionTitle.lineHeight,
            }}
          >
            {titleText}
          </Text>
        </View>
        <Text
          style={{
            color: theme.textSecondary,
            fontFamily: designTokens.semanticLayout.bodyFontFamily,
            fontSize: workforceOperationalTypography.body.fontSize,
            lineHeight: workforceOperationalTypography.body.lineHeight,
          }}
        >
          {bodyText}
        </Text>
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: workforceConfigurationDialog.actionGap,
            justifyContent: "flex-end",
          }}
        >
          <Pressable
            accessibilityLabel={stayAccessibilityLabel}
            accessibilityRole="button"
            onPress={onCancel}
            ref={stayButtonRef}
            style={({ pressed }) => ({
              alignItems: "center",
              backgroundColor: pressed ? theme.backgroundSubtle : theme.surface,
              borderColor: theme.borderStrong,
              borderRadius: workforceOperationalLayout.controlRadius,
              borderWidth: 1,
              flexGrow: viewportWidth < 700 ? 1 : 0,
              justifyContent: "center",
              minHeight: controlHeight,
              minWidth: 110,
              paddingHorizontal: 14,
            })}
          >
            <Text
              style={{
                color: theme.textSecondary,
                fontFamily: designTokens.semanticLayout.bodyFontFamily,
                fontSize: workforceOperationalTypography.body.fontSize,
                fontWeight: "700",
              }}
            >
              Stay
            </Text>
          </Pressable>
          <Pressable
            accessibilityLabel={confirmAccessibilityLabel}
            accessibilityRole="button"
            onPress={onConfirm}
            style={({ pressed }) => ({
              alignItems: "center",
              backgroundColor: pressed
                ? theme.statusChip.danger.background
                : theme.danger,
              borderColor: theme.danger,
              borderRadius: workforceOperationalLayout.controlRadius,
              borderWidth: 1,
              flexGrow: viewportWidth < 700 ? 1 : 0,
              justifyContent: "center",
              minHeight: controlHeight,
              minWidth: 154,
              paddingHorizontal: 14,
            })}
          >
            <Text
              style={{
                color: theme.accentContrast,
                fontFamily: designTokens.semanticLayout.bodyFontFamily,
                fontSize: workforceOperationalTypography.body.fontSize,
                fontWeight: "700",
              }}
            >
              {confirmLabel}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

function renderClinicianClinicalEditorModal(input: {
  discardConfirmationVisible: boolean;
  isSubmitting: boolean;
  layout: typeof designTokens.semanticLayout;
  modalState: ClinicianClinicalEditorModalState;
  onCancelDiscard: () => void;
  onClose: () => void;
  onConfirmDiscard: () => void;
  onSubmit: () => void;
  /** Positive patient identification line (MRN · DOB · phone). */
  patientIdentityText?: string | null;
  onUpdateField: (
    fieldName:
      | "bodyText"
      | "defaultBodyText"
      | "defaultDetailText"
      | "defaultTitle"
      | "detailText"
      | "title"
      | "visibility",
    value: string,
  ) => void;
  onUpdateStructuredField: (
    fieldName: keyof ClinicianStructuredRecordFields,
    value: string,
  ) => void;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  viewportHeight: number;
  viewportWidth: number;
}) {
  const {
    discardConfirmationVisible,
    isSubmitting,
    layout,
    modalState,
    onCancelDiscard,
    onClose,
    onConfirmDiscard,
    onSubmit,
    onUpdateField,
    onUpdateStructuredField,
    theme,
    tokens,
    viewportHeight,
    viewportWidth,
  } = input;

  if (modalState == null) {
    return null;
  }

  const isRecord = modalState.kind === "record";
  const isShortEditorViewport = viewportHeight < 480;
  const modalMaxHeight = Math.min(760, Math.max(0, viewportHeight - 32));
  const modalWidth = Math.min(
    workforceConfigurationDialog.maxFormWidth,
    Math.max(280, viewportWidth - 32),
  );
  const editorControlHeight =
    viewportWidth < 700
      ? workforceOperationalLayout.mobileControlHeight
      : workforceConfigurationDialog.controlHeight;
  const titleText = isRecord
    ? modalState.recordId
      ? "Edit clinical record"
      : "Add clinical record"
    : modalState.noteId
      ? "Edit clinical note"
      : "Add clinical note";
  const subtitleText = isRecord
    ? `${humanizeWorkspaceKey(modalState.targetKind)} record for ${modalState.patientName}`
    : `${humanizeWorkspaceKey(modalState.targetKind)} note for ${modalState.patientName}`;
  const primaryFieldValue = modalState.title;
  const bodyFieldValue = isRecord ? modalState.detailText : modalState.bodyText;
  const structuredRecordErrors = isRecord
    ? validateClinicianStructuredRecordFields(modalState.structuredFields, {
        previousFields: parseClinicianStructuredRecordFields(
          modalState.defaultDetailText,
        ),
      })
    : {};
  const canSubmitEditor =
    !isSubmitting &&
    primaryFieldValue.trim().length > 0 &&
    bodyFieldValue.trim().length > 0 &&
    Object.keys(structuredRecordErrors).length === 0;
  const titlePlaceholderText = isRecord
    ? `Enter ${humanizeWorkspaceKey(modalState.targetKind)} title`
    : "Enter note title";
  const titleFieldLabelText =
    modalState.kind !== "record"
      ? "Title"
      : modalState.targetKind === "medication"
        ? "Medication name"
        : modalState.targetKind === "procedure"
          ? "Procedure"
          : modalState.targetKind === "appointment"
            ? "Appointment title or purpose"
            : "Document title";
  const bodyPlaceholderText = isRecord
    ? modalState.targetKind === "appointment"
      ? "Add date, purpose, status, location, and coordinator notes."
      : modalState.targetKind === "procedure"
        ? "Add date, procedure, status, location, and follow-up notes."
        : modalState.targetKind === "medication"
          ? "Add medication, dose, route, status, and prescribing notes."
          : "Add status, summary, owner, and next action."
    : "Write the clinical note body.";
  const structuredRecordFieldConfigs: ReadonlyArray<{
    fieldName: keyof ClinicianStructuredRecordFields;
    labelText: string;
    placeholderText: string;
  }> =
    !isRecord
      ? []
      : modalState.targetKind === "medication"
        ? [
            {
              fieldName: "schedule",
              labelText: "Dose and schedule",
              placeholderText: "For example, 10 mg every morning",
            },
            {
              fieldName: "route",
              labelText: "Route",
              placeholderText: "Oral, topical, injection",
            },
            {
              fieldName: "status",
              labelText: "Status",
              placeholderText: "Active, held, stopped",
            },
            {
              fieldName: "clinician",
              labelText: "Prescriber",
              placeholderText: "Prescribing clinician",
            },
            {
              fieldName: "date",
              labelText: "Start or review date",
              placeholderText: "YYYY-MM-DD",
            },
          ]
        : [
            {
              fieldName: "date",
              labelText: "Date",
              placeholderText: "YYYY-MM-DD",
            },
            {
              fieldName: "time",
              labelText: "Time",
              placeholderText: "HH:MM",
            },
            {
              fieldName: "status",
              labelText: "Status",
              placeholderText: "Scheduled, completed, cancelled",
            },
            {
              fieldName: "clinician",
              labelText:
                modalState.targetKind === "procedure"
                  ? "Surgeon or clinician"
                  : "Clinician or owner",
              placeholderText: "Responsible clinician",
            },
            {
              fieldName:
                modalState.targetKind === "document" ? "source" : "location",
              labelText:
                modalState.targetKind === "document"
                  ? "Source"
                  : "Location",
              placeholderText:
                modalState.targetKind === "document"
                  ? "Upload, facility, or external source"
                  : "Facility, room, or remote",
            },
          ];

  const renderTextField = (field: {
    fieldName: "bodyText" | "detailText" | "title";
    labelText: string;
    multiline?: boolean;
    placeholderText: string;
    value: string;
  }) => (
    <View style={[styles.listGroup, { gap: 5 }]}>
      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: workforceOperationalTypography.metadata.fontSize,
            lineHeight: workforceOperationalTypography.metadata.lineHeight,
            textTransform: "uppercase",
          },
        ]}
      >
        {field.labelText}
      </Text>
      <TextInput
        accessibilityLabel={field.labelText}
        editable={!isSubmitting}
        multiline={field.multiline}
        numberOfLines={field.multiline ? 8 : 1}
        onChangeText={(nextValue) =>
          onUpdateField(field.fieldName, nextValue)
        }
        placeholder={field.placeholderText}
        placeholderTextColor={theme.textTertiary}
        style={[
          styles.searchInput,
          {
            backgroundColor: theme.surface,
            borderColor: theme.borderSubtle,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: workforceOperationalTypography.body.fontSize,
            lineHeight: field.multiline
              ? workforceOperationalTypography.body.lineHeight
              : undefined,
            minHeight: field.multiline
              ? 170
              : editorControlHeight,
            paddingHorizontal: 12,
            paddingVertical: field.multiline ? 12 : 9,
            textAlignVertical: field.multiline ? "top" : "center",
          },
        ]}
        value={field.value}
      />
    </View>
  );

  const renderStructuredRecordField = (field: {
    fieldName: keyof ClinicianStructuredRecordFields;
    labelText: string;
    multiline?: boolean;
    placeholderText: string;
  }) => {
    if (!isRecord) {
      return null;
    }

    const errorText =
      field.fieldName === "date" || field.fieldName === "time"
        ? structuredRecordErrors[field.fieldName]
        : undefined;

    return (
      <View
        key={field.fieldName}
        style={{
          flexBasis:
            field.multiline || viewportWidth < 700 ? "100%" : "48%",
          flexGrow: 1,
          gap: workforceConfigurationDialog.fieldLabelGap,
          minWidth: viewportWidth < 700 ? "100%" : 240,
        }}
      >
        <Text
          style={{
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: workforceOperationalTypography.metadata.fontSize,
            fontWeight: "700",
            lineHeight: workforceOperationalTypography.metadata.lineHeight,
            textTransform: "uppercase",
          }}
        >
          {field.labelText}
        </Text>
        <TextInput
          accessibilityLabel={field.labelText}
          accessibilityHint={errorText}
          aria-invalid={errorText != null}
          editable={!isSubmitting}
          multiline={field.multiline}
          onChangeText={(value) =>
            onUpdateStructuredField(field.fieldName, value)
          }
          placeholder={field.placeholderText}
          placeholderTextColor={theme.textTertiary}
          style={{
            backgroundColor: theme.surface,
            borderColor: errorText ? theme.danger : theme.borderSubtle,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: workforceOperationalTypography.body.fontSize,
            lineHeight: workforceOperationalTypography.body.lineHeight,
            minHeight: field.multiline
              ? 130
              : editorControlHeight,
            paddingHorizontal: 12,
            paddingVertical: field.multiline ? 10 : 8,
            textAlignVertical: field.multiline ? "top" : "center",
          }}
          value={modalState.structuredFields[field.fieldName]}
        />
        {errorText ? (
          <Text
            accessibilityLiveRegion="polite"
            style={{
              color: theme.danger,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
            }}
          >
            {errorText}
          </Text>
        ) : null}
      </View>
    );
  };

  const renderVisibilityChip = (
    value: "patient-visible" | "clinical",
    labelText: string,
  ) => {
    const isSelected =
      modalState.kind === "note" && modalState.visibility === value;

    return (
      <Pressable
        accessibilityLabel={labelText}
        accessibilityRole="radio"
        accessibilityState={{ checked: isSelected }}
        aria-checked={isSelected}
        onPress={() => onUpdateField("visibility", value)}
        style={[
          styles.inlinePill,
          {
            backgroundColor: isSelected
              ? theme.accentStrong
              : theme.backgroundSubtle,
            borderColor: isSelected ? theme.accentStrong : theme.borderSubtle,
            borderRadius: workforceOperationalLayout.controlRadius,
            minHeight: editorControlHeight,
            paddingHorizontal: 12,
            paddingVertical: 7,
          },
        ]}
      >
        <Text
          style={[
            styles.badgeText,
            {
              // Selected chip: contrast text on the accent fill — accent-on-
              // accent rendered the chosen visibility unreadable.
              color: isSelected ? theme.accentContrast : theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.table.fontSize,
              lineHeight: workforceOperationalTypography.table.lineHeight,
            },
          ]}
        >
          {labelText}
        </Text>
      </Pressable>
    );
  };
  const renderEditorFooterButton = (button: {
    disabled: boolean;
    label: string;
    onPress: () => void;
    tone: "accent" | "info";
  }) => {
    const toneColor =
      button.tone === "accent" ? theme.accentStrong : theme.info;

    return (
      <Pressable
        accessibilityLabel={button.label}
        accessibilityRole="button"
        accessibilityState={{ disabled: button.disabled }}
        aria-disabled={button.disabled}
        disabled={button.disabled}
        onPress={button.onPress}
        style={({ pressed }) => [
          styles.inlinePill,
          {
            alignItems: "center",
            backgroundColor: button.disabled
              ? theme.surfaceMuted
              : pressed
                ? theme.backgroundSubtle
                : theme.surface,
            borderColor: button.disabled ? theme.borderSubtle : toneColor,
            borderRadius: workforceOperationalLayout.controlRadius,
            flexDirection: "row",
            gap: 7,
            justifyContent: "center",
            minHeight: editorControlHeight,
            minWidth: button.tone === "accent" ? 94 : 86,
            opacity: button.disabled ? 0.55 : 1,
            paddingHorizontal: 12,
            paddingVertical: 6,
          },
        ]}
      >
        <View
          style={[
            styles.patientDesktopAgentIconButton,
            {
              backgroundColor: theme.backgroundSubtle,
              borderColor: button.disabled ? theme.borderSubtle : toneColor,
              borderRadius: 9,
              height: 18,
              width: 18,
            },
          ]}
        >
          <LineIcon
            color={button.disabled ? theme.textSecondary : toneColor}
            name={button.tone === "accent" ? "check" : "x"}
            size={12}
            strokeWidth={2.4}
          />
        </View>
        <Text
          numberOfLines={1}
          style={[
            styles.badgeText,
            {
              color: button.disabled ? theme.textSecondary : toneColor,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              lineHeight: 16,
            },
          ]}
        >
          {button.label}
        </Text>
      </Pressable>
    );
  };

  return (
    <Modal
      accessibilityLabel={
        discardConfirmationVisible
          ? "Discard clinical editor changes"
          : titleText
      }
      accessibilityViewIsModal={true}
      animationType="fade"
      onRequestClose={
        isSubmitting
          ? undefined
          : discardConfirmationVisible
            ? onCancelDiscard
            : onClose
      }
      transparent={true}
      visible={modalState != null}
    >
      <View style={styles.patientSupportModalOverlay}>
        <View
          pointerEvents="none"
          style={styles.patientSupportModalBackdrop}
        />
        <View
          pointerEvents="box-none"
          style={[
            styles.patientSupportModalContent,
            {
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              padding: workforceConfigurationDialog.paddingCompact,
            },
          ]}
        >
          <View
            accessibilityElementsHidden={discardConfirmationVisible}
            aria-hidden={discardConfirmationVisible}
            importantForAccessibility={
              discardConfirmationVisible ? "no-hide-descendants" : "auto"
            }
            pointerEvents={discardConfirmationVisible ? "none" : "auto"}
            style={[
              styles.clinicianDetailModalPanel,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: workforceOperationalLayout.panelRadius,
                borderWidth: 1,
                maxHeight: modalMaxHeight,
                maxWidth: modalWidth,
                overflow: "hidden",
                shadowColor: designTokens.palette.neutral[900],
                shadowOffset: { width: 0, height: 18 },
                shadowOpacity: 0.2,
                shadowRadius: 28,
                width: "100%",
              },
            ]}
          >
            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "flex-start",
                  backgroundColor: theme.surface,
                  borderBottomColor: theme.borderSubtle,
                  borderBottomWidth: 1,
                  gap: isShortEditorViewport
                    ? 6
                    : workforceConfigurationDialog.actionGap,
                  padding: isShortEditorViewport
                    ? 8
                    : workforceConfigurationDialog.paddingWide,
                },
              ]}
            >
              <View style={{ flex: 1, gap: 3, minWidth: 0 }}>
                {!isShortEditorViewport ? (
                  <Text
                    style={{
                      color: theme.accentStrong,
                      fontFamily: layout.bodyFontFamily,
                      fontSize:
                        workforceOperationalTypography.metadata.fontSize,
                      fontWeight: "700",
                      lineHeight:
                        workforceOperationalTypography.metadata.lineHeight,
                      textTransform: "uppercase",
                    }}
                  >
                    {isRecord
                      ? humanizeWorkspaceKey(modalState.targetKind)
                      : "Clinical note"}
                  </Text>
                ) : null}
                <Text
                  style={{
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize:
                      workforceOperationalTypography.sectionTitle.fontSize,
                    fontWeight: "700",
                    lineHeight:
                      workforceOperationalTypography.sectionTitle.lineHeight,
                  }}
                >
                  {titleText}
                </Text>
                {!isShortEditorViewport ? (
                  <Text
                    style={{
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.table.fontSize,
                      lineHeight:
                        workforceOperationalTypography.table.lineHeight,
                    }}
                  >
                    {subtitleText}
                  </Text>
                ) : null}
              </View>
              <Pressable
                accessibilityLabel="Close clinical editor"
                accessibilityRole="button"
                accessibilityState={{ disabled: isSubmitting }}
                aria-disabled={isSubmitting}
                disabled={isSubmitting}
                onPress={onClose}
                style={{
                  alignItems: "center",
                  borderColor: theme.borderSubtle,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  borderWidth: 1,
                  height: editorControlHeight,
                  justifyContent: "center",
                  opacity: isSubmitting ? 0.55 : 1,
                  width: editorControlHeight,
                }}
              >
                <LineIcon color={theme.textSecondary} name="x" size={18} />
              </Pressable>
            </View>

            <View
              style={{
                alignItems: "center",
                backgroundColor: theme.accentSubtle,
                borderBottomColor: theme.borderSubtle,
                borderBottomWidth: 1,
                flexDirection: "row",
                gap: isShortEditorViewport ? 4 : 10,
                paddingHorizontal: isShortEditorViewport
                  ? 10
                  : workforceConfigurationDialog.paddingWide,
                paddingVertical: isShortEditorViewport ? 4 : 10,
              }}
            >
              {!isShortEditorViewport ? (
                <View
                  style={{
                    alignItems: "center",
                    backgroundColor: theme.surface,
                    borderColor: theme.borderSubtle,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    borderWidth: 1,
                    height: editorControlHeight,
                    justifyContent: "center",
                    width: editorControlHeight,
                  }}
                >
                  <LineIcon
                    color={theme.accentStrong}
                    name="user"
                    size={17}
                  />
                </View>
              ) : null}
              <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
                <Text
                  numberOfLines={1}
                  style={{
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.body.fontSize,
                    fontWeight: "700",
                    lineHeight: workforceOperationalTypography.body.lineHeight,
                  }}
                >
                  {modalState.patientName}
                </Text>
                <Text
                  numberOfLines={isShortEditorViewport ? 1 : 2}
                  style={{
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.metadata.fontSize,
                    lineHeight:
                      workforceOperationalTypography.metadata.lineHeight,
                  }}
                >
                  {input.patientIdentityText ?? "Patient identifiers unavailable"}
                </Text>
              </View>
            </View>

            <ScrollView
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              style={{ flex: 1 }}
              contentContainerStyle={{
                alignSelf: "center",
                gap: workforceConfigurationDialog.fieldGap,
                maxWidth: workforceConfigurationDialog.maxFormWidth,
                padding: isShortEditorViewport
                  ? 12
                  : workforceConfigurationDialog.paddingWide,
                width: "100%",
              }}
            >
              {renderTextField({
                fieldName: "title",
                labelText: titleFieldLabelText,
                placeholderText: titlePlaceholderText,
                value: primaryFieldValue,
              })}
              {isRecord ? (
                <>
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: workforceConfigurationDialog.fieldGap,
                      width: "100%",
                    }}
                  >
                    {structuredRecordFieldConfigs.map(
                      renderStructuredRecordField,
                    )}
                  </View>
                  {renderStructuredRecordField({
                    fieldName: "notes",
                    labelText: "Clinical notes",
                    multiline: true,
                    placeholderText: bodyPlaceholderText,
                  })}
                </>
              ) : (
                renderTextField({
                  fieldName: "bodyText",
                  labelText: "Note body",
                  multiline: true,
                  placeholderText: bodyPlaceholderText,
                  value: bodyFieldValue,
                })
              )}
              {!isRecord ? (
                <View
                  style={{ gap: workforceConfigurationDialog.fieldLabelGap }}
                >
                  <Text
                    style={{
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize:
                        workforceOperationalTypography.metadata.fontSize,
                      fontWeight: "700",
                      lineHeight:
                        workforceOperationalTypography.metadata.lineHeight,
                      textTransform: "uppercase",
                    }}
                  >
                    Visibility
                  </Text>
                  <View
                    accessibilityLabel="Clinical note visibility"
                    role="radiogroup"
                    style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                  >
                    {renderVisibilityChip(
                      "patient-visible",
                      "Patient-visible",
                    )}
                    {renderVisibilityChip("clinical", "Clinical only")}
                  </View>
                </View>
              ) : null}
            </ScrollView>

            <View
              style={{
                backgroundColor: theme.surface,
                borderTopColor: theme.borderSubtle,
                borderTopWidth: 1,
                flexDirection: "row",
                flexWrap: "wrap",
                gap: workforceConfigurationDialog.actionGap,
                justifyContent: "flex-end",
                padding: isShortEditorViewport
                  ? 8
                  : workforceConfigurationDialog.paddingWide,
              }}
            >
              {renderEditorFooterButton({
                disabled: isSubmitting,
                label: "Cancel",
                onPress: onClose,
                tone: "info",
              })}
              {renderEditorFooterButton({
                disabled: !canSubmitEditor,
                label: isSubmitting ? "Saving" : "Save",
                onPress: onSubmit,
                tone: "accent",
              })}
            </View>
          </View>
          {discardConfirmationVisible ? (
            <ClinicianDiscardChangesPrompt
              bodyText="Your edits have not been saved. Leaving now discards them."
              confirmAccessibilityLabel="Discard unsaved clinical changes"
              confirmLabel="Discard changes"
              controlHeight={editorControlHeight}
              onCancel={onCancelDiscard}
              onConfirm={onConfirmDiscard}
              stayAccessibilityLabel="Stay in clinical editor"
              theme={theme}
              titleText="Discard unsaved clinical changes?"
              viewportWidth={viewportWidth}
            />
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

type OutboundQuestionnaireAnswerBadgeTone =
  | "danger"
  | "info"
  | "neutral"
  | "warning";

function formatOutboundQuestionnaireAnswerMetadataLabel(value: string): string {
  return value
    .replace(/_/gu, " ")
    .replace(/\b\w/gu, (match) => match.toUpperCase());
}

function buildOutboundQuestionnaireAnswerBadges(
  entry: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): Array<{
  labelText: string;
  tone: OutboundQuestionnaireAnswerBadgeTone;
}> {
  const badges: Array<{
    labelText: string;
    tone: OutboundQuestionnaireAnswerBadgeTone;
  }> = [];

  if (entry.reviewSignal === "red_flag" || entry.redFlagTriggered) {
    badges.push({
      labelText: entry.reviewSignalLabel ?? "Red flag",
      tone: "danger",
    });
  } else if (
    entry.reviewSignal === "yellow_flag" ||
    entry.clinicianReviewRequired
  ) {
    badges.push({
      labelText: entry.reviewSignalLabel ?? "Yellow flag",
      tone: "warning",
    });
  }

  if (entry.reviewReasonKeys?.includes("not_sure")) {
    const notSureReasonIndex = entry.reviewReasonKeys.indexOf("not_sure");

    badges.push({
      labelText:
        entry.reviewReasonLabels?.[notSureReasonIndex] ??
        entry.reviewReasonLabels?.find((label) =>
          label.toLowerCase().includes("not sure"),
        ) ??
        "Not sure",
      tone: "warning",
    });
  }

  if (entry.reviewPriority && entry.reviewPriority !== "routine") {
    badges.push({
      labelText:
        entry.reviewPriority === "urgent" ? "Urgent review" : "Review soon",
      tone: entry.reviewPriority === "urgent" ? "danger" : "warning",
    });
  }

  if (entry.reviewStatus === "reviewed") {
    badges.push({ labelText: "Reviewed", tone: "neutral" });
  } else if (entry.reviewStatus === "escalated") {
    badges.push({ labelText: "Escalated", tone: "danger" });
  } else if (entry.reviewStatus === "in_review") {
    badges.push({ labelText: "In review", tone: "info" });
  }

	  if (
	    entry.answerStatus !== "redacted" &&
	    (entry.changedSinceFirstAnswer === true ||
	      entry.answerStatus === "changed")
	  ) {
	    badges.push({ labelText: "Changed answer", tone: "warning" });
	  }

  if (entry.answerStatus === "redacted") {
    badges.push({ labelText: "Changed path removed", tone: "neutral" });
  }

  if (entry.definitionRequested === true || (entry.definitionRequestCount ?? 0) > 0) {
    badges.push({
      labelText:
        (entry.definitionRequestCount ?? 0) > 1
          ? `Definition requested ${String(entry.definitionRequestCount)}x`
          : "Definition requested",
      tone: "info",
    });
  }

  const medicationSummary = formatOutboundMedicationReconciliationSummary(entry);

  if (medicationSummary) {
    badges.push({
      labelText: medicationSummary,
      tone: "info",
    });
  }

  if (entry.answerStatus === "skipped") {
    badges.push({ labelText: "Skipped", tone: "neutral" });
  }

  return badges;
}

function formatOutboundQuestionnaireAnswerEventText(
  event: NonNullable<
    ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1["answerEvents"]
  >[number],
): string {
  const when = event.occurredAt
    ? `${event.occurredAt.replace("T", " ").slice(0, 16)}: `
    : "";

  if (event.eventType === "answer_changed") {
    return `${when}Changed from ${formatClinicianQuestionnaireAnswerText(
      event.previousAnswerSummary,
    )} to ${formatClinicianQuestionnaireAnswerText(event.answerSummary)}`;
  }

  if (event.eventType === "answer_redacted") {
    return `${when}${event.summaryText ?? "Changed path removed this answer"}`;
  }

  if (event.eventType === "answer_skipped") {
    return `${when}Skipped optional question`;
  }

  if (event.eventType === "definition_requested") {
    return `${when}${event.summaryText ?? "Definition requested"}`;
  }

  if (event.eventType === "repeat_requested") {
    return `${when}Question repeated`;
  }

  if (event.eventType === "go_back_requested") {
    return `${when}Previous question requested`;
  }

  if (event.eventType === "change_requested") {
    return `${when}Answer change requested`;
  }

  if (event.eventType === "skip_requested") {
    return `${when}Skip requested`;
  }

  return `${when}${event.summaryText ?? "Answer event recorded"}`;
}

function resolveOutboundQuestionnaireReviewNotes(
  call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
): string {
  const reviewNotes = call.answerPreview.find(
    (entry) => entry.reviewNotes?.trim(),
  )?.reviewNotes;

  return reviewNotes?.trim() ? reviewNotes : "";
}

function formatOutboundMedicationField(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }

  return "Unknown";
}

function formatOutboundMedicationEntryCountLabel(count: number): string {
  return `${String(count)} patient-reported medication entr${
    count === 1 ? "y" : "ies"
  }`;
}

function resolveOutboundMedicationEntryCount(
  entry: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): number {
  return Math.max(
    0,
    entry.medicationEntryCount ?? entry.medicationEntries?.length ?? 0,
  );
}

function formatOutboundMedicationMatchingReason(value: string | null | undefined): string {
  if (value === "rxnorm_or_ehr_medication_source_not_configured") {
    return "RxNorm/EHR matching is not configured";
  }

  return formatOutboundMedicationField(value);
}

function formatOutboundMedicationMatchingSummary(
  entry: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): string {
  return `External matching: Not enabled (${formatOutboundMedicationMatchingReason(
    entry.medicationIdentityMatching?.reason,
  )})`;
}

function formatOutboundMedicationReconciliationSummary(
  entry: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): string | null {
  const entryCount = resolveOutboundMedicationEntryCount(entry);

  if (entryCount > 0) {
    return `${formatOutboundMedicationEntryCountLabel(
      entryCount,
    )} captured; matching not enabled`;
  }

  if (entry.medicationReconciliationStatus === "no_patient_reported_medications") {
    return "No patient-reported medications captured; matching not enabled";
  }

  if (entry.medicationReconciliationStatus === "captured_patient_reported") {
    return "Medication details need review; no structured entries captured";
  }

  return entry.medicationReconciliationStatus
    ? `${formatOutboundQuestionnaireAnswerMetadataLabel(
        entry.medicationReconciliationStatus,
      )}; matching not enabled`
    : null;
}

function formatOutboundMedicationEntryTitle(
  entry: NonNullable<
    ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1["medicationEntries"]
  >[number],
  index: number,
): string {
  return (
    entry.name?.trim() ||
    entry.patientReportedName?.trim() ||
    `Unknown medication ${String(index + 1)}`
  );
}

function buildOutboundMedicationEntryDetailFields(
  entry: NonNullable<
    ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1["medicationEntries"]
  >[number],
): Array<{ labelText: string; valueText: string }> {
  const dose = [
    formatOutboundMedicationField(entry.dose),
    entry.unit?.trim() ?? "",
  ]
    .filter((value) => value && value !== "Unknown")
    .join(" ");

  return [
    {
      labelText: "Dose",
      valueText: dose || "Unknown",
    },
    {
      labelText: "Route",
      valueText: formatOutboundMedicationField(entry.route),
    },
    {
      labelText: "Frequency",
      valueText: formatOutboundMedicationField(entry.frequency),
    },
    {
      labelText: "Reason",
      valueText: formatOutboundMedicationField(entry.reason),
    },
    {
      labelText: "Last taken",
      valueText: formatOutboundMedicationField(entry.lastTaken),
    },
  ];
}

/**
 * Inline catalog drop-down for resolving a voice-captured medication (B4):
 * search the shared reference catalog, pick the drug, optionally pick the
 * exact strength, save. Rendered inside the outbound answers modal under the
 * entry being resolved.
 */
function renderClinicianMedicationResolutionEditor(input: {
  call: ClinicianWorkbenchOutboundQuestionnaireCallV1;
  controlHeight: number;
  layout: typeof designTokens.semanticLayout;
  onMedicationResolutionClose?: () => void;
  onMedicationResolutionQueryChange?: (query: string) => void;
  onMedicationResolutionSelect?: (
    drugName: string,
    rxcui: string | null,
    strength: string | null,
  ) => void;
  onMedicationResolve?: (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  onMedicationSearch?: (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  resolution: NonNullable<ClinicianMedicationResolutionState>;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}): ReactNode {
  const {
    call,
    controlHeight,
    layout,
    onMedicationResolutionClose,
    onMedicationResolutionQueryChange,
    onMedicationResolutionSelect,
    onMedicationResolve,
    onMedicationSearch,
    resolution,
    theme,
    tokens,
  } = input;
  const selectedCandidate =
    resolution.candidates?.find(
      (candidate) => candidate.drugName === resolution.selectedDrugName,
    ) ?? null;

  return (
    <View
      style={{
        backgroundColor: theme.surfaceMuted,
        borderColor: theme.borderSubtle,
        borderRadius: tokens.radius.md,
        borderWidth: 1,
        gap: tokens.spacing.xs,
        marginTop: 4,
        padding: tokens.spacing.sm,
      }}
    >
      <Text
        style={{
          color: theme.textSecondary,
          fontFamily: layout.bodyFontFamily,
          fontSize: tokens.typography.fontSize.xs,
          fontWeight: "700",
        }}
      >
        Resolve against the medication catalog
      </Text>
      <View style={{ flexDirection: "row", gap: tokens.spacing.xs }}>
        <TextInput
          accessibilityLabel="Medication search"
          editable={!resolution.isBusy}
          onChangeText={(value) => onMedicationResolutionQueryChange?.(value)}
          onSubmitEditing={() => onMedicationSearch?.(call)}
          placeholder="Drug name (e.g. lisinopril)"
          placeholderTextColor={theme.textTertiary}
          style={{
            backgroundColor: theme.surface,
            borderColor: theme.borderSubtle,
            borderRadius: tokens.radius.sm,
            borderWidth: 1,
            color: theme.textPrimary,
            flex: 1,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.sm,
            minHeight: controlHeight,
            paddingHorizontal: tokens.spacing.sm,
            paddingVertical: 6,
          }}
          value={resolution.query}
        />
        <Pressable
          accessibilityRole="button"
          disabled={resolution.isBusy}
          onPress={() => onMedicationSearch?.(call)}
          style={({ pressed }) => [
            {
              alignItems: "center",
              borderRadius: 8,
              borderWidth: 1,
              justifyContent: "center",
              borderColor: theme.accent,
              minHeight: controlHeight,
              opacity: resolution.isBusy ? 0.5 : pressed ? 0.7 : 1,
              paddingHorizontal: tokens.spacing.sm,
              paddingVertical: 6,
            },
          ]}
        >
          <Text
            style={{
              color: theme.accent,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: "700",
            }}
          >
            {resolution.isBusy ? "Working…" : "Search"}
          </Text>
        </Pressable>
      </View>
      {resolution.errorText ? (
        <Text
          style={{
            color: theme.statusChip.danger.text,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
          }}
        >
          {resolution.errorText}
        </Text>
      ) : null}
      {resolution.candidates != null && resolution.candidates.length === 0 ? (
        <Text
          style={{
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
          }}
        >
          No catalog matches — adjust the spelling and search again.
        </Text>
      ) : null}
      {(resolution.candidates ?? []).map((candidate) => (
        <Pressable
          accessibilityRole="button"
          key={candidate.drugName}
          onPress={() =>
            onMedicationResolutionSelect?.(
              candidate.drugName,
              candidate.rxcui,
              null,
            )
          }
          style={({ pressed }) => ({
            backgroundColor:
              resolution.selectedDrugName === candidate.drugName
                ? theme.accentSubtle
                : theme.surface,
            borderColor:
              resolution.selectedDrugName === candidate.drugName
                ? theme.accent
                : theme.borderSubtle,
            borderRadius: tokens.radius.sm,
            borderWidth: 1,
            gap: 4,
            minHeight: controlHeight,
            opacity: pressed ? 0.8 : 1,
            padding: tokens.spacing.sm,
          })}
        >
          <Text
            style={{
              color: theme.textPrimary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              fontWeight: "700",
            }}
          >
            {candidate.drugName}
          </Text>
          {resolution.selectedDrugName === candidate.drugName &&
          candidate.strengths.length > 0 ? (
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              {candidate.strengths.map((strength) => (
                <Pressable
                  accessibilityRole="button"
                  key={`${candidate.drugName}-${strength.strength ?? "none"}`}
                  onPress={() =>
                    onMedicationResolutionSelect?.(
                      candidate.drugName,
                      strength.rxcui ?? candidate.rxcui,
                      strength.strength,
                    )
                  }
                  style={{
                    backgroundColor:
                      resolution.selectedStrength === strength.strength
                        ? theme.accentSubtle
                        : theme.surfaceMuted,
                    borderColor: theme.borderSubtle,
                    borderRadius: tokens.radius.pill,
                    borderWidth: 1,
                    justifyContent: "center",
                    minHeight: controlHeight,
                    paddingHorizontal: tokens.spacing.sm,
                    paddingVertical: 3,
                  }}
                >
                  <Text
                    style={{
                      color:
                        resolution.selectedStrength === strength.strength
                          ? theme.accentStrong
                          : theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                    }}
                  >
                    {[strength.strength, strength.dosageForm]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                </Pressable>
              ))}
            </View>
          ) : null}
        </Pressable>
      ))}
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: tokens.spacing.xs,
        }}
      >
        <Pressable
          accessibilityRole="button"
          disabled={resolution.isBusy || resolution.selectedDrugName == null}
          onPress={() => onMedicationResolve?.(call)}
          style={({ pressed }) => [
            {
              alignItems: "center",
              borderRadius: 8,
              justifyContent: "center",
              backgroundColor:
                resolution.selectedDrugName == null
                  ? theme.borderSubtle
                  : theme.accentSubtle,
              minHeight: controlHeight,
              opacity: resolution.isBusy ? 0.5 : pressed ? 0.8 : 1,
              paddingHorizontal: tokens.spacing.md,
              paddingVertical: 6,
            },
          ]}
        >
          <Text
            style={{
              color: theme.accentStrong,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: "700",
            }}
          >
            Save match
          </Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          onPress={() => onMedicationResolutionClose?.()}
          style={({ pressed }) => [
            {
              alignItems: "center",
              borderRadius: 8,
              borderWidth: 1,
              justifyContent: "center",
              borderColor: theme.borderSubtle,
              minHeight: controlHeight,
              opacity: pressed ? 0.7 : 1,
              paddingHorizontal: tokens.spacing.md,
              paddingVertical: 6,
            },
          ]}
        >
          <Text
            style={{
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: "700",
            }}
          >
            Cancel
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

type ClinicianOutboundAnswersModalInput = {
  actorCanManageReview: boolean;
  clinicianUserId: string;
  discardConfirmationVisible: boolean;
  isSubmitting?: boolean;
  layout: typeof designTokens.semanticLayout;
  modalState: {
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1;
    patientName: string;
    reviewNotes: string;
  } | null;
  onCancelDiscard: () => void;
  onClose: () => void;
  onConfirmDiscard: () => void;
  onReviewNotesChange?: (value: string) => void;
  onReviewAction?: (
    action: "assign-review" | "mark-review-reviewed" | "escalate-review",
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  medicationResolution?: ClinicianMedicationResolutionState;
  onMedicationResolutionOpen?: (
    questionKey: string,
    entryIndex: number,
    initialQuery: string,
  ) => void;
  onMedicationResolutionClose?: () => void;
  onMedicationResolutionQueryChange?: (query: string) => void;
  onMedicationResolutionSelect?: (
    drugName: string,
    rxcui: string | null,
    strength: string | null,
  ) => void;
  onMedicationSearch?: (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  onMedicationResolve?: (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  patientIdentityText?: string | null;
  reviewNotes?: string;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  viewportWidth: number;
};

type ActiveClinicianOutboundAnswersModalInput = Omit<
  ClinicianOutboundAnswersModalInput,
  "modalState"
> & {
  modalState: NonNullable<ClinicianOutboundAnswersModalInput["modalState"]>;
};

type QuestionnaireReviewTabKeyDownEvent = {
  currentTarget?: unknown;
  key?: string;
  nativeEvent?: { key?: string };
  preventDefault?: () => void;
};

function handleQuestionnaireReviewTabKeyDown(
  event: QuestionnaireReviewTabKeyDownEvent,
  currentIndex: number,
  filterIds: readonly QuestionnaireReviewFilterId[],
  onSelect: (filterId: QuestionnaireReviewFilterId) => void,
): void {
  const nextIndex = resolveHorizontalTabNavigationIndex(
    event.key ?? event.nativeEvent?.key ?? "",
    currentIndex,
    filterIds.length,
  );
  const nextFilterId = nextIndex == null ? undefined : filterIds[nextIndex];

  if (nextIndex == null || nextFilterId == null) {
    return;
  }

  event.preventDefault?.();
  onSelect(nextFilterId);

  const currentTarget = event.currentTarget as HTMLElement | null | undefined;
  const tabList = currentTarget?.closest?.('[role="tablist"]');

  setTimeout(() => {
    tabList
      ?.querySelectorAll<HTMLElement>('[role="tab"]')
      .item(nextIndex)
      ?.focus();
  }, 0);
}

function renderClinicianOutboundAnswersModal(
  input: ClinicianOutboundAnswersModalInput,
): ReactNode {
  if (!input.modalState) {
    return null;
  }

  return (
    <ClinicianOutboundAnswersModal
      {...input}
      modalState={input.modalState}
    />
  );
}

function ClinicianOutboundAnswersModal(
  input: ActiveClinicianOutboundAnswersModalInput,
): ReactNode {
  const {
    actorCanManageReview,
    clinicianUserId,
    discardConfirmationVisible,
    isSubmitting = false,
    layout,
    modalState,
    onCancelDiscard,
    onClose,
    onConfirmDiscard,
    onReviewNotesChange,
    onReviewAction,
    medicationResolution = null,
    onMedicationResolutionOpen,
    onMedicationResolutionClose,
    onMedicationResolutionQueryChange,
    onMedicationResolutionSelect,
    onMedicationSearch,
    onMedicationResolve,
    patientIdentityText,
    reviewNotes = "",
    theme,
    tokens,
    viewportWidth,
  } = input;

  const { call, patientName } = modalState;
  const modalControlHeight =
    viewportWidth < 820
      ? workforceOperationalLayout.mobileControlHeight
      : workforceOperationalLayout.controlHeight;
  const usesCompactReviewLayout = viewportWidth < 820;
  const yellowFlagCount = call.answerPreview.filter(
    (entry) =>
      entry.reviewSignal === "yellow_flag" ||
      (entry.clinicianReviewRequired && !entry.redFlagTriggered),
  ).length;
  const changedAnswerCount = call.answerPreview.filter(
    (entry) =>
      entry.answerStatus !== "redacted" &&
      (entry.changedSinceFirstAnswer === true ||
        entry.answerStatus === "changed"),
  ).length;
  const redactedAnswerCount = call.answerPreview.filter(
    (entry) => entry.answerStatus === "redacted",
  ).length;
  const changedReviewAnswerCount = call.answerPreview.filter(
    questionnaireAnswerChanged,
  ).length;
  const medicationEntryCount = call.answerPreview.reduce(
    (total, entry) => total + resolveOutboundMedicationEntryCount(entry),
    0,
  );
  const medicationStatusCount = call.answerPreview.filter(
    (entry) => formatOutboundMedicationReconciliationSummary(entry) != null,
  ).length;
  const definitionRequestCount = call.answerPreview.reduce(
    (total, entry) => total + Math.max(0, entry.definitionRequestCount ?? 0),
    0,
  );
  const medicationAnswerCount = call.answerPreview.filter(
    questionnaireAnswerHasMedicationDetails,
  ).length;
  const attentionAnswerCount = call.answerPreview.filter(
    questionnaireAnswerNeedsAttention,
  ).length;
  const reviewSections = buildQuestionnaireReviewSections(call.answerPreview);
  const [reviewFilterId, setReviewFilterId] =
    useState<QuestionnaireReviewFilterId>("all");
  const [selectedReviewSectionKey, setSelectedReviewSectionKey] =
    useState<string>(questionnaireReviewAllSectionsKey);
  const [expandedReviewAnswerKeys, setExpandedReviewAnswerKeys] = useState<
    string[]
  >([]);

  useEffect(() => {
    setReviewFilterId("all");
    setSelectedReviewSectionKey(questionnaireReviewAllSectionsKey);
    setExpandedReviewAnswerKeys(
      call.answerPreview
        .filter(
          (entry) =>
            questionnaireAnswerNeedsAttention(entry) &&
            ((entry.answerEvents?.length ?? 0) > 0 ||
              questionnaireAnswerHasMedicationDetails(entry)),
        )
        .map((entry) => entry.questionKey),
    );
  }, [call.sessionId, call.targetId]);

  const reviewFilterOptions: Array<{
    count: number;
    id: QuestionnaireReviewFilterId;
    labelText: string;
  }> = [
    {
      count: call.answerPreview.length,
      id: "all",
      labelText: "All answers",
    },
    {
      count: attentionAnswerCount,
      id: "attention",
      labelText: "Needs review",
    },
    {
      count: changedReviewAnswerCount,
      id: "changed",
      labelText: "Changed",
    },
    {
      count: medicationAnswerCount,
      id: "medications",
      labelText: "Medications",
    },
  ];
  const reviewFilterIds = reviewFilterOptions.map((option) => option.id);
  const visibleReviewSections = reviewSections
    .map((section) => ({
      ...section,
      entries: section.entries.filter((entry) =>
        questionnaireAnswerMatchesFilter(entry, reviewFilterId),
      ),
    }))
    .filter(
      (section) =>
        section.entries.length > 0 &&
        (selectedReviewSectionKey === questionnaireReviewAllSectionsKey ||
          section.key === selectedReviewSectionKey),
    );
  const visibleReviewEntries = visibleReviewSections.flatMap(
    (section) => section.entries,
  );
  const visibleReviewAnswerCount = visibleReviewEntries.length;
  const hasHiddenAttentionAnswers = questionnaireReviewHasHiddenAttention(
    call.answerPreview,
    visibleReviewEntries,
  );
  const toggleReviewAnswerDetails = (questionKey: string): void => {
    setExpandedReviewAnswerKeys((current) =>
      current.includes(questionKey)
        ? current.filter((key) => key !== questionKey)
        : [...current, questionKey],
    );
  };
  const isTerminalQuestionnaireSession =
    call.sessionStatus === "completed" || call.sessionStatus === "review_required";
  const answerProgressBadge =
    call.sessionStatus === "review_required" &&
    call.totalQuestionCount > call.answeredQuestionCount
      ? `${String(call.answeredQuestionCount)} captured; review required`
      : isTerminalQuestionnaireSession &&
          call.totalQuestionCount > call.answeredQuestionCount
        ? `${String(call.answeredQuestionCount)} captured; conditional questions skipped`
        : `${String(call.answeredQuestionCount)} of ${String(
            call.totalQuestionCount,
        )} answered`;
  const answerBadges = [
    call.campaignDisplayName,
    `Attempt ${String(call.attemptCount)}`,
    answerProgressBadge,
    call.redFlagCount > 0
      ? `${String(call.redFlagCount)} red flag${
          call.redFlagCount === 1 ? "" : "s"
        }`
      : null,
    yellowFlagCount > 0
      ? `${String(yellowFlagCount)} yellow flag${
          yellowFlagCount === 1 ? "" : "s"
        }`
      : null,
    changedAnswerCount > 0
      ? `${String(changedAnswerCount)} changed answer${
          changedAnswerCount === 1 ? "" : "s"
        }`
      : null,
    redactedAnswerCount > 0
      ? `${String(redactedAnswerCount)} changed path removed`
      : null,
    medicationEntryCount > 0
      ? formatOutboundMedicationEntryCountLabel(medicationEntryCount)
      : medicationStatusCount > 0
        ? `${String(medicationStatusCount)} medication status${
            medicationStatusCount === 1 ? "" : "es"
          }`
        : null,
    definitionRequestCount > 0
      ? `${String(definitionRequestCount)} definition request${
          definitionRequestCount === 1 ? "" : "s"
        }`
      : null,
  ].filter(
    (badge): badge is string => typeof badge === "string" && badge.length > 0,
  );
  const hasOpenReview = call.answerPreview.some(
    (entry) =>
      entry.reviewStatus === "pending_review" ||
      entry.reviewStatus === "in_review" ||
      entry.reviewStatus === "escalated",
  );
  const needsReview =
    call.sessionStatus === "review_required" || hasOpenReview;
  const openReviewEntry =
    call.answerPreview.find(
      (entry) =>
        entry.reviewStatus === "pending_review" ||
        entry.reviewStatus === "in_review" ||
        entry.reviewStatus === "escalated",
    ) ??
    call.answerPreview.find(
      (entry) => entry.reviewOwnerKind != null || entry.reviewOwnerReference,
    ) ??
    null;
  const reviewOwnerKind = openReviewEntry?.reviewOwnerKind ?? null;
  const reviewOwnerLabel = openReviewEntry?.reviewOwnerLabel?.trim() ?? null;
  const reviewOwnerReference =
    openReviewEntry?.reviewOwnerReference?.trim() ?? null;
  const reviewOwnedByCurrentClinician =
    reviewOwnerKind === "clinician" &&
    reviewOwnerReference === clinicianUserId;
  const reviewOwnedByAnotherClinician =
    reviewOwnerKind === "clinician" &&
    reviewOwnerReference != null &&
    reviewOwnerReference !== clinicianUserId;
  const canTakeReview =
    actorCanManageReview ||
    reviewOwnedByCurrentClinician ||
    !reviewOwnedByAnotherClinician;
  const canUpdateReview = actorCanManageReview || reviewOwnedByCurrentClinician;
  const canMarkReviewReviewed =
    hasOpenReview && canUpdateReview && !hasHiddenAttentionAnswers;
  const reviewActionHint = !canUpdateReview
    ? reviewOwnedByAnotherClinician
      ? "This review is owned by another clinician."
      : "Take review before closing or escalating."
    : null;
  const reviewVisibilityHint = hasHiddenAttentionAnswers
    ? `Show all ${String(
        attentionAnswerCount,
      )} answers needing review before marking the questionnaire complete.`
    : null;

  return (
    <Modal
      accessibilityLabel={
        discardConfirmationVisible
          ? "Discard questionnaire review note"
          : "Questionnaire answers"
      }
      animationType="fade"
      accessibilityViewIsModal={true}
      onRequestClose={
        isSubmitting
          ? undefined
          : discardConfirmationVisible
            ? onCancelDiscard
            : onClose
      }
      transparent={true}
      visible={true}
    >
      <View
        style={[
          styles.patientSupportModalOverlay,
          { padding: usesCompactReviewLayout ? 8 : 22 },
        ]}
      >
        <View
          pointerEvents="none"
          style={styles.patientSupportModalBackdrop}
        />
        <View
          accessibilityElementsHidden={discardConfirmationVisible}
          aria-hidden={discardConfirmationVisible}
          importantForAccessibility={
            discardConfirmationVisible ? "no-hide-descendants" : "auto"
          }
          pointerEvents={discardConfirmationVisible ? "none" : "auto"}
          style={[
            styles.patientSupportModalPanel,
            {
              backgroundColor: theme.surface,
              borderColor: theme.borderSubtle,
              borderRadius: workforceOperationalLayout.panelRadius,
              height: usesCompactReviewLayout ? "96%" : "90%",
              maxHeight: usesCompactReviewLayout ? "96%" : "92%",
              maxWidth: 1120,
            },
          ]}
        >
          <View
            style={[
              styles.patientSupportModalHeader,
              {
                backgroundColor: theme.surface,
                borderBottomColor: theme.borderSubtle,
                borderBottomWidth: 1,
                paddingHorizontal: tokens.spacing.lg,
                paddingVertical: tokens.spacing.md,
              },
            ]}
          >
            <View style={[styles.listGroup, { flex: 1, gap: 4 }]}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: tokens.typography.fontSize.lg,
                    lineHeight: tokens.typography.lineHeight.lg,
                  },
                ]}
              >
                Questionnaire review
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {`${patientName} · ${call.questionnaireDisplayName}`}
              </Text>
              <Text
                numberOfLines={2}
                style={{
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  lineHeight:
                    workforceOperationalTypography.metadata.lineHeight,
                }}
              >
                {patientIdentityText ?? "Patient identifiers unavailable"}
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Close questionnaire answers"
              accessibilityRole="button"
              accessibilityState={{ disabled: isSubmitting }}
              aria-disabled={isSubmitting}
              disabled={isSubmitting}
              onPress={onClose}
              style={[
                styles.patientSupportModalCloseButton,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  height: modalControlHeight,
                  opacity: isSubmitting ? 0.55 : 1,
                  width: modalControlHeight,
                },
              ]}
            >
              <LineIcon
                color={theme.textSecondary}
                name="x"
                size={18}
              />
            </Pressable>
          </View>

          <View
            style={{
              flex: 1,
              flexDirection: usesCompactReviewLayout ? "column" : "row",
              minHeight: 0,
              width: "100%",
            }}
          >
            <View
              style={{
                backgroundColor: theme.surfaceMuted,
                borderBottomColor: theme.borderSubtle,
                borderBottomWidth: usesCompactReviewLayout ? 1 : 0,
                borderRightColor: theme.borderSubtle,
                borderRightWidth: usesCompactReviewLayout ? 0 : 1,
                flexShrink: 0,
                width: usesCompactReviewLayout ? "100%" : 232,
              }}
            >
              {!usesCompactReviewLayout ? (
                <Text
                  style={{
                    color: theme.textTertiary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize:
                      workforceOperationalTypography.metadata.fontSize,
                    fontWeight: "800",
                    lineHeight:
                      workforceOperationalTypography.metadata.lineHeight,
                    paddingHorizontal: tokens.spacing.md,
                    paddingTop: tokens.spacing.md,
                    textTransform: "uppercase",
                  }}
                >
                  Sections
                </Text>
              ) : null}
              <ScrollView
                horizontal={usesCompactReviewLayout}
                showsHorizontalScrollIndicator={false}
                style={usesCompactReviewLayout ? undefined : { flex: 1 }}
                contentContainerStyle={{
                  flexDirection: usesCompactReviewLayout ? "row" : "column",
                  gap: 4,
                  padding: usesCompactReviewLayout
                    ? tokens.spacing.sm
                    : tokens.spacing.md,
                }}
              >
                {[
                  {
                    attentionCount: attentionAnswerCount,
                    entries: call.answerPreview,
                    key: questionnaireReviewAllSectionsKey,
                    labelText: "All sections",
                  },
                  ...reviewSections,
                ].map((section) => {
                  const isSelected =
                    selectedReviewSectionKey === section.key;
                  const matchingCount = section.entries.filter((entry) =>
                    questionnaireAnswerMatchesFilter(entry, reviewFilterId),
                  ).length;

                  return (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityState={{ selected: isSelected }}
                      key={section.key}
                      onPress={() =>
                        setSelectedReviewSectionKey(section.key)
                      }
                      style={({ pressed }) => ({
                        alignItems: "center",
                        backgroundColor: isSelected
                          ? theme.accentSubtle
                          : pressed
                            ? theme.backgroundSubtle
                            : "transparent",
                        borderColor: isSelected
                          ? theme.accent
                          : "transparent",
                        borderRadius:
                          workforceOperationalLayout.controlRadius,
                        borderWidth: 1,
                        flexDirection: "row",
                        gap: tokens.spacing.xs,
                        justifyContent: "space-between",
                        minHeight: modalControlHeight,
                        opacity: matchingCount === 0 ? 0.5 : 1,
                        paddingHorizontal: tokens.spacing.sm,
                        paddingVertical: 7,
                        width: usesCompactReviewLayout ? undefined : "100%",
                      })}
                    >
                      <Text
                        numberOfLines={usesCompactReviewLayout ? 1 : 2}
                        style={{
                          color: isSelected
                            ? theme.accentStrong
                            : theme.textPrimary,
                          flexShrink: 1,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 12,
                          fontWeight: isSelected ? "800" : "700",
                          lineHeight: 16,
                        }}
                      >
                        {section.labelText}
                      </Text>
                      <View
                        style={{
                          alignItems: "center",
                          backgroundColor:
                            section.attentionCount > 0
                              ? theme.statusChip.warning.background
                              : theme.backgroundSubtle,
                          borderRadius: 999,
                          justifyContent: "center",
                          minHeight: 22,
                          minWidth: 24,
                          paddingHorizontal: 6,
                        }}
                      >
                        <Text
                          style={{
                            color:
                              section.attentionCount > 0
                                ? theme.statusChip.warning.text
                                : theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11,
                            fontWeight: "800",
                          }}
                        >
                          {matchingCount}
                        </Text>
                      </View>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>

            <View style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
              <View
                style={{
                  alignItems: usesCompactReviewLayout
                    ? "flex-start"
                    : "center",
                  borderBottomColor: theme.borderSubtle,
                  borderBottomWidth: 1,
                  flexDirection: usesCompactReviewLayout ? "column" : "row",
                  gap: tokens.spacing.sm,
                  justifyContent: "space-between",
                  paddingHorizontal: usesCompactReviewLayout
                    ? tokens.spacing.sm
                    : tokens.spacing.lg,
                  paddingVertical: tokens.spacing.sm,
                }}
              >
                <View
                  accessibilityRole="tablist"
                  style={[
                    styles.flagRow,
                    { gap: 4, flex: 1 },
                  ]}
                >
                  {reviewFilterOptions.map((filterOption, filterIndex) => {
                    const isSelected = reviewFilterId === filterOption.id;

                    return (
                      <Pressable
                        accessibilityRole="tab"
                        accessibilityState={{ selected: isSelected }}
                        aria-selected={isSelected}
                        key={filterOption.id}
                        onPress={() => setReviewFilterId(filterOption.id)}
                        tabIndex={isSelected ? 0 : -1}
                        {...(Platform.OS === "web"
                          ? {
                              onKeyDown: (event: unknown) =>
                                handleQuestionnaireReviewTabKeyDown(
                                  event as QuestionnaireReviewTabKeyDownEvent,
                                  filterIndex,
                                  reviewFilterIds,
                                  setReviewFilterId,
                                ),
                            }
                          : {})}
                        style={({ pressed }) => ({
                          alignItems: "center",
                          backgroundColor: isSelected
                            ? theme.accentStrong
                            : pressed
                              ? theme.backgroundSubtle
                              : theme.surface,
                          borderColor: isSelected
                            ? theme.accentStrong
                            : theme.borderSubtle,
                          borderRadius:
                            workforceOperationalLayout.controlRadius,
                          borderWidth: 1,
                          flexDirection: "row",
                          gap: 5,
                          justifyContent: "center",
                          minHeight: modalControlHeight,
                          paddingHorizontal: tokens.spacing.sm,
                          paddingVertical: 6,
                        })}
                      >
                        <Text
                          style={{
                            color: isSelected
                              ? theme.surface
                              : theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 12,
                            fontWeight: "800",
                          }}
                        >
                          {filterOption.labelText}
                        </Text>
                        <Text
                          style={{
                            color: isSelected
                              ? theme.surface
                              : theme.textTertiary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11,
                            fontWeight: "800",
                          }}
                        >
                          {filterOption.count}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
                <Text
                  style={{
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize:
                      workforceOperationalTypography.metadata.fontSize,
                    lineHeight:
                      workforceOperationalTypography.metadata.lineHeight,
                  }}
                >
                  {`${String(visibleReviewAnswerCount)} shown`}
                </Text>
              </View>

              <ScrollView
                style={styles.patientSupportModalScroll}
                contentContainerStyle={[
                  styles.patientSupportModalContent,
                  {
                    gap: tokens.spacing.md,
                    padding: usesCompactReviewLayout
                      ? tokens.spacing.sm
                      : tokens.spacing.lg,
                  },
                ]}
              >
            <View style={[styles.flagRow, { gap: tokens.spacing.xs }]}>
              {answerBadges.map((badgeText) => (
                <View
                  key={badgeText}
                  style={[
                    styles.inlinePill,
                    {
                      alignItems: "center",
                      backgroundColor: theme.surface,
                      borderColor: theme.borderSubtle,
                      borderRadius: 999,
                      justifyContent: "center",
                      minHeight: 32,
                      paddingHorizontal: tokens.spacing.sm,
                      paddingVertical: 4,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                      },
                    ]}
                  >
                    {badgeText}
                  </Text>
                </View>
              ))}
            </View>

            {call.answerPreview.length === 0 ? (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                    paddingVertical: tokens.spacing.md,
                    textAlign: "center",
                  },
                ]}
              >
                No answers recorded yet.
              </Text>
            ) : visibleReviewAnswerCount === 0 ? (
              <View
                style={{
                  alignItems: "center",
                  gap: tokens.spacing.sm,
                  paddingVertical: tokens.spacing.xl,
                }}
              >
                <Text
                  style={{
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    fontWeight: "800",
                    textAlign: "center",
                  }}
                >
                  No answers match this view
                </Text>
                <Text
                  style={{
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    lineHeight: tokens.typography.lineHeight.sm,
                    textAlign: "center",
                  }}
                >
                  Choose another section or return to all answers.
                </Text>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    setReviewFilterId("all");
                    setSelectedReviewSectionKey(
                      questionnaireReviewAllSectionsKey,
                    );
                  }}
                  style={({ pressed }) => ({
                    backgroundColor: pressed
                      ? theme.backgroundSubtle
                      : theme.surface,
                    borderColor: theme.borderStrong,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    borderWidth: 1,
                    minHeight: modalControlHeight,
                    justifyContent: "center",
                    paddingHorizontal: tokens.spacing.md,
                  })}
                >
                  <Text
                    style={{
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      fontWeight: "800",
                    }}
                  >
                    Show all answers
                  </Text>
                </Pressable>
              </View>
            ) : (
              visibleReviewSections.map((section) => (
                <View
                  key={section.key}
                  style={{ gap: tokens.spacing.sm }}
                >
                  <View
                    style={{
                      alignItems: "flex-end",
                      borderBottomColor: theme.borderSubtle,
                      borderBottomWidth: 1,
                      flexDirection: "row",
                      gap: tokens.spacing.sm,
                      justifyContent: "space-between",
                      paddingBottom: tokens.spacing.xs,
                    }}
                  >
                    <View style={{ flex: 1, gap: 2 }}>
                      <Text
                        style={{
                          color: theme.textPrimary,
                          fontFamily: layout.displayFontFamily,
                          fontSize:
                            workforceOperationalTypography.sectionTitle
                              .fontSize,
                          fontWeight: "800",
                          lineHeight:
                            workforceOperationalTypography.sectionTitle
                              .lineHeight,
                        }}
                      >
                        {section.labelText}
                      </Text>
                      <Text
                        style={{
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize:
                            workforceOperationalTypography.metadata.fontSize,
                          lineHeight:
                            workforceOperationalTypography.metadata.lineHeight,
                        }}
                      >
                        {`${String(section.entries.length)} recorded answer${
                          section.entries.length === 1 ? "" : "s"
                        }`}
                      </Text>
                    </View>
                    {section.attentionCount > 0 ? (
                      <View
                        style={{
                          backgroundColor:
                            theme.statusChip.warning.background,
                          borderColor: theme.statusChip.warning.border,
                          borderRadius:
                            workforceOperationalLayout.controlRadius,
                          borderWidth: 1,
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                        }}
                      >
                        <Text
                          style={{
                            color: theme.statusChip.warning.text,
                            fontFamily: layout.bodyFontFamily,
                            fontSize:
                              workforceOperationalTypography.metadata.fontSize,
                            fontWeight: "800",
                          }}
                        >
                          {`${String(section.attentionCount)} need${
                            section.attentionCount === 1 ? "s" : ""
                          } review`}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                  {section.entries.map((entry, entryIndex) => {
                const entryBadges =
                  buildOutboundQuestionnaireAnswerBadges(entry);
                const entryResponseText =
                  formatClinicianQuestionnaireAnswerText(
                    entry.answerStatus === "redacted"
                      ? entry.answerSummary
                      : entry.rawAnswerText ?? entry.answerSummary,
                  );
                const entryHasDangerSignal =
                  entry.redFlagTriggered ||
                  entry.reviewSignal === "red_flag";
                const entryHasWarningSignal =
                  !entryHasDangerSignal &&
                  (entry.clinicianReviewRequired ||
                    entry.reviewSignal === "yellow_flag");
                const entryAnswerPalette = entryHasDangerSignal
                  ? theme.statusChip.danger
                  : entryHasWarningSignal
                    ? theme.statusChip.warning
                    : {
                        background: theme.accentSubtle,
                        border: theme.accentSubtle,
                        text: theme.textPrimary,
                      };
                const historyEvents = (entry.answerEvents ?? [])
                  .filter((event) => event.eventType !== "answer_captured")
                  .slice(-4);
                const medicationEntries = entry.medicationEntries ?? [];
                const medicationSummary =
                  formatOutboundMedicationReconciliationSummary(entry);
                const hasExpandableDetails =
                  historyEvents.length > 0 ||
                  medicationEntries.length > 0 ||
                  medicationSummary != null;
                const isExpanded =
                  expandedReviewAnswerKeys.includes(entry.questionKey) ||
                  (medicationResolution?.questionKey === entry.questionKey &&
                    medicationResolution.targetId === call.targetId);

                return (
                  <View
                    key={entry.questionKey}
                    style={{
                      backgroundColor: theme.surface,
                      borderColor: entryHasDangerSignal
                        ? theme.statusChip.danger.border
                        : entryHasWarningSignal
                          ? theme.statusChip.warning.border
                          : theme.borderSubtle,
                      borderLeftColor: entryHasDangerSignal
                        ? theme.statusChip.danger.text
                        : entryHasWarningSignal
                          ? theme.statusChip.warning.text
                          : theme.accent,
                      borderLeftWidth: 3,
                      borderRadius: workforceOperationalLayout.panelRadius,
                      borderWidth: 1,
                      gap: 0,
                      overflow: "hidden",
                    }}
                  >
                    <View
                      style={{
                        backgroundColor: theme.surface,
                        gap: 4,
                        paddingHorizontal: tokens.spacing.md,
                        paddingVertical: tokens.spacing.sm,
                      }}
                    >
                      <View
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "center",
                            gap: tokens.spacing.sm,
                            justifyContent: "space-between",
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.metricLabel,
                            {
                              color: theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize:
                                workforceOperationalTypography.metadata
                                  .fontSize,
                              fontWeight: "800",
                              lineHeight:
                                workforceOperationalTypography.metadata
                                  .lineHeight,
                              textTransform: "uppercase",
                            },
                          ]}
                        >
                          {`Question ${String(entryIndex + 1)}`}
                        </Text>
                        {entry.answeredAt ? (
                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize:
                                  workforceOperationalTypography.metadata
                                    .fontSize,
                                lineHeight:
                                  workforceOperationalTypography.metadata
                                    .lineHeight,
                              },
                            ]}
                          >
                            {`Answered ${entry.answeredAt.replace("T", " ").slice(0, 16)}`}
                          </Text>
                        ) : null}
                      </View>
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.sm,
                            fontWeight: "700",
                            lineHeight: tokens.typography.lineHeight.md,
                          },
                        ]}
                      >
                        {resolveQuestionnaireReviewPromptText(
                          entry.questionKey,
                          entry.promptText,
                        )}
                      </Text>
                    </View>
                    <View
                      style={{
                        backgroundColor:
                          entryHasDangerSignal || entryHasWarningSignal
                            ? entryAnswerPalette.background
                            : theme.surfaceMuted,
                        borderTopColor: entryHasDangerSignal
                          ? theme.statusChip.danger.border
                          : entryHasWarningSignal
                            ? theme.statusChip.warning.border
                            : theme.borderSubtle,
                        borderTopWidth: 1,
                        gap: 6,
                        paddingHorizontal: tokens.spacing.md,
                        paddingVertical: tokens.spacing.sm,
                      }}
                    >
                      <View
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "center",
                            gap: tokens.spacing.sm,
                            justifyContent: "space-between",
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.metricLabel,
                            {
                              color: entryHasDangerSignal
                                ? theme.statusChip.danger.text
                                : entryHasWarningSignal
                                  ? theme.statusChip.warning.text
                                  : theme.accentStrong,
                              fontFamily: layout.bodyFontFamily,
                              fontSize:
                                workforceOperationalTypography.metadata.fontSize,
                              fontWeight: "800",
                              letterSpacing: 0,
                              lineHeight:
                                workforceOperationalTypography.metadata
                                  .lineHeight,
                              textTransform: "uppercase",
                            },
                          ]}
                        >
                          Patient answer
                        </Text>
                        {entryHasDangerSignal ? (
                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: theme.statusChip.danger.text,
                                fontFamily: layout.bodyFontFamily,
                                fontSize:
                                  workforceOperationalTypography.metadata
                                    .fontSize,
                                fontWeight: "800",
                                lineHeight:
                                  workforceOperationalTypography.metadata
                                    .lineHeight,
                              },
                            ]}
                          >
                            {entry.reviewSignalLabel ?? "Red flag"}
                          </Text>
                        ) : entryHasWarningSignal ? (
                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: theme.statusChip.warning.text,
                                fontFamily: layout.bodyFontFamily,
                                fontSize:
                                  workforceOperationalTypography.metadata
                                    .fontSize,
                                fontWeight: "800",
                                lineHeight:
                                  workforceOperationalTypography.metadata
                                    .lineHeight,
                              },
                            ]}
                          >
                            {entry.reviewSignalLabel ?? "Yellow flag"}
                          </Text>
                        ) : null}
                      </View>
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: entryAnswerPalette.text,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.sm,
                            lineHeight: tokens.typography.lineHeight.md,
                          },
                        ]}
                      >
                        {entryResponseText}
                      </Text>
                      {entryBadges.length > 0 ? (
                        <View
                          style={[
                            styles.flagRow,
                            {
                              gap: tokens.spacing.xs,
                              paddingTop: 2,
                            },
                          ]}
                        >
                          {entryBadges.map((badge) => {
                            const palette =
                              badge.tone === "danger"
                                ? theme.statusChip.danger
                                : badge.tone === "warning"
                                  ? theme.statusChip.warning
                                  : badge.tone === "info"
                                    ? theme.statusChip.success
                                    : {
                                        background: theme.surface,
                                        border: theme.borderStrong,
                                        text: theme.textSecondary,
                                      };

                            return (
                              <View
                                key={`${entry.questionKey}-${badge.labelText}`}
                                style={[
                                  styles.inlinePill,
                                  {
                                    backgroundColor: palette.background,
                                    borderColor: palette.border,
                                    borderRadius:
                                      workforceOperationalLayout.controlRadius,
                                    minHeight: 22,
                                    paddingHorizontal: 8,
                                    paddingVertical: 2,
                                  },
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.pillValueText,
                                    {
                                      color: palette.text,
                                      fontFamily: layout.bodyFontFamily,
                                      fontSize:
                                        workforceOperationalTypography.metadata
                                          .fontSize,
                                      fontWeight: "800",
                                      lineHeight:
                                        workforceOperationalTypography.metadata
                                          .lineHeight,
                                    },
                                  ]}
                                >
                                  {badge.labelText}
                                </Text>
                              </View>
                            );
                          })}
                        </View>
                      ) : null}
                      {hasExpandableDetails ? (
                        <Pressable
                          accessibilityLabel={`${
                            isExpanded ? "Hide" : "Show"
                          } details for ${entry.questionKey}`}
                          accessibilityRole="button"
                          accessibilityState={{ expanded: isExpanded }}
                          onPress={() =>
                            toggleReviewAnswerDetails(entry.questionKey)
                          }
                          style={({ pressed }) => ({
                            alignItems: "center",
                            alignSelf: "flex-start",
                            flexDirection: "row",
                            gap: 5,
                            minHeight: modalControlHeight,
                            opacity: pressed ? 0.65 : 1,
                            paddingVertical: 3,
                          })}
                        >
                          <LineIcon
                            color={theme.textSecondary}
                            name={
                              isExpanded
                                ? "chevron-left"
                                : "chevron-right"
                            }
                            size={14}
                          />
                          <Text
                            style={{
                              color: theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 12,
                              fontWeight: "800",
                            }}
                          >
                            {isExpanded
                              ? "Hide clinical details"
                              : "Show clinical details"}
                          </Text>
                        </Pressable>
                      ) : null}
                      {isExpanded ? (
                        <>
                      {medicationEntries.length > 0 || medicationSummary ? (
                        <View
                          style={{
                            borderTopColor: entry.redFlagTriggered
                              ? theme.statusChip.danger.border
                              : theme.borderSubtle,
                            borderTopWidth: 1,
                            gap: 4,
                            paddingTop: tokens.spacing.xs,
                          }}
                        >
                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: theme.textTertiary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize:
                                  workforceOperationalTypography.metadata
                                    .fontSize,
                                fontWeight: "800",
                                lineHeight:
                                  workforceOperationalTypography.metadata
                                    .lineHeight,
                                textTransform: "uppercase",
                              },
                            ]}
                          >
                            Medication reconciliation
                          </Text>
                          {medicationSummary ? (
                            <Text
                              style={[
                                styles.sectionSubtitle,
                                {
                                  color: entry.redFlagTriggered
                                    ? theme.statusChip.danger.text
                                    : theme.textSecondary,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: tokens.typography.fontSize.xs,
                                  lineHeight: tokens.typography.lineHeight.sm,
                                },
                              ]}
                            >
                              {medicationSummary}
                            </Text>
                          ) : null}
                          {medicationEntries.length > 0 ? (
                            medicationEntries.map((medicationEntry, index) => {
                              const detailFields =
                                buildOutboundMedicationEntryDetailFields(
                                  medicationEntry,
                                );
                              const medicationIsMatched =
                                medicationEntry.matchProvenance ===
                                  "clinician_resolved" ||
                                medicationEntry.identityMatchStatus ===
                                  "matched";
                              const matchLabel = medicationEntry.needsResolution
                                ? "Needs resolution"
                                : medicationEntry.matchProvenance ===
                                    "clinician_resolved"
                                  ? `Resolved by ${
                                      medicationEntry.resolvedByLabel ??
                                      "clinician"
                                    }`
                                  : medicationEntry.identityMatchStatus ===
                                      "matched"
                                    ? "Catalog matched"
                                    : "Matching not enabled";
                              const matchPalette = medicationEntry.needsResolution
                                ? theme.statusChip.warning
                                : medicationIsMatched
                                  ? theme.statusChip.success
                                  : {
                                      background: theme.surface,
                                      border: theme.borderStrong,
                                      text: theme.textSecondary,
                                    };

                              return (
                                <View
                                  key={`${entry.questionKey}-medication-${String(index)}`}
                                  style={{
                                    borderTopColor:
                                      index === 0
                                        ? "transparent"
                                        : theme.borderSubtle,
                                    borderTopWidth: index === 0 ? 0 : 1,
                                    gap: tokens.spacing.sm,
                                    paddingTop:
                                      index === 0 ? 2 : tokens.spacing.sm,
                                  }}
                                >
                                  <View
                                    style={[
                                      styles.patientRowHeader,
                                      {
                                        alignItems: "center",
                                        gap: tokens.spacing.sm,
                                        justifyContent: "space-between",
                                      },
                                    ]}
                                  >
                                    <Text
                                      style={[
                                        styles.sectionSubtitle,
                                        {
                                          color: entry.redFlagTriggered
                                            ? theme.statusChip.danger.text
                                            : theme.textPrimary,
                                          flex: 1,
                                          fontFamily: layout.bodyFontFamily,
                                          fontSize:
                                            tokens.typography.fontSize.sm,
                                          fontWeight: "800",
                                          lineHeight:
                                            tokens.typography.lineHeight.md,
                                        },
                                      ]}
                                    >
                                      {formatOutboundMedicationEntryTitle(
                                        medicationEntry,
                                        index,
                                      )}
                                    </Text>
                                    <View
                                      style={{
                                        backgroundColor:
                                          matchPalette.background,
                                        borderColor: matchPalette.border,
                                        borderRadius:
                                          workforceOperationalLayout.controlRadius,
                                        borderWidth: 1,
                                        paddingHorizontal: 7,
                                        paddingVertical: 2,
                                      }}
                                    >
                                      <Text
                                        style={{
                                          color: matchPalette.text,
                                          fontFamily: layout.bodyFontFamily,
                                          fontSize:
                                            workforceOperationalTypography
                                              .metadata.fontSize,
                                          fontWeight: "800",
                                          lineHeight:
                                            workforceOperationalTypography
                                              .metadata.lineHeight,
                                        }}
                                      >
                                        {matchLabel}
                                      </Text>
                                    </View>
                                  </View>
                                  <View
                                    style={{
                                      flexDirection: "row",
                                      flexWrap: "wrap",
                                      gap: tokens.spacing.sm,
                                    }}
                                  >
                                    {detailFields.map((field) => (
                                      <View
                                        key={`${entry.questionKey}-${String(index)}-${field.labelText}`}
                                        style={{
                                          flexBasis: usesCompactReviewLayout
                                            ? 136
                                            : 148,
                                          flexGrow: 1,
                                          gap: 1,
                                          minWidth: 0,
                                        }}
                                      >
                                        <Text
                                          style={{
                                            color: theme.textTertiary,
                                            fontFamily: layout.bodyFontFamily,
                                            fontSize:
                                              workforceOperationalTypography
                                                .metadata.fontSize,
                                            fontWeight: "800",
                                            lineHeight:
                                              workforceOperationalTypography
                                                .metadata.lineHeight,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {field.labelText}
                                        </Text>
                                        <Text
                                          style={{
                                            color: entry.redFlagTriggered
                                              ? theme.statusChip.danger.text
                                              : theme.textPrimary,
                                            fontFamily: layout.bodyFontFamily,
                                            fontSize:
                                              tokens.typography.fontSize.xs,
                                            lineHeight:
                                              tokens.typography.lineHeight.sm,
                                          }}
                                        >
                                          {field.valueText}
                                        </Text>
                                      </View>
                                    ))}
                                  </View>
                                  <View
                                    style={{
                                      borderTopColor: theme.borderSubtle,
                                      borderTopWidth: 1,
                                      gap: 2,
                                      paddingTop: tokens.spacing.xs,
                                    }}
                                  >
                                    <Text
                                      style={{
                                        color: theme.textTertiary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize:
                                          workforceOperationalTypography
                                            .metadata.fontSize,
                                        fontWeight: "800",
                                        lineHeight:
                                          workforceOperationalTypography
                                            .metadata.lineHeight,
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Patient wording
                                    </Text>
                                    <Text
                                      style={{
                                        color: entry.redFlagTriggered
                                          ? theme.statusChip.danger.text
                                          : theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                        lineHeight:
                                          tokens.typography.lineHeight.sm,
                                      }}
                                    >
                                      {formatOutboundMedicationField(
                                        medicationEntry.source.rawText,
                                      )}
                                    </Text>
                                    <Text
                                      style={{
                                        color: entry.redFlagTriggered
                                          ? theme.statusChip.danger.text
                                          : theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize:
                                          workforceOperationalTypography
                                            .metadata.fontSize,
                                        lineHeight:
                                          workforceOperationalTypography
                                            .metadata.lineHeight,
                                      }}
                                    >
                                      {`Capture confidence: ${formatOutboundMedicationField(
                                        medicationEntry.confidenceLevel,
                                      )}`}
                                    </Text>
                                  </View>
                                  {medicationEntry.needsResolution &&
                                  onMedicationResolutionOpen &&
                                  (medicationResolution == null ||
                                    medicationResolution.questionKey !==
                                      entry.questionKey ||
                                    medicationResolution.entryIndex !==
                                      (medicationEntry.entryIndex ?? index)) ? (
                                    <Pressable
                                      accessibilityLabel={`Resolve ${formatOutboundMedicationEntryTitle(
                                        medicationEntry,
                                        index,
                                      )}`}
                                      accessibilityRole="button"
                                      onPress={() =>
                                        onMedicationResolutionOpen(
                                          entry.questionKey,
                                          medicationEntry.entryIndex ?? index,
                                          medicationEntry.patientReportedName ??
                                            medicationEntry.name ??
                                            "",
                                        )
                                      }
                                      style={({ pressed }) => [
                                        {
                                          alignItems: "center",
                                          alignSelf: "flex-start",
                                          borderColor: theme.accent,
                                          borderRadius:
                                            workforceOperationalLayout.controlRadius,
                                          borderWidth: 1,
                                          justifyContent: "center",
                                          minHeight: modalControlHeight,
                                          opacity: pressed ? 0.7 : 1,
                                          paddingHorizontal: tokens.spacing.sm,
                                          paddingVertical: 4,
                                        },
                                      ]}
                                    >
                                      <Text
                                        style={{
                                          color: theme.accent,
                                          fontFamily: layout.bodyFontFamily,
                                          fontSize:
                                            tokens.typography.fontSize.xs,
                                          fontWeight: "700",
                                        }}
                                      >
                                        Resolve medication
                                      </Text>
                                    </Pressable>
                                  ) : null}
                                  {medicationResolution != null &&
                                  medicationResolution.questionKey ===
                                    entry.questionKey &&
                                  medicationResolution.entryIndex ===
                                    (medicationEntry.entryIndex ?? index)
                                    ? renderClinicianMedicationResolutionEditor({
                                        call,
                                        controlHeight: modalControlHeight,
                                        layout,
                                        onMedicationResolutionClose,
                                        onMedicationResolutionQueryChange,
                                        onMedicationResolutionSelect,
                                        onMedicationResolve,
                                        onMedicationSearch,
                                        resolution: medicationResolution,
                                        theme,
                                        tokens,
                                      })
                                    : null}
                                </View>
                              );
                            })
                          ) : (
                            <Text
                              style={[
                                styles.sectionSubtitle,
                                {
                                  color: theme.textSecondary,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: tokens.typography.fontSize.xs,
                                  lineHeight: tokens.typography.lineHeight.sm,
                                },
                              ]}
                            >
                              No patient-reported medication entries captured.
                            </Text>
                          )}
                          {entry.medicationIdentityMatching ? (
                            <Text
                              style={[
                                styles.sectionSubtitle,
                                {
                                  color: theme.textSecondary,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: tokens.typography.fontSize.xs,
                                  lineHeight: tokens.typography.lineHeight.sm,
                                },
                              ]}
                            >
                              {formatOutboundMedicationMatchingSummary(entry)}
                            </Text>
                          ) : null}
                        </View>
                      ) : null}
                      {historyEvents.length > 0 ? (
                        <View
                          style={{
                            borderTopColor: entry.redFlagTriggered
                              ? theme.statusChip.danger.border
                              : theme.borderSubtle,
                            borderTopWidth: 1,
                            gap: 3,
                            paddingTop: tokens.spacing.xs,
                          }}
                        >
                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: theme.textTertiary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize:
                                  workforceOperationalTypography.metadata
                                    .fontSize,
                                fontWeight: "800",
                                lineHeight:
                                  workforceOperationalTypography.metadata
                                    .lineHeight,
                                textTransform: "uppercase",
                              },
                            ]}
                          >
                            Answer history
                          </Text>
                          {historyEvents.map((event) => (
                            <Text
                              key={event.eventKey}
                              style={[
                                styles.sectionSubtitle,
                                {
                                  color: entry.redFlagTriggered
                                    ? theme.statusChip.danger.text
                                    : theme.textSecondary,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: tokens.typography.fontSize.xs,
                                  lineHeight: tokens.typography.lineHeight.sm,
                                },
                              ]}
                            >
                              {formatOutboundQuestionnaireAnswerEventText(
                                event,
                              )}
                            </Text>
                          ))}
                        </View>
                      ) : null}
                        </>
                      ) : null}
                    </View>
                  </View>
                );
                  })}
                </View>
              ))
            )}
              </ScrollView>
            </View>
          </View>

          <View
            style={[
              styles.patientRowFooter,
              {
                gap: tokens.spacing.sm,
                borderTopColor: theme.borderSubtle,
                borderTopWidth: 1,
                justifyContent: "flex-end",
                paddingHorizontal: tokens.spacing.lg,
                paddingVertical: tokens.spacing.md,
              },
            ]}
          >
            {needsReview && onReviewAction ? (
              <>
                <View
                  style={{
                    alignItems: usesCompactReviewLayout
                      ? "stretch"
                      : "flex-end",
                    flexDirection: usesCompactReviewLayout ? "column" : "row",
                    gap: tokens.spacing.sm,
                    width: "100%",
                  }}
                >
                <View
                  style={{
                    flex: 1,
                    gap: 5,
                    minWidth: 220,
                    width: usesCompactReviewLayout ? "100%" : undefined,
                  }}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textTertiary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize:
                          workforceOperationalTypography.metadata.fontSize,
                        fontWeight: "800",
                        lineHeight:
                          workforceOperationalTypography.metadata.lineHeight,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    Review note
                  </Text>
                  <TextInput
                    accessibilityLabel="Questionnaire review note"
                    editable={!isSubmitting}
                    multiline={true}
                    numberOfLines={3}
                    onChangeText={onReviewNotesChange}
                    placeholder="Add clinical review context"
                    placeholderTextColor={theme.textTertiary}
                    style={{
                      backgroundColor: theme.surface,
                      borderColor: theme.borderSubtle,
                      borderRadius: workforceOperationalLayout.controlRadius,
                      borderWidth: 1,
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.body.fontSize,
                      lineHeight:
                        workforceOperationalTypography.body.lineHeight,
                      minHeight: 52,
                      opacity: isSubmitting ? 0.6 : 1,
                      paddingHorizontal: tokens.spacing.sm,
                      paddingVertical: tokens.spacing.xs,
                      textAlignVertical: "top",
                      width: "100%",
                    }}
                    value={reviewNotes}
                  />
                  {reviewOwnerLabel ? (
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                          lineHeight: tokens.typography.lineHeight.sm,
                        },
                      ]}
                    >
                      {`Review owner: ${reviewOwnerLabel}`}
                    </Text>
                  ) : null}
                  {reviewVisibilityHint ? (
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.statusChip.warning.text,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                          lineHeight: tokens.typography.lineHeight.sm,
                        },
                      ]}
                    >
                      {reviewVisibilityHint}
                    </Text>
                  ) : null}
                  {reviewActionHint ? (
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                          lineHeight: tokens.typography.lineHeight.sm,
                        },
                      ]}
                    >
                      {reviewActionHint}
                    </Text>
                  ) : null}
                </View>
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: tokens.spacing.xs,
                    justifyContent: "flex-end",
                    width: usesCompactReviewLayout ? "100%" : undefined,
                  }}
                >
                <Pressable
                  accessibilityLabel="Take questionnaire review"
                  accessibilityRole="button"
                  disabled={isSubmitting || !canTakeReview}
                  onPress={() => onReviewAction("assign-review", call)}
                  style={({ pressed }) => ({
                    alignItems: "center",
                    backgroundColor: pressed ? theme.backgroundSubtle : theme.surface,
                    borderColor: theme.borderStrong,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    borderWidth: 1,
                    flexDirection: "row",
                    flexGrow: usesCompactReviewLayout ? 1 : 0,
                    gap: 6,
                    justifyContent: "center",
                    minHeight: modalControlHeight,
                    paddingHorizontal: 14,
                    paddingVertical: 7,
                    opacity: isSubmitting || !canTakeReview ? 0.6 : 1,
                  })}
                >
                  <LineIcon
                    color={theme.textPrimary}
                    name="user"
                    size={14}
                  />
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        fontWeight: "700",
                        lineHeight: 16,
                      },
                    ]}
                  >
                    Take review
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityLabel="Escalate questionnaire review"
                  accessibilityRole="button"
                  disabled={isSubmitting || !canUpdateReview}
                  onPress={() => onReviewAction("escalate-review", call)}
                  style={({ pressed }) => ({
                    alignItems: "center",
                    backgroundColor: pressed
                      ? theme.statusChip.warning.background
                      : theme.surface,
                    borderColor: theme.statusChip.warning.border,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    borderWidth: 1,
                    flexDirection: "row",
                    flexGrow: usesCompactReviewLayout ? 1 : 0,
                    gap: 6,
                    justifyContent: "center",
                    minHeight: modalControlHeight,
                    paddingHorizontal: 14,
                    paddingVertical: 7,
                    opacity: isSubmitting || !canUpdateReview ? 0.6 : 1,
                  })}
                >
                  <LineIcon
                    color={theme.statusChip.warning.text}
                    name="flag"
                    size={14}
                  />
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.statusChip.warning.text,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        fontWeight: "700",
                        lineHeight: 16,
                      },
                    ]}
                  >
                    Escalate
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityLabel="Mark questionnaire review reviewed"
                  accessibilityRole="button"
                  disabled={isSubmitting || !canMarkReviewReviewed}
                  onPress={() => onReviewAction("mark-review-reviewed", call)}
                  style={({ pressed }) => ({
                    alignItems: "center",
                    backgroundColor: pressed
                      ? theme.statusChip.success.background
                      : theme.accentStrong,
                    borderColor: theme.accentStrong,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    borderWidth: 1,
                    flexDirection: "row",
                    flexGrow: usesCompactReviewLayout ? 1 : 0,
                    gap: 6,
                    justifyContent: "center",
                    minHeight: modalControlHeight,
                    paddingHorizontal: 14,
                    paddingVertical: 7,
                    opacity:
                      isSubmitting || !canMarkReviewReviewed ? 0.6 : 1,
                  })}
                >
                  <LineIcon
                    color={theme.surface}
                    name="check"
                    size={14}
                  />
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.surface,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        fontWeight: "700",
                        lineHeight: 16,
                      },
                    ]}
                  >
                    Mark reviewed
                  </Text>
                </Pressable>
                </View>
                </View>
              </>
            ) : reviewNotes.trim() || reviewOwnerLabel ? (
              <View style={{ gap: 5, width: "100%" }}>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textTertiary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize:
                        workforceOperationalTypography.metadata.fontSize,
                      fontWeight: "800",
                      lineHeight:
                        workforceOperationalTypography.metadata.lineHeight,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  Review record
                </Text>
                {reviewNotes.trim() ? (
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: workforceOperationalTypography.body.fontSize,
                        lineHeight:
                          workforceOperationalTypography.body.lineHeight,
                      },
                    ]}
                  >
                    {reviewNotes}
                  </Text>
                ) : null}
                {reviewOwnerLabel ? (
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        lineHeight: tokens.typography.lineHeight.sm,
                      },
                    ]}
                  >
                    {`Review owner: ${reviewOwnerLabel}`}
                  </Text>
                ) : null}
              </View>
            ) : null}
          </View>
        </View>
        {discardConfirmationVisible ? (
          <ClinicianDiscardChangesPrompt
            bodyText="Your questionnaire review note has not been submitted. Leaving now discards it."
            confirmAccessibilityLabel="Discard questionnaire review note"
            confirmLabel="Discard note"
            controlHeight={modalControlHeight}
            onCancel={onCancelDiscard}
            onConfirm={onConfirmDiscard}
            stayAccessibilityLabel="Stay in questionnaire review"
            theme={theme}
            titleText="Discard review note?"
            viewportWidth={viewportWidth}
          />
        ) : null}
      </View>
    </Modal>
  );
}

function renderVoiceCallTranscriptModal(input: {
  layout: typeof designTokens.semanticLayout;
  modalState: VoiceCallTranscriptModalState;
  onClose: () => void;
  patientIdentityText?: string | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  viewportWidth: number;
}): ReactNode {
  const {
    layout,
    modalState,
    onClose,
    patientIdentityText,
    theme,
    tokens,
    viewportWidth,
  } = input;

  if (!modalState) {
    return null;
  }

  const { patientName, voiceCall } = modalState;
  const modalControlHeight =
    viewportWidth < 700
      ? workforceOperationalLayout.mobileControlHeight
      : workforceOperationalLayout.controlHeight;
  const voiceCallBadges = [
    voiceCall.questionClassText ? `Class: ${voiceCall.questionClassText}` : null,
    voiceCall.sourceText,
    voiceCall.hasClinicianHandoff ? "Staff handoff" : null,
  ].filter((badge): badge is string => typeof badge === "string" && badge.length > 0);

  return (
    <Modal
      accessibilityLabel="Phone call transcript"
      animationType="fade"
      accessibilityViewIsModal={true}
      onRequestClose={onClose}
      transparent={true}
      visible={true}
    >
      <View style={styles.patientSupportModalOverlay}>
        <View
          pointerEvents="none"
          style={styles.patientSupportModalBackdrop}
        />
        <View
          style={[
            styles.patientSupportModalPanel,
            {
              backgroundColor: theme.surface,
              borderColor: theme.borderSubtle,
              borderRadius: workforceOperationalLayout.panelRadius,
              maxWidth: 860,
            },
          ]}
        >
          <View
            style={[
              styles.patientSupportModalHeader,
              {
                backgroundColor: theme.surface,
                borderBottomColor: theme.borderSubtle,
                borderBottomWidth: 1,
                paddingHorizontal: tokens.spacing.lg,
                paddingVertical: tokens.spacing.md,
              },
            ]}
          >
            <View style={[styles.listGroup, { flex: 1, gap: 4 }]}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: tokens.typography.fontSize.lg,
                    lineHeight: tokens.typography.lineHeight.lg,
                  },
                ]}
              >
                Phone call transcript
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {`${patientName} | ${voiceCall.receivedAtText}`}
              </Text>
              <Text
                numberOfLines={2}
                style={{
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  lineHeight:
                    workforceOperationalTypography.metadata.lineHeight,
                }}
              >
                {patientIdentityText ?? "Patient identifiers unavailable"}
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Close phone call transcript"
              accessibilityRole="button"
              onPress={onClose}
              style={[
                styles.patientSupportModalCloseButton,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  height: modalControlHeight,
                  width: modalControlHeight,
                },
              ]}
            >
              <LineIcon
                color={theme.textSecondary}
                name="x"
                size={18}
              />
            </Pressable>
          </View>

          <ScrollView
            style={styles.patientSupportModalScroll}
            contentContainerStyle={[
              styles.patientSupportModalContent,
              {
                gap: tokens.spacing.md,
                padding: tokens.spacing.lg,
              },
            ]}
          >
            <View style={[styles.flagRow, { gap: tokens.spacing.xs }]}>
              {voiceCallBadges.map((badgeText) => (
                <View
                  key={badgeText}
                  style={[
                    styles.inlinePill,
                    {
                      alignItems: "center",
                      backgroundColor: theme.surface,
                      borderColor: theme.borderSubtle,
                      borderRadius: 999,
                      justifyContent: "center",
                      minHeight: 32,
                      paddingHorizontal: tokens.spacing.sm,
                      paddingVertical: 4,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                      },
                    ]}
                  >
                    {badgeText}
                  </Text>
                </View>
              ))}
            </View>

            {voiceCall.transcriptTurns.map((turn) => {
              const isPatientTurn = turn.speakerKind === "patient";

              return (
                <View
                  key={turn.id}
                  style={[
                    styles.detailSectionCard,
                    {
                      backgroundColor: isPatientTurn
                        ? theme.accentSubtle
                        : theme.backgroundSubtle,
                      borderColor: isPatientTurn
                        ? theme.accentStrong
                        : theme.borderSubtle,
                      borderRadius: workforceOperationalLayout.panelRadius,
                      gap: tokens.spacing.xs,
                      padding: tokens.spacing.md,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.patientRowHeader,
                      {
                        alignItems: "center",
                        gap: tokens.spacing.sm,
                        justifyContent: "space-between",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: isPatientTurn ? theme.accentStrong : theme.info,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      {turn.speakerLabelText}
                    </Text>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                        },
                      ]}
                    >
                      {turn.timestampText}
                    </Text>
                  </View>
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                        lineHeight: tokens.typography.lineHeight.md,
                      },
                    ]}
                  >
                    {turn.bodyText}
                  </Text>
                </View>
              );
            })}
          </ScrollView>

        </View>
      </View>
    </Modal>
  );
}

function renderClinicianOutboundCallConfirmModal(input: {
  isSubmitting: boolean;
  layout: typeof designTokens.semanticLayout;
  modalState: ClinicianOutboundCallConfirmModalState;
  onClose: () => void;
  onConfirm: (modalState: NonNullable<ClinicianOutboundCallConfirmModalState>) => void;
  patientIdentityText?: string | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  viewportWidth: number;
}): ReactNode {
  const {
    isSubmitting,
    layout,
    modalState,
    onClose,
    onConfirm,
    patientIdentityText,
    theme,
    tokens,
    viewportWidth,
  } = input;

  if (!modalState) {
    return null;
  }

  const isRepeatRound = modalState.action === "start-new-round";
  const modalControlHeight =
    viewportWidth < 700
      ? workforceOperationalLayout.mobileControlHeight
      : workforceOperationalLayout.controlHeight;
  const titleText = isRepeatRound
    ? "Start another questionnaire round?"
    : modalState.action === "queue-resume-now"
      ? "Queue questionnaire resume?"
      : "Start questionnaire call?";
  const confirmLabel = isSubmitting
    ? "Starting..."
    : isRepeatRound
      ? "Start another round"
      : modalState.action === "queue-resume-now"
        ? "Queue resume"
        : "Start call";
  const summaryText = isRepeatRound
    ? `${modalState.patientName} already has questionnaire call history for ${modalState.call.campaignDisplayName}. This will create a supervised repeat round for the same campaign.`
    : `${modalState.patientName} will be called for ${modalState.call.campaignDisplayName}.`;

  return (
    <Modal
      accessibilityLabel={titleText}
      animationType="fade"
      accessibilityViewIsModal={true}
      onRequestClose={isSubmitting ? () => {} : onClose}
      transparent={true}
      visible={true}
    >
      <View style={styles.patientSupportModalOverlay}>
        <View
          pointerEvents="none"
          style={styles.patientSupportModalBackdrop}
        />
        <View
          style={[
            styles.patientSupportModalPanel,
            {
              backgroundColor: theme.surface,
              borderColor: theme.borderSubtle,
              borderRadius: workforceOperationalLayout.panelRadius,
              maxWidth: 560,
            },
          ]}
        >
          <View
            style={[
              styles.patientSupportModalHeader,
              {
                backgroundColor: theme.surface,
                borderBottomColor: theme.borderSubtle,
                borderBottomWidth: 1,
                paddingHorizontal: tokens.spacing.lg,
                paddingVertical: tokens.spacing.md,
              },
            ]}
          >
            <View style={[styles.listGroup, { flex: 1, gap: 4 }]}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: tokens.typography.fontSize.lg,
                    lineHeight: tokens.typography.lineHeight.lg,
                  },
                ]}
              >
                {titleText}
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {modalState.call.questionnaireDisplayName}
              </Text>
              <Text
                numberOfLines={2}
                style={{
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  lineHeight:
                    workforceOperationalTypography.metadata.lineHeight,
                }}
              >
                {patientIdentityText ?? "Patient identifiers unavailable"}
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Close questionnaire call confirmation"
              accessibilityRole="button"
              disabled={isSubmitting}
              onPress={onClose}
              style={[
                styles.patientSupportModalCloseButton,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  height: modalControlHeight,
                  opacity: isSubmitting ? 0.55 : 1,
                  width: modalControlHeight,
                },
              ]}
            >
              <LineIcon
                color={theme.textSecondary}
                name="x"
                size={18}
              />
            </Pressable>
          </View>

          <ScrollView
            style={[
              styles.patientSupportModalScroll,
              { flexShrink: 1, minHeight: 0 },
            ]}
            contentContainerStyle={[
              styles.patientSupportModalContent,
              {
                gap: tokens.spacing.md,
                padding: tokens.spacing.lg,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 15,
                  lineHeight: 22,
                },
              ]}
            >
              {summaryText}
            </Text>

            {modalState.errorText ? (
              <View
                accessibilityLiveRegion="polite"
                accessibilityRole="alert"
                style={{
                  backgroundColor: theme.backgroundSubtle,
                  borderColor: theme.danger,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  borderWidth: 1,
                  padding: tokens.spacing.sm,
                }}
              >
                <Text
                  style={{
                    color: theme.danger,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 13,
                    lineHeight: 18,
                  }}
                >
                  {modalState.errorText}
                </Text>
              </View>
            ) : null}

            <View
              style={{
                backgroundColor: theme.backgroundSubtle,
                borderColor: theme.borderSubtle,
                borderRadius: workforceOperationalLayout.controlRadius,
                borderWidth: 1,
                gap: 6,
                padding: 12,
              }}
            >
              {[
                {
                  label: "Phone",
                  value: modalState.call.phoneNumberLast4
                    ? `Ending ${modalState.call.phoneNumberLast4}`
                    : "Not available",
                },
                {
                  label: "Previous attempts",
                  value: String(Math.max(0, modalState.call.attemptCount)),
                },
                {
                  label: "Call window",
                  value: modalState.call.outsideCallWindow
                    ? "Outside configured window"
                    : "Within configured window",
                },
              ].map((row) => (
                <View
                  key={row.label}
                  style={{
                    alignItems: "center",
                    flexDirection: "row",
                    gap: 12,
                    justifyContent: "space-between",
                  }}
                >
                  <Text
                    style={{
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize:
                        workforceOperationalTypography.metadata.fontSize,
                      lineHeight:
                        workforceOperationalTypography.metadata.lineHeight,
                    }}
                  >
                    {row.label}
                  </Text>
                  <Text
                    style={{
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.table.fontSize,
                      fontWeight: "700",
                      lineHeight:
                        workforceOperationalTypography.table.lineHeight,
                      textAlign: "right",
                    }}
                  >
                    {row.value}
                  </Text>
                </View>
              ))}
            </View>

            <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
              {modalState.warnings.map((warningText) => (
                <View
                  key={warningText}
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "flex-start",
                      backgroundColor: theme.backgroundSubtle,
                      borderColor: theme.borderSubtle,
                      borderRadius: workforceOperationalLayout.controlRadius,
                      borderWidth: 1,
                      gap: tokens.spacing.sm,
                      padding: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.detailHeaderAvatar,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderSubtle,
                        borderRadius: 9,
                        height: 32,
                        width: 32,
                      },
                    ]}
                  >
                    <LineIcon
                      color={theme.warning}
                      name="flag"
                      size={16}
                      strokeWidth={2.3}
                    />
                  </View>
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 13,
                        lineHeight: 18,
                      },
                    ]}
                  >
                    {warningText}
                  </Text>
                </View>
              ))}
            </View>
          </ScrollView>

          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "center",
                backgroundColor: theme.surface,
                borderTopColor: theme.borderSubtle,
                borderTopWidth: 1,
                gap: tokens.spacing.sm,
                justifyContent: "flex-end",
                paddingHorizontal: tokens.spacing.lg,
                paddingVertical: tokens.spacing.sm,
              },
            ]}
          >
            <Pressable
              accessibilityLabel="Cancel questionnaire call"
              accessibilityRole="button"
              disabled={isSubmitting}
              onPress={onClose}
              style={({ pressed }) => [
                styles.patientSupportSecondaryButton,
                {
                  backgroundColor: pressed ? theme.backgroundSubtle : theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  minHeight: modalControlHeight,
                  opacity: isSubmitting ? 0.55 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.badgeText,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.body.fontSize,
                    lineHeight: workforceOperationalTypography.body.lineHeight,
                  },
                ]}
              >
                Cancel
              </Text>
            </Pressable>
            <Pressable
              accessibilityLabel={confirmLabel}
              accessibilityRole="button"
              disabled={isSubmitting}
              onPress={() => onConfirm(modalState)}
              style={({ pressed }) => [
                styles.patientSupportPrimaryButton,
                {
                  backgroundColor: pressed ? theme.accentStrong : theme.accent,
                  borderColor: "transparent",
                  borderRadius: workforceOperationalLayout.controlRadius,
                  minHeight: modalControlHeight,
                  opacity: isSubmitting ? 0.7 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.badgeText,
                  {
                    color: theme.headerText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.body.fontSize,
                    lineHeight: workforceOperationalTypography.body.lineHeight,
                  },
                ]}
              >
                {confirmLabel}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function formatPatientWorkspaceVoiceTimestamp(value: string): string {
  const trimmedValue = value.trim();

  return trimmedValue.length ? trimmedValue.slice(0, 16).replace("T", " ") : "Time unavailable";
}

function buildPatientWorkspaceVoiceCallTranscriptText(
  voiceCall: PatientWorkspaceVoiceCallSummary,
): string {
  const transcriptText = voiceCall.transcriptTurns
    .map(
      (turn) =>
        `${formatPatientWorkspaceVoiceTimestamp(turn.sentAt)} ${turn.speakerLabel}: ${turn.bodyText}`,
    )
    .join("\n");

  return [
    `Phone call ${formatPatientWorkspaceVoiceTimestamp(voiceCall.receivedAt)}`,
    voiceCall.questionClass ? `Class: ${voiceCall.questionClass}` : null,
    voiceCall.previewText.trim().length ? `Preview: ${voiceCall.previewText}` : null,
    transcriptText,
  ]
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .join("\n");
}

function buildPatientWorkspaceVoiceCallReviewPrompt(
  voiceCall: PatientWorkspaceVoiceCallSummary,
): string {
  return [
    "Please review this phone call transcript with me.",
    "Summarize the key points, tell me whether there are any next steps, and suggest what I should ask my care team.",
    buildPatientWorkspaceVoiceCallTranscriptText(voiceCall),
  ].join("\n\n");
}

function buildPatientWorkspaceDocumentReviewPrompt(
  document: PatientWorkspaceDocumentSummary,
): string {
  const availabilityText =
    document.availability === "download-ready"
      ? "Download-ready"
      : "Metadata-only";
  const noteText = (document.notes ?? [])
    .map((note) => note.bodyText.trim())
    .filter((bodyText) => bodyText.length > 0)
    .slice(0, 3)
    .join("\n");

  return [
    `Please review this document with me: ${document.label}.`,
    [
      `Status: ${document.statusLabel}.`,
      `Availability: ${availabilityText}.`,
      `Source: ${document.createdByLabel}.`,
      `Created: ${document.createdAt.slice(0, 16).replace("T", " ")}.`,
    ].join(" "),
    document.summaryText.trim().length
      ? `Summary: ${document.summaryText.trim()}`
      : null,
    noteText.length ? `Notes:\n${noteText}` : null,
    "Summarize what matters, call out any follow-up questions I should ask, and tell me if anything needs the care team's attention.",
  ]
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .join("\n\n");
}

function patientWorkspaceVoiceCallMatchesQuery(
  voiceCall: PatientWorkspaceVoiceCallSummary,
  query: string,
): boolean {
  const normalizedQuery = query.trim().toLowerCase();

  if (normalizedQuery.length === 0) {
    return true;
  }

  return [
    voiceCall.previewText,
    voiceCall.questionClass,
    voiceCall.sourceLabel,
    voiceCall.receivedAt,
    ...voiceCall.transcriptTurns.flatMap((turn) => [
      turn.speakerLabel,
      turn.bodyText,
      turn.sentAt,
    ]),
  ]
    .filter((part): part is string => typeof part === "string")
    .join(" ")
    .toLowerCase()
    .includes(normalizedQuery);
}

function renderPatientWorkspaceCallsSurface(input: {
  layout: typeof designTokens.semanticLayout;
  noticeText: string | null;
  onCopyTranscript: (voiceCall: PatientWorkspaceVoiceCallSummary) => void;
  onNavigateTab: (value: PatientWorkspaceTabId) => void;
  onRequestCallback: () => void;
  onReviewCall: (voiceCall: PatientWorkspaceVoiceCallSummary) => void;
  onSearchQueryChange: (value: string) => void;
  searchQuery: string;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}): ReactNode {
  const {
    layout,
    noticeText,
    onCopyTranscript,
    onNavigateTab,
    onRequestCallback,
    onReviewCall,
    onSearchQueryChange,
    searchQuery,
    summary,
    theme,
    tokens,
  } = input;
  const voiceCalls = summary?.voiceCallSummaries ?? [];
  const filteredVoiceCalls = voiceCalls.filter((voiceCall) =>
    patientWorkspaceVoiceCallMatchesQuery(voiceCall, searchQuery),
  );

  return (
    <View style={[styles.listGroup, { gap: tokens.spacing.md }]}>
      <View style={[styles.sectionChromeCopy, { gap: tokens.spacing.xs }]}>
        <Text
          style={[
            styles.sectionTitle,
            {
              color: theme.textPrimary,
              fontFamily: layout.displayFontFamily,
              fontSize: tokens.typography.fontSize.xl,
              lineHeight: tokens.typography.lineHeight.xl,
            },
          ]}
        >
          Phone calls
        </Text>
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          Voice assistant call transcripts and AI answers.
        </Text>
      </View>

      {noticeText ? (
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: theme.info,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          {noticeText}
        </Text>
      ) : null}

      <View
        style={[
          styles.legacyComposerInputSurface,
          {
            backgroundColor: theme.surface,
            borderColor: theme.borderSubtle,
            borderRadius: 18,
            flexGrow: 0,
            flexShrink: 0,
            gap: 12,
            marginBottom: 10,
            minHeight: 126,
            overflow: "hidden",
            padding: 12,
            shadowColor: theme.accentStrong,
            shadowOffset: { height: 6, width: 0 },
            shadowOpacity: 0.06,
            shadowRadius: 14,
          },
        ]}
      >
        <View
          style={[
            styles.patientRowFooter,
            {
              alignItems: "center",
              backgroundColor: theme.backgroundSubtle,
              borderColor: theme.borderSubtle,
              borderRadius: 14,
              borderWidth: 1,
              flexWrap: "nowrap",
              gap: 8,
              minHeight: 46,
              paddingHorizontal: 12,
            },
          ]}
        >
          <View
            style={{
              alignItems: "center",
              backgroundColor: theme.surface,
              borderRadius: 999,
              height: 26,
              justifyContent: "center",
              width: 26,
            }}
          >
            <LineIcon
              color={theme.textSecondary}
              name="search"
              size={14}
              strokeWidth={2.2}
            />
          </View>
          <TextInput
            onChangeText={onSearchQueryChange}
            placeholder="Search calls or transcripts"
            placeholderTextColor={theme.textTertiary}
            style={[
              styles.searchInput,
              {
                color: theme.textPrimary,
                flex: 1,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                minHeight: 36,
              },
            ]}
            value={searchQuery}
          />
        </View>
        <Pressable
          accessibilityLabel="Request a callback"
          accessibilityRole="button"
          onPress={onRequestCallback}
          style={[
            styles.inlinePill,
            {
              alignItems: "center",
              alignSelf: "center",
              backgroundColor: theme.accentSubtle,
              borderColor: theme.accentStrong,
              borderRadius: 14,
              flexDirection: "row",
              justifyContent: "center",
              minHeight: 42,
              paddingHorizontal: tokens.spacing.md,
              paddingVertical: 0,
              width: "88%",
            },
          ]}
        >
          <View
            style={{
              alignItems: "center",
              height: 18,
              justifyContent: "center",
              marginRight: 7,
              width: 18,
            }}
          >
            <LineIcon
              color={theme.accentStrong}
              name="phone"
              size={14}
              strokeWidth={2.35}
            />
          </View>
          <Text
            style={[
              styles.navigationText,
              {
                color: theme.accentStrong,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: "700",
                lineHeight: tokens.typography.lineHeight.sm,
                textAlign: "center",
              },
            ]}
          >
            Request callback
          </Text>
          <View
            style={{
              height: 18,
              marginLeft: 7,
              width: 18,
            }}
          />
        </Pressable>
      </View>

      {filteredVoiceCalls.length > 0 ? (
        filteredVoiceCalls.map((voiceCall, voiceCallIndex) => (
          <PatientWorkspaceVoiceCallCard
            key={voiceCall.callId}
            defaultOpen={voiceCallIndex === 0}
            layout={layout}
            onCopyTranscript={onCopyTranscript}
            onReviewCall={onReviewCall}
            theme={theme}
            tokens={tokens}
            voiceCall={voiceCall}
          />
        ))
      ) : (
        <View
          style={[
            styles.detailSectionCard,
            {
              backgroundColor: theme.surface,
              borderColor: theme.borderSubtle,
              borderRadius: layout.controlRadius,
              gap: tokens.spacing.md,
              padding: 12,
            },
          ]}
        >
          <Text
            style={[
              styles.sectionTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.md,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {voiceCalls.length > 0 ? "No calls match your search" : "No phone calls yet"}
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {voiceCalls.length > 0
              ? "Try another search term or clear the transcript search."
              : "Voice assistant call transcripts will appear here when available."}
          </Text>
          <Pressable
            accessibilityRole="button"
            onPress={() => onNavigateTab("messages")}
            style={[
              styles.patientNavButton,
              {
                alignItems: "center",
                alignSelf: "center",
                backgroundColor: theme.accentSubtle,
                borderColor: theme.accentStrong,
                borderRadius: 14,
                flexDirection: "row",
                justifyContent: "center",
                minHeight: 42,
                paddingHorizontal: tokens.spacing.md,
                paddingVertical: 0,
                width: "88%",
              },
            ]}
          >
            <View
              style={{
                alignItems: "center",
                height: 18,
                justifyContent: "center",
                marginRight: 7,
                width: 18,
              }}
            >
              <LineIcon
                color={theme.accentStrong}
                name="message"
                size={14}
                strokeWidth={2.35}
              />
            </View>
            <Text
              style={[
                styles.navigationText,
                {
                  color: theme.accentStrong,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: "700",
                  lineHeight: tokens.typography.lineHeight.sm,
                  textAlign: "center",
                },
              ]}
            >
              Open messages
            </Text>
            <View
              style={{
                height: 18,
                marginLeft: 7,
                width: 18,
              }}
            />
          </Pressable>
        </View>
      )}
    </View>
  );
}

function PatientWorkspaceVoiceCallCard(props: {
  defaultOpen: boolean;
  layout: typeof designTokens.semanticLayout;
  onCopyTranscript: (voiceCall: PatientWorkspaceVoiceCallSummary) => void;
  onReviewCall: (voiceCall: PatientWorkspaceVoiceCallSummary) => void;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  voiceCall: PatientWorkspaceVoiceCallSummary;
}) {
  const {
    defaultOpen,
    layout,
    onCopyTranscript,
    onReviewCall,
    theme,
    tokens,
    voiceCall,
  } = props;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const titleText = voiceCall.questionClass
    ? `${voiceCall.questionClass
        .charAt(0)
        .toUpperCase()}${voiceCall.questionClass.slice(1)} call`
    : "Voice assistant call";
  const metaText = [
    formatPatientWorkspaceVoiceTimestamp(voiceCall.receivedAt),
    `${voiceCall.turnCount} ${voiceCall.turnCount === 1 ? "turn" : "turns"}`,
  ]
    .filter((part) => part.trim().length > 0)
    .join(" · ");

  const renderCallActionButton = (
    label: string,
    iconName: LineIconName,
    onPress: () => void,
  ) => (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.detailHeaderAvatar,
        {
          backgroundColor: pressed ? theme.backgroundSubtle : theme.surface,
          borderColor: theme.accent,
          borderRadius: 9,
          height: 34,
          width: 34,
        },
      ]}
    >
      <LineIcon
        color={theme.accent}
        name={iconName}
        size={15}
        strokeWidth={2.2}
      />
    </Pressable>
  );

  return (
    <View
      key={voiceCall.callId}
      style={[
        styles.detailSectionCard,
        {
          backgroundColor: theme.surface,
          borderColor: theme.borderSubtle,
          borderRadius: 16,
          gap: 0,
          overflow: "hidden",
          padding: 0,
        },
      ]}
    >
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            borderBottomColor: theme.borderSubtle,
            borderBottomWidth: isOpen ? 1 : 0,
            gap: tokens.spacing.sm,
            paddingHorizontal: tokens.spacing.md,
            paddingVertical: tokens.spacing.sm,
          },
        ]}
      >
        <View
          style={{
            alignItems: "center",
            backgroundColor: theme.accentSubtle,
            borderRadius: 11,
            height: 40,
            justifyContent: "center",
            width: 40,
          }}
        >
          <Text style={{ fontSize: 18 }}>{String.fromCodePoint(0x1f4de)}</Text>
        </View>
        <Pressable
          accessibilityLabel={`${titleText} — ${isOpen ? "collapse" : "expand"} transcript`}
          accessibilityRole="button"
          onPress={() => setIsOpen((value) => !value)}
          style={{ flex: 1, gap: 4, minWidth: 0 }}
        >
          <Text
            numberOfLines={1}
            style={[
              styles.sectionTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: 15,
                fontWeight: "800",
                lineHeight: 18,
              },
            ]}
          >
            {titleText}
          </Text>
          <View
            style={[
              styles.patientRowHeader,
              { alignItems: "center", flexWrap: "wrap", gap: 8 },
            ]}
          >
            <View
              style={{
                alignItems: "center",
                backgroundColor: theme.statusChip.success.background,
                borderRadius: 999,
                flexDirection: "row",
                gap: 5,
                paddingHorizontal: 9,
                paddingVertical: 3,
              }}
            >
              <View
                style={{
                  backgroundColor: theme.statusChip.success.text,
                  borderRadius: 999,
                  height: 6,
                  width: 6,
                }}
              />
              <Text
                style={[
                  styles.pillValueText,
                  {
                    color: theme.statusChip.success.text,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    fontWeight: "700",
                  },
                ]}
              >
                Recorded
              </Text>
            </View>
            {voiceCall.sourceLabel ? (
              <View
                style={{
                  alignItems: "center",
                  backgroundColor: theme.backgroundSubtle,
                  borderRadius: 999,
                  paddingHorizontal: 9,
                  paddingVertical: 3,
                }}
              >
                <Text
                  style={[
                    styles.pillValueText,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 10,
                      fontWeight: "700",
                    },
                  ]}
                >
                  {voiceCall.sourceLabel}
                </Text>
              </View>
            ) : null}
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textTertiary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                },
              ]}
            >
              {metaText}
            </Text>
          </View>
        </Pressable>
        <View
          style={[
            styles.patientRowHeader,
            { alignItems: "center", flexShrink: 0, gap: 8 },
          ]}
        >
          {renderCallActionButton("Copy transcript", "document", () =>
            onCopyTranscript(voiceCall),
          )}
          {renderCallActionButton("Review with AI", "bot", () =>
            onReviewCall(voiceCall),
          )}
          <Pressable
            accessibilityLabel={isOpen ? "Collapse transcript" : "Expand transcript"}
            accessibilityRole="button"
            onPress={() => setIsOpen((value) => !value)}
            style={{
              alignItems: "center",
              height: 34,
              justifyContent: "center",
              transform: [{ rotate: isOpen ? "90deg" : "0deg" }],
              width: 30,
            }}
          >
            <LineIcon
              color={theme.textTertiary}
              name="chevron-right"
              size={18}
              strokeWidth={2.2}
            />
          </Pressable>
        </View>
      </View>

      {isOpen ? (
        <View
          style={{
            backgroundColor: theme.background,
            gap: tokens.spacing.sm,
            padding: tokens.spacing.md,
          }}
        >
          {voiceCall.transcriptTurns.map((turn) => {
          const isPatientTurn = turn.speakerKind === "patient";

          return (
            <View
              key={turn.id}
              style={{
                alignItems: "flex-start",
                flexDirection: isPatientTurn ? "row-reverse" : "row",
                gap: 8,
              }}
            >
              <View
                style={{
                  alignItems: "center",
                  backgroundColor: isPatientTurn
                    ? theme.textTertiary
                    : theme.accentStrong,
                  borderRadius: 999,
                  height: 28,
                  justifyContent: "center",
                  width: 28,
                }}
              >
                <LineIcon
                  color={theme.surface}
                  name={isPatientTurn ? "user" : "bot"}
                  size={14}
                  strokeWidth={2.2}
                />
              </View>
              <View
                style={{
                  backgroundColor: isPatientTurn
                    ? theme.accentStrong
                    : theme.surface,
                  borderColor: isPatientTurn
                    ? "transparent"
                    : theme.borderStrong,
                  borderRadius: 14,
                  borderTopLeftRadius: isPatientTurn ? 14 : 4,
                  borderTopRightRadius: isPatientTurn ? 4 : 14,
                  borderWidth: isPatientTurn ? 0 : 1,
                  gap: 4,
                  maxWidth: "76%",
                  paddingHorizontal: 13,
                  paddingVertical: 10,
                }}
              >
                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "center",
                      gap: 10,
                      justifyContent: "space-between",
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: isPatientTurn
                          ? theme.headerTextMuted
                          : theme.accentStrong,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    {turn.speakerLabel}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: isPatientTurn
                          ? theme.headerTextMuted
                          : theme.textTertiary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                      },
                    ]}
                  >
                    {formatPatientWorkspaceVoiceTimestamp(turn.sentAt)}
                  </Text>
                </View>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: isPatientTurn
                        ? theme.headerText
                        : theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 13,
                      lineHeight: 18,
                    },
                  ]}
                >
                  {turn.bodyText}
                </Text>
              </View>
            </View>
          );
        })}
        </View>
      ) : null}
    </View>
  );
}

function renderPatientWorkspaceMessagesSurface(input: {
  conversationMessages: ChatMessageViewModel[];
  draftValue: string;
  isMessageCommandSubmitting: boolean;
  isWideLayout: boolean;
  layout: typeof designTokens.semanticLayout;
  onConversationContentSizeChange: () => void;
  onNavigateTab: (value: PatientWorkspaceTabId) => void;
  noticeText: string | null;
  onDraftChange: (value: string) => void;
  onNoticeChange: (value: string | null) => void;
  onOpenSupportModal: (input: PatientSupportModalState) => void;
  readAloudControls: PatientWorkspaceReadAloudControls;
  onSendMessage: (input: PatientMessageCommandInput) => Promise<boolean>;
  patientConversationScrollRef: RefObject<ScrollView | null>;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  voiceInputControls: PatientWorkspaceVoiceInputControls;
}) {
  const {
    conversationMessages,
    draftValue,
    isMessageCommandSubmitting,
    isWideLayout,
    layout,
    onConversationContentSizeChange,
    onNavigateTab,
    noticeText,
    onDraftChange,
    onNoticeChange,
    onOpenSupportModal,
    readAloudControls,
    onSendMessage,
    patientConversationScrollRef,
    summary,
    theme,
    tokens,
    voiceInputControls,
  } = input;
  const agentSummaryItems = [
    {
      id: "agent-conversation",
      labelText: "Conversation",
      valueText: String(conversationMessages.length),
      toneColor: theme.info,
    },
    {
      id: "agent-unread",
      labelText: "Unread",
      valueText: String(summary?.unreadMessageCount ?? 0),
      toneColor: theme.warning,
    },
    {
      id: "agent-documents",
      labelText: "Pending docs",
      valueText: String(summary?.pendingDocumentCount ?? 0),
      toneColor: theme.accentStrong,
    },
    {
      id: "agent-touchpoint",
      labelText: "Next touchpoint",
      valueText: summary?.nextAppointmentText?.trim().length
        ? summary.nextAppointmentText
        : "Not scheduled",
      toneColor: theme.textPrimary,
    },
  ];
  const promptMacroItems = [
    {
      id: "macro-medication",
      labelText: "Medication question",
      draftText: "Can you remind me how and when to take my medications?",
    },
    {
      id: "macro-documents",
      labelText: "Document question",
      draftText: "Which records are ready for me to review before my next step?",
    },
    {
      id: "macro-appointment",
      labelText: "Appointment prep",
      draftText: "How should I prepare for my upcoming appointment?",
    },
    {
      id: "macro-callback",
      labelText: "Request callback",
      draftText:
        "Please ask my care team to call me back when they are available.",
    },
  ];
  const latestAgentMessage =
    findLatestReadablePatientAgentMessage(conversationMessages);

  return (
    <View
      style={[
        styles.surfaceCard,
        {
          backgroundColor: theme.surfaceElevated,
          borderColor: theme.borderStrong,
          borderRadius: layout.cardRadius,
          gap: tokens.spacing.md,
          padding: tokens.spacing.lg,
          width: "100%",
        },
      ]}
    >
      {renderLegacySurfaceHeader({
        eyebrowText: "Agent",
        title: `${patientWorkspaceBrand.assistantName} conversation`,
        subtitle:
          "Ask questions, read replies aloud, and send feedback to the care team.",
        layout,
        theme,
        tokens,
      })}

      <View
        style={[
          styles.workspaceGrid,
          {
            flexDirection: isWideLayout ? "row" : "column",
            gap: tokens.spacing.md,
          },
        ]}
      >
        <View
          style={[
            styles.legacyChatAvatarRail,
            {
              backgroundColor: theme.backgroundSubtle,
              borderColor: theme.borderSubtle,
              borderRadius: layout.cardRadius,
              gap: tokens.spacing.md,
              maxWidth: isWideLayout ? 260 : undefined,
              padding: tokens.spacing.lg,
              width: isWideLayout ? 260 : "100%",
            },
          ]}
        >
          <View
            style={[
              styles.patientAvatar,
              {
                alignSelf: "center",
                backgroundColor: theme.headerBand,
                borderColor: theme.headerBand,
                borderRadius: 44,
                height: 88,
                width: 88,
              },
            ]}
          >
            <Text
              style={[
                styles.patientAvatarText,
                {
                  color: theme.headerText,
                  fontFamily: layout.displayFontFamily,
                  fontSize: tokens.typography.fontSize.xl,
                },
              ]}
            >
              AC
            </Text>
          </View>

          <View
            style={[
              styles.workspaceGrid,
              {
                gap: tokens.spacing.xs,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: tokens.typography.fontSize.xl,
                  lineHeight: tokens.typography.lineHeight.xl,
                  textAlign: "center",
                },
              ]}
            >
              {patientWorkspaceBrand.assistantName}
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                  textAlign: "center",
                },
              ]}
            >
              {summary?.displayName
                ? `Supporting ${summary.displayName} with reminders, questions, and care-plan follow-up.`
                : "Supporting your reminders, questions, and care-plan follow-up."}
            </Text>
          </View>

          <View
            style={[
              styles.patientMetaGrid,
              {
                gap: tokens.spacing.sm,
              },
            ]}
          >
            {agentSummaryItems.map((item) => (
              <View
                key={item.id}
                style={[
                  styles.patientMetaCard,
                  {
                    backgroundColor: theme.surface,
                    borderColor: theme.borderSubtle,
                    borderRadius: layout.controlRadius,
                    gap: 2,
                    minHeight: item.id === "agent-touchpoint" ? 86 : undefined,
                    padding: tokens.spacing.sm,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: item.toneColor,
                      fontFamily: layout.displayFontFamily,
                      fontSize:
                        item.id === "agent-touchpoint"
                          ? tokens.typography.fontSize.sm
                          : tokens.typography.fontSize.md,
                    },
                  ]}
                >
                  {item.valueText}
                </Text>
              </View>
            ))}
          </View>

          <View
            style={[
              styles.flagRow,
              {
                gap: tokens.spacing.xs,
                justifyContent: "center",
              },
            ]}
          >
            {renderWorkspaceFilterChip({
              isSelected: readAloudControls.isAutoReadAloudEnabled,
              labelText: "Read aloud",
              onPress: readAloudControls.toggleAutoReadAloud,
              theme,
              tokens,
            })}
            {renderWorkspaceFilterChip({
              isSelected: false,
              labelText: "Helpful",
              onPress: () =>
                onOpenSupportModal({
                  kind: "chat-feedback",
                  message: latestAgentMessage,
                  sentiment: "helpful",
                  source: "patient-agent:helpful",
                }),
              theme,
              tokens,
            })}
            {renderWorkspaceFilterChip({
              isSelected: false,
              labelText: "Needs work",
              onPress: () =>
                onOpenSupportModal({
                  kind: "chat-feedback",
                  message: latestAgentMessage,
                  sentiment: "needs-work",
                  source: "patient-agent:needs-work",
                }),
              theme,
              tokens,
            })}
          </View>

          <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
            {renderWorkbenchActionButton({
              disabled: false,
              label: "Open documents",
              onPress: () => onNavigateTab("documents"),
              size: "compact",
              theme,
              tone: "accent",
              tokens,
            })}
            {renderWorkbenchActionButton({
              disabled: false,
              label: "Open appointments",
              onPress: () => onNavigateTab("appointments"),
              size: "compact",
              theme,
              tone: "success",
              tokens,
            })}
          </View>
        </View>

        <View
          style={[
            styles.workspaceGrid,
            {
              flex: 1,
              gap: tokens.spacing.md,
            },
          ]}
        >
          {noticeText ? (
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.info,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {noticeText}
            </Text>
          ) : null}

          <ScrollView
            ref={patientConversationScrollRef}
            onContentSizeChange={onConversationContentSizeChange}
            showsVerticalScrollIndicator={false}
            style={styles.patientWorkspaceMessageScroll}
            contentContainerStyle={[
              styles.listGroup,
              {
                gap: tokens.spacing.sm,
              },
            ]}
          >
            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "center",
                  gap: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    textTransform: "uppercase",
                  },
                ]}
              >
                Conversation lane
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                  },
                ]}
              >
                {summary?.coordinatorLabel?.trim().length
                  ? `Care team ${summary.coordinatorLabel}`
                  : "Care team available"}
              </Text>
            </View>

            {conversationMessages.length > 0 ? (
              conversationMessages.map((message, index) => {
                const deliveryLabelKey = resolveMessageDeliveryLabelKey(
                  message.deliveryState,
                );
                const messageLeadText =
                  message.senderKind === "patient"
                    ? "YOU"
                    : message.senderKind === "clinician"
                      ? "CARE"
                      : "NOTE";
                const messageLaneText =
                  message.senderKind === "patient"
                    ? "Patient update"
                    : message.senderKind === "clinician"
                      ? `${patientWorkspaceBrand.assistantName} reply`
                      : "System note";

                return (
                  <View
                    key={message.id}
                    style={[
                      {
                        alignSelf:
                          message.alignment === "right"
                            ? "flex-end"
                            : message.alignment === "center"
                              ? "center"
                              : "flex-start",
                        maxWidth: "94%",
                        width:
                          message.alignment === "center" ? "100%" : undefined,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.detailBodyCard,
                        {
                          backgroundColor: getMessageBubbleBackground(
                            message,
                            theme,
                            "patient",
                          ),
                          borderColor: getMessageBubbleBorderColor(
                            message,
                            theme,
                            "patient",
                          ),
                          borderRadius: layout.controlRadius,
                          gap: tokens.spacing.sm,
                          padding: tokens.spacing.md,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "stretch",
                            gap: tokens.spacing.sm,
                          },
                        ]}
                      >
                        <View
                          style={[
                            styles.detailDocumentLeadColumn,
                            {
                              backgroundColor: theme.surface,
                              borderColor: theme.borderSubtle,
                              borderRadius: layout.controlRadius,
                              gap: 2,
                              minWidth: 60,
                              paddingHorizontal: tokens.spacing.xs,
                              paddingVertical: tokens.spacing.sm,
                            },
                          ]}
                        >
                          <Text
                            style={[
                              styles.detailDocumentGlyphText,
                              {
                                color: getMessageSenderTextColor(
                                  message,
                                  theme,
                                  "patient",
                                ),
                                fontFamily: layout.displayFontFamily,
                              },
                            ]}
                          >
                            {messageLeadText}
                          </Text>
                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: tokens.typography.fontSize.xs,
                                textAlign: "center",
                              },
                            ]}
                          >
                            {String(index + 1).padStart(2, "0")}
                          </Text>
                        </View>

                        <View
                          style={[
                            styles.patientIdentityCopy,
                            {
                              flex: 1,
                              gap: tokens.spacing.xs,
                            },
                          ]}
                        >
                          <View
                            style={[
                              styles.headerRow,
                              {
                                alignItems: "center",
                                justifyContent: "space-between",
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.navigationText,
                                {
                                  color: getMessageSenderTextColor(
                                    message,
                                    theme,
                                    "patient",
                                  ),
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: tokens.typography.fontSize.xs,
                                },
                              ]}
                            >
                              {message.senderText}
                            </Text>
                            <Text
                              style={[
                                styles.metricLabel,
                                {
                                  color: getMessageTimestampTextColor(
                                    message,
                                    theme,
                                    "patient",
                                  ),
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: tokens.typography.fontSize.xs,
                                },
                              ]}
                            >
                              {message.timestampText}
                            </Text>
                          </View>

                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: tokens.typography.fontSize.xs,
                                textTransform: "uppercase",
                              },
                            ]}
                          >
                            {messageLaneText}
                          </Text>

                          <View
                            style={[
                              styles.messageBodyPanel,
                              {
                                backgroundColor: getMessageBodyPanelBackground(
                                  message,
                                  theme,
                                  "patient",
                                ),
                                borderColor: getMessageBodyPanelBorderColor(
                                  message,
                                  theme,
                                  "patient",
                                ),
                                borderRadius: layout.controlRadius,
                                padding: tokens.spacing.sm,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.sectionSubtitle,
                                {
                                  color: getMessageTextColor(
                                    message,
                                    theme,
                                    "patient",
                                  ),
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: tokens.typography.fontSize.sm,
                                  lineHeight: tokens.typography.lineHeight.md,
                                },
                              ]}
                            >
                              {message.bodyText}
                            </Text>
                          </View>

                          <View
                            style={[
                              styles.flagRow,
                              {
                                gap: tokens.spacing.xs,
                              },
                            ]}
                          >
                            {deliveryLabelKey ? (
                              <View
                                style={[
                                  styles.inlinePill,
                                  {
                                    alignSelf: "flex-start",
                                    backgroundColor:
                                      getMessageDeliveryBackground(
                                        message.deliveryState,
                                        theme,
                                      ),
                                    borderColor: theme.borderSubtle,
                                    borderRadius: tokens.radius.pill,
                                    paddingHorizontal: tokens.spacing.sm,
                                    paddingVertical: tokens.spacing.xs,
                                  },
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.pillValueText,
                                    {
                                      color: getMessageDeliveryTextColor(
                                        message.deliveryState,
                                        theme,
                                      ),
                                      fontFamily: layout.bodyFontFamily,
                                    },
                                  ]}
                                >
                                  {deliveryLabelKey ===
                                  translationKeys.shell
                                    .workspaceChatDeliveryDelivered
                                    ? "Delivered"
                                    : "Sent"}
                                </Text>
                              </View>
                            ) : null}

                            {message.senderKind !== "patient"
                              ? renderWorkspaceFilterChip({
                                  isSelected: false,
                                  labelText:
                                    readAloudControls.activeReadAloudMessageId ===
                                      message.id &&
                                    readAloudControls.isReadingAloud
                                      ? "Stop reading"
                                      : "Read aloud",
                                  onPress: () =>
                                    readAloudControls.readMessageAloud(message),
                                  theme,
                                  tokens,
                                })
                              : null}
                            {message.senderKind !== "patient"
                              ? renderWorkspaceFilterChip({
                                  isSelected: false,
                                  labelText: "Send feedback",
                                  onPress: () =>
                                    onOpenSupportModal({
                                      kind: "chat-feedback",
                                      message,
                                      source: "patient-agent:message-feedback",
                                    }),
                                  theme,
                                  tokens,
                                })
                              : null}
                          </View>

                          {message.review ? (
                            <View
                              style={[
                                styles.detailBodyCard,
                                {
                                  backgroundColor: getMessageReviewBackground(
                                    message.review.state,
                                    theme,
                                  ),
                                  borderColor: theme.borderSubtle,
                                  borderRadius: layout.controlRadius,
                                  gap: tokens.spacing.xs,
                                  padding: tokens.spacing.sm,
                                },
                              ]}
                            >
                              <Text
                                style={[
                                  styles.navigationText,
                                  {
                                    color: getMessageReviewTextColor(
                                      message.review.state,
                                      theme,
                                    ),
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: tokens.typography.fontSize.xs,
                                  },
                                ]}
                              >
                                {message.review.state === "pending-approval"
                                  ? "Pending review"
                                  : message.review.state === "approved"
                                    ? "Approved"
                                    : message.review.state === "approved-edited"
                                      ? "Approved with edits"
                                      : "Rejected"}
                              </Text>
                              {message.review.clinicianNotesText ? (
                                <Text
                                  style={[
                                    styles.sectionSubtitle,
                                    {
                                      color: theme.textSecondary,
                                      fontFamily: layout.bodyFontFamily,
                                      fontSize: tokens.typography.fontSize.xs,
                                      lineHeight:
                                        tokens.typography.lineHeight.sm,
                                    },
                                  ]}
                                >
                                  {message.review.clinicianNotesText}
                                </Text>
                              ) : null}
                            </View>
                          ) : null}
                        </View>
                      </View>
                    </View>
                  </View>
                );
              })
            ) : (
              <View
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: theme.surfaceMuted,
                    borderColor: theme.borderSubtle,
                    borderRadius: layout.controlRadius,
                    padding: tokens.spacing.md,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  {`Hi there. I am your ${resolvePatientBrand().assistantName}. How can I assist you`}
                  today?
                </Text>
              </View>
            )}
          </ScrollView>

          <View
            style={[
              styles.composerRow,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderStrong,
                borderRadius: layout.controlRadius,
                gap: tokens.spacing.sm,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <View
              style={[
                styles.patientMetaGrid,
                {
                  gap: tokens.spacing.sm,
                },
              ]}
            >
              {[
                {
                  id: "composer-program",
                  labelText: "Program",
                  valueText: summary?.programLabel?.trim().length
                    ? summary.programLabel
                    : "Not assigned",
                },
                {
                  id: "composer-coordinator",
                  labelText: "Coordinator",
                  valueText: summary?.coordinatorLabel?.trim().length
                    ? summary.coordinatorLabel
                    : "Not assigned",
                },
                {
                  id: "composer-touchpoint",
                  labelText: "Next touchpoint",
                  valueText: summary?.nextAppointmentText?.trim().length
                    ? summary.nextAppointmentText
                    : "Not scheduled",
                },
              ].map((item) => (
                <View
                  key={item.id}
                  style={[
                    styles.patientMetaCard,
                    {
                      backgroundColor: theme.backgroundSubtle,
                      borderColor: theme.borderSubtle,
                      borderRadius: layout.controlRadius,
                      gap: tokens.spacing.xs,
                      minHeight:
                        item.id === "composer-touchpoint" ? 84 : undefined,
                      padding: tokens.spacing.sm,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {item.labelText}
                  </Text>
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize:
                          item.id === "composer-touchpoint"
                            ? tokens.typography.fontSize.sm
                            : tokens.typography.fontSize.md,
                      },
                    ]}
                  >
                    {item.valueText}
                  </Text>
                </View>
              ))}
            </View>

            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "center",
                  gap: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {`Ask ${patientWorkspaceBrand.assistantName}`}
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                  },
                ]}
              >
                Voice, read-aloud, and prompt macros ready
              </Text>
            </View>

            <View
              style={[
                styles.flagRow,
                {
                  gap: tokens.spacing.xs,
                },
              ]}
            >
              {promptMacroItems.map((item) => (
                <Fragment key={item.id}>
                  {renderWorkspaceFilterChip({
                    isSelected: draftValue.trim() === item.draftText.trim(),
                    labelText: item.labelText,
                    onPress: () => onDraftChange(item.draftText),
                    theme,
                    tokens,
                  })}
                </Fragment>
              ))}
            </View>

            <View
              style={[
                styles.legacyComposerInputSurface,
                {
                  backgroundColor: theme.backgroundSubtle,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  paddingHorizontal: tokens.spacing.md,
                  paddingVertical: tokens.spacing.sm,
                },
              ]}
            >
              <TextInput
                multiline={true}
                onChangeText={onDraftChange}
                placeholder="Ask a question about your medications, appointments, or documents"
                placeholderTextColor={theme.textTertiary}
                style={[
                  styles.composerInput,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
                value={draftValue}
              />
            </View>

            <View
              style={[
                styles.patientRowFooter,
                {
                  gap: tokens.spacing.xs,
                },
              ]}
            >
              {renderWorkbenchActionButton({
                disabled: false,
                label: "Open medications",
                onPress: () => onNavigateTab("medications"),
                size: "compact",
                theme,
                tone: "success",
                tokens,
              })}
              {renderWorkbenchActionButton({
                disabled:
                  draftValue.trim().length === 0 ||
                  isMessageCommandSubmitting ||
                  summary == null,
                label: isMessageCommandSubmitting
                  ? "Sending..."
                  : "Send to care team",
                onPress: () => {
                  const trimmedDraft = draftValue.trim();

                  if (!trimmedDraft) {
                    onNoticeChange("Enter a question before sending.");
                    return;
                  }

                  if (!summary) {
                    onNoticeChange(
                      "No active patient workspace is available for messaging.",
                    );
                    return;
                  }

                  onNoticeChange(
                    "Sending portal message and preparing the review draft...",
                  );

                  void onSendMessage({ bodyText: trimmedDraft }).then(
                    (wasApplied) => {
                      if (wasApplied) {
                        onDraftChange("");
                        onNoticeChange(
                          `Message saved. Check the conversation for ${patientWorkspaceBrand.assistantName}'s response.`,
                        );
                        return;
                      }

                      onNoticeChange(
                        "The message could not be saved. Please retry after the workspace refreshes.",
                      );
                    },
                  );
                },
                size: "compact",
                theme,
                tone: "accent",
                tokens,
              })}
              {renderWorkbenchActionButton({
                disabled: false,
                label: voiceInputControls.isListening
                  ? "Stop voice"
                  : "Voice note",
                onPress: voiceInputControls.toggleListening,
                size: "compact",
                theme,
                tone: "success",
                tokens,
              })}
              {renderWorkbenchActionButton({
                disabled: false,
                label: "Open onboarding",
                onPress: () => onNavigateTab("onboarding"),
                size: "compact",
                theme,
                tone: "accent",
                tokens,
              })}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

function renderPatientWorkspaceActionableListSurface(input: {
  currentReminderWakeUpTime?: string | null;
  detailEyebrowText: string;
  detailTitle: string;
  emptyStateText: string;
  isOnboardingCommandSubmitting?: boolean;
  isWideLayout: boolean;
  items: readonly PatientWorkspaceActionableListItem[];
  layout: typeof designTokens.semanticLayout;
  listEyebrowText: string;
  listSubtitle: string;
  listTitle: string;
  noticeText: string | null;
  onNavigateTab: (value: PatientWorkspaceTabId) => void;
  onNoticeChange: (value: string | null) => void;
  onRecordOnboarding?: (
    input: PatientOnboardingCommandInput,
  ) => Promise<PatientWorkspaceOnboardingCommandResponseV1 | null>;
  selectedItemId: string;
  setSelectedItemId: (value: string) => void;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    currentReminderWakeUpTime,
    detailEyebrowText,
    detailTitle,
    emptyStateText,
    isOnboardingCommandSubmitting,
    isWideLayout,
    items,
    layout,
    listEyebrowText,
    listSubtitle,
    listTitle,
    noticeText,
    onNavigateTab,
    onNoticeChange,
    onRecordOnboarding,
    selectedItemId,
    setSelectedItemId,
    theme,
    tokens,
  } = input;
  const selectedItem =
    items.find((item) => item.id === selectedItemId) ?? items[0] ?? null;
  const selectedItemIndex =
    selectedItem == null
      ? -1
      : items.findIndex((item) => item.id === selectedItem.id);
  const findSelectedDetailValue = (
    matcher: (
      row: PatientWorkspaceActionableListItem["detailRows"][number],
    ) => boolean,
  ) => {
    return selectedItem?.detailRows.find(matcher)?.valueText ?? "";
  };
  const selectedProgramText = findSelectedDetailValue((row) =>
    row.labelText.toLowerCase().includes("program"),
  );
  const selectedContactText = findSelectedDetailValue((row) =>
    row.labelText.toLowerCase().includes("contact"),
  );
  const selectedDocumentText = findSelectedDetailValue(
    (row) =>
      row.labelText.toLowerCase().includes("document") ||
      row.labelText.toLowerCase().includes("record"),
  );
  const selectedMessageText = findSelectedDetailValue((row) =>
    row.labelText.toLowerCase().includes("message"),
  );
  const selectedCoordinatorText = findSelectedDetailValue(
    (row) =>
      row.labelText.toLowerCase().includes("coordinator") ||
      row.labelText.toLowerCase().includes("clinician"),
  );
  const detailSummaryItems =
    selectedItem == null
      ? []
      : [
          {
            id: "detail-step",
            labelText: "Current step",
            valueText:
              selectedItemIndex >= 0
                ? `${selectedItemIndex + 1} of ${items.length}`
                : `1 of ${items.length}`,
            toneColor: theme.accentStrong,
          },
          {
            id: "detail-program",
            labelText: "Program or team",
            valueText:
              selectedProgramText || selectedCoordinatorText || "Not assigned",
            toneColor: theme.textPrimary,
          },
          {
            id: "detail-documents",
            labelText: "Documents",
            valueText: selectedDocumentText || "No document detail",
            toneColor: theme.warning,
          },
          {
            id: "detail-messages",
            labelText: "Messages or contact",
            valueText:
              selectedMessageText || selectedContactText || "No message detail",
            toneColor: theme.info,
          },
        ];
  const detailCommandItems =
    selectedItem == null
      ? []
      : [
          {
            id: "detail-primary",
            label: selectedItem.primaryActionLabel,
            onPress: () => onNoticeChange(selectedItem.primaryActionNotice),
            tone: "accent" as const,
          },
          ...(selectedItem.secondaryActionLabel &&
          selectedItem.secondaryActionNotice
            ? [
                {
                  id: "detail-secondary",
                  label: selectedItem.secondaryActionLabel,
                  onPress: () =>
                    onNoticeChange(selectedItem.secondaryActionNotice ?? null),
                  tone: "success" as const,
                },
              ]
            : []),
          ...(listEyebrowText === "Appointments" ||
          listEyebrowText === "Procedures"
            ? [
                {
                  id: "detail-open-docs",
                  label: "Open documents",
                  onPress: () => onNavigateTab("documents"),
                  tone: "accent" as const,
                },
                {
                  id: "detail-open-messages",
                  label: "Open messages",
                  onPress: () => onNavigateTab("messages"),
                  tone: "success" as const,
                },
              ]
            : listEyebrowText === "Onboarding"
              ? [
                  ...(onRecordOnboarding
                    ? [
                        {
                          id: "detail-request-otp",
                          label: isOnboardingCommandSubmitting
                            ? "Requesting code"
                            : "Request portal OTP",
                          onPress: () => {
                            const promptFn = (
                              globalThis as typeof globalThis & {
                                prompt?: (
                                  message: string,
                                  defaultValue?: string,
                                ) => string | null;
                              }
                            ).prompt;
                            const defaultPhoneNumber = /\d{4,}/u.test(
                              selectedContactText,
                            )
                              ? selectedContactText
                              : "";
                            const requestedPhoneNumber =
                              typeof promptFn === "function"
                                ? promptFn(
                                    "Enter the phone number for this portal code.",
                                    defaultPhoneNumber,
                                  )
                                : defaultPhoneNumber;

                            if (requestedPhoneNumber == null) {
                              onNoticeChange("Portal OTP request cancelled.");
                              return;
                            }

                            onNoticeChange(
                              "Requesting a portal code.",
                            );

                            void onRecordOnboarding({
                              authChallenge: {
                                action: "request",
                                phoneNumber:
                                  requestedPhoneNumber?.trim() || null,
                              },
                              entryPointRoute: "onboarding",
                            }).then((response) => {
                              const developmentCode =
                                response?.authChallenge?.developmentCode;

                              onNoticeChange(
                                response
                                  ? developmentCode
                                    ? `Portal code issued. Use code ${developmentCode}.`
                                    : "Portal code issued."
                                  : "Portal OTP could not be issued. Please try again.",
                              );
                            });
                          },
                          tone: "accent" as const,
                        },
                        {
                          id: "detail-verify-otp",
                          label: isOnboardingCommandSubmitting
                            ? "Verifying code"
                            : "Verify portal OTP",
                          onPress: () => {
                            const promptFn = (
                              globalThis as typeof globalThis & {
                                prompt?: (
                                  message: string,
                                  defaultValue?: string,
                                ) => string | null;
                              }
                            ).prompt;
                            const otpCode =
                              typeof promptFn === "function"
                                ? promptFn("Enter the 6-digit portal OTP code.")
                                : null;

                            if (!otpCode) {
                              onNoticeChange(
                                "Enter a portal OTP code before verification.",
                              );
                              return;
                            }

                            onNoticeChange(
                              "Verifying the portal OTP challenge.",
                            );

                            void onRecordOnboarding({
                              authChallenge: {
                                action: "verify",
                                otpCode,
                              },
                              entryPointRoute: "onboarding",
                            }).then((response) =>
                              onNoticeChange(
                                response?.authChallenge?.status === "verified"
                                  ? "Portal code verified."
                                  : "Portal OTP could not be verified. Request a new code or try again.",
                              ),
                            );
                          },
                          tone: "success" as const,
                        },
                        {
                          id: "detail-save-onboarding",
                          label: isOnboardingCommandSubmitting
                            ? "Saving onboarding"
                            : "Complete onboarding",
                          onPress: () => {
                            onNoticeChange(
                              "Saving onboarding completion and entry route.",
                            );

                            void onRecordOnboarding({
                              entryPointRoute: "onboarding",
                              initialFlowComplete: true,
                            }).then((response) =>
                              onNoticeChange(
                                response
                                  ? "Onboarding completion saved. Verify portal OTP separately if auth is still pending."
                                  : "Onboarding could not be saved. Please try again.",
                              ),
                            );
                          },
                          tone: "accent" as const,
                        },
                        {
                          id: "detail-save-reminder",
                          label: isOnboardingCommandSubmitting
                            ? "Saving reminders"
                            : "Save reminder time",
                          onPress: () => {
                            const wakeUpTime =
                              currentReminderWakeUpTime ??
                              "07:00";
                            const displayTime =
                              formatPatientMobilePreferenceTime(wakeUpTime);

                            onNoticeChange(
                              "Saving reminder preference in the patient workspace.",
                            );

                            void onRecordOnboarding({
                              entryPointRoute: "onboarding",
                              remindersEnabled: true,
                              wakeUpTime,
                            }).then((response) =>
                              onNoticeChange(
                                response
                                  ? `Reminder preference saved for ${displayTime} portal reminders.`
                                  : "Reminder preference could not be saved. Please try again.",
                              ),
                            );
                          },
                          tone: "success" as const,
                        },
                      ]
                    : []),
                  {
                    id: "detail-open-docs",
                    label: "Open documents",
                    onPress: () => onNavigateTab("documents"),
                    tone: "accent" as const,
                  },
                  {
                    id: "detail-open-meds",
                    label: "Open medications",
                    onPress: () => onNavigateTab("medications"),
                    tone: "success" as const,
                  },
                ]
              : []),
        ];

  return (
    <View
      style={[
        styles.workspaceGrid,
        {
          flexDirection: isWideLayout ? "row" : "column",
          gap: tokens.spacing.lg,
        },
      ]}
    >
      <View
        style={[
          styles.surfaceCard,
          {
            backgroundColor: theme.surfaceElevated,
            borderColor: theme.borderStrong,
            borderRadius: layout.cardRadius,
            flex: isWideLayout ? 0.95 : 1,
            gap: tokens.spacing.md,
            padding: tokens.spacing.lg,
          },
        ]}
      >
        {renderLegacySurfaceHeader({
          eyebrowText: listEyebrowText,
          title: listTitle,
          subtitle: listSubtitle,
          layout,
          theme,
          tokens,
        })}

        <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
          {items.length > 0 ? (
            items.map((item) => {
              const isSelected = item.id === selectedItem?.id;
              const palette = getLegacyDetailPreviewPalette(item.tone, theme);

              return (
                <Pressable
                  key={item.id}
                  onPress={() => setSelectedItemId(item.id)}
                  style={[
                    styles.detailBodyCard,
                    {
                      backgroundColor: isSelected
                        ? palette.backgroundColor
                        : theme.surface,
                      borderColor: isSelected
                        ? palette.leadingTextColor
                        : theme.borderSubtle,
                      borderLeftWidth: isSelected ? 4 : 1,
                      borderRadius: layout.controlRadius,
                      gap: tokens.spacing.sm,
                      padding: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.patientRowHeader,
                      {
                        alignItems: "flex-start",
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.detailDocumentLeadColumn,
                        {
                          backgroundColor: palette.leadingBackgroundColor,
                          borderColor: theme.borderSubtle,
                          borderRadius: layout.controlRadius,
                          gap: 2,
                          padding: tokens.spacing.sm,
                          width: 68,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.detailDocumentGlyphText,
                          {
                            color: palette.leadingTextColor,
                            fontFamily: layout.displayFontFamily,
                          },
                        ]}
                      >
                        {item.leadPrimaryText ??
                          resolveLegacyCompactLeadText(item.title)}
                      </Text>
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.xs,
                            textAlign: "center",
                          },
                        ]}
                      >
                        {item.leadSecondaryText ??
                          resolveLegacyPreviewFallbackSecondaryText(item.title)}
                      </Text>
                    </View>

                    <View
                      style={[
                        styles.patientIdentityCopy,
                        {
                          gap: tokens.spacing.xs,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.sm,
                          },
                        ]}
                      >
                        {item.title}
                      </Text>
                      {item.subtitle ? (
                        <Text
                          style={[
                            styles.sectionSubtitle,
                            {
                              color: theme.textPrimary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: tokens.typography.fontSize.sm,
                              lineHeight: tokens.typography.lineHeight.md,
                            },
                          ]}
                        >
                          {item.subtitle}
                        </Text>
                      ) : null}
                      {item.supportingText ? (
                        <Text
                          style={[
                            styles.metricLabel,
                            {
                              color: theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: tokens.typography.fontSize.xs,
                            },
                          ]}
                        >
                          {item.supportingText}
                        </Text>
                      ) : null}
                    </View>
                  </View>
                </Pressable>
              );
            })
          ) : (
            <View
              style={[
                styles.detailBodyCard,
                {
                  backgroundColor: theme.surfaceMuted,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  padding: tokens.spacing.md,
                },
              ]}
            >
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {emptyStateText}
              </Text>
            </View>
          )}
        </View>
      </View>

      <View
        style={[
          styles.surfaceCard,
          {
            backgroundColor: theme.surfaceElevated,
            borderColor: theme.borderStrong,
            borderRadius: layout.cardRadius,
            flex: isWideLayout ? 1.05 : 1,
            gap: tokens.spacing.md,
            padding: tokens.spacing.lg,
          },
        ]}
      >
        {renderLegacySurfaceHeader({
          eyebrowText: detailEyebrowText,
          title: selectedItem?.detailTitle ?? detailTitle,
          subtitle: selectedItem?.detailSummaryText ?? emptyStateText,
          layout,
          theme,
          tokens,
        })}

        {noticeText ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.info,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {noticeText}
          </Text>
        ) : null}

        {selectedItem ? (
          <>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: tokens.typography.fontSize.xl,
                  lineHeight: tokens.typography.lineHeight.xl,
                },
              ]}
            >
              {selectedItem.subtitle ?? selectedItem.title}
            </Text>

            <View
              style={[
                styles.patientMetaGrid,
                {
                  gap: tokens.spacing.sm,
                },
              ]}
            >
              {detailSummaryItems.map((item) => (
                <View
                  key={item.id}
                  style={[
                    styles.patientMetaCard,
                    {
                      backgroundColor: theme.backgroundSubtle,
                      borderColor: theme.borderSubtle,
                      borderRadius: layout.controlRadius,
                      gap: tokens.spacing.xs,
                      minHeight:
                        item.id === "detail-program" ||
                        item.id === "detail-documents" ||
                        item.id === "detail-messages"
                          ? 84
                          : undefined,
                      padding: tokens.spacing.sm,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {item.labelText}
                  </Text>
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: item.toneColor,
                        fontFamily: layout.bodyFontFamily,
                        fontSize:
                          item.id === "detail-step"
                            ? tokens.typography.fontSize.md
                            : tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {item.valueText}
                  </Text>
                </View>
              ))}
            </View>

            <View
              style={[
                styles.patientMetaGrid,
                {
                  gap: tokens.spacing.sm,
                },
              ]}
            >
              {selectedItem.detailRows.map((row) => (
                <View
                  key={row.id}
                  style={[
                    styles.patientMetaCard,
                    {
                      backgroundColor: theme.backgroundSubtle,
                      borderColor: theme.borderSubtle,
                      borderRadius: layout.controlRadius,
                      gap: tokens.spacing.xs,
                      padding: tokens.spacing.sm,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {row.labelText}
                  </Text>
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {row.valueText}
                  </Text>
                </View>
              ))}
            </View>

            <View
              style={[
                styles.listGroup,
                {
                  gap: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    textTransform: "uppercase",
                  },
                ]}
              >
                Self-service actions
              </Text>

              <View
                style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}
              >
                {detailCommandItems.map((action) =>
                  renderWorkbenchActionButton({
                    disabled:
                      Boolean(isOnboardingCommandSubmitting) &&
                      (action.id.startsWith("detail-save-") ||
                        action.id.startsWith("detail-request-") ||
                        action.id.startsWith("detail-verify-")),
                    label: action.label,
                    onPress: action.onPress,
                    size: "compact",
                    theme,
                    tone: action.tone,
                    tokens,
                  }),
                )}
              </View>
            </View>
          </>
        ) : (
          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceMuted,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {emptyStateText}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

function renderPatientWorkspaceMedicationsSurface(input: {
  layout: typeof designTokens.semanticLayout;
  noticeText: string | null;
  onNavigateTab: (value: PatientWorkspaceTabId) => void;
  onNoticeChange: (value: string | null) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  updateRows: PatientWorkspaceSummarySurfaceRow[];
}) {
  const {
    layout,
    noticeText,
    onNavigateTab,
    onNoticeChange,
    summary,
    theme,
    tokens,
    updateRows,
  } = input;
  const medicationRows = buildPatientMedicationWorkspaceRows(
    summary,
    updateRows,
  );
  const medicationSummaryItems = [
    {
      id: "med-summary-rows",
      labelText: "Tracked rows",
      valueText: String(medicationRows.length),
      toneColor: theme.accentStrong,
    },
    {
      id: "med-summary-unread",
      labelText: "Unread updates",
      valueText: String(summary?.unreadMessageCount ?? 0),
      toneColor: theme.info,
    },
    {
      id: "med-summary-docs",
      labelText: "Pending docs",
      valueText: String(summary?.pendingDocumentCount ?? 0),
      toneColor: theme.warning,
    },
    {
      id: "med-summary-team",
      labelText: "Medication support",
      valueText: summary?.coordinatorLabel?.trim().length
        ? summary.coordinatorLabel
        : "Not assigned",
      toneColor: theme.textPrimary,
    },
  ];
  const medicationCommandItems = [
    {
      id: "med-command-refill",
      title: "Request refill",
      subtitle:
        "Surface refill support directly from the medication workspace.",
      label: "Request refill",
      onPress: () =>
        onNoticeChange("Refill request started from the medication workspace."),
      tone: "accent" as const,
    },
    {
      id: "med-command-side-effect",
      title: "Report side effect",
      subtitle:
        "Escalate questions or symptoms without leaving the medication tab.",
      label: "Report issue",
      onPress: () =>
        onNoticeChange(
          "Medication issue reporting started from the medication workspace.",
        ),
      tone: "success" as const,
    },
    {
      id: "med-command-snooze",
      title: "Snooze reminder",
      subtitle:
        "Adjust the next reminder window from the same self-service workspace.",
      label: "Snooze",
      onPress: () =>
        onNoticeChange(
          "Reminder snooze started from the medication workspace.",
        ),
      tone: "accent" as const,
    },
  ];

  return (
    <View
      style={[
        styles.workspaceGrid,
        {
          gap: tokens.spacing.lg,
        },
      ]}
    >
      <View
        style={[
          styles.surfaceCard,
          {
            backgroundColor: theme.surfaceElevated,
            borderColor: theme.borderStrong,
            borderRadius: layout.cardRadius,
            gap: tokens.spacing.md,
            padding: tokens.spacing.lg,
            width: "100%",
          },
        ]}
      >
        {renderLegacySurfaceHeader({
          eyebrowText: "Medications",
          title: "Medication workspace",
          subtitle:
            "Medication notes, timing, and reminder actions.",
          layout,
          theme,
          tokens,
        })}

        <View style={[styles.flagRow, { gap: tokens.spacing.xs }]}>
          {[
            "Wake-up 6:00 AM",
            "Morning meds 8:00 AM",
            "Evening follow-up 9:00 PM",
          ].map((chipText) => (
            <View
              key={chipText}
              style={[
                styles.inlinePill,
                {
                  backgroundColor: theme.backgroundSubtle,
                  borderColor: theme.borderSubtle,
                  borderRadius: tokens.radius.pill,
                  paddingHorizontal: tokens.spacing.sm,
                  paddingVertical: tokens.spacing.xs,
                },
              ]}
            >
              <Text
                style={[
                  styles.pillValueText,
                  {
                    color: theme.accentStrong,
                    fontFamily: layout.bodyFontFamily,
                  },
                ]}
              >
                {chipText}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[
            styles.patientMetaGrid,
            {
              gap: tokens.spacing.sm,
            },
          ]}
        >
          {medicationSummaryItems.map((item) => (
            <View
              key={item.id}
              style={[
                styles.patientMetaCard,
                {
                  backgroundColor: theme.backgroundSubtle,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  gap: tokens.spacing.xs,
                  minHeight: item.id === "med-summary-team" ? 84 : undefined,
                  padding: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: item.toneColor,
                    fontFamily: layout.bodyFontFamily,
                    fontSize:
                      item.id === "med-summary-team"
                        ? tokens.typography.fontSize.sm
                        : tokens.typography.fontSize.md,
                  },
                ]}
              >
                {item.valueText}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[
            styles.patientQuickActionRow,
            {
              alignItems: "stretch",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: tokens.spacing.md,
            },
          ]}
        >
          {medicationCommandItems.map((item) => (
            <View
              key={item.id}
              style={[
                styles.detailSectionCard,
                {
                  backgroundColor: theme.backgroundSubtle,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  flexBasis: 220,
                  flexGrow: 1,
                  gap: tokens.spacing.sm,
                  padding: tokens.spacing.md,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {item.title}
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {item.subtitle}
              </Text>
              {renderWorkbenchActionButton({
                disabled: false,
                label: item.label,
                onPress: item.onPress,
                size: "compact",
                theme,
                tone: item.tone,
                tokens,
              })}
            </View>
          ))}
        </View>

        {noticeText ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.info,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {noticeText}
          </Text>
        ) : null}

        <View
          style={[
            styles.patientDocumentTable,
            {
              borderColor: theme.borderStrong,
              borderRadius: layout.controlRadius,
            },
          ]}
        >
          <View
            style={[
              styles.patientDocumentTableHeader,
              {
                backgroundColor: theme.backgroundSubtle,
                borderBottomColor: theme.borderStrong,
                gap: tokens.spacing.sm,
                paddingHorizontal: tokens.spacing.sm,
                paddingVertical: tokens.spacing.sm,
              },
            ]}
          >
            {[
              { labelText: "Medication", flexValue: 1.5 },
              { labelText: "Routine", flexValue: 0.9 },
              { labelText: "Guidance", flexValue: 1.8 },
              { labelText: "Status", flexValue: 1 },
              { labelText: "Actions", flexValue: 1.2 },
            ].map(({ labelText, flexValue }) => (
              <Text
                key={labelText}
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    flex: flexValue,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {labelText}
              </Text>
            ))}
          </View>

          {medicationRows.map((row, index) => (
            <View
              key={row.id}
              style={[
                styles.patientDocumentTableRow,
                {
                  backgroundColor:
                    index % 2 === 0 ? theme.surface : theme.backgroundSubtle,
                  borderBottomColor: theme.borderSubtle,
                  gap: tokens.spacing.sm,
                  paddingHorizontal: tokens.spacing.sm,
                  paddingVertical: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    flex: 1.5,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {row.labelText}
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textPrimary,
                    flex: 0.9,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {row.routineText}
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textPrimary,
                    flex: 1.8,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {row.noteText}
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.warning,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {row.statusText}
              </Text>
              <View style={[styles.patientDocumentTableCell, { flex: 1.2 }]}>
                {renderWorkbenchActionButton({
                  disabled: false,
                  label: row.actionText,
                  onPress: () => onNoticeChange(row.noticeText),
                  size: "compact",
                  theme,
                  tone: "accent",
                  tokens,
                })}
              </View>
            </View>
          ))}
        </View>

        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {renderWorkbenchActionButton({
            disabled: false,
            label: "Ask nurse",
            onPress: () =>
              onNoticeChange(
                "Medication nurse outreach requested from the medication workspace.",
              ),
            size: "compact",
            theme,
            tone: "success",
            tokens,
          })}
          {renderWorkbenchActionButton({
            disabled: false,
            label: "Launch agent",
            onPress: () => onNavigateTab("messages"),
            size: "compact",
            theme,
            tone: "accent",
            tokens,
          })}
          {renderWorkbenchActionButton({
            disabled: false,
            label: "Open documents",
            onPress: () => onNavigateTab("documents"),
            size: "compact",
            theme,
            tone: "success",
            tokens,
          })}
        </View>
      </View>
    </View>
  );
}

function renderPatientWorkspaceOverviewSurface(input: {
  featureFlags: PatientPortalFeatureFlagsV1;
  isWideLayout: boolean;
  layout: typeof designTokens.semanticLayout;
  noticeText: string | null;
  onNavigateTab: (value: PatientWorkspaceTabId) => void;
  onNoticeChange: (value: string | null) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  updateRows: PatientWorkspaceSummarySurfaceRow[];
}) {
  const {
    featureFlags,
    isWideLayout,
    layout,
    noticeText,
    onNavigateTab,
    onNoticeChange,
    summary,
    theme,
    tokens,
    updateRows,
  } = input;
  const isOverviewFeatureTabEnabled = (
    tabId: PatientWorkspaceTabId | null | undefined,
  ) => tabId == null || isPatientWorkspaceTabEnabled(tabId, featureFlags);
  const homeMilestones = [
    {
      featureTabId: "appointments" as const,
      id: "overview-home-appointment",
      labelText: "Next appointment",
      valueText: summary?.nextAppointmentText ?? "Not scheduled yet",
      supportingText: summary?.contactWindowText?.trim().length
        ? `Contact window ${summary.contactWindowText}`
        : "Contact window not yet confirmed",
      toneColor: theme.info,
    },
    {
      featureTabId: "procedures" as const,
      id: "overview-home-procedure",
      labelText: "Procedure",
      valueText: summary?.procedureSummaryText ?? "No procedure on file",
      supportingText: summary?.statusLabel?.trim().length
        ? `Current status ${summary.statusLabel}`
        : "Status not available",
      toneColor: theme.warning,
    },
    {
      id: "overview-home-care-team",
      labelText: "Care team",
      valueText: summary?.coordinatorLabel ?? "Not assigned",
      supportingText: summary?.programLabel?.trim().length
        ? `Program ${summary.programLabel}`
        : "Program not assigned",
      toneColor: theme.accentStrong,
    },
    {
      featureTabId: "documents" as const,
      id: "overview-home-documents",
      labelText: "Pending documents",
      valueText: String(summary?.pendingDocumentCount ?? 0),
      supportingText:
        (summary?.documentSummaries.length ?? 0) > 0
          ? `${summary?.documentSummaries.length ?? 0} records in workspace`
          : "No records available yet",
      toneColor: theme.textPrimary,
    },
  ].filter(
    (item) =>
      isOverviewFeatureTabEnabled(
        "featureTabId" in item ? item.featureTabId : null,
      ),
  );
  const agendaRows = [
    {
      id: "overview-agenda-check-in",
      title: "Check-in window",
      subtitle: summary?.contactWindowText ?? "Not recorded yet",
      supportingText: summary?.nextAppointmentText?.trim().length
        ? `Next appointment ${summary.nextAppointmentText}`
        : "Appointment not scheduled yet",
      leadPrimaryText: "TODAY",
      leadSecondaryText: "plan",
      tone: "accent" as const,
    },
    {
      id: "overview-agenda-onboarding",
      title: "Onboarding progress",
      subtitle:
        (summary?.pendingDocumentCount ?? 0) > 0
          ? `${summary?.pendingDocumentCount ?? 0} items still pending`
          : "Ready for next step",
      supportingText: summary?.documentSummaries.length
        ? `${summary.documentSummaries.length} records already in workspace`
        : "No records available yet",
      leadPrimaryText: "STEP",
      leadSecondaryText: "next",
      tone: "warning" as const,
    },
  ];
  const actionCenterItems = [
    {
      id: "overview-action-onboarding",
      title: "Finish onboarding",
      subtitle:
        "Review consent, records, medications, and welcome steps from a dedicated onboarding flow.",
      label: "Open onboarding",
      onPress: () => onNavigateTab("onboarding"),
      tone: "accent" as const,
    },
    {
      featureTabId: "documents" as const,
      id: "overview-action-documents",
      title: "Review records",
      subtitle:
        "Go straight to the document workspace to filter files, review records, or download what is ready.",
      label: "Open documents",
      onPress: () => onNavigateTab("documents"),
      tone: "success" as const,
    },
    {
      featureTabId: "messages" as const,
      id: "overview-action-agent",
      title: `Ask ${patientWorkspaceBrand.assistantName}`,
      subtitle:
        "Launch the patient conversation for reminders, intake questions, and care-plan follow-up.",
      label: "Open agent",
      onPress: () => onNavigateTab("messages"),
      tone: "accent" as const,
    },
  ].filter(
    (item) =>
      isOverviewFeatureTabEnabled(
        "featureTabId" in item ? item.featureTabId : null,
      ),
  );
  const medicationsEnabled = isPatientWorkspaceTabEnabled(
    "medications",
    featureFlags,
  );
  const appointmentsEnabled = isPatientWorkspaceTabEnabled(
    "appointments",
    featureFlags,
  );
  const callsEnabled = isPatientWorkspaceTabEnabled("calls", featureFlags);
  const proceduresEnabled = isPatientWorkspaceTabEnabled(
    "procedures",
    featureFlags,
  );
  const documentsEnabled = isPatientWorkspaceTabEnabled(
    "documents",
    featureFlags,
  );

  return (
    <View
      style={[
        styles.workspaceGrid,
        {
          gap: tokens.spacing.lg,
        },
      ]}
    >
      <View
        style={[
          styles.workspaceGrid,
          {
            flexDirection: isWideLayout ? "row" : "column",
            gap: tokens.spacing.lg,
          },
        ]}
      >
        <View
          style={[
            styles.surfaceCard,
            {
              backgroundColor: theme.surfaceElevated,
              borderColor: theme.borderStrong,
              borderRadius: layout.cardRadius,
              flex: isWideLayout ? 1.4 : 1,
              gap: tokens.spacing.md,
              padding: tokens.spacing.lg,
            },
          ]}
        >
          {renderLegacySurfaceHeader({
            eyebrowText: "Home",
            title: `Good afternoon, ${resolvePatientWorkspaceGivenName(summary?.displayName)}`,
            subtitle:
              "Your care plan, next steps, and reminders.",
            layout,
            theme,
            tokens,
          })}

          <Text
            style={[
              styles.sectionTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: tokens.typography.fontSize["2xl"],
                lineHeight: tokens.typography.lineHeight["2xl"],
              },
            ]}
          >
            {summary?.summaryText ?? "Your care plan is being prepared."}
          </Text>

          <View
            style={[
              styles.patientMetaGrid,
              {
                gap: tokens.spacing.sm,
              },
            ]}
          >
            {homeMilestones.map((item) => (
              <View
                key={item.id}
                style={[
                  styles.patientMetaCard,
                  {
                    backgroundColor: theme.backgroundSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: layout.controlRadius,
                    gap: tokens.spacing.xs,
                    minHeight: 88,
                    padding: tokens.spacing.sm,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: item.toneColor,
                      fontFamily: layout.displayFontFamily,
                      fontSize: tokens.typography.fontSize.md,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  {item.valueText}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                    },
                  ]}
                >
                  {item.supportingText}
                </Text>
              </View>
            ))}
          </View>

          {noticeText ? (
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.info,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {noticeText}
            </Text>
          ) : null}
        </View>

        <View
          style={[
            styles.surfaceCard,
            {
              backgroundColor: theme.surfaceElevated,
              borderColor: theme.borderStrong,
              borderRadius: layout.cardRadius,
              flex: isWideLayout ? 0.95 : 1,
              gap: tokens.spacing.md,
              padding: tokens.spacing.lg,
            },
          ]}
        >
          {renderLegacySurfaceHeader({
            eyebrowText: "Action center",
            title: "Self-service actions",
            subtitle:
              "Primary patient actions stay one click away so the patient workspace feels less read-only.",
            layout,
            theme,
            tokens,
          })}

          <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
            {actionCenterItems.map((item) => (
              <View
                key={item.id}
                style={[
                  styles.detailSectionCard,
                  {
                    backgroundColor: theme.backgroundSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: layout.controlRadius,
                    gap: tokens.spacing.sm,
                    padding: tokens.spacing.md,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {item.title}
                </Text>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  {item.subtitle}
                </Text>
                {renderWorkbenchActionButton({
                  disabled: false,
                  label: item.label,
                  onPress: item.onPress,
                  size: "compact",
                  theme,
                  tone: item.tone,
                  tokens,
                })}
              </View>
            ))}

            {callsEnabled || appointmentsEnabled ? (
              <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
                {callsEnabled
                  ? renderWorkbenchActionButton({
                      disabled: false,
                      label: "Request callback",
                      onPress: () =>
                        onNoticeChange(
                          "Callback request queued from the patient home action center.",
                        ),
                      size: "compact",
                      theme,
                      tone: "success",
                      tokens,
                    })
                  : null}
                {appointmentsEnabled
                  ? renderWorkbenchActionButton({
                      disabled: false,
                      label: "Confirm attendance",
                      onPress: () =>
                        onNoticeChange(
                          "Appointment attendance confirmation recorded from the patient home action center.",
                        ),
                      size: "compact",
                      theme,
                      tone: "accent",
                      tokens,
                    })
                  : null}
              </View>
            ) : null}
          </View>
        </View>
      </View>

      <View
        style={[
          styles.workspaceGrid,
          {
            flexDirection: isWideLayout ? "row" : "column",
            gap: tokens.spacing.lg,
          },
        ]}
      >
        {renderPatientWorkspaceSummarySurface({
          eyebrowText: "Today",
          title: "Today's agenda",
          subtitle:
            "Your upcoming care steps.",
          emptyStateText:
            "Your next care step will appear here when the patient workspace summary is available.",
          layout,
          rows: agendaRows,
          theme,
          tokens,
        })}

        {medicationsEnabled
          ? renderPatientWorkspaceSummarySurface({
              eyebrowText: "Medications",
              title: "Medication runway",
              subtitle:
                "Medication guidance and care-team support.",
              emptyStateText:
                "Medication guidance will appear here when the patient workspace summary is available.",
              layout,
              rows: [
                {
                  id: "overview-medication-note",
                  title: "Medication note",
                  subtitle:
                    summary?.medicationSummaryText ?? "No medication note on file",
                  supportingText:
                    (summary?.unreadMessageCount ?? 0) > 0
                      ? `${summary?.unreadMessageCount ?? 0} unread conversation updates`
                      : "No unread conversation updates",
                  leadPrimaryText: "RX",
                  leadSecondaryText: "today",
                  tone: "info",
                },
                {
                  id: "overview-medication-team",
                  title: "Medication support",
                  subtitle: summary?.coordinatorLabel ?? "Not assigned",
                  supportingText: summary?.programLabel?.trim().length
                    ? `Program ${summary.programLabel}`
                    : "Program not assigned",
                  leadPrimaryText: "CARE",
                  leadSecondaryText: "team",
                  tone: "accent",
                },
              ],
              theme,
              tokens,
            })
          : null}
      </View>

      <View
        style={[
          styles.workspaceGrid,
          {
            flexDirection: isWideLayout ? "row" : "column",
            gap: tokens.spacing.lg,
          },
        ]}
      >
        {proceduresEnabled
          ? renderPatientWorkspaceSummarySurface({
              eyebrowText: "Procedure",
              title: "Procedure prep",
              subtitle:
                "Procedure timing and care-plan notes.",
              emptyStateText:
                "Procedure planning details will appear here when they are available.",
              layout,
              rows: [
                {
                  id: "overview-procedure-main",
                  title: "Procedure",
                  subtitle: summary?.procedureSummaryText ?? "No procedure on file",
                  supportingText: summary?.summaryText ?? null,
                  leadPrimaryText: "PROC",
                  leadSecondaryText: "prep",
                  tone: "warning",
                },
                {
                  id: "overview-procedure-window",
                  title: "Contact window",
                  subtitle: summary?.contactWindowText ?? "Not recorded yet",
                  supportingText: summary?.statusLabel ?? null,
                  leadPrimaryText: "TIME",
                  leadSecondaryText: "reach",
                  tone: "neutral",
                },
              ],
              theme,
              tokens,
            })
          : null}

        {documentsEnabled
          ? renderPatientWorkspaceSummarySurface({
              eyebrowText: "Documents",
              title: "Recent documents",
              subtitle:
                "Document summaries stay visible on the home screen so the workspace feels document-first.",
              emptyStateText:
                "Recent patient documents will appear here when they are available.",
              layout,
              rows: buildPatientWorkspaceDocumentRows(summary, 3),
              theme,
              tokens,
            })
          : null}
      </View>

      {renderPatientWorkspaceSummarySurface({
        eyebrowText: "Updates",
        title: "Care updates and reminders",
        subtitle:
          "Tasks, reminders, and outbound updates grouped into one home queue instead of a generic dashboard feed.",
        emptyStateText:
          "No patient tasks or delivery updates are available yet for this workspace.",
        layout,
        rows: updateRows,
        theme,
        tokens,
      })}
    </View>
  );
}

function renderPatientWorkspaceDocumentsSurface(input: {
  availabilityFilter: PatientDocumentAvailabilityFilter;
  dateRangeFilter: PatientDocumentDateRangeFilter;
  isUploadSubmitting: boolean;
  layout: typeof designTokens.semanticLayout;
  noticeText: string | null;
  onAvailabilityFilterChange: (
    value: PatientDocumentAvailabilityFilter,
  ) => void;
  onDateRangeFilterChange: (value: PatientDocumentDateRangeFilter) => void;
  onDownloadDocument: (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => void;
  onReviewDocument: (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => void;
  onSearchQueryChange: (value: string) => void;
  onTypeFilterChange: (value: PatientDocumentTypeFilter) => void;
  onUploadDocument: () => void;
  searchQuery: string;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  typeFilter: PatientDocumentTypeFilter;
}) {
  const {
    availabilityFilter,
    dateRangeFilter,
    isUploadSubmitting,
    layout,
    noticeText,
    onAvailabilityFilterChange,
    onDateRangeFilterChange,
    onDownloadDocument,
    onReviewDocument,
    onSearchQueryChange,
    onTypeFilterChange,
    onUploadDocument,
    searchQuery,
    summary,
    theme,
    tokens,
    typeFilter,
  } = input;
  const filteredDocuments = filterPatientWorkspaceDocuments({
    availabilityFilter,
    dateRangeFilter,
    searchQuery,
    summary,
    typeFilter,
  });
  const totalDocuments = summary?.documentSummaries.length ?? 0;
  const downloadReadyCount =
    summary?.documentSummaries.filter(
      (document) => document.availability === "download-ready",
    ).length ?? 0;
  const metadataOnlyCount =
    summary?.documentSummaries.filter(
      (document) => document.availability === "metadata-only",
    ).length ?? 0;
  const documentSummaryItems = [
    {
      id: "doc-total",
      labelText: "Total records",
      valueText: String(totalDocuments),
      toneColor: theme.accentStrong,
    },
    {
      id: "doc-filtered",
      labelText: "Filtered view",
      valueText: String(filteredDocuments.length),
      toneColor: theme.info,
    },
    {
      id: "doc-ready",
      labelText: "Download ready",
      valueText: String(downloadReadyCount),
      toneColor: theme.success,
    },
    {
      id: "doc-metadata",
      labelText: "Metadata only",
      valueText: String(metadataOnlyCount),
      toneColor: theme.warning,
    },
  ];
  return (
    <View
      style={[
        styles.workspaceGrid,
        {
          gap: tokens.spacing.lg,
        },
      ]}
    >
      <View
        style={[
          styles.surfaceCard,
          {
            backgroundColor: theme.surfaceElevated,
            borderColor: theme.borderStrong,
            borderRadius: layout.cardRadius,
            gap: tokens.spacing.md,
            padding: tokens.spacing.lg,
            width: "100%",
          },
        ]}
      >
        {renderLegacySurfaceHeader({
          eyebrowText: "Documents",
          title: "Document workspace",
          subtitle:
            "Filter, review, and download care documents.",
          layout,
          theme,
          tokens,
        })}

        <View
          style={[
            styles.patientRowFooter,
            {
              gap: tokens.spacing.xs,
              justifyContent: "flex-start",
            },
          ]}
        >
          {renderWorkbenchActionButton({
            disabled: isUploadSubmitting,
            label: isUploadSubmitting ? "Uploading" : "Upload document",
            minWidth: 148,
            onPress: onUploadDocument,
            theme,
            tone: "accent",
            tokens,
          })}
        </View>

        <View
          style={[
            styles.patientMetaGrid,
            {
              gap: tokens.spacing.sm,
            },
          ]}
        >
          {documentSummaryItems.map((item) => (
            <View
              key={item.id}
              style={[
                styles.patientMetaCard,
                {
                  backgroundColor: theme.backgroundSubtle,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  gap: tokens.spacing.xs,
                  padding: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: item.toneColor,
                    fontFamily: layout.displayFontFamily,
                    fontSize: tokens.typography.fontSize.md,
                  },
                ]}
              >
                {item.valueText}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[
            styles.workspaceGrid,
            {
              gap: tokens.spacing.md,
            },
          ]}
        >
          <View
            style={[
              styles.searchSurface,
              {
                alignItems: "center",
                backgroundColor: theme.backgroundSubtle,
                borderColor: theme.borderSubtle,
                borderRadius: tokens.radius.pill,
                flexDirection: "row",
                gap: tokens.spacing.xs,
                paddingHorizontal: tokens.spacing.md,
                paddingVertical: tokens.spacing.xs,
              },
            ]}
          >
            <TextInput
              onChangeText={onSearchQueryChange}
              placeholder="Search documents"
              placeholderTextColor={theme.textTertiary}
              style={[
                styles.searchInput,
                {
                  color: theme.textPrimary,
                  flex: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                },
              ]}
              value={searchQuery}
            />
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textTertiary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                },
              ]}
            >
              Search
            </Text>
          </View>

          <View
            style={[
              styles.workspaceGrid,
              {
                gap: tokens.spacing.sm,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                },
              ]}
            >
              Date range
            </Text>
            <View
              style={[
                styles.flagRow,
                {
                  gap: tokens.spacing.xs,
                },
              ]}
            >
              {[
                ["all", "All dates"],
                ["last-30-days", "Last 30 days"],
                ["last-90-days", "Last 90 days"],
              ].map(([value, labelText]) =>
                renderWorkspaceFilterChip({
                  isSelected: dateRangeFilter === value,
                  labelText,
                  onPress: () =>
                    onDateRangeFilterChange(
                      value as PatientDocumentDateRangeFilter,
                    ),
                  theme,
                  tokens,
                }),
              )}
            </View>
          </View>

          <View
            style={[
              styles.workspaceGrid,
              {
                gap: tokens.spacing.sm,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                },
              ]}
            >
              Document type
            </Text>
            <View
              style={[
                styles.flagRow,
                {
                  gap: tokens.spacing.xs,
                },
              ]}
            >
              {[
                ["all", "All"],
                ["consents", "Consents"],
                ["questionnaires", "Questionnaires"],
                ["lab-results", "Lab results"],
                ["post-procedure-notes", "Post-procedure notes"],
                ["other", "Other"],
              ].map(([value, labelText]) =>
                renderWorkspaceFilterChip({
                  isSelected: typeFilter === value,
                  labelText,
                  onPress: () =>
                    onTypeFilterChange(value as PatientDocumentTypeFilter),
                  theme,
                  tokens,
                }),
              )}
            </View>
          </View>

          <View
            style={[
              styles.workspaceGrid,
              {
                gap: tokens.spacing.sm,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                },
              ]}
            >
              Availability
            </Text>
            <View
              style={[
                styles.flagRow,
                {
                  gap: tokens.spacing.xs,
                },
              ]}
            >
              {[
                ["all", "All records"],
                ["download-ready", "Download ready"],
                ["metadata-only", "Metadata only"],
              ].map(([value, labelText]) =>
                renderWorkspaceFilterChip({
                  isSelected: availabilityFilter === value,
                  labelText,
                  onPress: () =>
                    onAvailabilityFilterChange(
                      value as PatientDocumentAvailabilityFilter,
                    ),
                  theme,
                  tokens,
                }),
              )}
            </View>
          </View>
        </View>

        {noticeText ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.info,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {noticeText}
          </Text>
        ) : null}

        <View
          style={[
            styles.patientDocumentTable,
            {
              borderColor: theme.borderStrong,
              borderRadius: layout.controlRadius,
            },
          ]}
        >
          <View
            style={[
              styles.patientDocumentTableHeader,
              {
                backgroundColor: theme.backgroundSubtle,
                borderBottomColor: theme.borderStrong,
                gap: tokens.spacing.sm,
                paddingHorizontal: tokens.spacing.sm,
                paddingVertical: tokens.spacing.sm,
              },
            ]}
          >
            {[
              { labelText: "Document", flexValue: 2.4 },
              { labelText: "Type", flexValue: 1 },
              { labelText: "Date", flexValue: 1 },
              { labelText: "Status", flexValue: 1.2 },
              { labelText: "Actions", flexValue: 1.6 },
            ].map(({ labelText, flexValue }) => (
              <Text
                key={labelText}
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    flex: flexValue,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {labelText}
              </Text>
            ))}
          </View>

          {filteredDocuments.length > 0 ? (
            filteredDocuments.map((document, index) => {
              const documentType = resolvePatientDocumentType(document.label);
              const canDownload = document.availability === "download-ready";

              return (
                <View
                  key={document.documentId}
                  style={[
                    styles.patientDocumentTableRow,
                    {
                      backgroundColor:
                        index % 2 === 0
                          ? theme.surface
                          : theme.backgroundSubtle,
                      borderBottomColor: theme.borderSubtle,
                      gap: tokens.spacing.sm,
                      paddingHorizontal: tokens.spacing.sm,
                      paddingVertical: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.patientDocumentTableCell,
                      {
                        flex: 2.4,
                        gap: 2,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {document.label}
                    </Text>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                        },
                      ]}
                    >
                      {`${document.createdByLabel} | ${document.summaryText}`}
                    </Text>
                  </View>

                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {humanizeWorkspaceKey(documentType)}
                  </Text>

                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {document.createdAt.slice(0, 10)}
                  </Text>

                  <View
                    style={[
                      styles.patientDocumentTableCell,
                      {
                        flex: 1.2,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.inlinePill,
                        {
                          backgroundColor: canDownload
                            ? theme.backgroundSubtle
                            : theme.surface,
                          borderColor: canDownload
                            ? theme.info
                            : theme.borderSubtle,
                          borderRadius: tokens.radius.pill,
                          paddingHorizontal: tokens.spacing.sm,
                          paddingVertical: tokens.spacing.xs,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.pillValueText,
                          {
                            color: canDownload
                              ? theme.info
                              : theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                          },
                        ]}
                      >
                        {document.statusLabel}
                      </Text>
                    </View>
                  </View>

                  <View
                    style={[
                      styles.patientDocumentTableCell,
                      {
                        flex: 1.6,
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: tokens.spacing.xs,
                      },
                    ]}
                  >
                    {renderWorkbenchActionButton({
                      disabled: !canDownload,
                      label: "Download",
                      minWidth: 96,
                      onPress: () => onDownloadDocument(document),
                      size: "compact",
                      theme,
                      tone: "success",
                      tokens,
                    })}
                    {renderWorkbenchActionButton({
                      disabled: false,
                      label: "Review with AI",
                      minWidth: 116,
                      onPress: () => onReviewDocument(document),
                      size: "compact",
                      theme,
                      tone: "accent",
                      tokens,
                    })}
                  </View>
                </View>
              );
            })
          ) : (
            <View
              style={[
                styles.detailBodyCard,
                {
                  backgroundColor: theme.surfaceMuted,
                  borderColor: theme.borderSubtle,
                  borderRadius: 0,
                  padding: tokens.spacing.md,
                },
              ]}
            >
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {totalDocuments > 0
                  ? "No records match the current document filters."
                  : "No patient-facing documents are available yet for this workspace."}
              </Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

function renderPatientWorkspaceSettingsSurface(input: {
  activeWorkforceSession: WorkforceSessionSummaryV1 | null;
  canSwitchWorkspaces: boolean;
  layout: typeof designTokens.semanticLayout;
  medicationReminderPreferences: PatientMedicationReminderPreferences;
  noticeText: string | null;
  onChangeReminderTime: (
    slotId: PatientMedicationReminderSlotId,
    wakeUpTime: string,
  ) => void;
  onSetRemindersEnabled: (enabled: boolean) => void;
  onSignOut: () => void;
  onStartOnboarding: () => void;
  onUpdateReminderTime: (
    input: PatientMedicationReminderTimeUpdateInput,
  ) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    activeWorkforceSession,
    canSwitchWorkspaces,
    layout,
    medicationReminderPreferences,
    noticeText,
    onChangeReminderTime,
    onSetRemindersEnabled,
    onSignOut,
    onStartOnboarding,
    onUpdateReminderTime,
    summary,
    theme,
    tokens,
  } = input;
  const wakeRows = buildPatientMedicationReminderRows(
    medicationReminderPreferences,
  );
  const reminderRow = wakeRows[0] ?? null;
  const remindersEnabled = summary?.onboarding.preferences.remindersEnabled !== false;
  const patientName = formatPatientDisplayName(
    summary?.legalName ?? summary?.displayName,
  );
  const formattedDateOfBirth = formatPatientProfileDate(summary?.dateOfBirth);
  const identifierRows = [
    {
      label: "Legal name",
      value: patientName,
    },
    ...(formattedDateOfBirth
      ? [
          {
            label: "Date of birth",
            value: formattedDateOfBirth,
          },
        ]
      : []),
    {
      label: "System patient ID",
      value: readPatientProfileText(summary?.identifier),
    },
    ...(summary?.medicalRecordNumber?.trim()
      ? [
          {
            label: "MRN",
            value: summary.medicalRecordNumber.trim(),
          },
        ]
      : []),
  ];
  const careTeamRows = [
    {
      label: "Care coordinator",
      value: readPatientProfileText(summary?.coordinatorLabel, "Not assigned"),
    },
    {
      label: "Primary program",
      value: readPatientProfileText(summary?.programLabel),
    },
    {
      label: "Next appointment",
      value: readPatientProfileText(summary?.nextAppointmentText),
    },
    {
      label: "Contact window",
      value: readPatientProfileText(summary?.contactWindowText),
    },
  ];
  const renderProfileFactCard = (row: { label: string; value: string }) => (
    <View
      key={row.label}
      style={[
        styles.patientMetricTile,
        {
          backgroundColor: patientMobilePalette.white,
          borderColor: patientMobilePalette.line,
          borderRadius: 8,
          minHeight: 68,
          paddingHorizontal: 14,
          paddingVertical: 12,
        },
      ]}
    >
      <Text
        style={[
          styles.metricLabel,
          {
            color: patientMobilePalette.actionBlue,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
            fontWeight: "800",
            lineHeight: tokens.typography.lineHeight.sm,
          },
        ]}
      >
        {row.label}
      </Text>
      <Text
        style={[
          styles.navigationText,
          {
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.md,
            fontWeight: "800",
            lineHeight: tokens.typography.lineHeight.lg,
          },
        ]}
      >
        {row.value}
      </Text>
    </View>
  );

  return (
    <View
      style={[
        styles.workspaceGrid,
        {
          alignSelf: "center",
          gap: 16,
          maxWidth: 820,
          width: "100%",
        },
      ]}
    >
      <View style={[styles.workspaceGrid, { gap: 4 }]}>
        <Text
          style={[
            styles.sectionTitle,
            {
              color: theme.textPrimary,
              fontFamily: layout.displayFontFamily,
              fontSize: tokens.typography.fontSize["2xl"],
              lineHeight: tokens.typography.lineHeight["2xl"],
            },
          ]}
        >
          Profile
        </Text>
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          Patient identity, reminder preferences, and care-team details.
        </Text>
      </View>
      {noticeText ? (
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: theme.info,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          {noticeText}
        </Text>
      ) : null}
      <View
        style={[
          styles.patientSummaryCard,
          {
            backgroundColor: patientMobilePalette.white,
            borderColor: patientMobilePalette.line,
            borderRadius: 10,
            borderWidth: 1,
            gap: 18,
            paddingHorizontal: 22,
            paddingVertical: 20,
          },
        ]}
      >
        <View style={[styles.workspaceGrid, { gap: 12 }]}>
          <Text
            style={[
              styles.navigationText,
              {
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.md,
                fontWeight: "800",
              },
            ]}
          >
            Identifiers
          </Text>
          <View
            style={[
              styles.patientMetricGrid,
              {
                gap: 10,
              },
            ]}
          >
            {identifierRows.map(renderProfileFactCard)}
          </View>
        </View>
        <View style={[styles.workspaceGrid, { gap: 12 }]}>
          <Text
            style={[
              styles.navigationText,
              {
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.md,
                fontWeight: "800",
              },
            ]}
          >
            Preferences
          </Text>
          <Pressable
            onPress={() => onSetRemindersEnabled(!remindersEnabled)}
            style={[
              styles.patientRowHeader,
              {
                backgroundColor: patientMobilePalette.actionSoft,
                borderColor: patientMobilePalette.line,
                borderRadius: 8,
                borderWidth: 1,
                gap: 12,
                paddingHorizontal: 14,
                paddingVertical: 12,
              },
            ]}
          >
            <View
              style={[
                styles.patientDesktopToggle,
                {
                  alignItems: remindersEnabled ? "flex-end" : "flex-start",
                  backgroundColor: remindersEnabled
                    ? patientMobilePalette.actionBlue
                    : patientMobilePalette.softBlue,
                  borderColor: remindersEnabled
                    ? patientMobilePalette.actionBlue
                    : patientMobilePalette.line,
                  borderRadius: 13,
                  borderWidth: 1,
                  height: 24,
                  justifyContent: "center",
                  paddingHorizontal: 3,
                  width: 46,
                },
              ]}
            >
              <View
                style={{
                  backgroundColor: patientMobilePalette.white,
                  borderRadius: 9,
                  height: 18,
                  width: 18,
                }}
              />
            </View>
            <View style={[styles.workspaceGrid, { flex: 1, gap: 2 }]}>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    fontWeight: "800",
                  },
                ]}
              >
                Reminders
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    lineHeight: tokens.typography.lineHeight.sm,
                  },
                ]}
              >
                {remindersEnabled ? "Enabled for this workspace" : "Paused for this workspace"}
              </Text>
            </View>
          </Pressable>
          {reminderRow ? (
            <View
              style={[
                styles.patientDesktopToggleRow,
                {
                  gap: 12,
                  maxWidth: 620,
                },
              ]}
            >
              <View style={[styles.workspaceGrid, { gap: 2, width: 148 }]}>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: patientMobilePalette.actionBlue,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                      fontWeight: "800",
                    },
                  ]}
                >
                  Default reminder time
                </Text>
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.md,
                      fontWeight: "800",
                    },
                  ]}
                >
                  {reminderRow.displayTime}
                </Text>
              </View>
              <View style={[styles.patientMedicationPreferenceSliderHost, { flex: 1 }]}>
                {renderPatientMedicationPreferenceSlider({
                  activeColor: patientMobilePalette.actionBlue,
                  label: "Default reminder time",
                  maxMinutes: reminderRow.maxMinutes,
                  minMinutes: reminderRow.minMinutes,
                  minutes: reminderRow.minutes,
                  onChange: (wakeUpTime) =>
                    onChangeReminderTime(reminderRow.id, wakeUpTime),
                  onCommit: (wakeUpTime) =>
                    onUpdateReminderTime({
                      displayTime:
                        formatPatientMobilePreferenceTime(wakeUpTime),
                      label: "Default",
                      slotId: reminderRow.id,
                      wakeUpTime,
                    }),
                  trackColor: patientMobilePalette.softBlue,
                })}
              </View>
            </View>
          ) : null}
          <View
            style={[
              styles.patientMetricGrid,
              {
                gap: 10,
              },
            ]}
          >
            {renderProfileFactCard({
              label: "Notification channel",
              value: summary?.onboarding.preferences.reminderChannel === "portal"
                ? "In-app"
                : readPatientProfileText(summary?.onboarding.preferences.reminderChannel),
            })}
            {summary?.onboarding.preferences.updatedAt
              ? renderProfileFactCard({
                  label: "Last updated",
                  value: readPatientProfileText(
                    summary.onboarding.preferences.updatedAt,
                  ),
                })
              : null}
          </View>
        </View>
        <View style={[styles.workspaceGrid, { gap: 12 }]}>
          <Text
            style={[
              styles.navigationText,
              {
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.md,
                fontWeight: "800",
              },
            ]}
          >
            Care team
          </Text>
          <View
            style={[
              styles.patientMetricGrid,
              {
                gap: 10,
              },
            ]}
          >
            {careTeamRows.map(renderProfileFactCard)}
          </View>
        </View>
        <View
          style={[
            styles.patientRowFooter,
            {
              gap: 10,
              justifyContent: "flex-start",
            },
          ]}
        >
          <Pressable
            onPress={() => onStartOnboarding()}
            style={[
              styles.patientSupportPrimaryButton,
              {
                backgroundColor: patientMobilePalette.actionSoft,
                borderColor: patientMobilePalette.line,
                borderRadius: 6,
                minHeight: 36,
                paddingHorizontal: 16,
                paddingVertical: 8,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.actionBlue,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: "700",
                },
              ]}
            >
              Launch onboarding guide
            </Text>
          </Pressable>
          <Pressable
            onPress={onSignOut}
            style={[
              styles.patientSupportPrimaryButton,
              {
                backgroundColor: patientMobilePalette.actionSoft,
                borderColor: patientMobilePalette.line,
                borderRadius: 6,
                minHeight: 36,
                paddingHorizontal: 16,
                paddingVertical: 8,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.actionBlue,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: "700",
                },
              ]}
            >
              Sign out
            </Text>
          </Pressable>
        </View>
        {canSwitchWorkspaces ? (
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
              },
            ]}
          >
            {`Active workspace: ${activeWorkforceSession?.displayName ?? summary?.displayName ?? "Patient"}`}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function renderPatientWorkspaceTabContent(input: {
  activeWorkforceSession: WorkforceSessionSummaryV1 | null;
  canSwitchWorkspaces: boolean;
  conversationMessages: ChatMessageViewModel[];
  featureFlags: PatientPortalFeatureFlagsV1;
  isDocumentUploadSubmitting: boolean;
  isMessageCommandSubmitting: boolean;
  isOnboardingCommandSubmitting: boolean;
  isWideLayout: boolean;
  layout: typeof designTokens.semanticLayout;
  medicationReminderPreferences: PatientMedicationReminderPreferences;
  onConversationContentSizeChange: () => void;
  onChangeReminderTime: (
    slotId: PatientMedicationReminderSlotId,
    wakeUpTime: string,
  ) => void;
  onCopyCallTranscript: (voiceCall: PatientWorkspaceVoiceCallSummary) => void;
  onDownloadDocument: (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => void;
  onUploadDocument: () => void;
  onRecordMessage: (input: PatientMessageCommandInput) => Promise<boolean>;
  onRecordOnboarding: (
    input: PatientOnboardingCommandInput,
  ) => Promise<PatientWorkspaceOnboardingCommandResponseV1 | null>;
  onOpenSupportModal: (input: PatientSupportModalState) => void;
  onRequestCallback: () => void;
  onReviewCall: (voiceCall: PatientWorkspaceVoiceCallSummary) => void;
  onSetRemindersEnabled: (enabled: boolean) => void;
  onSignOut: () => void;
  onStartOnboarding: () => void;
  onUpdateReminderTime: (
    input: PatientMedicationReminderTimeUpdateInput,
  ) => void;
  patientCallSearchQuery: string;
  patientDocumentAvailabilityFilter: PatientDocumentAvailabilityFilter;
  patientDocumentDateRangeFilter: PatientDocumentDateRangeFilter;
  patientDocumentSearchQuery: string;
  patientDocumentTypeFilter: PatientDocumentTypeFilter;
  patientDocumentWorkspaceNotice: string | null;
  patientConversationScrollRef: RefObject<ScrollView | null>;
  patientWorkspaceInteractionNotice: string | null;
  patientWorkspaceMessageDraft: string;
  readAloudControls: PatientWorkspaceReadAloudControls;
  selectedPatientWorkspaceTabId: PatientWorkspaceTabId;
  selectedPatientOnboardingItemId: string;
  selectedPatientAppointmentItemId: string;
  selectedPatientProcedureItemId: string;
  setPatientDocumentAvailabilityFilter: (
    value: PatientDocumentAvailabilityFilter,
  ) => void;
  setPatientCallSearchQuery: (value: string) => void;
  setPatientDocumentDateRangeFilter: (
    value: PatientDocumentDateRangeFilter,
  ) => void;
  setPatientDocumentSearchQuery: (value: string) => void;
  setPatientDocumentTypeFilter: (value: PatientDocumentTypeFilter) => void;
  setPatientDocumentWorkspaceNotice: (value: string | null) => void;
  setPatientWorkspaceInteractionNotice: (value: string | null) => void;
  setPatientWorkspaceMessageDraft: (value: string) => void;
  setSelectedPatientWorkspaceTabId: (value: PatientWorkspaceTabId) => void;
  setSelectedPatientOnboardingItemId: (value: string) => void;
  setSelectedPatientAppointmentItemId: (value: string) => void;
  setSelectedPatientProcedureItemId: (value: string) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  voiceInputControls: PatientWorkspaceVoiceInputControls;
}) {
  const {
    activeWorkforceSession,
    canSwitchWorkspaces,
    conversationMessages,
    featureFlags,
    isDocumentUploadSubmitting,
    isMessageCommandSubmitting,
    isOnboardingCommandSubmitting,
    isWideLayout,
    layout,
    medicationReminderPreferences,
    onChangeReminderTime,
    onConversationContentSizeChange,
    onCopyCallTranscript,
    onDownloadDocument,
    onUploadDocument,
    onRecordMessage,
    onRecordOnboarding,
    onOpenSupportModal,
    onRequestCallback,
    onReviewCall,
    onSetRemindersEnabled,
    onSignOut,
    onStartOnboarding,
    onUpdateReminderTime,
    patientCallSearchQuery,
    patientDocumentAvailabilityFilter,
    patientDocumentDateRangeFilter,
    patientDocumentSearchQuery,
    patientDocumentTypeFilter,
    patientDocumentWorkspaceNotice,
    patientConversationScrollRef,
    patientWorkspaceInteractionNotice,
    patientWorkspaceMessageDraft,
    readAloudControls,
    selectedPatientWorkspaceTabId,
    selectedPatientOnboardingItemId,
    selectedPatientAppointmentItemId,
    selectedPatientProcedureItemId,
    setPatientDocumentAvailabilityFilter,
    setPatientCallSearchQuery,
    setPatientDocumentDateRangeFilter,
    setPatientDocumentSearchQuery,
    setPatientDocumentTypeFilter,
    setPatientDocumentWorkspaceNotice,
    setPatientWorkspaceInteractionNotice,
    setPatientWorkspaceMessageDraft,
    setSelectedPatientWorkspaceTabId,
    setSelectedPatientOnboardingItemId,
    setSelectedPatientAppointmentItemId,
    setSelectedPatientProcedureItemId,
    summary,
    theme,
    tokens,
    voiceInputControls,
  } = input;
  const updateRows = buildPatientWorkspaceUpdateRows(summary);
  const navigatePatientWorkspaceTab = (value: PatientWorkspaceTabId) => {
    if (!isPatientWorkspaceTabEnabled(value, featureFlags)) {
      setSelectedPatientWorkspaceTabId("overview");
      setPatientWorkspaceInteractionNotice(
        "That patient portal section is currently unavailable.",
      );
      return;
    }

    if (value === "messages") {
      setPatientWorkspaceMessageDraft("");
    }

    setSelectedPatientWorkspaceTabId(value);
  };
  const renderedPatientWorkspaceTabId = isPatientWorkspaceTabEnabled(
    selectedPatientWorkspaceTabId,
    featureFlags,
  )
    ? selectedPatientWorkspaceTabId
    : "overview";
  const reviewPatientDocumentWithAgent = (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => {
    setPatientDocumentWorkspaceNotice(null);
    setPatientWorkspaceInteractionNotice(
      `Sending document review to ${patientWorkspaceBrand.assistantName}.`,
    );
    void onRecordMessage({
      bodyText: buildPatientWorkspaceDocumentReviewPrompt(document),
    }).then((wasApplied) => {
      setPatientWorkspaceInteractionNotice(
        wasApplied
          ? `Document review request saved. Check messages for ${patientWorkspaceBrand.assistantName}'s response.`
          : "The document review request could not be saved. Please retry after the workspace refreshes.",
      );

      if (wasApplied) {
        navigatePatientWorkspaceTab("messages");
      }
    });
  };
  const sendPatientCareReviewMessage = (input: {
    bodyText: string;
    labelText: string;
  }) => {
    setPatientWorkspaceInteractionNotice(
      `Sending ${input.labelText} review to ${patientWorkspaceBrand.assistantName}.`,
    );
    void onRecordMessage({ bodyText: input.bodyText }).then((wasApplied) => {
      setPatientWorkspaceInteractionNotice(
        wasApplied
          ? `Review request saved. Check messages for ${patientWorkspaceBrand.assistantName}'s response.`
          : "The review request could not be saved. Please retry after the workspace refreshes.",
      );

      if (wasApplied) {
        navigatePatientWorkspaceTab("messages");
      }
    });
  };
  const reviewPatientActionItemWithAgent = (
    tabId: "appointments" | "procedures",
    item: PatientWorkspaceActionableListItem,
  ) => {
    const kindText = tabId === "appointments" ? "appointment" : "procedure";
    const detailText = item.detailRows
      .map((row) => `${row.labelText}: ${row.valueText}`)
      .join("; ");

    sendPatientCareReviewMessage({
      bodyText: [
        `Please review this ${kindText} with me: ${item.title}.`,
        item.subtitle ? `Schedule: ${item.subtitle}.` : null,
        detailText ? `Details: ${detailText}.` : null,
        "Summarize what matters and tell me what I should ask next.",
      ]
        .filter((part): part is string => typeof part === "string")
        .join(" "),
      labelText: kindText,
    });
  };
  const reviewPatientMedicationWithAgent = (
    row: ReturnType<typeof buildPatientMedicationWorkspaceRows>[number],
  ) => {
    sendPatientCareReviewMessage({
      bodyText: [
        `Please review this medication with me: ${row.labelText}.`,
        row.noteText ? `Instructions: ${row.noteText}.` : null,
        row.statusText ? `Status: ${row.statusText}.` : null,
        "Summarize what matters and tell me what I should ask next.",
      ]
        .filter((part): part is string => typeof part === "string")
        .join(" "),
      labelText: "medication",
    });
  };

  if (renderedPatientWorkspaceTabId === "messages") {
    return renderPatientWorkspaceMessagesSurface({
      conversationMessages,
      draftValue: patientWorkspaceMessageDraft,
      isWideLayout,
      isMessageCommandSubmitting,
      layout,
      onConversationContentSizeChange,
      onNavigateTab: navigatePatientWorkspaceTab,
      noticeText: patientWorkspaceInteractionNotice,
      onDraftChange: setPatientWorkspaceMessageDraft,
      onNoticeChange: setPatientWorkspaceInteractionNotice,
      onOpenSupportModal,
      readAloudControls,
      onSendMessage: onRecordMessage,
      patientConversationScrollRef,
      summary,
      theme,
      tokens,
      voiceInputControls,
    });
  }

  if (renderedPatientWorkspaceTabId === "overview") {
    return renderPatientWorkspaceOverviewSurface({
      isWideLayout,
      featureFlags,
      layout,
      noticeText: patientWorkspaceInteractionNotice,
      onNavigateTab: (value) => {
        setPatientWorkspaceInteractionNotice(null);
        if (value === "documents") {
          setPatientDocumentWorkspaceNotice(null);
        }
        if (value === "onboarding") {
          setSelectedPatientOnboardingItemId("onboarding-intake");
        }
        setPatientWorkspaceMessageDraft("");
        setSelectedPatientAppointmentItemId("appointment-visit");
        setSelectedPatientProcedureItemId("procedure-plan");
        navigatePatientWorkspaceTab(value);
      },
      onNoticeChange: setPatientWorkspaceInteractionNotice,
      summary,
      theme,
      tokens,
      updateRows,
    });
  }

  if (renderedPatientWorkspaceTabId === "onboarding") {
    return renderPatientWorkspaceActionableListSurface({
      detailEyebrowText: "Onboarding detail",
      detailTitle: "Onboarding steps",
      currentReminderWakeUpTime:
        summary?.onboarding.preferences.wakeUpTime ?? null,
      emptyStateText:
        "Onboarding steps are not available yet for this patient workspace.",
      isWideLayout,
      isOnboardingCommandSubmitting,
      items: buildPatientWorkspaceOnboardingItems(summary, updateRows),
      layout,
      listEyebrowText: "Onboarding",
      listSubtitle:
        "Intake, records, medications, and welcome steps.",
      listTitle: "Onboarding checklist",
      noticeText: patientWorkspaceInteractionNotice,
      onNavigateTab: navigatePatientWorkspaceTab,
      onNoticeChange: setPatientWorkspaceInteractionNotice,
      onRecordOnboarding,
      selectedItemId: selectedPatientOnboardingItemId,
      setSelectedItemId: setSelectedPatientOnboardingItemId,
      theme,
      tokens,
    });
  }

  if (renderedPatientWorkspaceTabId === "appointments") {
    return renderPatientWorkspaceActionableListSurface({
      detailEyebrowText: "Appointment detail",
      detailTitle: "Visit details",
      emptyStateText:
        "Upcoming appointment details are not available yet for this patient workspace.",
      isWideLayout,
      items: buildPatientWorkspaceAppointmentItems(summary, updateRows),
      layout,
      listEyebrowText: "Appointments",
      listSubtitle:
        "Visits, paperwork, and reminders.",
      listTitle: "Appointment list",
      noticeText: patientWorkspaceInteractionNotice,
      onNavigateTab: navigatePatientWorkspaceTab,
      onNoticeChange: setPatientWorkspaceInteractionNotice,
      selectedItemId: selectedPatientAppointmentItemId,
      setSelectedItemId: setSelectedPatientAppointmentItemId,
      theme,
      tokens,
    });
  }

  if (renderedPatientWorkspaceTabId === "procedures") {
    return renderPatientWorkspaceActionableListSurface({
      detailEyebrowText: "Procedure detail",
      detailTitle: "Procedure plan",
      emptyStateText:
        "Procedure planning details are not available yet for this patient workspace.",
      isWideLayout,
      items: buildPatientWorkspaceProcedureItems(summary, updateRows),
      layout,
      listEyebrowText: "Procedures",
      listSubtitle:
        "Procedure planning, checklist, and recovery details.",
      listTitle: "Procedure list",
      noticeText: patientWorkspaceInteractionNotice,
      onNavigateTab: navigatePatientWorkspaceTab,
      onNoticeChange: setPatientWorkspaceInteractionNotice,
      selectedItemId: selectedPatientProcedureItemId,
      setSelectedItemId: setSelectedPatientProcedureItemId,
      theme,
      tokens,
    });
  }

  if (renderedPatientWorkspaceTabId === "medications") {
    return renderPatientWorkspaceMedicationsSurface({
      layout,
      noticeText: patientWorkspaceInteractionNotice,
      onNavigateTab: navigatePatientWorkspaceTab,
      onNoticeChange: setPatientWorkspaceInteractionNotice,
      summary,
      theme,
      tokens,
      updateRows,
    });
  }

  if (renderedPatientWorkspaceTabId === "calls") {
    return renderPatientWorkspaceCallsSurface({
      layout,
      noticeText: patientWorkspaceInteractionNotice,
      onCopyTranscript: onCopyCallTranscript,
      onNavigateTab: navigatePatientWorkspaceTab,
      onRequestCallback,
      onReviewCall,
      onSearchQueryChange: setPatientCallSearchQuery,
      searchQuery: patientCallSearchQuery,
      summary,
      theme,
      tokens,
    });
  }

  if (renderedPatientWorkspaceTabId === "settings") {
    return renderPatientWorkspaceSettingsSurface({
      activeWorkforceSession,
      canSwitchWorkspaces,
      layout,
      medicationReminderPreferences,
      noticeText: patientWorkspaceInteractionNotice,
      onChangeReminderTime,
      onSetRemindersEnabled,
      onSignOut,
      onStartOnboarding,
      onUpdateReminderTime,
      summary,
      theme,
      tokens,
    });
  }

  return renderPatientWorkspaceDocumentsSurface({
    availabilityFilter: patientDocumentAvailabilityFilter,
    dateRangeFilter: patientDocumentDateRangeFilter,
    layout,
    isUploadSubmitting: isDocumentUploadSubmitting,
    noticeText: patientDocumentWorkspaceNotice,
    onAvailabilityFilterChange: setPatientDocumentAvailabilityFilter,
    onDateRangeFilterChange: setPatientDocumentDateRangeFilter,
    onDownloadDocument,
    onReviewDocument: reviewPatientDocumentWithAgent,
    onUploadDocument,
    onSearchQueryChange: setPatientDocumentSearchQuery,
    onTypeFilterChange: setPatientDocumentTypeFilter,
    searchQuery: patientDocumentSearchQuery,
    summary,
    theme,
    tokens,
    typeFilter: patientDocumentTypeFilter,
  });
}
